/* eslint-disable react/no-unescaped-entities, no-empty */
import { useState, useEffect, useCallback } from 'react';
import StudentsFilterDrawer from '@/components/admin/StudentsFilterDrawer';
import StudentsDownloadModal from '@/components/admin/StudentsDownloadModal';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Users, Student, ChatCircle, Eye, PaperPlaneTilt, XCircle, CheckCircle, ClockCountdown, ClockCounterClockwise, DownloadSimple, FileText, Image as ImageIcon, MagnifyingGlass, CurrencyDollar, Plus, Trash, TrashSimple, EnvelopeSimple, UserCirclePlus, UploadSimple, Key, Buildings, PencilSimple, ArrowsClockwise, ArrowCounterClockwise, DotsThreeVertical, Megaphone, Sparkle, CalendarBlank, Paperclip, FilePdf, CircleNotch, Lock, GraduationCap, UsersThree, UserSwitch, WarningCircle, MapPin, ShieldCheck, IdentificationCard, FunnelSimple } from '@phosphor-icons/react';
import ScoreReportGenerator from '@/components/score-report/ScoreReportGenerator';
import SaFinanceTab from '@/pages/admin/SaFinanceTab';
import FinanceLedgerPanel from '@/components/admin/FinanceLedgerPanel';
import CreditCardsPanel from '@/components/admin/CreditCardsPanel';
import PreAuthorizationPanel from '@/components/admin/PreAuthorizationPanel';
import RecruiterEodPanel from '@/components/admin/RecruiterEodPanel';
import PreAuthorizationModal from '@/components/PreAuthorizationModal';
import SelfCompletePreAuthModal from '@/components/SelfCompletePreAuthModal';
import TrashPanel from '@/pages/admin/TrashPanel';
import TestRequestsPanel from '@/pages/admin/TestRequestsPanel';
import InstitutionsPanel from '@/pages/admin/InstitutionsPanel';
import AdminSubAgentsPanel from '@/pages/admin/AdminSubAgentsPanel';
import InvoiceHistoryPanel from '@/pages/admin/InvoiceHistoryPanel';
import { startImpersonation } from '@/utils/impersonation';
import FinancialsModal, { FinancialsSummary } from '@/components/FinancialsModal';
import NotificationsBell from '@/pages/admin/NotificationsBell';
import IntakeAccordion, { GroupByIntakeToggle, useGroupByIntake } from '@/components/IntakeAccordion';
import { PotentialEarningGrandCard, PotentialEarningPill } from '@/components/PotentialEarning';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from 'recharts';
import { toast } from 'sonner';

import { API } from '@/config/api';
// Auth handled by axios interceptor

const statusColors = { pending: 'bg-yellow-50 text-yellow-700 border-yellow-200', agreement_sent: 'bg-blue-50 text-blue-700 border-blue-200', agreement_signed: 'bg-purple-50 text-purple-700 border-purple-200', active: 'bg-green-50 text-green-700 border-green-200', rejected: 'bg-red-50 text-red-700 border-red-200' };
const statusLabels = { pending: 'Pending', agreement_sent: 'Agreement Sent', agreement_signed: 'Agreement Signed', active: 'Active', rejected: 'Rejected' };
const commStatusColors = { pending: 'bg-yellow-50 text-yellow-700 border-yellow-200', paid: 'bg-green-50 text-green-700 border-green-200', cancelled: 'bg-red-50 text-red-700 border-red-200' };
const appStatusColors = { new: 'bg-yellow-50 text-yellow-700 border-yellow-200', under_review: 'bg-blue-50 text-blue-700 border-blue-200', documents_requested: 'bg-orange-50 text-orange-700 border-orange-200', exam_prep: 'bg-purple-50 text-purple-700 border-purple-200', application_submitted: 'bg-indigo-50 text-indigo-700 border-indigo-200', admitted: 'bg-green-50 text-green-700 border-green-200', enrolled: 'bg-green-50 text-green-800 border-green-300', rejected: 'bg-red-50 text-red-700 border-red-200' };
const appStatusLabels = { new: 'New', under_review: 'Under Review', documents_requested: 'Docs Requested', exam_prep: 'Exam Prep', application_submitted: 'Applied', admitted: 'Admitted', enrolled: 'Enrolled', rejected: 'Rejected' };
const APP_STATUS_OPTIONS = ['new','under_review','documents_requested','exam_prep','application_submitted','admitted','enrolled','rejected'];

// Pipeline stages for student-to-college flow (admin-managed).
// Full pipeline (Aug 2026): adds `docs_under_review` + `docs_verified` between
// application_sent and EA-sent for the institution portal doc-verification flow.
// `coe_sent` / `half_money_received` / `second_money_received` are three
// distinct President-managed stages (previously combined as coe_sent_money_received).
const PIPELINE_STAGES = ['profile_created', 'application_sent', 'docs_under_review', 'docs_verified', 'enrollment_agreement_sent', 'enrollment_agreement_signed', 'sabc_in_process', 'coe_sent', 'half_money_received', 'second_money_received', 'rejected', 'withdrawn'];
const TERMINAL_STAGES = ['rejected', 'withdrawn'];
const PIPELINE_STAGE_LABELS = {
  profile_created: 'Profile Created',
  application_sent: 'Application Sent to College',
  docs_under_review: 'Docs Under Review (College)',
  docs_verified: 'Docs Verified (College)',
  enrollment_agreement_sent: 'Enrollment Agreement Sent',
  enrollment_agreement_signed: 'Enrollment Agreement Signed',
  sabc_in_process: 'SABC In Process',
  coe_sent: 'COE Sent',
  half_money_received: '1/2 Money Received',
  second_money_received: '2/2 Money Received',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};
const PIPELINE_STAGE_COLORS = {
  profile_created: 'bg-blue-100 text-blue-700 border-blue-200',
  application_sent: 'bg-purple-100 text-purple-700 border-purple-200',
  docs_under_review: 'bg-amber-100 text-amber-700 border-amber-200',
  docs_verified: 'bg-teal-100 text-teal-800 border-teal-300',
  enrollment_agreement_sent: 'bg-amber-100 text-amber-800 border-amber-200',
  enrollment_agreement_signed: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  sabc_in_process: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
  coe_sent: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  half_money_received: 'bg-lime-100 text-lime-800 border-lime-300',
  second_money_received: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  withdrawn: 'bg-slate-100 text-slate-700 border-slate-300',
};
const PIPELINE_STAGE_BAR = {
  profile_created: 'bg-blue-500',
  application_sent: 'bg-purple-500',
  docs_under_review: 'bg-amber-400',
  docs_verified: 'bg-teal-500',
  enrollment_agreement_sent: 'bg-amber-500',
  enrollment_agreement_signed: 'bg-yellow-500',
  sabc_in_process: 'bg-fuchsia-500',
  coe_sent: 'bg-cyan-500',
  half_money_received: 'bg-lime-500',
  second_money_received: 'bg-emerald-600',
  rejected: 'bg-red-500',
  withdrawn: 'bg-slate-500',
};
// Realised revenue is locked once a student's second (final) commission is booked.
const REALISED_STAGE = 'second_money_received';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('recruiters');
  const [stats, setStats] = useState(null);
  const [recruiters, setRecruiters] = useState([]);
  const [students, setStudents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [commSummary, setCommSummary] = useState(null);
  const [studentApps, setStudentApps] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [preAuthStudent, setPreAuthStudent] = useState(null);
  const [selfPreAuthStudent, setSelfPreAuthStudent] = useState(null);
  const [studentFiles, setStudentFiles] = useState([]);
  const [studentScoreReports, setStudentScoreReports] = useState([]);
  const [filePreview, setFilePreview] = useState(null);
  const [loading, setLoading] = useState(true);

  // Search/filter
  const [studentSearch, setStudentSearch] = useState('');
  const [studentStatusFilter, setStudentStatusFilter] = useState([]);
  const [studentRecruiterFilter, setStudentRecruiterFilter] = useState('all');

  // Selection for bulk delete
  const [selectedRecruiterIds, setSelectedRecruiterIds] = useState([]);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [announcementDraft, setAnnouncementDraft] = useState({ subject: '', body: '' });
  const [announcementSending, setAnnouncementSending] = useState(false);
  const [showAnnouncementHistory, setShowAnnouncementHistory] = useState(false);
  const [announcementHistory, setAnnouncementHistory] = useState([]);
  const [announcementHistoryLoading, setAnnouncementHistoryLoading] = useState(false);
  const openAnnouncementHistory = async () => {
    setShowAnnouncementHistory(true);
    setAnnouncementHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/announcements`);
      setAnnouncementHistory(Array.isArray(data) ? data : (data.announcements || []));
    } catch {
      toast.error('Failed to load announcement history');
    } finally {
      setAnnouncementHistoryLoading(false);
    }
  };
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [bulkStageValue, setBulkStageValue] = useState('');
  const [bulkStageApplying, setBulkStageApplying] = useState(false);
  const [selectedAppIds, setSelectedAppIds] = useState([]);
  const [selectedCommIds, setSelectedCommIds] = useState([]);

  // ID-match verifier state
  const [idMatchReport, setIdMatchReport] = useState(null);         // single-student report
  const [idMatchLoading, setIdMatchLoading] = useState(false);
  const [showIdMatchDialog, setShowIdMatchDialog] = useState(false);
  const [idMatchStudent, setIdMatchStudent] = useState(null);       // which student the report belongs to
  const [bulkScanJob, setBulkScanJob] = useState(null);             // polled job doc
  const [showBulkScanDialog, setShowBulkScanDialog] = useState(false);
  const [bulkScanStarting, setBulkScanStarting] = useState(false);

  // DocuSign Bulk Upload export dialog
  const [showDsBulkDialog, setShowDsBulkDialog] = useState(false);
  const [dsBulkExporting, setDsBulkExporting] = useState(false);
  const [dsBulkScope, setDsBulkScope] = useState('filtered');       // 'filtered' or 'all'
  const [dsBulkDefaults, setDsBulkDefaults] = useState({
    program_start_date: '',
    program_end_date: '',
    credential: 'Diploma',
    delivery: 'Self Paced/Online',
  });

  // Bulk Stage from Excel dialog (upload a sheet of students → set stage in one shot)
  const [showBulkStageExcelDialog, setShowBulkStageExcelDialog] = useState(false);
  const [bulkStageExcelFile, setBulkStageExcelFile] = useState(null);
  const [bulkStageExcelPreview, setBulkStageExcelPreview] = useState(null);
  const [bulkStageExcelPreviewing, setBulkStageExcelPreviewing] = useState(false);
  const [bulkStageExcelTarget, setBulkStageExcelTarget] = useState('');
  const [bulkStageExcelApplying, setBulkStageExcelApplying] = useState(false);
  const [bulkStageExcelPicks, setBulkStageExcelPicks] = useState({}); // {rowKey: {student_id, label}}
  const [bulkStageExcelCheckedMatched, setBulkStageExcelCheckedMatched] = useState({}); // {student_id: true}
  const [bulkStageExcelCheckedReview, setBulkStageExcelCheckedReview] = useState({});   // {student_id: true}

  // Email compose dialog
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: 'Study Alliance Recruitment Agreement', body: '' });
  const [agreementFile, setAgreementFile] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Commission form
  const [showCommForm, setShowCommForm] = useState(false);
  const [commForm, setCommForm] = useState({ recruiter_id: '', student_id: '', student_name: '', amount: '', description: '' });

  // Recruiter students view
  const [recruiterStudentsView, setRecruiterStudentsView] = useState(null);
  const [recruiterStudents, setRecruiterStudents] = useState([]);

  // Trash (soft-deleted recruiters + students)
  const [trashRecruiters, setTrashRecruiters] = useState([]);
  const [trashStudents, setTrashStudents] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);

  // Colleges
  const [colleges, setColleges] = useState([]);
  const [showCollegeForm, setShowCollegeForm] = useState(false);
  const [editingCollege, setEditingCollege] = useState(null);
  const [collegeForm, setCollegeForm] = useState({ name: '', location: '', country: '', website: '', notes: '', college_fee: '', representative_email: '', programs: [] });
  const [newProgramForm, setNewProgramForm] = useState({ name: '', default_sale_price: '' });
  const [newIntakeForm, setNewIntakeForm] = useState({ start_date: '', end_date: '' });
  // Bulk-assign-intake dialog state: opened from the Students-tab toolbar
  // when >=1 students are selected.
  const [showBulkIntakeDialog, setShowBulkIntakeDialog] = useState(false);
  const [bulkIntakeCollegeId, setBulkIntakeCollegeId] = useState('');
  const [bulkIntakeStart, setBulkIntakeStart] = useState('');
  const [bulkIntakeEnd, setBulkIntakeEnd] = useState('');
  const [bulkIntakeSaving, setBulkIntakeSaving] = useState(false);
  const [studentCollegeFilter, setStudentCollegeFilter] = useState('all');
  const [studentIntakeFilter, setStudentIntakeFilter] = useState('all');
  // New simplified filter/download experience: drawer + download modal.
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [showClassicFilters, setShowClassicFilters] = useState(false);
  // Accuplacer-pending filter: students who have NO English test proof,
  // NO 2-yr Canadian transcript, and WANT the Accuplacer. Registrar uses
  // this to schedule the in-house placement test.
  const [showAccuplacerPending, setShowAccuplacerPending] = useState(false);
  const [adminGroupByIntake, setAdminGroupByIntake] = useGroupByIntake('admin.students.groupByIntake', true);
  // Global system setting — added on top of every student's sale price to
  // compute the final tuition displayed to the student / on reports.
  const [adminFee, setAdminFee] = useState(250);
  const [savingAdminFee, setSavingAdminFee] = useState(false);
  const [adminFeeDraft, setAdminFeeDraft] = useState('');
  // Commission-invoice pending: aggregate count for the yellow banner + a
  // "only pending" filter for the Students tab.
  const [pendingInvoices, setPendingInvoices] = useState(0);
  const [pendingInvoicesFilter, setPendingInvoicesFilter] = useState(false);
  const [markingInvoiceId, setMarkingInvoiceId] = useState(null);
  // ── Commission Invoice Action (bulk) ─────────────────────────────
  // Chooser: pick between "remind recruiter" and "send to college".
  const [invoiceChooserOpen, setInvoiceChooserOpen] = useState(false);
  // Recruiter-reminder popup
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderSubject, setReminderSubject] = useState('Reminder — Please raise your commission invoice');
  const [reminderBody, setReminderBody] = useState('');
  const [reminderPreview, setReminderPreview] = useState(null); // { recruiter_groups, commission_first_pct }
  const [reminderSending, setReminderSending] = useState(false);
  // College-invoice popup
  const [invoicePopupOpen, setInvoicePopupOpen] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null); // full build-preview response
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceSending, setInvoiceSending] = useState(false);
  // Date-range filter on `created_at` (ISO YYYY-MM-DD, inclusive on both ends).
  // Empty string means "no bound on that side".
  const [studentStartDate, setStudentStartDate] = useState('');
  const [studentEndDate, setStudentEndDate] = useState('');
  // "Show only not-yet-downloaded" toggle (Students tab, right of the counter).
  const [showOnlyNotDownloaded, setShowOnlyNotDownloaded] = useState(false);
  const [assigningCollegeFor, setAssigningCollegeFor] = useState(null);
  const [selectedCollegeToAssign, setSelectedCollegeToAssign] = useState('');
  const [selectedProgramToAssign, setSelectedProgramToAssign] = useState('');
  const [salePriceToAssign, setSalePriceToAssign] = useState('');

  // Activation with commission
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [activatingRecruiter, setActivatingRecruiter] = useState(null);
  const [activateCommission, setActivateCommission] = useState('');

  // Editing recruiter commission inline was retired — the row-level
  // Comm./Stud. column and its inline editor were removed once multi-tier
  // pricing moved into the recruiter detail drawer.

  // Commission Tiers (Option A) — admin editor inside Recruiter Detail dialog
  const [tierDraft, setTierDraft] = useState([]); // [{ sale_price, commission_amount }]
  const [newTierRow, setNewTierRow] = useState({ sale_price: '', commission_amount: '' });
  const [savingTiers, setSavingTiers] = useState(false);

  // Inline pricing edit in student detail
  const [pricingDraft, setPricingDraft] = useState({ sale_price: '', college_fee_snapshot: '', recruiter_commission_snapshot: '' });
  const [pricingDirty, setPricingDirty] = useState(false);

  // Messages
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [convMessages, setConvMessages] = useState([]);
  const [messageDraft, setMessageDraft] = useState('');
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Create-recruiter-in-person dialog
  const [showAddRecruiterDialog, setShowAddRecruiterDialog] = useState(false);
  const [newRecruiter, setNewRecruiter] = useState({ full_name: '', email: '', company_name: '', country: 'Canada', phone: '', commission_per_student: '', commission_type: 'flat', commission_percent: '' });
  const [newRecruiterAgreement, setNewRecruiterAgreement] = useState(null);
  // Has the recruiter already signed a paper agreement?
  //   true  → require PDF upload, set status = agreement_signed (in-person walk-in).
  //   false → no PDF needed, set status = pending, admin sends DocuSign next.
  // Defaults to true for the "Add Recruiter (In-Person)" button, false for the
  // lead-conversion path (so we don't prematurely flip agreement_signed=true).
  const [newRecruiterHasSignedAgreement, setNewRecruiterHasSignedAgreement] = useState(true);
  const [creatingRecruiter, setCreatingRecruiter] = useState(false);
  // Tier table for the in-person Add Recruiter dialog (optional)
  const [newRecruiterTiers, setNewRecruiterTiers] = useState([]); // [{ sale_price, commission_amount }]
  const [newRecruiterTierRow, setNewRecruiterTierRow] = useState({ sale_price: '', commission_amount: '' });

  // ===== OUTREACH (cold leads + mass email + AI) =====
  const [outreachLeads, setOutreachLeads] = useState([]);
  const [outreachStats, setOutreachStats] = useState({ total: 0, emailed: 0, new: 0, replied: 0, converted: 0, not_interested: 0 });
  const [outreachSearch, setOutreachSearch] = useState('');
  const [outreachStatusFilter, setOutreachStatusFilter] = useState('all'); // 'all' | 'new' | 'emailed' | 'replied' | 'converted' | 'not_interested'
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [outreachView, setOutreachView] = useState('leads'); // 'leads' | 'history'
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryTotal, setEmailHistoryTotal] = useState(0);
  const [historySearch, setHistorySearch] = useState('');
  const [viewEmailEntry, setViewEmailEntry] = useState(null); // history entry being viewed
  const [showAddLeadDialog, setShowAddLeadDialog] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState({ company_name: '', company_email: '', company_number: '', status: 'new' });
  const [addingLead, setAddingLead] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null); // last import result for review
  const [showMassEmailDialog, setShowMassEmailDialog] = useState(false);
  const [massEmailDraft, setMassEmailDraft] = useState({ subject: '', body: '', delay_seconds: 2 });
  const [sendingMass, setSendingMass] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiBrief, setAIBrief] = useState('');
  const [aiTone, setAITone] = useState('professional and friendly');
  const [aiGenerating, setAIGenerating] = useState(false);
  const [massEmailJob, setMassEmailJob] = useState(null); // live progress
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  // Compose-dialog file attachments (Resend)
  const [composeAttachments, setComposeAttachments] = useState([]); // [{file_id, filename, size, content_type}]
  const [uploadingComposeAttachment, setUploadingComposeAttachment] = useState(false);

  // Direct Leads (student_applications) bulk import
  const [showLeadImportDialog, setShowLeadImportDialog] = useState(false);
  const [leadImportFile, setLeadImportFile] = useState(null);
  const [leadImporting, setLeadImporting] = useState(false);
  const [leadImportResult, setLeadImportResult] = useState(null);

  // Direct Leads — status filter + email + WhatsApp
  const [directLeadsStatusFilter, setDirectLeadsStatusFilter] = useState('all');
  const [directLeadsView, setDirectLeadsView] = useState('leads'); // 'leads' | 'history'
  const [directLeadsEmailHistory, setDirectLeadsEmailHistory] = useState([]);
  const [directLeadsEmailHistoryTotal, setDirectLeadsEmailHistoryTotal] = useState(0);
  const [directLeadsHistorySearch, setDirectLeadsHistorySearch] = useState('');
  // Direct Lead detail dialog (click a row to see full info + email history sent to that lead)
  const [directLeadDetail, setDirectLeadDetail] = useState(null);
  const [directLeadDetailLoading, setDirectLeadDetailLoading] = useState(false);

  // Admin notes per student
  const [noteDialogStudent, setNoteDialogStudent] = useState(null); // {id, full_name}
  const [studentNotes, setStudentNotes] = useState([]);
  const [studentNotesLoading, setStudentNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [showDirectLeadEmailDialog, setShowDirectLeadEmailDialog] = useState(false);
  const [showDirectLeadWhatsAppDialog, setShowDirectLeadWhatsAppDialog] = useState(false);
  const [directLeadEmailDraft, setDirectLeadEmailDraft] = useState({ subject: '', body: '', delay_seconds: 2 });
  const [directLeadEmailAttachments, setDirectLeadEmailAttachments] = useState([]);
  const [directLeadEmailUploadingAttachment, setDirectLeadEmailUploadingAttachment] = useState(false);
  const [directLeadEmailTestAddr, setDirectLeadEmailTestAddr] = useState('');
  const [directLeadEmailSendingTest, setDirectLeadEmailSendingTest] = useState(false);
  const [directLeadEmailSendingMass, setDirectLeadEmailSendingMass] = useState(false);
  const [directLeadEmailJob, setDirectLeadEmailJob] = useState(null);
  const [showDirectLeadAIDialog, setShowDirectLeadAIDialog] = useState(false);
  const [directLeadAIBrief, setDirectLeadAIBrief] = useState('');
  const [directLeadAITone, setDirectLeadAITone] = useState('warm and friendly');
  const [directLeadAIGenerating, setDirectLeadAIGenerating] = useState(false);
  const [directLeadWhatsAppMessage, setDirectLeadWhatsAppMessage] = useState('Hi {{first_name}},\n\nThanks for applying to Study Alliance. I\'d love to set up a quick 15-min chat about your goals — pick a time here: https://studyalliance.ca/call-appointment\n\nStudy Alliance');

  // ===== CALLS / BOOKINGS =====
  const [bookings, setBookings] = useState([]);
  const [bookingsStats, setBookingsStats] = useState({ total: 0, upcoming: 0, scheduled: 0, completed: 0, cancelled: 0, no_show: 0 });
  const [bookingsFilter, setBookingsFilter] = useState('upcoming'); // 'upcoming' | 'all' | 'scheduled' | 'completed' | 'cancelled' | 'no_show'

  // Idle-reminder compose dialog
  const [showIdleReminderDialog, setShowIdleReminderDialog] = useState(false);
  const [idleReminderDraft, setIdleReminderDraft] = useState({ subject: '', body: '', dates_text: '' });
  const [idleReminderCandidates, setIdleReminderCandidates] = useState([]);
  const [idleReminderActiveList, setIdleReminderActiveList] = useState([]);
  const [idleReminderMode, setIdleReminderMode] = useState('auto'); // 'auto' | 'manual'
  const [idleReminderPickedIds, setIdleReminderPickedIds] = useState([]);
  const [idleReminderSearch, setIdleReminderSearch] = useState('');
  const [loadingIdlePreview, setLoadingIdlePreview] = useState(false);
  const [sendingIdleReminder, setSendingIdleReminder] = useState(false);
  const [testEmailValue, setTestEmailValue] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, recRes, stuRes, conRes, commRes, commSumRes, appsRes, collRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/recruiters`),
        axios.get(`${API}/students?limit=5000`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/commissions`),
        axios.get(`${API}/admin/commissions/summary`),
        axios.get(`${API}/admin/student-applications`),
        axios.get(`${API}/admin/colleges`),
      ]);
      // Sort recruiters by student_count DESC, tie-break by recency (newest first),
      // then by name so equal-count rows stay stable across renders.
      const sortedRecs = [...(recRes.data || [])].sort((a, b) => {
        const diff = (b.student_count || 0) - (a.student_count || 0);
        if (diff !== 0) return diff;
        const at = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (bt !== at) return bt - at;
        return (a.full_name || '').localeCompare(b.full_name || '');
      });
      setStats(statsRes.data); setRecruiters(sortedRecs); setStudents(stuRes.data);
      setContacts(conRes.data); setCommissions(commRes.data); setCommSummary(commSumRes.data);
      setStudentApps(appsRes.data); setColleges(collRes.data);
    } catch (err) { console.error('Fetch failed:', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // On mount: fetch the latest scan job so admin can revisit it after refresh.
  // If the latest is still running, resume polling.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/id-match-jobs`);
        const latest = Array.isArray(data) && data.length ? data[0] : null;
        if (!cancelled && latest) {
          setBulkScanJob(latest);
          if (latest.status === 'running' || latest.status === 'queued') {
            const poll = async () => {
              try {
                const { data: job } = await axios.get(`${API}/admin/id-match-jobs/${latest.id}`);
                if (cancelled) return;
                setBulkScanJob(job);
                if (job.status === 'running' || job.status === 'queued') setTimeout(poll, 3000);
              } catch { /* best-effort */ }
            };
            setTimeout(poll, 2000);
          }
        }
      } catch { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load the current admin fee so we can display "sale + fee = tuition" and
  // let admin edit it from the pricing block.
  useEffect(() => {
    axios.get(`${API}/admin-fee`).then(res => {
      const amt = Number(res.data?.amount || 250);
      setAdminFee(amt);
      setAdminFeeDraft(String(amt));
    }).catch(() => {});
  }, []);

  // Poll the pending-invoices count every 45 s so the banner reflects
  // fresh college activity without a full dashboard reload.
  const refreshPendingInvoices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/pending-invoices/count`);
      setPendingInvoices(Number(data?.count || 0));
    } catch { /* silent — banner just stays hidden */ }
  }, []);
  useEffect(() => {
    refreshPendingInvoices();
    const t = setInterval(refreshPendingInvoices, 45000);
    return () => clearInterval(t);
  }, [refreshPendingInvoices]);

  const handleMarkInvoiceRaised = async (student) => {
    if (!student?.id) return;
    if (!window.confirm(`Mark commission invoice as raised for ${student.full_name || 'this student'}?`)) return;
    setMarkingInvoiceId(student.id);
    try {
      await axios.put(`${API}/admin/students/${student.id}/mark-invoice-raised`, { note: '' });
      toast.success('Marked as invoice raised');
      // Optimistically clear on the row + refresh banner
      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, commission_invoice_pending: false } : s));
      refreshPendingInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark invoice raised');
    } finally {
      setMarkingInvoiceId(null);
    }
  };

  // Popup opened from the amber "Raise Invoice" pill on a single student row.
  // Same chooser (Remind Recruiter / Send to College) as the bulk action, but
  // pre-scoped to one student. Detects tranche automatically from flags.
  const openInvoiceActionForStudent = async (student) => {
    if (!student) return;
    const tranche = student.commission_invoice_pending_2 ? 2 : student.commission_invoice_pending ? 1 : null;
    if (!tranche) { toast.error('This student has no pending commission invoice.'); return; }
    setInvoiceLoading(true);
    try {
      const res = await axios.post(`${API}/admin/invoices/build-preview`, { student_ids: [student.id], tranche });
      setInvoicePreview({ ...res.data, tranche });
      setReminderPreview({ recruiter_groups: res.data.recruiter_groups || [], commission_first_pct: res.data.tranche_pct || res.data.commission_first_pct || (tranche === 2 ? 40 : 60), tranche });
      setSelectedStudentIds([student.id]); // scope subsequent actions to this student
      setInvoiceChooserOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to build invoice preview');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // ── Commission Invoice bulk-action handlers ────────────────────────
  // Opened from the "Invoice Action" bulk button. Fires build-preview so both
  // sub-popups (Remind Recruiter + Send to College) have the numbers ready.
  const openInvoiceAction = async () => {
    if (!selectedStudentIds.length) return;
    // Detect tranche — all selected must share one pending flag.
    const pending1 = selectedStudents.every(s => s.commission_invoice_pending);
    const pending2 = selectedStudents.every(s => s.commission_invoice_pending_2);
    if (!pending1 && !pending2) { toast.error('Some selected students are not flagged for a commission invoice.'); return; }
    if (pending1 && pending2) { toast.error('Selection mixes 1/2 and 2/2 pending students — please invoice one tranche at a time.'); return; }
    const tranche = pending2 ? 2 : 1;
    setInvoiceLoading(true);
    try {
      const res = await axios.post(`${API}/admin/invoices/build-preview`, { student_ids: selectedStudentIds, tranche });
      setInvoicePreview({ ...res.data, tranche });
      setReminderPreview({ recruiter_groups: res.data.recruiter_groups || [], commission_first_pct: res.data.tranche_pct || res.data.commission_first_pct || 60, tranche });
      setInvoiceChooserOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to build invoice preview');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Popup A — send the recruiter reminder emails.
  const openReminderPopup = () => {
    setInvoiceChooserOpen(false);
    const pct = reminderPreview?.commission_first_pct ?? 60;
    const groups = reminderPreview?.recruiter_groups || [];
    // Compose a default body preview showing all recruiters. Admin can edit.
    const lines = groups.map(g => {
      const rows = (g.students || []).map(st => `  • ${st.student_name} — C$${(st.commission_first_tranche || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`).join('\n');
      return `[to ${g.recruiter_email || 'unknown'}]\n${rows}`;
    }).join('\n\n');
    setReminderSubject('Reminder — Please raise your commission invoice');
    setReminderBody(
      `Hi,\n\nThe college has confirmed 1/2 Money Received for the following students. ` +
      `Please raise your commission invoice for the amounts listed below (representing ${pct}% of your commission — the remaining ${100 - pct}% follows in the second tranche):\n\n` +
      `${lines}\n\nReply to this email with your invoice PDF at your earliest convenience.\n\nRegards,\nStudy Alliance Admin`
    );
    setReminderOpen(true);
  };

  const sendReminderEmails = async () => {
    if (!selectedStudentIds.length) return;
    setReminderSending(true);
    try {
      const res = await axios.post(`${API}/admin/invoices/remind-recruiter`, {
        student_ids: selectedStudentIds,
        email_subject: reminderSubject,
        email_body_override: reminderBody,
        tranche: reminderPreview?.tranche || 1,
      });
      toast.success(`Reminder sent to ${res.data?.count || 0} recruiter(s)`);
      setReminderOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setReminderSending(false);
    }
  };

  // Popup B — Editable invoice preview
  const openInvoicePopup = () => {
    setInvoiceChooserOpen(false);
    setInvoicePopupOpen(true);
  };

  const updateInvoiceField = (path, value) => {
    setInvoicePreview(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const updateLineItem = (idx, field, value) => {
    setInvoicePreview(prev => {
      if (!prev) return prev;
      const items = [...(prev.line_items || [])];
      const num = ['tuition_fee', 'application_fee', 'first_payment_claimed', 'commission_claimed'].includes(field);
      items[idx] = { ...items[idx], [field]: num ? (parseFloat(value) || 0) : value };
      return { ...prev, line_items: items };
    });
  };

  const removeLineItem = (idx) => {
    setInvoicePreview(prev => {
      if (!prev) return prev;
      const items = (prev.line_items || []).filter((_, i) => i !== idx);
      return { ...prev, line_items: items };
    });
  };

  const invoiceGrandTotal = (() => {
    const items = invoicePreview?.line_items || [];
    return items.reduce((sum, li) => sum + (parseFloat(li.commission_claimed) || 0), 0);
  })();

  const sendInvoiceToCollege = async () => {
    if (!invoicePreview) return;
    if (!invoicePreview.recipient_email) { toast.error('Recipient email is required'); return; }
    if (!(invoicePreview.line_items || []).length) { toast.error('At least one line item is required'); return; }
    if (!window.confirm(`Send Invoice #${invoicePreview.invoice_number} to ${invoicePreview.recipient_email}?\nGrand Total: C$${invoiceGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)) return;
    setInvoiceSending(true);
    try {
      const payload = {
        invoice_number: invoicePreview.invoice_number,
        invoice_date: invoicePreview.invoice_date,
        college_id: invoicePreview.college_id || '',
        recipient_email: invoicePreview.recipient_email,
        email_subject: invoicePreview.email_subject,
        email_body: invoicePreview.email_body,
        billed_to: invoicePreview.billed_to || {},
        line_items: invoicePreview.line_items || [],
        tranche: invoicePreview.tranche || 1,
      };
      const res = await axios.post(`${API}/admin/invoices/send-to-college`, payload);
      toast.success(`Invoice #${res.data?.invoice_number} sent to ${payload.recipient_email}`);
      // Clear pending flag locally on included students so the banner + button
      // reflect the new state without requiring a full refetch.
      const sentIds = new Set((payload.line_items || []).map(li => li.student_id).filter(Boolean));
      const clearField = payload.tranche === 2 ? 'commission_invoice_pending_2' : 'commission_invoice_pending';
      const invField = payload.tranche === 2 ? 'commission_invoice_number_2' : 'commission_invoice_number';
      setStudents(prev => prev.map(s => sentIds.has(s.id) ? { ...s, [clearField]: false, [invField]: res.data?.invoice_number } : s));
      setSelectedStudentIds([]);
      setInvoicePopupOpen(false);
      setInvoicePreview(null);
      refreshPendingInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setInvoiceSending(false);
    }
  };


  const handleSaveAdminFee = async () => {
    const amt = parseFloat(adminFeeDraft);
    if (isNaN(amt) || amt < 0) { toast.error('Enter a valid admin fee (0 or higher)'); return; }
    setSavingAdminFee(true);
    try {
      const res = await axios.put(`${API}/admin/admin-fee`, { amount: amt });
      const saved = Number(res.data?.amount || amt);
      setAdminFee(saved);
      setAdminFeeDraft(String(saved));
      toast.success(`Admin fee updated to $${saved.toLocaleString()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update admin fee');
    } finally {
      setSavingAdminFee(false);
    }
  };

  const loadTrash = useCallback(async () => {
    setTrashLoading(true);
    try {
      const [tr, ts] = await Promise.all([
        axios.get(`${API}/admin/trash/recruiters`),
        axios.get(`${API}/admin/trash/students`),
      ]);
      setTrashRecruiters(tr.data || []);
      setTrashStudents(ts.data || []);
    } catch (err) {
      console.error('Trash fetch failed:', err);
    } finally { setTrashLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'trash') loadTrash(); }, [tab, loadTrash]);

  // Currently-selected student rows — used by the bulk toolbar to hide/show
  // per-selection controls (e.g. "Un-mark downloaded" only appears when at
  // least one of the selected rows is already flagged).
  const selectedStudents = students.filter(s => selectedStudentIds.includes(s.id));

  // A student counts as "has English proof" when EITHER the onboarding
  // Q&A boolean is true OR an English-test-proof document has been
  // uploaded. Mirrors the backend auto-reconcile on upload, so historical
  // records whose boolean was never flipped also drop out of the pending
  // bucket on the next page refresh. Same pattern for the 2-yr diploma.
  const _hasEnglishProof = (s) => (
    s.english_test_proof === true
    || (Array.isArray(s.documents) && s.documents.some(d => d?.doc_type === 'english_test_proof'))
  );
  const _hasCanadian2yr = (s) => (
    s.canadian_transcript_2yr === true
    || (Array.isArray(s.documents) && s.documents.some(d => d?.doc_type === 'canadian_2yr_diploma' || d?.doc_type === 'canadian_transcript_2yr'))
  );
  const _isAccuplacerPending = (s) => (
    !_hasEnglishProof(s)
    && !_hasCanadian2yr(s)
    && s.accuplacer_intent === true
  );

  const filteredStudents = students.filter(s => {
    const effectiveStage = s.application_stage || 'profile_created';
    if (studentStatusFilter.length > 0 && !studentStatusFilter.includes(effectiveStage)) return false;
    if (studentRecruiterFilter !== 'all' && s.recruiter_id !== studentRecruiterFilter) return false;
    if (studentCollegeFilter !== 'all') {
      if (studentCollegeFilter === 'unassigned') { if (s.assigned_college) return false; }
      else if (s.assigned_college !== studentCollegeFilter) return false;
    }
    if (studentIntakeFilter !== 'all') {
      if (studentIntakeFilter === 'unassigned') { if (s.intake_label || s.intake_date) return false; }
      else if ((s.intake_label || '') !== studentIntakeFilter) return false;
    }
    if (studentSearch) { const q = studentSearch.toLowerCase(); if (!(s.full_name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q) || s.preferred_program?.toLowerCase().includes(q) || s.nationality?.toLowerCase().includes(q))) return false; }
    // Date-range filter is inclusive on both ends. Compare against `created_at`
    // truncated to YYYY-MM-DD so ISO timestamps like 2026-04-19T04:28:39 match cleanly.
    if (studentStartDate || studentEndDate) {
      const created = (s.created_at || '').slice(0, 10);
      if (!created) return false;
      if (studentStartDate && created < studentStartDate) return false;
      if (studentEndDate && created > studentEndDate) return false;
    }
    if (showOnlyNotDownloaded && s.last_downloaded_at) return false;
    if (pendingInvoicesFilter && !s.commission_invoice_pending) return false;
    if (showAccuplacerPending && !_isAccuplacerPending(s)) return false;
    return true;
  });

  // Pre-compute the Accuplacer-pending count so we can show it in the
  // filter chip. Does NOT apply the other filters — we want the chip to
  // show the TOTAL pool so admin always knows how many need scheduling.
  const accuplacerPendingCount = students.filter(_isAccuplacerPending).length;

  // Potential SA earning for a list of students. Admin-only; sums
  //   max(sale − college_fee − recruiter_commission, 0)
  // over every non-rejected student (deleted students never enter the array).
  // Used by the grand card at the top of the Students tab and by the per-intake
  // pill in the accordion header.
  const potentialFor = (list) => {
    let total = 0;
    for (const s of list || []) {
      if (TERMINAL_STAGES.includes(s.application_stage || 'profile_created')) continue;
      const collegeObj = colleges.find(c => c.id === s.assigned_college);
      const recObj = recruiters.find(r => r.id === s.recruiter_id);
      const sale = Number(s.sale_price || 0);
      const fee = Number(s.college_fee_snapshot ?? collegeObj?.college_fee ?? collegeObj?.commission_amount ?? 0);
      const comm = Number(s.recruiter_commission_snapshot ?? recObj?.commission_per_student ?? 0);
      total += Math.max(sale - fee - comm, 0);
    }
    return total;
  };
  // Dropdown source = canonical recruiters list from /admin/recruiters (which
  // KNOWS all recruiters including any whose students weren't fetched due to
  // the client-side page cap). Falls back to whatever the students state
  // contains if the recruiters fetch hasn't completed yet.
  const uniqueRecruiters = recruiters.length
    ? recruiters.map(r => ({ id: r.id, name: r.full_name || r.email || r.id }))
    : [...new Map(students.map(s => [s.recruiter_id, { id: s.recruiter_id, name: s.recruiter_name }]).filter(([k]) => k)).values()];

  // When admin narrows by a specific recruiter, refetch students from the
  // server with ?recruiter_id=X so we're never limited by the initial 5000-row
  // fetch. Guarantees the count in the recruiters list (e.g. Mehul: 18) always
  // matches what the Students-tab filter shows.
  useEffect(() => {
    if (studentRecruiterFilter === 'all') return;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/students?limit=5000&recruiter_id=${encodeURIComponent(studentRecruiterFilter)}`);
        if (!active) return;
        const rid = studentRecruiterFilter;
        setStudents(prev => {
          // Keep rows for other recruiters intact; replace only rows for the
          // selected recruiter with the freshly-fetched, un-truncated set.
          const others = prev.filter(s => s.recruiter_id !== rid);
          return [...others, ...res.data];
        });
      } catch (err) { console.error('Recruiter-scoped student refetch failed:', err); }
    })();
    return () => { active = false; };
  }, [studentRecruiterFilter]);

  // Handlers
  const handleViewStudent = async (student) => {
    setSelectedStudent(student); setStudentFiles([]); setFilePreview(null); setStudentScoreReports([]);
    try { const res = await axios.get(`${API}/files/student/${student.id}`); setStudentFiles(res.data); } catch {}
    // Fetch saved Score Reports (Accuplacer) for this student. Non-blocking.
    try { const res = await axios.get(`${API}/admin/students/${student.id}/score-reports`); setStudentScoreReports(res.data || []); } catch {}
  };

  const handleDownloadStudentZip = async (student) => {
    const t = toast.loading(`Preparing ${student.full_name}'s zip…`);
    try {
      const res = await axios.get(`${API}/admin/students/${student.id}/download-zip`, { responseType: 'blob' });
      // Try to read the filename from Content-Disposition; fall back to a reasonable default
      const dispo = res.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(dispo);
      const filename = match ? match[1] : `${(student.recruiter_name||'Direct').replace(/\s+/g,'_')}__${(student.full_name||'Student').replace(/\s+/g,'_')}_application.zip`;
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Downloaded ${filename}`, { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to download', { id: t });
    }
  };
  // MIME → extension lookup used by `withExtension`. Short list covering what
  // students actually upload; anything missing falls back to the raw filename
  // so we never silently rename something exotic. Note: this is intentionally
  // *local* to the admin Dashboard — recruiter/institution views have their
  // own download helpers.
  const MIME_EXT = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'image/heic': '.heic',
    'image/heif': '.heif',
    'image/gif': '.gif',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/zip': '.zip',
    'text/csv': '.csv',
    'text/plain': '.txt',
  };
  const withExtension = (filename, contentType, blobType) => {
    const name = (filename || '').trim() || 'download';
    // Already carries an extension? Keep it. Guard: last dot must be in the
    // last 6 chars and followed by 1–5 letters/digits.
    if (/\.[A-Za-z0-9]{1,5}$/.test(name)) return name;
    const mime = (contentType || blobType || '').split(';')[0].trim().toLowerCase();
    const ext = MIME_EXT[mime] || '';
    return ext ? `${name}${ext}` : name;
  };

  const handleDownloadFile = async (file) => {
    try {
      const res = await axios.get(`${API}/files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      // Guarantee an extension on the download name so Windows doesn't treat
      // extensionless uploads (e.g. "Passport") as a generic disk file. The
      // extension comes from the server-reported content_type when the stored
      // filename didn't already carry one.
      const safeName = withExtension(file.original_filename, file.content_type, res.data?.type);
      if (file.content_type?.startsWith('image/')) setFilePreview({ url, type: 'image', name: safeName });
      else if (file.content_type === 'application/pdf') setFilePreview({ url, type: 'pdf', name: safeName });
      else {
        const a = document.createElement('a'); a.href = url; a.download = safeName; a.click(); URL.revokeObjectURL(url);
      }
    } catch (err) {
      // Surface the real reason — generic "Download failed" was useless for debugging
      // backfilled score-report files that pointed to a now-missing storage blob.
      const status = err?.response?.status;
      let detail = '';
      try {
        if (err?.response?.data) {
          // responseType blob — need to read it as text to get the JSON detail
          const txt = await err.response.data.text?.();
          try { detail = JSON.parse(txt)?.detail || txt; } catch { detail = txt; }
        }
      } catch { /* ignore */ }
      toast.error(status ? `Download failed (${status}): ${detail || 'unknown error'}` : 'Download failed');
      // eslint-disable-next-line no-console
      console.error('download failed', { file, status, detail, err });
    }
  };

  const handleSendAgreement = (recruiter) => {
    const isResend = ['agreement_sent', 'agreement_signed', 'active'].includes(recruiter.status);
    const isRenewal = recruiter.status === 'active';
    setEmailTarget({ ...recruiter, _isResend: isResend, _isRenewal: isRenewal });
    setEmailForm({
      subject: isRenewal
        ? 'Renewed Study Alliance Recruitment Agreement'
        : isResend
          ? 'Updated Study Alliance Recruitment Agreement'
          : 'Study Alliance Recruitment Agreement',
      body: isRenewal
        ? `Dear ${recruiter.full_name},\n\nPlease find the renewed partnership agreement attached for your signature. Your existing account remains active — once you sign the new agreement it will supersede the previous one on file.\n\nKindly review and sign at your earliest convenience.\n\nBest regards,\nStudy Alliance Team`
        : isResend
          ? `Dear ${recruiter.full_name},\n\nPlease find the updated partnership agreement attached for your signature. The previous version has been superseded.\n\nKindly review and sign at your earliest convenience.\n\nBest regards,\nStudy Alliance Team`
          : `Dear ${recruiter.full_name},\n\nThank you for your interest in becoming a Study Alliance recruitment partner. We are pleased to move forward with your application.\n\nPlease find the partnership agreement attached. Kindly review the terms and sign it at your earliest convenience.\n\nBest regards,\nStudy Alliance Team`,
    });
    setAgreementFile(null);
    setShowEmailCompose(true);
  };

  const handleSendEmailCompose = async () => {
    if (!emailTarget) return;
    if (!agreementFile) { toast.error('Please upload the agreement PDF for this recruiter'); return; }
    setSendingEmail(true);
    try {
      const formData = new FormData();
      formData.append('subject', emailForm.subject);
      formData.append('body', emailForm.body);
      formData.append('agreement_file', agreementFile);
      const res = await axios.post(`${API}/admin/recruiters/${emailTarget.id}/send-agreement`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const method = res.data.method;
      if (method === 'docusign') {
        toast.success('Agreement sent via DocuSign! Recruiter will receive an email from DocuSign to sign.');
      } else if (method === 'email') {
        toast.success('Agreement sent via email with signing link!');
      } else {
        toast.success(res.data.message);
      }
      setShowEmailCompose(false); setEmailTarget(null); setAgreementFile(null);
      fetchData();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = detail
        ? (typeof detail === 'string' ? detail : JSON.stringify(detail))
        : (err?.message || 'Unknown error');
      // Surface the underlying reason so we can diagnose prod issues (e.g. 502/520
      // means the backend was unreachable — different fix path than a 4xx from us).
      toast.error(`Failed to send agreement${status ? ` (${status})` : ''}: ${msg}`);
      console.error('send-agreement error:', { status, detail, err });
    } finally { setSendingEmail(false); }
  };

  const handleReject = async (id) => { try { await axios.post(`${API}/admin/recruiters/${id}/reject`, {}); toast.success('Recruiter rejected'); fetchData(); } catch { toast.error('Failed'); } };
  const openActivateDialog = (recruiter) => {
    setActivatingRecruiter(recruiter);
    setActivateCommission(recruiter.commission_per_student ? String(recruiter.commission_per_student) : '');
    setShowActivateDialog(true);
  };
  const handleConfirmActivate = async () => {
    if (!activatingRecruiter) return;
    const amt = activateCommission === '' ? null : parseFloat(activateCommission);
    if (activateCommission !== '' && (isNaN(amt) || amt < 0)) { toast.error('Enter a valid non-negative number or leave empty'); return; }
    try {
      const res = await axios.post(`${API}/admin/recruiters/${activatingRecruiter.id}/activate`, amt !== null ? { commission_per_student: amt } : {});
      toast.success(res.data.message);
      if (res.data.temp_password) toast.info(`Temp password: ${res.data.temp_password}`, { duration: 10000 });
      setShowActivateDialog(false);
      setActivatingRecruiter(null);
      setActivateCommission('');
      fetchData();
    } catch { toast.error('Activation failed'); }
  };

  // Commission Tiers handlers
  const handleAddTier = () => {
    const sp = parseFloat(newTierRow.sale_price);
    const ca = parseFloat(newTierRow.commission_amount);
    if (isNaN(sp) || sp < 0) { toast.error('Enter a valid sale price'); return; }
    if (isNaN(ca) || ca < 0) { toast.error('Enter a valid commission'); return; }
    if (tierDraft.some(t => Number(t.sale_price) === sp)) {
      toast.error('A tier with this sale price already exists'); return;
    }
    const next = [...tierDraft, { sale_price: sp, commission_amount: ca }].sort((a, b) => Number(a.sale_price) - Number(b.sale_price));
    setTierDraft(next);
    setNewTierRow({ sale_price: '', commission_amount: '' });
  };
  const handleRemoveTier = (idx) => {
    setTierDraft(prev => prev.filter((_, i) => i !== idx));
  };
  const handleEditTier = (idx, field, value) => {
    setTierDraft(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };
  const handleSaveTiers = async () => {
    if (!selectedRecruiter) return;
    // Validate all rows have numeric values
    const cleaned = [];
    for (const t of tierDraft) {
      const sp = parseFloat(t.sale_price);
      const ca = parseFloat(t.commission_amount);
      if (isNaN(sp) || sp < 0 || isNaN(ca) || ca < 0) {
        toast.error('All tiers must have non-negative numbers'); return;
      }
      cleaned.push({ sale_price: sp, commission_amount: ca });
    }
    cleaned.sort((a, b) => a.sale_price - b.sale_price);
    setSavingTiers(true);
    try {
      const res = await axios.put(`${API}/admin/recruiters/${selectedRecruiter.id}/tiers`, { tiers: cleaned });
      toast.success('Commission tiers saved');
      setTierDraft(res.data.tiers || cleaned);
      // Update the recruiter object locally so it persists if user reopens dialog
      setRecruiters(prev => prev.map(r => r.id === selectedRecruiter.id ? { ...r, commission_tiers: res.data.tiers || cleaned } : r));
      setSelectedRecruiter(prev => prev ? { ...prev, commission_tiers: res.data.tiers || cleaned } : prev);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save tiers');
    } finally {
      setSavingTiers(false);
    }
  };
  const handleSendLoginInfo = async (id) => { try { const res = await axios.post(`${API}/admin/recruiters/${id}/send-login-info`); toast.success(res.data.message); if (res.data.temp_password) toast.info(`New temp password: ${res.data.temp_password}`, { duration: 10000 }); fetchData(); } catch { toast.error('Failed to send login info'); } };

  // ===== OUTREACH HANDLERS =====
  const fetchOutreach = useCallback(async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/outreach/leads`),
        axios.get(`${API}/admin/outreach/stats`),
      ]);
      setOutreachLeads(leadsRes.data || []);
      setOutreachStats(statsRes.data || { total: 0, emailed: 0, new: 0 });
    } catch (err) {
      console.error('Outreach fetch failed', err);
    }
  }, []);

  useEffect(() => {
    if (tab === 'outreach') fetchOutreach();
  }, [tab, fetchOutreach]);

  const fetchEmailHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/outreach/email-history`);
      setEmailHistory(res.data.items || []);
      setEmailHistoryTotal(res.data.total || 0);
    } catch (err) {
      console.error('Email history fetch failed', err);
    }
  }, []);

  useEffect(() => {
    if (tab === 'outreach' && outreachView === 'history') fetchEmailHistory();
  }, [tab, outreachView, fetchEmailHistory]);

  const fetchDirectLeadsEmailHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/student-applications/email-history`);
      setDirectLeadsEmailHistory(res.data.items || []);
      setDirectLeadsEmailHistoryTotal(res.data.total || 0);
    } catch (err) {
      console.error('Direct-leads email history fetch failed', err);
    }
  }, []);

  useEffect(() => {
    if (tab === 'applications' && directLeadsView === 'history') fetchDirectLeadsEmailHistory();
  }, [tab, directLeadsView, fetchDirectLeadsEmailHistory]);

  const fetchBookings = useCallback(async () => {
    try {
      const [list, st] = await Promise.all([
        axios.get(`${API}/admin/outreach/bookings`),
        axios.get(`${API}/admin/outreach/bookings/stats`),
      ]);
      setBookings(list.data || []);
      setBookingsStats(st.data || {});
    } catch (err) {
      console.error('Bookings fetch failed', err);
    }
  }, []);

  useEffect(() => {
    if (tab === 'calls') {
      fetchBookings();
      const iv = setInterval(fetchBookings, 30000); // refresh every 30s
      return () => clearInterval(iv);
    }
  }, [tab, fetchBookings]);

  const handleSetBookingStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/outreach/bookings/${id}/status`, { status });
      toast.success(`Marked as ${status.replace('_', ' ')}`);
      fetchBookings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };
  const handleDeleteBooking = async (id) => {
    if (!window.confirm('Delete this booking? The lead will not be notified.')) return;
    try {
      await axios.delete(`${API}/admin/outreach/bookings/${id}`);
      toast.success('Booking deleted');
      fetchBookings();
    } catch { toast.error('Delete failed'); }
  };

  const handleAddLead = async () => {
    if (!addLeadForm.company_email.trim()) { toast.error('Email is required'); return; }
    setAddingLead(true);
    try {
      await axios.post(`${API}/admin/outreach/leads`, addLeadForm);
      toast.success('Lead added');
      setShowAddLeadDialog(false);
      setAddLeadForm({ company_name: '', company_email: '', company_number: '', status: 'new' });
      fetchOutreach();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add lead');
    } finally {
      setAddingLead(false);
    }
  };

  const handleImportLeads = async () => {
    if (!importFile) { toast.error('Pick a file first'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await axios.post(`${API}/admin/outreach/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { inserted, duplicates, invalid, total_rows } = res.data;
      toast.success(`Imported ${inserted} new leads (${duplicates} already in DB, ${invalid} skipped, ${total_rows} total parsed)`);
      setImportReport(res.data);
      setShowImportDialog(false);
      setImportFile(null);
      fetchOutreach();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleToggleLeadSelection = (id) => {
    setSelectedLeadIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const handleToggleAllLeads = () => {
    const visible = outreachLeads.filter(l => {
      if (!outreachSearch) return true;
      const s = outreachSearch.toLowerCase();
      return (l.company_name || '').toLowerCase().includes(s) || (l.company_email || '').toLowerCase().includes(s) || (l.company_number || '').toLowerCase().includes(s);
    });
    const allIds = visible.map(l => l.id);
    const allSelected = allIds.every(id => selectedLeadIds.includes(id));
    setSelectedLeadIds(allSelected ? selectedLeadIds.filter(id => !allIds.includes(id)) : Array.from(new Set([...selectedLeadIds, ...allIds])));
  };

  const handleDeleteLead = async (id) => {
    try {
      await axios.delete(`${API}/admin/outreach/leads/${id}`);
      toast.success('Lead deleted');
      setSelectedLeadIds(prev => prev.filter(x => x !== id));
      fetchOutreach();
    } catch { toast.error('Delete failed'); }
  };
  const handleBulkDeleteLeads = async () => {
    if (selectedLeadIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedLeadIds.length} leads? This cannot be undone.`)) return;
    try {
      const res = await axios.post(`${API}/admin/outreach/leads/bulk-delete`, { ids: selectedLeadIds });
      toast.success(`Deleted ${res.data.deleted} leads`);
      setSelectedLeadIds([]);
      fetchOutreach();
    } catch { toast.error('Bulk delete failed'); }
  };

  const handleSetLeadStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/admin/outreach/leads/${leadId}/status`, { status });
      toast.success(
        status === 'replied' ? 'Marked as replied' :
        status === 'converted' ? 'Marked as converted' :
        status === 'not_interested' ? 'Marked as not interested' :
        status === 'new' ? 'Reset to new' :
        'Status updated'
      );
      fetchOutreach();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleConvertToRecruiter = async (lead) => {
    // Mark lead as converted in outreach, then open the Add Recruiter dialog
    // pre-filled with this lead's details. IMPORTANT: by default the new
    // recruiter is created as `pending` (no signed agreement yet) — the admin
    // then clicks "Send Agreement" to issue the DocuSign. Previously the form
    // forced an immediate `agreement_signed` flag which was a P0 data-integrity
    // bug because the recruiter hadn't actually signed anything yet.
    try {
      await axios.put(`${API}/admin/outreach/leads/${lead.id}/status`, { status: 'converted' });
      toast.info('Opening recruiter form…');
      setNewRecruiter({
        full_name: lead.company_name || '',
        email: lead.company_email || '',
        company_name: lead.company_name || '',
        country: 'Canada',
        phone: lead.company_number || '',
        commission_per_student: '',
      });
      setNewRecruiterAgreement(null);
      setNewRecruiterHasSignedAgreement(false); // ← default to NO signed agreement for lead conversion
      setNewRecruiterTiers([]);
      setNewRecruiterTierRow({ sale_price: '', commission_amount: '' });
      setShowAddRecruiterDialog(true);
      fetchOutreach();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to convert');
    }
  };

  const handleGenerateAI = async () => {
    if (!aiBrief.trim()) { toast.error('Tell the AI what to write'); return; }
    setAIGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/outreach/ai-suggest`, { brief: aiBrief, tone: aiTone });
      setMassEmailDraft(prev => ({ ...prev, subject: res.data.subject, body: res.data.body }));
      toast.success('AI draft inserted — feel free to edit');
      setShowAIDialog(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI generation failed');
    } finally {
      setAIGenerating(false);
    }
  };

  const pollJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/admin/outreach/jobs/${jobId}`);
      setMassEmailJob(res.data);
      if (res.data.status === 'completed') {
        toast.success(`Mass email finished: ${res.data.sent} sent, ${res.data.failed} failed`);
        fetchOutreach();
        return;
      }
      setTimeout(() => pollJob(jobId), 2000);
    } catch {
      // silently stop polling on error
    }
  };

  const handleSendTestEmail = async () => {
    const to = (testEmail || '').trim();
    if (!to) { toast.error('Enter an email to send test to'); return; }
    if (!massEmailDraft.subject.trim() || !massEmailDraft.body.trim()) {
      toast.error('Subject and body must be filled out before testing'); return;
    }
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/admin/outreach/send-test`, {
        to_email: to,
        subject: massEmailDraft.subject,
        body: massEmailDraft.body,
        attachment_ids: composeAttachments.map(a => a.file_id),
      });
      toast.success(`Test sent to ${to} — check your inbox`);
      // eslint-disable-next-line no-unused-vars
      const _ = res;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendMassEmail = async () => {
    if (selectedLeadIds.length === 0) { toast.error('No leads selected'); return; }
    if (!massEmailDraft.subject.trim() || !massEmailDraft.body.trim()) { toast.error('Subject and body are required'); return; }
    setSendingMass(true);
    try {
      const res = await axios.post(`${API}/admin/outreach/mass-email`, {
        lead_ids: selectedLeadIds,
        subject: massEmailDraft.subject,
        body: massEmailDraft.body,
        delay_seconds: Number(massEmailDraft.delay_seconds) || 0,
        attachment_ids: composeAttachments.map(a => a.file_id),
      });
      toast.success(`Started sending to ${res.data.total} leads — running in background`);
      setMassEmailJob({ id: res.data.job_id, total: res.data.total, sent: 0, failed: 0, status: 'running' });
      setShowMassEmailDialog(false);
      setComposeAttachments([]);
      pollJob(res.data.job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSendingMass(false);
    }
  };

  const handleComposeAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingComposeAttachment(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`"${f.name}" is larger than 10 MB and was skipped`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/admin/outreach/upload-attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setComposeAttachments(prev => [...prev, res.data]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingComposeAttachment(false);
      // Reset the input so picking the same file again still triggers onChange
      e.target.value = '';
    }
  };

  const handleRemoveComposeAttachment = async (fileId) => {
    try {
      await axios.delete(`${API}/admin/outreach/attachments/${fileId}`);
    } catch {
      // best-effort — even if the delete fails on the backend, drop it from the local list
    }
    setComposeAttachments(prev => prev.filter(a => a.file_id !== fileId));
  };

  const formatAttachmentSize = (bytes) => {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleToggleHideCommission = async (checked) => {
    if (!selectedRecruiter) return;
    try {
      await axios.put(`${API}/admin/recruiters/${selectedRecruiter.id}/hide-commission`, { hide_commission: checked });
      toast.success(checked ? 'Commission hidden from this recruiter' : 'Commission visible to this recruiter');
      setSelectedRecruiter(prev => prev ? { ...prev, hide_commission: checked } : prev);
      setRecruiters(prev => prev.map(r => r.id === selectedRecruiter.id ? { ...r, hide_commission: checked } : r));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update commission visibility');
    }
  };

  const handleToggleDeclarationForm = async (checked) => {
    if (!selectedRecruiter) return;
    try {
      await axios.put(`${API}/admin/recruiters/${selectedRecruiter.id}/declaration-form-enabled`, { enabled: checked });
      toast.success(checked ? 'Student Declaration Form enabled for this recruiter' : 'Student Declaration Form disabled for this recruiter');
      setSelectedRecruiter(prev => prev ? { ...prev, declaration_form_enabled: checked } : prev);
      setRecruiters(prev => prev.map(r => r.id === selectedRecruiter.id ? { ...r, declaration_form_enabled: checked } : r));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update declaration form setting');
    }
  };

  // Admin whitelist flip for self-completing Pre-Authorization. When ON, the
  // recruiter sees "Self-Complete" instead of the email flow on EA-signed rows.
  const handleToggleSelfPreAuth = async (checked) => {
    if (!selectedRecruiter) return;
    try {
      await axios.put(`${API}/admin/recruiters/${selectedRecruiter.id}/pre-auth-permissions`, { can_self_complete_pre_auth: checked });
      toast.success(checked ? 'Self-Complete Pre-Authorization enabled for this recruiter' : 'Self-Complete Pre-Authorization disabled for this recruiter');
      setSelectedRecruiter(prev => prev ? { ...prev, can_self_complete_pre_auth: checked } : prev);
      setRecruiters(prev => prev.map(r => r.id === selectedRecruiter.id ? { ...r, can_self_complete_pre_auth: checked } : r));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update self-complete setting');
    }
  };

  // Admin-edit recruiter profile (name, email, phone, company, etc.) and reset password
  const [editingRecruiter, setEditingRecruiter] = useState(false);
  const [recruiterEditForm, setRecruiterEditForm] = useState({ full_name: '', company_name: '', email: '', phone: '', country: '', city: '', years_of_experience: '', message: '' });
  const [savingRecruiterEdit, setSavingRecruiterEdit] = useState(false);
  const [recruiterNewPassword, setRecruiterNewPassword] = useState('');
  const [recruiterMustChangePw, setRecruiterMustChangePw] = useState(true);
  const [settingRecruiterPassword, setSettingRecruiterPassword] = useState(false);

  const beginEditRecruiter = () => {
    if (!selectedRecruiter) return;
    setRecruiterEditForm({
      full_name: selectedRecruiter.full_name || '',
      company_name: selectedRecruiter.company_name || '',
      email: selectedRecruiter.email || '',
      phone: selectedRecruiter.phone || '',
      country: selectedRecruiter.country || '',
      city: selectedRecruiter.city || '',
      years_of_experience: selectedRecruiter.years_of_experience || '',
      message: selectedRecruiter.message || '',
    });
    setEditingRecruiter(true);
  };

  const handleSaveRecruiterEdit = async () => {
    if (!selectedRecruiter) return;
    setSavingRecruiterEdit(true);
    try {
      const res = await axios.put(`${API}/admin/recruiters/${selectedRecruiter.id}`, recruiterEditForm);
      const updated = res.data?.recruiter;
      if (updated) {
        setSelectedRecruiter(prev => prev ? { ...prev, ...updated } : prev);
        setRecruiters(prev => prev.map(r => r.id === selectedRecruiter.id ? { ...r, ...updated } : r));
      }
      toast.success('Profile updated');
      setEditingRecruiter(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update profile');
    } finally { setSavingRecruiterEdit(false); }
  };

  const handleSetRecruiterPassword = async () => {
    if (!selectedRecruiter) return;
    if (!recruiterNewPassword || recruiterNewPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!window.confirm(`Set a new login password for ${selectedRecruiter.email}? They will be able to sign in immediately with the new password.`)) return;
    setSettingRecruiterPassword(true);
    try {
      await axios.post(`${API}/admin/recruiters/${selectedRecruiter.id}/set-password`, { new_password: recruiterNewPassword, must_change_password: recruiterMustChangePw });
      toast.success('Password updated. Share the new password with the recruiter securely.');
      setRecruiterNewPassword('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update password');
    } finally { setSettingRecruiterPassword(false); }
  };

  // Admin can toggle a student's "uploaded on Student Aid BC" flag too
  const handleAdminToggleSABC = async (studentId, current) => {
    try {
      const res = await axios.patch(`${API}/students/${studentId}/student-aid-bc`, { uploaded: !current });
      // update selected student + table list
      setSelectedStudent(prev => prev && prev.id === studentId ? { ...prev,
        student_aid_bc_uploaded: res.data.student_aid_bc_uploaded,
        student_aid_bc_uploaded_at: res.data.student_aid_bc_uploaded_at,
        student_aid_bc_uploaded_by: res.data.student_aid_bc_uploaded_by,
      } : prev);
      setStudents(prev => prev.map(s => s.id === studentId ? { ...s,
        student_aid_bc_uploaded: res.data.student_aid_bc_uploaded,
        student_aid_bc_uploaded_at: res.data.student_aid_bc_uploaded_at,
      } : s));
      toast.success(res.data.student_aid_bc_uploaded ? 'Marked uploaded on Student Aid BC' : 'Marked as not uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  // One-click "ping this recruiter" — opens the idle-reminder dialog with this recruiter
  // pre-selected in manual mode so admin can review/edit before sending.
  const handlePingSingleRecruiter = async (recruiterId) => {
    setLoadingIdlePreview(true);
    setShowIdleReminderDialog(true);
    setTestEmailValue('');
    setIdleReminderMode('manual');
    setIdleReminderPickedIds([recruiterId]);
    setIdleReminderSearch('');
    try {
      const res = await axios.get(`${API}/admin/recruiters/idle-reminders/preview`);
      setIdleReminderDraft({
        subject: res.data.default_subject || '',
        body: res.data.default_body || '',
        dates_text: res.data.default_dates || '',
      });
      setIdleReminderCandidates(res.data.candidates || []);
      setIdleReminderActiveList(res.data.active_recruiters || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load preview');
      setShowIdleReminderDialog(false);
    } finally {
      setLoadingIdlePreview(false);
    }
  };
  const openIdleReminderDialog = async () => {
    setLoadingIdlePreview(true);
    setShowIdleReminderDialog(true);
    setTestEmailValue('');
    setIdleReminderMode('auto');
    setIdleReminderPickedIds([]);
    setIdleReminderSearch('');
    try {
      const res = await axios.get(`${API}/admin/recruiters/idle-reminders/preview`);
      setIdleReminderDraft({
        subject: res.data.default_subject || '',
        body: res.data.default_body || '',
        dates_text: res.data.default_dates || '',
      });
      setIdleReminderCandidates(res.data.candidates || []);
      setIdleReminderActiveList(res.data.active_recruiters || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load preview');
      setShowIdleReminderDialog(false);
    } finally {
      setLoadingIdlePreview(false);
    }
  };

  const handleSendIdleReminders = async (testEmail = null) => {
    if (!idleReminderDraft.subject.trim() || !idleReminderDraft.body.trim()) {
      toast.error('Subject and message body are required'); return;
    }
    const isManual = !testEmail && idleReminderMode === 'manual';
    if (isManual && idleReminderPickedIds.length === 0) {
      toast.error('Pick at least one recruiter to send to'); return;
    }
    if (!testEmail && !isManual && idleReminderCandidates.length === 0) {
      if (!window.confirm('No idle recruiters match the criteria right now (everyone is either active or in cooldown). Send anyway?')) return;
    }
    setSendingIdleReminder(true);
    try {
      const payload = {
        subject: idleReminderDraft.subject,
        body: idleReminderDraft.body,
        dates_text: idleReminderDraft.dates_text,
      };
      if (testEmail) payload.test_email = testEmail;
      else if (isManual) payload.recipient_ids = idleReminderPickedIds;
      const res = await axios.post(`${API}/admin/recruiters/send-idle-reminders`, payload);
      toast.success(res.data.message);
      if (!testEmail) {
        setShowIdleReminderDialog(false);
        fetchData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingIdleReminder(false);
    }
  };

  // Open the signed agreement PDF in a new tab (Bearer token required)
  const handleViewAgreement = async (recruiterId) => {
    try {
      const res = await axios.get(`${API}/admin/recruiters/${recruiterId}/agreement-file`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: res.data.type || 'application/pdf' }));
      const win = window.open(blobUrl, '_blank');
      if (!win) toast.error('Pop-up blocked — allow pop-ups to view the agreement');
      // Revoke after a delay so the tab has time to load
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load agreement');
    }
  };

  // Open the recruiter's uploaded business license (from signup) in a new tab
  const handleViewBusinessLicense = async (recruiterId) => {
    try {
      const res = await axios.get(`${API}/admin/recruiters/${recruiterId}/business-license-file`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: res.data.type || 'application/octet-stream' }));
      const win = window.open(blobUrl, '_blank');
      if (!win) toast.error('Pop-up blocked — allow pop-ups to view the license');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load business license');
    }
  };

  // Re-check DocuSign envelope status — moves status to agreement_signed if recipient has signed
  const [refreshingStatusFor, setRefreshingStatusFor] = useState(null);
  const handleRefreshAgreementStatus = async (recruiterId) => {
    setRefreshingStatusFor(recruiterId);
    try {
      const res = await axios.get(`${API}/admin/recruiters/${recruiterId}/docusign-status`);
      const ds = res.data?.status;
      if (ds === 'completed') {
        toast.success('Signed! Status updated — you can now Activate this recruiter.');
      } else if (ds === 'no_envelope') {
        toast.info('No DocuSign envelope for this recruiter (was likely sent as HTML fallback).');
      } else {
        toast.info(`DocuSign status: ${ds || 'unknown'}`);
      }
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to check status');
    } finally {
      setRefreshingStatusFor(null);
    }
  };

  // Create recruiter manually. Either with a paper-signed agreement (in-person)
  // or as a pending lead-conversion (no PDF, status=pending, admin will send
  // DocuSign next). Driven by `newRecruiterHasSignedAgreement`.
  const handleCreateRecruiterInPerson = async () => {
    if (!newRecruiter.full_name.trim() || !newRecruiter.email.trim()) {
      toast.error('Full name and email are required'); return;
    }
    if (newRecruiterHasSignedAgreement) {
      if (!newRecruiterAgreement) {
        toast.error('Signed agreement PDF is required'); return;
      }
      if (newRecruiterAgreement.type !== 'application/pdf' && !newRecruiterAgreement.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Agreement must be a PDF'); return;
      }
    }
    setCreatingRecruiter(true);
    try {
      const fd = new FormData();
      fd.append('full_name', newRecruiter.full_name.trim());
      fd.append('email', newRecruiter.email.trim());
      fd.append('company_name', newRecruiter.company_name.trim());
      fd.append('country', newRecruiter.country.trim());
      fd.append('phone', newRecruiter.phone.trim());
      fd.append('commission_type', newRecruiter.commission_type || 'flat');
      if ((newRecruiter.commission_type || 'flat') === 'percent') {
        fd.append('commission_percent', String(Number(newRecruiter.commission_percent) || 0));
        fd.append('commission_per_student', '0');
      } else {
        fd.append('commission_per_student', String(Number(newRecruiter.commission_per_student) || 0));
        fd.append('commission_percent', '0');
      }
      fd.append('has_signed_agreement', newRecruiterHasSignedAgreement ? 'true' : 'false');
      if (newRecruiterHasSignedAgreement && newRecruiterAgreement) {
        fd.append('agreement_file', newRecruiterAgreement);
      }
      const res = await axios.post(`${API}/admin/recruiters/create-in-person`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // If admin added any commission tiers, persist them in a follow-up call
      const cleanedTiers = (newRecruiterTiers || [])
        .map(t => ({ sale_price: parseFloat(t.sale_price), commission_amount: parseFloat(t.commission_amount) }))
        .filter(t => !isNaN(t.sale_price) && !isNaN(t.commission_amount) && t.sale_price >= 0 && t.commission_amount >= 0);
      const newRecruiterId = res?.data?.recruiter?.id;
      if (cleanedTiers.length > 0 && newRecruiterId) {
        try {
          await axios.put(`${API}/admin/recruiters/${newRecruiterId}/tiers`, { tiers: cleanedTiers });
        } catch (e) {
          toast.error('Recruiter created, but failed to save commission tiers — please add them from the recruiter details.');
        }
      }
      toast.success(res.data.message || (newRecruiterHasSignedAgreement ? 'Recruiter created. Click Activate to send login.' : 'Recruiter created. Click Send Agreement to issue DocuSign.'));
      setShowAddRecruiterDialog(false);
      setNewRecruiter({ full_name: '', email: '', company_name: '', country: 'Canada', phone: '', commission_per_student: '', commission_type: 'flat', commission_percent: '' });
      setNewRecruiterAgreement(null);
      setNewRecruiterHasSignedAgreement(true);
      setNewRecruiterTiers([]);
      setNewRecruiterTierRow({ sale_price: '', commission_amount: '' });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create recruiter');
    } finally {
      setCreatingRecruiter(false);
    }
  };

  // Delete handlers
  const handleDeleteRecruiter = async (id) => { if (!window.confirm('Move this recruiter to Trash? You can restore them from the Trash tab.')) return; try { await axios.delete(`${API}/admin/recruiters/${id}`); toast.success('Recruiter moved to Trash'); fetchData(); } catch { toast.error('Failed to delete'); } };
  const handleBulkDeleteRecruiters = async () => { if (!selectedRecruiterIds.length) return; if (!window.confirm(`Move ${selectedRecruiterIds.length} recruiter(s) to Trash? You can restore them from the Trash tab.`)) return; try { await axios.post(`${API}/admin/recruiters/bulk-delete`, { ids: selectedRecruiterIds }); toast.success(`${selectedRecruiterIds.length} recruiter(s) moved to Trash`); setSelectedRecruiterIds([]); fetchData(); } catch { toast.error('Failed'); } };

  const handleSendAnnouncement = async () => {
    const subject = (announcementDraft.subject || '').trim();
    const body = (announcementDraft.body || '').trim();
    if (!subject || !body) { toast.error('Subject and body are both required'); return; }
    if (!selectedRecruiterIds.length) { toast.error('Pick at least one recruiter'); return; }
    setAnnouncementSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/announcements`, {
        subject, body, recipient_ids: selectedRecruiterIds,
      });
      toast.success(`Announcement sent to ${data.sent}/${data.total} recruiter${data.total === 1 ? '' : 's'}`);
      setShowAnnouncementDialog(false);
      setAnnouncementDraft({ subject: '', body: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setAnnouncementSending(false);
    }
  };

  // Impersonation — swap the admin's token for a short-lived recruiter token
  // and jump into their dashboard. Backup + banner + exit are handled by
  // /utils/impersonation + <ImpersonationBanner/>.
  const handleImpersonateRecruiter = async (r) => {
    if (!window.confirm(`Log in as ${r.full_name || r.email}? You'll see their dashboard exactly as they see it. Use the yellow banner to exit.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/recruiters/${r.id}/impersonate`);
      startImpersonation({ token: data.token, info: data.impersonating });
      toast.success(`Viewing as ${data.impersonating.name}`);
      // Full reload so AuthContext, WS hooks, and axios interceptor all pick up the new token.
      window.location.href = data.impersonating.redirect_to || '/recruiter';
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Impersonation failed');
    }
  };
  const handleDeleteStudent = async (id) => { if (!window.confirm('Move this student to Trash? You can restore them from the Trash tab.')) return; try { await axios.delete(`${API}/admin/students/${id}`); toast.success('Student moved to Trash'); fetchData(); } catch { toast.error('Failed to delete'); } };
  const handleBulkDeleteStudents = async () => { if (!selectedStudentIds.length) return; if (!window.confirm(`Move ${selectedStudentIds.length} student(s) to Trash? You can restore them from the Trash tab.`)) return; try { await axios.post(`${API}/admin/students/bulk-delete`, { ids: selectedStudentIds }); toast.success(`${selectedStudentIds.length} student(s) moved to Trash`); setSelectedStudentIds([]); fetchData(); } catch { toast.error('Failed'); } };

  // ----- PDC Declaration (Oct 2026 intake, EA signed) -----
  // Admin can email the "Confirmation of Enrollment & Tuition Payment
  // Undertaking" PDF to the student, with point 4 auto-stamped as
  // (60% × sale_price + $250). All other fields stay blank for the student
  // to fill in themselves. Sent via Resend from the ITD Canada mailbox.
  const [pdcStudent, setPdcStudent] = useState(null);
  const [pdcMeta, setPdcMeta] = useState(null);
  const [pdcLoading, setPdcLoading] = useState(false);
  const [pdcSending, setPdcSending] = useState(false);
  const [pdcForm, setPdcForm] = useState({ to: '', subject: '', body: '' });
  const isPdcEligible = (s) => {
    if (!s) return false;
    if (s.application_stage !== 'enrollment_agreement_signed') return false;
    const intake = (s.intake_date || s.assigned_intake_start || s.preferred_intake_date || '').toString();
    if (intake.startsWith('2026-10')) return true;
    const label = ((s.intake_label || s.preferred_intake || '') + '').toLowerCase();
    return label.includes('oct') && label.includes('2026');
  };
  const openPdcModal = async (s) => {
    setPdcStudent(s);
    setPdcMeta(null);
    setPdcForm({ to: '', subject: '', body: '' });
    setPdcLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/students/${s.id}/pdc-declaration/meta`);
      setPdcMeta(data);
      setPdcForm({ to: data.student_email || '', subject: data.default_subject || '', body: data.default_body || '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load PDC declaration details');
      setPdcStudent(null);
    } finally {
      setPdcLoading(false);
    }
  };
  const previewPdcPdf = () => {
    if (!pdcStudent) return;
    const token = localStorage.getItem('access_token') || '';
    // Open preview in a new tab. We use fetch to attach the auth header,
    // then open the blob URL — browsers do not forward Authorization on
    // plain window.open().
    (async () => {
      try {
        const resp = await fetch(`${API}/admin/students/${pdcStudent.id}/pdc-declaration/preview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(await resp.text());
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
      } catch (e) {
        toast.error('Could not open preview');
      }
    })();
  };
  const sendPdcDeclaration = async () => {
    if (!pdcStudent) return;
    if (!pdcForm.to || !pdcForm.to.includes('@')) { toast.error('Enter a valid recipient email'); return; }
    setPdcSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/students/${pdcStudent.id}/pdc-declaration/send`, {
        to_email: pdcForm.to.trim(),
        subject: pdcForm.subject.trim(),
        body: pdcForm.body,
      });
      toast.success(`PDC Declaration sent to ${data.to}`);
      setPdcStudent(null);
      setPdcMeta(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send declaration');
    } finally {
      setPdcSending(false);
    }
  };

  // ----- ID Match Verifier -----
  const handleVerifyIdMatch = async (student) => {
    if (!student) return;
    setIdMatchStudent(student);
    setIdMatchLoading(true);
    setIdMatchReport(student.id_match_report || null);
    setShowIdMatchDialog(true);
    try {
      const { data } = await axios.post(`${API}/admin/students/${student.id}/verify-id-match`);
      setIdMatchReport(data);
      if (data?.flagged_fields?.length) {
        toast.error(`Flagged: ${data.flagged_fields.join(', ')} does not match the ID`);
      } else if (data?.docs_checked > 0) {
        toast.success('All fields match the uploaded ID(s)');
      } else {
        toast('No ID documents found to verify against', { icon: 'ℹ️' });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Verification failed');
    } finally {
      setIdMatchLoading(false);
    }
  };

  const handleStartBulkIdScan = async (opts = {}) => {
    // Default: scan whatever the current filters show. opts.allStudents forces full scan.
    const useFiltered = !opts.allStudents;
    const scopeIds = useFiltered ? filteredStudents.map(s => s.id) : null;
    const scopeLabel = useFiltered ? _describeStudentFilters() : 'All eligible students';
    if (useFiltered && (!scopeIds || scopeIds.length === 0)) {
      toast.error('No students match the current filters — adjust filters or use "Scan Everyone".');
      return;
    }
    setBulkScanStarting(true);
    setShowBulkScanDialog(true);
    setBulkScanJob({ status: 'queued', total: 0, processed: 0, flagged: 0, flagged_students: [], scope_label: scopeLabel });
    try {
      const body = { scope_label: scopeLabel };
      if (scopeIds !== null) body.student_ids = scopeIds;
      const { data } = await axios.post(`${API}/admin/students/verify-id-match-bulk`, body);
      const jobId = data.job_id;
      // Poll every 2.5s until status is done/failed
      const poll = async () => {
        try {
          const { data: job } = await axios.get(`${API}/admin/id-match-jobs/${jobId}`);
          setBulkScanJob(job);
          if (job.status === 'running' || job.status === 'queued') {
            setTimeout(poll, 2500);
          } else if (job.status === 'done') {
            toast.success(`Scan complete — ${job.flagged} flagged student(s) out of ${job.total}`);
          } else if (job.status === 'failed') {
            toast.error(`Scan failed: ${job.error || 'unknown'}`);
          }
        } catch (e) {
          toast.error('Lost connection to scan job');
        }
      };
      setTimeout(poll, 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start scan');
      setBulkScanJob(null);
    } finally {
      setBulkScanStarting(false);
    }
  };

  // Build a readable summary of currently-applied filters for scan dialog + logs
  const _describeStudentFilters = () => {
    const parts = [];
    if (studentIntakeFilter && studentIntakeFilter !== 'all') parts.push(`Intake: ${studentIntakeFilter}`);
    if (studentStatusFilter && studentStatusFilter.length > 0) {
      parts.push(`Status: ${studentStatusFilter.map(s => PIPELINE_STAGE_LABELS[s] || s).join(', ')}`);
    }
    if (studentRecruiterFilter && studentRecruiterFilter !== 'all') {
      const r = uniqueRecruiters.find(x => x.id === studentRecruiterFilter);
      parts.push(`Recruiter: ${r?.name || studentRecruiterFilter}`);
    }
    if (studentCollegeFilter && studentCollegeFilter !== 'all') {
      const c = colleges.find(x => x.id === studentCollegeFilter);
      parts.push(`College: ${c?.name || studentCollegeFilter}`);
    }
    if (studentSearch) parts.push(`Search: "${studentSearch}"`);
    if (studentStartDate) parts.push(`From: ${studentStartDate}`);
    if (studentEndDate) parts.push(`To: ${studentEndDate}`);
    return parts.length ? parts.join(' · ') : `All ${filteredStudents.length} student(s) in current view`;
  };

  const openStudentFromScan = async (studentId) => {
    const s = students.find(x => x.id === studentId);
    if (s) {
      setShowBulkScanDialog(false);
      await handleViewStudent(s);
    }
  };


  const [bulkScoreReportLoading, setBulkScoreReportLoading] = useState(false);
  const [bulkDeleteScoreReportsLoading, setBulkDeleteScoreReportsLoading] = useState(false);
  const handleBulkGenerateScoreReports = async () => {
    if (!selectedStudentIds.length) return;
    const regen = window.confirm(
      `Generate Accuplacer score reports for ${selectedStudentIds.length} student(s)?\n\n`
      + `OK = skip students who already have a report (recommended)\n`
      + `Cancel to abort, or hold Shift when clicking to force-regenerate.`
    );
    if (!regen) return;
    // Prompt ONCE for a batch-wide intake date. Every randomised test/print
    // timestamp on every generated report will fall strictly BEFORE this
    // date. Blank = today's ceiling. Cancel = abort the whole batch.
    const raw = window.prompt(
      `Enter intake date (YYYY-MM-DD) for all ${selectedStudentIds.length} reports.\n`
      + `All randomised dates on every report will fall BEFORE this date.\n`
      + `Leave blank to use today.`,
      ''
    );
    if (raw === null) return; // admin hit Cancel
    const intakeDate = (raw || '').trim();
    if (intakeDate && !/^\d{4}-\d{2}-\d{2}$/.test(intakeDate)) {
      toast.error('Intake date must be in YYYY-MM-DD format.');
      return;
    }
    setBulkScoreReportLoading(true);
    const toastId = toast.loading(`Generating ${selectedStudentIds.length} score report${selectedStudentIds.length === 1 ? '' : 's'}…`);
    // Chunk in 10s so a long batch doesn't tie up the backend.
    const CHUNK = 10;
    const chunks = [];
    for (let i = 0; i < selectedStudentIds.length; i += CHUNK) chunks.push(selectedStudentIds.slice(i, i + CHUNK));
    const total = { generated: 0, skipped: 0, errored: 0, errorRows: [] };
    try {
      for (let ci = 0; ci < chunks.length; ci += 1) {
        const { data } = await axios.post(`${API}/admin/students/score-reports/bulk-generate`, {
          student_ids: chunks[ci],
          institution: 'focus',
          regenerate: false,
          intake_date: intakeDate || undefined,
        });
        total.generated += data.counts.generated;
        total.skipped += data.counts.skipped;
        total.errored += data.counts.errored;
        if (data.errored?.length) total.errorRows.push(...data.errored);
        toast.loading(`Batch ${ci + 1}/${chunks.length} done · ${total.generated} generated · ${total.skipped} skipped · ${total.errored} errors`, { id: toastId });
      }
      toast.dismiss(toastId);
      const summary = `${total.generated} generated · ${total.skipped} skipped · ${total.errored} errors`;
      if (total.errored > 0) {
        toast.error(`${summary}. First error: ${total.errorRows[0]?.reason || 'unknown'}`, { duration: 8000 });
      } else {
        toast.success(summary);
      }
      // Refresh the student list so score_reports_count badges update.
      fetchData();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err?.response?.data?.detail || 'Bulk score report failed');
    } finally {
      setBulkScoreReportLoading(false);
    }
  };

  // Bulk delete every generated score report for the selected students.
  // Used from the main Students list toolbar — admin ticks students who
  // have reports and purges them in one shot. Only shows up when at least
  // one selected student has `score_reports_count > 0`.
  const handleBulkDeleteScoreReports = async () => {
    if (!selectedStudentIds.length) return;
    const withReports = selectedStudents.filter(s => (s.score_reports_count || 0) > 0);
    if (!withReports.length) {
      toast.error('None of the selected students have generated score reports.');
      return;
    }
    const totalReports = withReports.reduce((n, s) => n + (s.score_reports_count || 0), 0);
    if (!window.confirm(
      `Permanently delete ${totalReports} generated score report(s) across ${withReports.length} student(s)?\n\n`
      + `This also removes them from the registrar and president views. This cannot be undone.`
    )) return;
    setBulkDeleteScoreReportsLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/students/score-reports/bulk-delete`, {
        student_ids: withReports.map(s => s.id),
      });
      toast.success(`${data.deleted} score report(s) deleted across ${data.students_affected} student(s)`);
      setSelectedStudentIds([]);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleteScoreReportsLoading(false);
    }
  };

  // Toggle the "already downloaded" flag on all selected students. Used for
  // backfilling records that were exported before we started tracking this,
  // or for clearing the badge to mark a student as needing re-download.
  const handleBulkMarkDownloaded = async (mark) => {
    if (!selectedStudentIds.length) return;
    const verb = mark ? 'mark' : 'clear the downloaded flag on';
    if (!window.confirm(`${mark ? 'Mark' : 'Un-mark'} ${selectedStudentIds.length} student(s) as downloaded? This just updates the badge — it does NOT trigger an actual download.`)) return;
    try {
      const res = await axios.post(`${API}/admin/students/bulk-mark-downloaded`, {
        student_ids: selectedStudentIds,
        mark: !!mark,
      });
      const now = mark ? new Date().toISOString() : null;
      const me = 'admin';
      setStudents(prev => prev.map(s => selectedStudentIds.includes(s.id)
        ? { ...s, last_downloaded_at: now || undefined, last_downloaded_by: now ? me : undefined }
        : s));
      setSelectedStudentIds([]);
      toast.success(`${res.data?.modified ?? selectedStudentIds.length} student(s) ${mark ? 'marked as downloaded' : 'un-marked'}`);
      fetchData();  // canonical source-of-truth refresh
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${verb} students`);
    }
  };

  // Bulk-assign an intake window (start + end) to every selected student.
  // Admin can either pick one of the college's saved intakes (auto-fills the
  // two date inputs) or type custom dates.
  // If set, the intake dialog operates on ONE student (opened from the
  // student detail modal) instead of the multi-select checkbox pool.
  const [intakeDialogSingleId, setIntakeDialogSingleId] = useState('');
  // Admin can inspect/edit institution-entered financials from the student detail modal.
  const [adminFinancialsOpen, setAdminFinancialsOpen] = useState(false);

  // ── Bulk pricing dialog ──────────────────────────────────────────
  const [bulkPricingOpen, setBulkPricingOpen] = useState(false);
  const [bulkPricingSaving, setBulkPricingSaving] = useState(false);
  const [bulkPricingForm, setBulkPricingForm] = useState({ sale_price: '', college_fee_snapshot: '', recruiter_commission_snapshot: '' });

  const openBulkPricing = () => {
    // Pre-fill with any value that's already common across the selection
    // (helps admins tweak instead of retyping from scratch).
    const commonValue = (field) => {
      const vals = new Set(selectedStudents.map(s => (s[field] ?? '')).map(v => v === 0 ? '' : String(v ?? '')));
      return vals.size === 1 ? [...vals][0] : '';
    };
    setBulkPricingForm({
      sale_price: commonValue('sale_price'),
      college_fee_snapshot: commonValue('college_fee_snapshot'),
      recruiter_commission_snapshot: commonValue('recruiter_commission_snapshot'),
    });
    setBulkPricingOpen(true);
  };

  const submitBulkPricing = async () => {
    if (!selectedStudentIds.length) { toast.error('Select students first'); return; }
    const recIds = new Set(selectedStudents.map(s => s.recruiter_id).filter(Boolean));
    if (recIds.size !== 1) { toast.error('All selected students must belong to the same recruiter'); return; }
    const payload = { student_ids: selectedStudentIds, recruiter_id: [...recIds][0] };
    let touched = 0;
    for (const key of ['sale_price', 'college_fee_snapshot', 'recruiter_commission_snapshot']) {
      const raw = bulkPricingForm[key];
      if (raw === '' || raw === null || raw === undefined) continue;
      const num = Number(raw);
      if (Number.isNaN(num) || num < 0) { toast.error(`${key.replace('_snapshot','').replace('_',' ')} must be a non-negative number`); return; }
      payload[key] = num;
      touched += 1;
    }
    if (!touched) { toast.error('Enter at least one pricing field to update'); return; }
    setBulkPricingSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/students/bulk-pricing`, payload);
      toast.success(`Pricing set on ${data.modified} student${data.modified === 1 ? '' : 's'}`);
      if ((data.mismatched_student_ids || []).length) {
        toast.warning(`${data.mismatched_student_ids.length} student(s) didn't belong to this recruiter and were skipped`);
      }
      // Update local rows so the "Study Alliance Earning" strip and the
      // pricing panel refresh without a full refetch.
      setStudents(prev => prev.map(s => {
        if (!selectedStudentIds.includes(s.id) || s.recruiter_id !== payload.recruiter_id) return s;
        const patch = {};
        if ('sale_price' in payload) patch.sale_price = payload.sale_price;
        if ('college_fee_snapshot' in payload) patch.college_fee_snapshot = payload.college_fee_snapshot;
        if ('recruiter_commission_snapshot' in payload) patch.recruiter_commission_snapshot = payload.recruiter_commission_snapshot;
        return { ...s, ...patch };
      }));
      setBulkPricingOpen(false);
      setSelectedStudentIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk pricing failed');
    } finally { setBulkPricingSaving(false); }
  };

  const openChangeIntakeForStudent = (student) => {
    if (!student) return;
    setIntakeDialogSingleId(student.id);
    setBulkIntakeStart(student.assigned_intake_start || '');
    setBulkIntakeEnd(student.assigned_intake_end || '');
    setBulkIntakeCollegeId(student.assigned_college || '');
    setShowBulkIntakeDialog(true);
  };

  const handleBulkAssignIntake = async () => {
    // Single-student flow wins when opened from the detail modal; otherwise
    // fall back to the multi-select checkbox pool.
    const targetIds = intakeDialogSingleId ? [intakeDialogSingleId] : selectedStudentIds;
    if (!targetIds.length) { toast.error('Select students first'); return; }
    if (!bulkIntakeStart || !bulkIntakeEnd) { toast.error('Pick both start and end dates'); return; }
    if (bulkIntakeEnd < bulkIntakeStart) { toast.error('End must be on or after start'); return; }
    setBulkIntakeSaving(true);
    try {
      const res = await axios.post(`${API}/admin/students/bulk-assign-intake`, {
        student_ids: targetIds,
        start_date: bulkIntakeStart,
        end_date: bulkIntakeEnd,
      });
      const newLabel = res.data?.intake_label || '';
      toast.success(`Intake assigned to ${res.data?.modified ?? targetIds.length} student(s)${newLabel ? ` · ${newLabel}` : ''}`);
      setShowBulkIntakeDialog(false);
      setIntakeDialogSingleId('');
      setStudents(prev => prev.map(s => targetIds.includes(s.id)
        ? { ...s, assigned_intake_start: bulkIntakeStart, assigned_intake_end: bulkIntakeEnd, intake_label: newLabel || s.intake_label }
        : s));
      // Keep the detail modal in sync so the change is visible immediately.
      if (selectedStudent && targetIds.includes(selectedStudent.id)) {
        setSelectedStudent(s => s ? { ...s, assigned_intake_start: bulkIntakeStart, assigned_intake_end: bulkIntakeEnd, intake_label: newLabel || s.intake_label } : s);
      }
      if (!intakeDialogSingleId) setSelectedStudentIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to assign intake');
    } finally { setBulkIntakeSaving(false); }
  };

  const [bulkZipLoading, setBulkZipLoading] = useState(false);

  const handleDeleteStudentFile = async (file) => {
    if (!file?.id) return;
    if (!window.confirm(`Permanently remove "${file.original_filename}" from this student's file? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/files/${file.id}`);
      toast.success(`Removed ${file.original_filename}`);
      // Drop it from the currently-displayed list
      setStudentFiles(prev => prev.filter(x => x.id !== file.id));
      if (filePreview?.id === file.id) setFilePreview(null);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(`Failed to remove file${status ? ` (${status})` : ''}: ${typeof detail === 'string' ? detail : (err?.message || 'unknown error')}`);
    }
  };

  const handleQuickScoreReport = async (student) => {
    // Pick an institution based on the student's assigned college name if possible;
    // fall back to Focus. Admin can regenerate a different one from the Score Report tab.
    const collegeName = (student?.assigned_college_name || student?.preferred_college_name || '').toLowerCase();
    const institution = /\bitd\b/.test(collegeName) ? 'itd' : 'focus';
    // Prompt the admin for the intake date (defaults to whatever's on the student)
    const suggested = student?.preferred_intake_date || student?.preferred_intake || '';
    const promptMsg = `Enter intake date (YYYY-MM-DD) for ${student.full_name || 'this student'}.\nAll randomised dates on the report will fall BEFORE this date.\nLeave blank to use today.`;
    const intakeDate = window.prompt(promptMsg, suggested) ?? null;
    if (intakeDate === null) return; // user cancelled
    const trimmed = (intakeDate || '').trim();
    if (trimmed && !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      toast.error('Date must be in YYYY-MM-DD format.');
      return;
    }
    const toastId = toast.loading(`Generating score report for ${student.full_name || 'student'}\u2026`);
    try {
      const res = await axios.post(`${API}/admin/students/${student.id}/score-reports`, {
        institution,
        intake_date: trimmed || null,
      });
      toast.dismiss(toastId);
      const filename = res.data?.filename || 'Score Report';
      toast.success(`Score report saved: ${filename}`);
      // Optimistically flip the row badge so the admin sees the change instantly
      setStudents(prev => prev.map(row => row.id === student.id
        ? { ...row, score_reports_count: (row.score_reports_count || 0) + 1 }
        : row));
      // If the student detail dialog is open for this student, refresh its list
      if (selectedStudent && selectedStudent.id === student.id) {
        try { const r = await axios.get(`${API}/admin/students/${student.id}/score-reports`); setStudentScoreReports(r.data || []); } catch { /* silent */ }
      }
    } catch (err) {
      toast.dismiss(toastId);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(`Score report failed${status ? ` (${status})` : ''}: ${typeof detail === 'string' ? detail : (err?.message || 'unknown error')}`);
    }
  };

  const handleBulkDownloadStudentsZip = async () => {
    if (!selectedStudentIds.length) return;
    setBulkZipLoading(true);
    const count = selectedStudentIds.length;
    const toastId = toast.loading(`Preparing ZIP of ${count} student${count > 1 ? 's' : ''}\u2026`);
    try {
      // 1) Kick off the background job — returns instantly with a job_id.
      const start = await axios.post(`${API}/admin/students/bulk-download-zip`, { ids: selectedStudentIds });
      const jobId = start.data.job_id;
      const filename = start.data.filename || `study_alliance_students_${new Date().toISOString().slice(0, 10)}.zip`;

      // 2) Poll every 2s until state is done/failed. Cap at 15 minutes.
      const deadline = Date.now() + 15 * 60 * 1000;
      let lastLabel = '';
      const phaseLabel = (job) => {
        const state = job.state;
        const done = job.done || 0;
        const tot = job.total || count;
        const cur = job.current_student ? ` \u2014 ${job.current_student}` : '';
        if (state === 'queued') return 'Queued\u2026';
        if (state === 'running') return `Packaging ${done}/${tot}${cur}`;
        if (state === 'assembling') return `Finalising archive\u2026 (${tot} students)`;
        if (state === 'uploading') {
          const mb = job.size ? ` \u2014 ${(job.size / (1024 * 1024)).toFixed(1)} MB` : '';
          return `Uploading archive${mb}\u2026`;
        }
        return `Preparing ZIP\u2026 ${done}/${tot}`;
      };
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 2000));
        let job;
        try {
          const st = await axios.get(`${API}/admin/students/bulk-download-zip-jobs/${jobId}`);
          job = st.data;
        } catch (pollErr) {
          // Transient poll failure — keep trying until deadline.
          continue;
        }
        const label = phaseLabel(job);
        if (label !== lastLabel) {
          toast.loading(label, { id: toastId });
          lastLabel = label;
        }
        if (job.state === 'done') {
          // Native browser download via anchor + one-time token in the URL.
          // Buffering a 100+ MB blob in JS memory (old approach) was killing
          // the download with "Network Failed" on large batches — this hands
          // the download off to the browser's native download manager which
          // streams straight from the backend through Cloudflare.
          const token = job.download_token;
          if (!token) {
            toast.dismiss(toastId);
            toast.error('Bulk ZIP finished but download token is missing — please retry');
            return;
          }
          const dlUrl = `${API}/admin/students/bulk-download-zip-jobs/${jobId}/download?t=${encodeURIComponent(token)}`;
          const a = document.createElement('a');
          a.href = dlUrl;
          a.download = filename;
          a.rel = 'noopener';
          document.body.appendChild(a); a.click(); a.remove();
          toast.dismiss(toastId);
          toast.success(`ZIP with ${job.total} student${job.total > 1 ? 's' : ''} — download started`);
          return;
        }
        if (job.state === 'failed') {
          toast.dismiss(toastId);
          toast.error(`Bulk ZIP failed: ${job.error || 'unknown error'}`);
          return;
        }
      }
      toast.dismiss(toastId);
      toast.error('Bulk ZIP is taking longer than expected \u2014 please try a smaller batch');
    } catch (err) {
      toast.dismiss(toastId);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(`Bulk ZIP failed${status ? ` (${status})` : ''}: ${typeof detail === 'string' ? detail : (err?.message || 'unknown error')}`);
    } finally { setBulkZipLoading(false); }
  };

  const [aprilExportLoading, setAprilExportLoading] = useState(false);
  const handleAprilExport = async () => {
    if (!selectedStudentIds.length) return;
    setAprilExportLoading(true);
    const count = selectedStudentIds.length;
    const toastId = toast.loading(`Queuing Quarterly Report for ${count} student${count > 1 ? 's' : ''}\u2026`);
    try {
      // 1) Kick off the background job — this returns instantly.
      const kick = await axios.post(
        `${API}/admin/students/quarterly-report-jobs`,
        { ids: selectedStudentIds, ocr_missing: true }
      );
      const jobId = kick.data.job_id;
      const filename = kick.data.filename || `Quarterly_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const total = kick.data.total || count;

      // 2) Poll every 2 s. Cap at 5 min — an xlsx build is much lighter than
      //    a ZIP so anything over that means something is wrong on the backend.
      const deadline = Date.now() + 5 * 60 * 1000;
      let lastLabel = '';
      const phaseLabel = (job) => {
        const phase = job.phase || job.state;
        if (job.state === 'queued') return 'Queued\u2026';
        if (phase === 'loading') return 'Loading students\u2026';
        if (phase === 'ocr') return `Reading IDs\u2026 ${job.ocr_done || 0}/${job.ocr_total || '?'}`;
        if (phase === 'assembling') return `Building spreadsheet\u2026 (${total} students)`;
        if (phase === 'uploading') {
          const kb = job.size ? ` \u2014 ${(job.size / 1024).toFixed(0)} KB` : '';
          return `Uploading${kb}\u2026`;
        }
        return `Working\u2026 ${job.state || ''}`;
      };
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 2000));
        let job;
        try {
          const st = await axios.get(`${API}/admin/students/quarterly-report-jobs/${jobId}`);
          job = st.data;
        } catch {
          continue;
        }
        const label = phaseLabel(job);
        if (label !== lastLabel) {
          toast.loading(label, { id: toastId });
          lastLabel = label;
        }
        if (job.state === 'done') {
          const token = job.download_token;
          const dlUrl = `${API}/admin/students/quarterly-report-jobs/${jobId}/download?t=${encodeURIComponent(token)}`;
          const a = document.createElement('a');
          a.href = dlUrl;
          a.download = filename;
          a.rel = 'noopener';
          document.body.appendChild(a); a.click(); a.remove();
          toast.dismiss(toastId);
          toast.success(`Quarterly Report ready \u2014 ${total} student${total > 1 ? 's' : ''}`);
          return;
        }
        if (job.state === 'failed') {
          toast.dismiss(toastId);
          toast.error(`Quarterly Report failed: ${job.error || 'unknown error'}`);
          return;
        }
      }
      toast.dismiss(toastId);
      toast.error('Quarterly Report timed out after 5 min — try a smaller batch');
    } catch (err) {
      toast.dismiss(toastId);
      const detail = err?.response?.data?.detail;
      toast.error(`Quarterly Report failed${err?.response?.status ? ` (${err.response.status})` : ''}: ${typeof detail === 'string' ? detail : (err?.message || 'unknown error')}`);
    } finally { setAprilExportLoading(false); }
  };

  const [matchLoading, setMatchLoading] = useState(false);
  const handleMatchRecruiterFromXlsx = async (e) => {
    const f = e.target?.files?.[0];
    e.target.value = ''; // reset so the same file can be re-uploaded
    if (!f) return;
    setMatchLoading(true);
    const toastId = toast.loading(`Matching ${f.name}\u2026`);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(
        `${API}/admin/students/match-recruiter-by-name.xlsx`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, responseType: 'blob' }
      );
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m ? m[1] : f.name.replace(/\.xlsx$/i, '_matched.xlsx');
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss(toastId);
      toast.success('Matched — file downloaded. Open the "Match Summary" sheet inside for counts.');
    } catch (err) {
      toast.dismiss(toastId);
      let detail = err?.response?.data?.detail;
      if (err?.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          const parsed = JSON.parse(txt);
          detail = parsed?.detail;
        } catch { /* ignore */ }
      }
      toast.error(`Match failed: ${detail || err?.message || 'unknown error'}`);
    } finally { setMatchLoading(false); }
  };

  const [backfillCollegeLoading, setBackfillCollegeLoading] = useState(false);
  const [backfillIntakeLoading, setBackfillIntakeLoading] = useState(false);
  const handleBackfillIntakes = async () => {
    if (!window.confirm('Auto-assign intake dates to every student without one? Students created on/before May 31, 2026 get "Apr 2026"; students created after get "Jul 2026". Existing intakes are left alone.')) return;
    setBackfillIntakeLoading(true);
    const toastId = toast.loading('Backfilling intakes…');
    try {
      const res = await axios.post(`${API}/admin/students/backfill-intakes`);
      const { apr_2026 = 0, jul_2026 = 0, total = 0 } = res.data || {};
      toast.dismiss(toastId);
      if (total > 0) {
        toast.success(`Assigned intakes to ${total} students (Apr 2026: ${apr_2026}, Jul 2026: ${jul_2026}).`);
        try {
          const stuRes = await axios.get(`${API}/students?limit=5000`);
          setStudents(stuRes.data);
        } catch { /* ignore */ }
      } else {
        toast.success('Every student already has an intake set.');
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(`Backfill failed: ${err?.response?.data?.detail || err?.message || 'unknown error'}`);
    } finally { setBackfillIntakeLoading(false); }
  };
  const [backfillAddressLoading, setBackfillAddressLoading] = useState(false);
  const handleBackfillAddresses = async () => {
    if (!window.confirm('Scan every student whose typed address is missing city/province/postal-code subfields and auto-fill them from the combined address string. Existing values are never overwritten.')) return;
    setBackfillAddressLoading(true);
    const toastId = toast.loading('Backfilling addresses…');
    try {
      const res = await axios.post(`${API}/admin/students/backfill-addresses`);
      const { updated = 0, checked = 0, no_op = 0 } = res.data || {};
      toast.dismiss(toastId);
      if (updated > 0) {
        toast.success(`Auto-parsed ${updated} students (checked ${checked}, ${no_op} unchanged).`);
        try {
          const stuRes = await axios.get(`${API}/students?limit=5000`);
          setStudents(stuRes.data);
        } catch { /* ignore */ }
      } else {
        toast.success(`No students needed backfill (checked ${checked}).`);
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(`Backfill failed: ${err?.response?.data?.detail || err?.message || 'unknown error'}`);
    } finally { setBackfillAddressLoading(false); }
  };
  const [showCoeSheetDialog, setShowCoeSheetDialog] = useState(false);
  const [coeSheetFile, setCoeSheetFile] = useState(null);
  const [coeSheetSyncing, setCoeSheetSyncing] = useState(false);
  const [coeSheetMeta, setCoeSheetMeta] = useState({ last_synced_at: null, last_synced_by: null, last_result: null });
  const [coeSheetResult, setCoeSheetResult] = useState(null);
  useEffect(() => {
    if (!showCoeSheetDialog) return;
    setCoeSheetFile(null);
    setCoeSheetResult(null);
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/settings/coe-sheet`);
        setCoeSheetMeta({
          last_synced_at: res.data?.last_synced_at || null,
          last_synced_by: res.data?.last_synced_by || null,
          last_result: res.data?.last_result || null,
        });
      } catch { /* non-fatal */ }
    })();
  }, [showCoeSheetDialog]);
  const handleSyncCoeSheet = async () => {
    if (!coeSheetFile) { toast.error('Pick an Excel or CSV file first.'); return; }
    setCoeSheetSyncing(true);
    setCoeSheetResult(null);
    const form = new FormData();
    form.append('file', coeSheetFile);
    try {
      const res = await axios.post(`${API}/admin/students/sync-coe-file`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCoeSheetResult(res.data);
      const { updated = 0, matched = 0, total_rows = 0 } = res.data || {};
      toast.success(`Sync done — ${updated}/${matched} advanced to "COE Sent" (${total_rows} row${total_rows === 1 ? '' : 's'} scanned).`);
      try {
        const stuRes = await axios.get(`${API}/students?limit=5000`);
        setStudents(stuRes.data);
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(`Sync failed: ${err?.response?.data?.detail || err.message}`);
    } finally { setCoeSheetSyncing(false); }
  };
  const handleBackfillCollegeAssignment = async () => {
    if (!window.confirm('Auto-assign colleges to every student that doesn\'t have one? I\'ll try (in order): the "Preferred College" the recruiter picked, the parent college of their program, then the campus/branch match. Only touches students without an assignment.')) return;
    setBackfillCollegeLoading(true);
    const toastId = toast.loading('Backfilling college assignments\u2026');
    try {
      const res = await axios.post(`${API}/admin/students/backfill-college-assignment`);
      const { updated = 0, checked = 0, by_strategy = {}, message } = res.data || {};
      toast.dismiss(toastId);
      if (updated > 0) {
        const bits = [];
        if (by_strategy.by_preferred_college) bits.push(`${by_strategy.by_preferred_college} via preferred college`);
        if (by_strategy.by_program) bits.push(`${by_strategy.by_program} via program`);
        if (by_strategy.by_branch) bits.push(`${by_strategy.by_branch} via campus/branch`);
        const detail = bits.length ? ` (${bits.join(', ')})` : '';
        toast.success(`Assigned ${updated} student${updated === 1 ? '' : 's'}${detail}. ${checked - updated} still need manual assignment.`);
        try {
          const stuRes = await axios.get(`${API}/students?limit=5000`);
          setStudents(stuRes.data);
        } catch { /* ignore */ }
      } else {
        toast.success(message || (checked > 0
          ? `Checked ${checked} students — none had enough info (preferred college, program, or campus) to auto-assign. Use "Assign College" per row.`
          : 'All students already have a college assigned.'));
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(`Backfill failed: ${err?.response?.data?.detail || err?.message || 'unknown error'}`);
    } finally { setBackfillCollegeLoading(false); }
  };

  const handleExportStudentsXlsx = async () => {
    // Mirror the current admin Students-tab filters into the export query so
    // the downloaded workbook reflects exactly what's on screen.
    try {
      const params = new URLSearchParams();
      if (studentRecruiterFilter !== 'all') params.set('recruiter_id', studentRecruiterFilter);
      if (studentStatusFilter.length > 0) params.set('status', studentStatusFilter.join(','));
      if (studentCollegeFilter !== 'all') params.set('college_id', studentCollegeFilter);
      if (studentIntakeFilter && studentIntakeFilter !== 'all') params.set('intake', studentIntakeFilter);
      if (studentSearch) params.set('search', studentSearch);
      if (studentStartDate) params.set('start_date', studentStartDate);
      if (studentEndDate) params.set('end_date', studentEndDate);
      const res = await axios.get(`${API}/admin/students/export.xlsx?${params.toString()}`, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m ? m[1] : `students_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Students exported');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to export students');
    }
  };

  // DocuSign Bulk Upload export — downloads a .xlsx matching the 29-column
  // template.  Dialog collects global defaults (program dates, credential,
  // delivery, campus, tuition split, application fee) applied to every row.
  const handleDsBulkExport = async () => {
    setDsBulkExporting(true);
    try {
      const body = { defaults: { ...dsBulkDefaults } };
      if (dsBulkScope === 'filtered') {
        // Dedupe + drop any empty id values that may have snuck into state.
        const seen = new Set();
        body.student_ids = [];
        for (const s of filteredStudents) {
          if (s?.id && !seen.has(s.id)) {
            seen.add(s.id);
            body.student_ids.push(s.id);
          }
        }
        if (!body.student_ids.length) {
          toast.error('No students in the current filtered view');
          setDsBulkExporting(false);
          return;
        }
      }
      const res = await axios.post(`${API}/admin/students/export/docusign-bulk`, body, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m ? m[1] : `DocuSign Bulk Upload - ${new Date().toISOString().slice(0, 10)}.xlsx`;
      // Count info is embedded in the filename like "(50 of 55 - 5 missing)" or "(50 students)".
      const missMatch = /\((\d+) of (\d+) - (\d+) missing\)/.exec(filename);
      const okMatch = /\((\d+) students\)/.exec(filename);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      if (missMatch) {
        const exported = parseInt(missMatch[1], 10);
        const requested = parseInt(missMatch[2], 10);
        const missing = parseInt(missMatch[3], 10);
        toast.error(`Exported ${exported} of ${requested} students — ${missing} selected IDs were not found in the database (likely hard-deleted). Sheet downloaded anyway.`, { duration: 10000 });
      } else if (okMatch) {
        toast.success(`Downloaded ${okMatch[1]} student(s)`);
      } else {
        toast.success(`Downloaded ${filename}`);
      }
      setShowDsBulkDialog(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed');
    } finally {
      setDsBulkExporting(false);
    }
  };

  // PTIB DocuSign-bulk export — one recruiter, 29-column workbook matching the
  // provided DocuSign_Bulk_Karan.xlsx template. Requires a specific recruiter
  // to be selected in the Students-tab filter (server rejects "all").
  const handlePtibExport = async () => {
    if (studentRecruiterFilter === 'all' || !studentRecruiterFilter) {
      toast.error('Select a specific recruiter in the filter first');
      return;
    }
    try {
      const res = await axios.get(
        `${API}/admin/students/ptib-export.xlsx?recruiter_id=${encodeURIComponent(studentRecruiterFilter)}`,
        { responseType: 'blob' }
      );
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m ? m[1] : `DocuSign_Bulk_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PTIB export ready');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate PTIB export');
    }
  };

  // Bulk pipeline-stage change. Sends one request to the new
  // POST /api/admin/students/bulk-stage endpoint; backend audits each student
  // and notifies each unique recruiter in a consolidated email.
  const handleBulkUpdateStage = async () => {
    if (!selectedStudentIds.length || !bulkStageValue) return;
    const label = PIPELINE_STAGE_LABELS[bulkStageValue] || bulkStageValue;
    if (!window.confirm(`Move ${selectedStudentIds.length} student(s) to "${label}"?`)) return;
    setBulkStageApplying(true);
    try {
      const res = await axios.post(`${API}/admin/students/bulk-stage`, { ids: selectedStudentIds, stage: bulkStageValue });
      const updated = res.data?.updated ?? selectedStudentIds.length;
      toast.success(`${updated} student${updated === 1 ? '' : 's'} moved to ${label}`);
      // Optimistic local update — avoids a full refetch for snappier UX
      setStudents(prev => prev.map(s => selectedStudentIds.includes(s.id) ? { ...s, application_stage: bulkStageValue } : s));
      setSelectedStudentIds([]);
      setBulkStageValue('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update stage');
    } finally {
      setBulkStageApplying(false);
    }
  };

  // ----- Bulk Stage from Excel upload ---------------------------------------
  // Admin exports the DocuSign Bulk Upload sheet, runs the DocuSign send, then
  // uploads the SAME sheet back here to flip every one of those students to a
  // single chosen stage (e.g. "Enrollment Agreement Sent to Student"). The
  // backend buckets rows into matched / review / not_found; admin ticks what
  // to apply and (optionally) maps "not found" rows to real students by name.
  const resetBulkStageExcel = () => {
    setBulkStageExcelFile(null);
    setBulkStageExcelPreview(null);
    setBulkStageExcelPreviewing(false);
    setBulkStageExcelTarget('');
    setBulkStageExcelApplying(false);
    setBulkStageExcelPicks({});
    setBulkStageExcelCheckedMatched({});
    setBulkStageExcelCheckedReview({});
  };

  const handleBulkStageExcelUpload = async () => {
    if (!bulkStageExcelFile) return;
    setBulkStageExcelPreviewing(true);
    setBulkStageExcelPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkStageExcelFile);
      const res = await axios.post(`${API}/admin/students/bulk-stage/preview-excel`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkStageExcelPreview(res.data);
      // Default: all matched are pre-checked, review all unchecked.
      const m = {};
      (res.data?.matched || []).forEach(r => { if (r.student_id) m[r.student_id] = true; });
      setBulkStageExcelCheckedMatched(m);
      setBulkStageExcelCheckedReview({});
      setBulkStageExcelPicks({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not parse the Excel file');
    } finally {
      setBulkStageExcelPreviewing(false);
    }
  };

  const handleBulkStageExcelApply = async () => {
    if (!bulkStageExcelTarget) {
      toast.error('Pick a target stage first');
      return;
    }
    const ids = new Set();
    (bulkStageExcelPreview?.matched || []).forEach(r => {
      if (r.student_id && bulkStageExcelCheckedMatched[r.student_id]) ids.add(r.student_id);
    });
    (bulkStageExcelPreview?.review || []).forEach(r => {
      if (r.student_id && bulkStageExcelCheckedReview[r.student_id]) ids.add(r.student_id);
    });
    Object.values(bulkStageExcelPicks).forEach(p => { if (p?.student_id) ids.add(p.student_id); });
    const idList = Array.from(ids);
    if (!idList.length) {
      toast.error('No rows selected to update');
      return;
    }
    const label = PIPELINE_STAGE_LABELS[bulkStageExcelTarget] || bulkStageExcelTarget;
    if (!window.confirm(`Move ${idList.length} student(s) to "${label}"?`)) return;
    setBulkStageExcelApplying(true);
    try {
      const res = await axios.post(`${API}/admin/students/bulk-stage`, { ids: idList, stage: bulkStageExcelTarget });
      const updated = res.data?.updated ?? idList.length;
      toast.success(`${updated} student${updated === 1 ? '' : 's'} moved to ${label}`);
      setStudents(prev => prev.map(s => idList.includes(s.id) ? { ...s, application_stage: bulkStageExcelTarget } : s));
      setShowBulkStageExcelDialog(false);
      resetBulkStageExcel();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply stage change');
    } finally {
      setBulkStageExcelApplying(false);
    }
  };

  // Inline student picker for a "not found" row.
  const BulkStagePickStudent = ({ rowKey }) => {
    const [q, setQ] = useState('');
    const [opts, setOpts] = useState([]);
    const [loading, setLoading] = useState(false);
    const picked = bulkStageExcelPicks[rowKey];
    useEffect(() => {
      if (!q || q.length < 2 || picked) { setOpts([]); return; }
      let active = true;
      setLoading(true);
      const t = setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/students`, { params: { search: q, limit: 10 } });
          if (active) setOpts(res.data || []);
        } catch {
          if (active) setOpts([]);
        } finally {
          if (active) setLoading(false);
        }
      }, 300);
      return () => { active = false; clearTimeout(t); };
    }, [q, picked]);
    if (picked) {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Matched to: {picked.label}</Badge>
          {picked.recruiter_name && (
            <Badge variant="outline" className="text-[11px] text-gray-700">Recruiter: {picked.recruiter_name}</Badge>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
            onClick={() => setBulkStageExcelPicks(prev => { const nx = { ...prev }; delete nx[rowKey]; return nx; })}
            data-testid={`bulk-stage-excel-unpick-${rowKey}`}
          >Clear</Button>
        </div>
      );
    }
    return (
      <div className="relative">
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by name or email…"
          className="h-8 text-xs"
          data-testid={`bulk-stage-excel-search-${rowKey}`}
        />
        {q.length >= 2 && (
          <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
            {loading ? (
              <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
            ) : opts.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500">No students found</div>
            ) : opts.map(o => (
              <button
                key={o.id}
                type="button"
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                onClick={() => {
                  setBulkStageExcelPicks(prev => ({ ...prev, [rowKey]: { student_id: o.id, label: `${o.full_name} · ${o.email}`, recruiter_name: o.recruiter_name || '' } }));
                  setQ('');
                  setOpts([]);
                }}
                data-testid={`bulk-stage-excel-pick-option-${o.id}`}
              >
                <div className="font-medium text-gray-900">{o.full_name}</div>
                <div className="text-gray-500">{o.email} · {PIPELINE_STAGE_LABELS[o.application_stage] || o.application_stage || '—'}</div>
                {o.recruiter_name && (
                  <div className="text-gray-500">Recruiter: <span className="text-gray-700">{o.recruiter_name}</span></div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ----- Admin notes per student -----

  const openStudentNotes = async (student) => {
    setNoteDialogStudent({ id: student.id, full_name: student.full_name });
    setNewNoteText('');
    setStudentNotes([]);
    setStudentNotesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/students/${student.id}/notes`);
      setStudentNotes(res.data.notes || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load notes');
    } finally {
      setStudentNotesLoading(false);
    }
  };

  const handleAddStudentNote = async () => {
    const text = (newNoteText || '').trim();
    if (!text) { toast.error('Type a note first'); return; }
    if (!noteDialogStudent?.id) return;
    setSavingNote(true);
    try {
      const res = await axios.post(`${API}/admin/students/${noteDialogStudent.id}/notes`, { text });
      setStudentNotes(prev => [res.data.note, ...prev]);
      setNewNoteText('');
      toast.success('Note added');
      // Update count in the table without a full refetch
      setStudents(prev => prev.map(s => s.id === noteDialogStudent.id ? { ...s, admin_notes_count: (s.admin_notes_count || 0) + 1 } : s));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteStudentNote = async (noteId) => {
    if (!noteDialogStudent?.id) return;
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/admin/students/${noteDialogStudent.id}/notes/${noteId}`);
      setStudentNotes(prev => prev.filter(n => n.id !== noteId));
      setStudents(prev => prev.map(s => s.id === noteDialogStudent.id ? { ...s, admin_notes_count: Math.max(0, (s.admin_notes_count || 1) - 1) } : s));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete note');
    }
  };

  const toggleRecruiterSelect = (id) => setSelectedRecruiterIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllRecruiters = () => setSelectedRecruiterIds(prev => prev.length === recruiters.length ? [] : recruiters.map(r => r.id));
  const toggleStudentSelect = (id) => setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllStudents = () => setSelectedStudentIds(prev => prev.length === filteredStudents.length ? [] : filteredStudents.map(s => s.id));

  // App selection & delete
  const toggleAppSelect = (id) => setSelectedAppIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllApps = () => setSelectedAppIds(prev => prev.length === studentApps.length ? [] : studentApps.map(a => a.id));
  const handleDeleteApp = async (id) => { if (!window.confirm('Delete this application?')) return; try { await axios.delete(`${API}/admin/student-applications/${id}`); toast.success('Application deleted'); fetchData(); } catch { toast.error('Failed'); } };
  const handleBulkDeleteApps = async () => { if (!selectedAppIds.length || !window.confirm(`Delete ${selectedAppIds.length} application(s)?`)) return; try { await axios.post(`${API}/admin/student-applications/bulk-delete`, { ids: selectedAppIds }); toast.success(`${selectedAppIds.length} application(s) deleted`); setSelectedAppIds([]); fetchData(); } catch { toast.error('Failed'); } };

  // Open the Direct Lead detail dialog. Seed the dialog with the row data so it
  // renders immediately, then fetch the full record (incl. email history) to
  // enrich the view. Hits new endpoint GET /admin/student-applications/{id}.
  const openDirectLeadDetail = async (lead) => {
    setDirectLeadDetail({ ...lead, email_history: [] });
    setDirectLeadDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/student-applications/${lead.id}`);
      setDirectLeadDetail(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load lead details');
    } finally {
      setDirectLeadDetailLoading(false);
    }
  };

  const handleImportDirectLeads = async () => {
    if (!leadImportFile) { toast.error('Choose a .xlsx or .csv file first'); return; }
    const fd = new FormData();
    fd.append('file', leadImportFile);
    setLeadImporting(true);
    setLeadImportResult(null);
    try {
      const res = await axios.post(`${API}/admin/student-applications/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLeadImportResult(res.data);
      toast.success(`Imported ${res.data.inserted} new lead${res.data.inserted === 1 ? '' : 's'}`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setLeadImporting(false);
    }
  };

  const handleDownloadDirectLeadTemplate = () => {
    const csv = 'Full Name,Phone,Email,Lead Source\nAlex Johnson,+1 604 555 1001,alex@example.com,Instagram\nPriya Singh,+91 98765 43210,priya@example.com,Referral\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'direct_leads_template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  // ----- Direct Leads email + WhatsApp -----

  const pollDirectLeadEmailJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/admin/student-applications/email-jobs/${jobId}`);
      setDirectLeadEmailJob({ ...res.data, id: jobId });
      if (res.data.status === 'completed') {
        toast.success(`Email blast complete: ${res.data.sent} sent, ${res.data.failed} failed`);
        fetchData();
        fetchDirectLeadsEmailHistory();
        return;
      }
      setTimeout(() => pollDirectLeadEmailJob(jobId), 2000);
    } catch { /* silently stop */ }
  };

  const handleDirectLeadEmailAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setDirectLeadEmailUploadingAttachment(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`"${f.name}" larger than 10 MB — skipped`); continue; }
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/admin/outreach/upload-attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setDirectLeadEmailAttachments(prev => [...prev, res.data]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setDirectLeadEmailUploadingAttachment(false);
      e.target.value = '';
    }
  };

  const handleDirectLeadEmailRemoveAttachment = async (fileId) => {
    try { await axios.delete(`${API}/admin/outreach/attachments/${fileId}`); } catch { /* best-effort */ }
    setDirectLeadEmailAttachments(prev => prev.filter(a => a.file_id !== fileId));
  };

  const handleDirectLeadSendTest = async () => {
    const to = (directLeadEmailTestAddr || '').trim();
    if (!to) { toast.error('Enter an email to send test to'); return; }
    if (!directLeadEmailDraft.subject.trim() || !directLeadEmailDraft.body.trim()) { toast.error('Subject and body must be filled out'); return; }
    setDirectLeadEmailSendingTest(true);
    try {
      await axios.post(`${API}/admin/student-applications/send-test-email`, {
        to_email: to,
        subject: directLeadEmailDraft.subject,
        body: directLeadEmailDraft.body,
        attachment_ids: directLeadEmailAttachments.map(a => a.file_id),
      });
      toast.success(`Test sent to ${to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally {
      setDirectLeadEmailSendingTest(false);
    }
  };

  const handleDirectLeadSendMass = async () => {
    if (selectedAppIds.length === 0) { toast.error('Select at least one lead'); return; }
    if (!directLeadEmailDraft.subject.trim() || !directLeadEmailDraft.body.trim()) { toast.error('Subject and body are required'); return; }
    setDirectLeadEmailSendingMass(true);
    try {
      const res = await axios.post(`${API}/admin/student-applications/mass-email`, {
        lead_ids: selectedAppIds,
        subject: directLeadEmailDraft.subject,
        body: directLeadEmailDraft.body,
        delay_seconds: Number(directLeadEmailDraft.delay_seconds) || 0,
        attachment_ids: directLeadEmailAttachments.map(a => a.file_id),
      });
      toast.success(`Started sending to ${res.data.total} leads`);
      setDirectLeadEmailJob({ id: res.data.job_id, total: res.data.total, sent: 0, failed: 0, status: 'running' });
      setShowDirectLeadEmailDialog(false);
      setDirectLeadEmailAttachments([]);
      pollDirectLeadEmailJob(res.data.job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setDirectLeadEmailSendingMass(false);
    }
  };

  const handleDirectLeadGenerateAI = async () => {
    if (!directLeadAIBrief.trim()) { toast.error('Describe what you want to say'); return; }
    setDirectLeadAIGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/student-applications/ai-suggest`, { brief: directLeadAIBrief, tone: directLeadAITone });
      setDirectLeadEmailDraft(prev => ({ ...prev, subject: res.data.subject, body: res.data.body }));
      setShowDirectLeadAIDialog(false);
      toast.success('AI draft inserted — review & edit before sending');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI generation failed');
    } finally {
      setDirectLeadAIGenerating(false);
    }
  };

  // Strip everything except digits for wa.me URL format
  const buildWhatsAppLink = (phone, message) => {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits) return null;
    const text = encodeURIComponent(message || '');
    return `https://wa.me/${digits}?text=${text}`;
  };

  const renderLeadWhatsAppMessage = (lead, template) => {
    const fullName = lead.full_name || 'there';
    const firstName = fullName.split(' ')[0] || 'there';
    return (template || '')
      .replace(/\{\{full_name\}\}/g, fullName)
      .replace(/\{\{first_name\}\}/g, firstName)
      .replace(/\{\{reference_number\}\}/g, lead.reference_number || '');
  };

  const handleOpenWhatsAppLink = async (lead, link) => {
    window.open(link, '_blank', 'noopener,noreferrer');
    try {
      await axios.post(`${API}/admin/student-applications/${lead.id}/mark-whatsapped`, { lead_id: lead.id, message: directLeadWhatsAppMessage.slice(0, 500) });
    } catch { /* tracking is best-effort */ }
  };

  // Filtered + selected lead helpers
  const filteredDirectLeads = directLeadsStatusFilter === 'all'
    ? studentApps
    : studentApps.filter(a => (a.status || 'new') === directLeadsStatusFilter);
  const selectedDirectLeads = studentApps.filter(a => selectedAppIds.includes(a.id));
  const selectedDirectLeadsWithPhone = selectedDirectLeads.filter(a => (a.phone || '').replace(/\D/g, '').length >= 7);

  // Commission selection & delete
  const toggleCommSelect = (id) => setSelectedCommIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllComms = () => setSelectedCommIds(prev => prev.length === commissions.length ? [] : commissions.map(c => c.id));
  const handleDeleteComm = async (id) => { if (!window.confirm('Delete this commission?')) return; try { await axios.delete(`${API}/admin/commissions/${id}`); toast.success('Commission deleted'); fetchData(); } catch { toast.error('Failed'); } };
  const handleBulkDeleteComms = async () => { if (!selectedCommIds.length || !window.confirm(`Delete ${selectedCommIds.length} commission(s)?`)) return; try { await axios.post(`${API}/admin/commissions/bulk-delete`, { ids: selectedCommIds }); toast.success(`${selectedCommIds.length} commission(s) deleted`); setSelectedCommIds([]); fetchData(); } catch { toast.error('Failed'); } };

  // Recruiter students click
  const handleRecruiterNameClick = async (recruiter) => {
    setRecruiterStudentsView(recruiter);
    try { const res = await axios.get(`${API}/admin/recruiters/${recruiter.id}/students`); setRecruiterStudents(res.data); } catch { setRecruiterStudents([]); }
  };

  // Commission
  const handleCreateCommission = async () => { if (!commForm.recruiter_id || !commForm.amount) { toast.error('Recruiter and amount required'); return; } try { await axios.post(`${API}/admin/commissions`, { ...commForm, amount: parseFloat(commForm.amount) }); toast.success('Commission created'); setShowCommForm(false); setCommForm({ recruiter_id: '', student_id: '', student_name: '', amount: '', description: '' }); fetchData(); } catch { toast.error('Failed'); } };
  const handleUpdateCommissionStatus = async (id, status) => { try { await axios.put(`${API}/admin/commissions/${id}`, { status }); toast.success(`Marked ${status}`); fetchData(); } catch { toast.error('Failed'); } };

  const handleUpdateAppStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/admin/student-applications/${appId}/status`, { status });
      toast.success(`Status updated to ${appStatusLabels[status] || status}`);
      fetchData();
    } catch { toast.error('Failed to update status'); }
  };

  // Colleges
  const openCollegeForm = (college = null) => {
    if (college) {
      setEditingCollege(college);
      setCollegeForm({
        name: college.name || '',
        location: college.location || '',
        locations: Array.isArray(college.locations) && college.locations.length > 0
          ? college.locations
          : (college.location ? [college.location] : []),
        country: college.country || '',
        website: college.website || '',
        notes: college.notes || '',
        representative_email: college.representative_email || '',
        college_fee: (college.college_fee ?? college.commission_amount) ?? '',
        programs: college.programs || [],
        requires_declaration_form: !!college.requires_declaration_form,
        declaration_template_filename: college.declaration_template_filename || '',
        docusign_ea_template_id: college.docusign_ea_template_id || '',
        ptib_admission_rules_notes: college.ptib_admission_rules_notes || '',
      });
    } else {
      setEditingCollege(null);
      setCollegeForm({ name: '', location: '', locations: [], country: '', website: '', notes: '', college_fee: '', representative_email: '', programs: [], requires_declaration_form: false, declaration_template_filename: '', docusign_ea_template_id: '', ptib_admission_rules_notes: '' });
    }
    setNewProgramForm({ name: '', default_sale_price: '' });
    setShowCollegeForm(true);
  };
  const handleSaveCollege = async () => {
    if (!collegeForm.name.trim()) { toast.error('College name is required'); return; }
    const locationsList = (collegeForm.locations || [])
      .map(l => (l || '').trim())
      .filter(l => l.length > 0);
    const payload = {
      name: collegeForm.name,
      location: locationsList.join(', '), // keep legacy single-location field in sync
      locations: locationsList,
      country: collegeForm.country,
      website: collegeForm.website,
      notes: collegeForm.notes,
      representative_email: collegeForm.representative_email.trim(),
      college_fee: collegeForm.college_fee === '' ? 0 : parseFloat(collegeForm.college_fee) || 0,
      programs: collegeForm.programs.map(p => ({ id: p.id, name: p.name, default_sale_price: Number(p.default_sale_price || 0) })),
      requires_declaration_form: !!collegeForm.requires_declaration_form,
      docusign_ea_template_id: (collegeForm.docusign_ea_template_id || '').trim() || null,
      ptib_admission_rules_notes: (collegeForm.ptib_admission_rules_notes || '').trim() || null,
    };
    try {
      if (editingCollege) {
        await axios.put(`${API}/admin/colleges/${editingCollege.id}`, payload);
        toast.success('College updated');
      } else {
        await axios.post(`${API}/admin/colleges`, payload);
        toast.success('College added');
      }
      setShowCollegeForm(false); setEditingCollege(null);
      fetchData();
    } catch { toast.error('Failed to save college'); }
  };
  const handleAddProgramToForm = () => {
    if (!newProgramForm.name.trim()) { toast.error('Program name required'); return; }
    const p = { id: `new-${Date.now()}`, name: newProgramForm.name.trim(), default_sale_price: parseFloat(newProgramForm.default_sale_price) || 0 };
    setCollegeForm(prev => ({ ...prev, programs: [...prev.programs, p] }));
    setNewProgramForm({ name: '', default_sale_price: '' });
  };
  const handleRemoveProgramFromForm = (pid) => {
    setCollegeForm(prev => ({ ...prev, programs: prev.programs.filter(p => p.id !== pid) }));
  };
  const handleEditProgramInForm = (pid, field, value) => {
    setCollegeForm(prev => ({
      ...prev,
      programs: prev.programs.map(p => p.id === pid ? { ...p, [field]: field === 'default_sale_price' ? value : value } : p),
    }));
  };
  const handleDeleteCollege = async (id) => {
    if (!window.confirm('Delete this college? Students assigned to it will be unassigned.')) return;
    try { await axios.delete(`${API}/admin/colleges/${id}`); toast.success('College deleted'); fetchData(); } catch { toast.error('Failed'); }
  };
  const openAssignCollege = (student) => {
    setAssigningCollegeFor(student);
    setSelectedCollegeToAssign(student.assigned_college || '');
    setSelectedProgramToAssign(student.assigned_program_id || '');
    setSalePriceToAssign(student.sale_price ? String(student.sale_price) : '');
  };
  const handleAssignCollege = async () => {
    if (!assigningCollegeFor) return;
    const payload = { college_id: selectedCollegeToAssign || '' };
    if (selectedCollegeToAssign && selectedProgramToAssign) payload.program_id = selectedProgramToAssign;
    if (salePriceToAssign !== '') {
      const sp = parseFloat(salePriceToAssign);
      if (isNaN(sp) || sp < 0) { toast.error('Sale price must be a non-negative number'); return; }
      payload.sale_price = sp;
    }
    try {
      const res = await axios.put(`${API}/admin/students/${assigningCollegeFor.id}/college`, payload);
      toast.success(selectedCollegeToAssign ? 'College assigned' : 'College unassigned');
      setAssigningCollegeFor(null);
      if (selectedStudent && selectedStudent.id === assigningCollegeFor.id) {
        setSelectedStudent(prev => ({
          ...prev,
          assigned_college: res.data.assigned_college,
          assigned_college_name: res.data.assigned_college_name,
          assigned_program_id: res.data.assigned_program_id || '',
          assigned_program_name: res.data.assigned_program_name || '',
        }));
      }
      fetchData();
    } catch { toast.error('Failed to assign college'); }
  };

  const handleUpdateStage = async (studentId, stage) => {
    try {
      await axios.put(`${API}/admin/students/${studentId}/stage`, { stage });
      toast.success(`Stage updated to ${PIPELINE_STAGE_LABELS[stage] || stage}`);
      // Optimistic UI: update list + selected immediately so the column reflects right away
      setStudents(prev => prev.map(s => s.id === studentId ? { ...s, application_stage: stage } : s));
      if (selectedStudent && selectedStudent.id === studentId) {
        setSelectedStudent(prev => ({ ...prev, application_stage: stage }));
      }
      fetchData();
    } catch { toast.error('Failed to update stage'); }
  };

  const handleSavePricing = async () => {
    if (!selectedStudent) return;
    const payload = {};
    for (const k of ['sale_price', 'college_fee_snapshot', 'recruiter_commission_snapshot']) {
      if (pricingDraft[k] !== '' && pricingDraft[k] !== null && pricingDraft[k] !== undefined) {
        const n = parseFloat(pricingDraft[k]);
        if (isNaN(n) || n < 0) { toast.error(`${k} must be a non-negative number`); return; }
        payload[k] = n;
      } else {
        payload[k] = 0;
      }
    }
    try {
      await axios.put(`${API}/admin/students/${selectedStudent.id}/pricing`, payload);
      toast.success('Pricing saved');
      setSelectedStudent(prev => ({ ...prev, ...payload }));
      setPricingDirty(false);
      fetchData();
    } catch { toast.error('Failed to save pricing'); }
  };

  useEffect(() => {
    if (selectedStudent) {
      const col = colleges.find(c => c.id === selectedStudent.assigned_college);
      const rec = recruiters.find(r => r.id === selectedStudent.recruiter_id);
      setPricingDraft({
        sale_price: selectedStudent.sale_price ? String(selectedStudent.sale_price) : '',
        college_fee_snapshot: (selectedStudent.college_fee_snapshot ?? col?.college_fee ?? col?.commission_amount) ? String(selectedStudent.college_fee_snapshot ?? col?.college_fee ?? col?.commission_amount) : '',
        recruiter_commission_snapshot: (selectedStudent.recruiter_commission_snapshot ?? rec?.commission_per_student) ? String(selectedStudent.recruiter_commission_snapshot ?? rec?.commission_per_student) : '',
      });
      setPricingDirty(false);
    }
  }, [selectedStudent, colleges, recruiters]);

  // Forward-to-college
  const [forwarding, setForwarding] = useState(false);

  // Forward-to-college dialog state
  const [showForwardDialog, setShowForwardDialog] = useState(false);
  const [forwardStudentId, setForwardStudentId] = useState(null);
  const [forwardDraft, setForwardDraft] = useState({ override_email: '', extra_notes: '' });
  const [forwardExtras, setForwardExtras] = useState([]); // File[] picked by admin
  const [forwardDefaultEmail, setForwardDefaultEmail] = useState('');
  const [forwardStudentName, setForwardStudentName] = useState('');
  const [forwardExistingDocs, setForwardExistingDocs] = useState(0);

  const openForwardDialog = (studentId) => {
    const student = students.find(s => s.id === studentId) || selectedStudent;
    if (!student) { toast.error('Student not found'); return; }
    const collegeId = student.assigned_college || student.preferred_college_id;
    const college = colleges.find(c => c.id === collegeId);
    const repEmail = college?.representative_email?.trim() || '';
    setForwardStudentId(studentId);
    setForwardStudentName(student.full_name || 'student');
    setForwardDefaultEmail(repEmail);
    setForwardDraft({ override_email: repEmail, extra_notes: '' });
    setForwardExtras([]);
    setForwardExistingDocs((studentFiles && studentFiles.length) || (student.documents?.length || 0));
    setShowForwardDialog(true);
  };

  const handleForwardToCollege = () => openForwardDialog(selectedStudent?.id);

  // ── Full Reset ────────────────────────────────────────────────────────
  // Wipes every stage-derived field (EA envelope, COE stamps, tranche
  // financials, invoice flags) and drops the student back to
  // profile_created. Used mostly during dev/QA — nice to have a real
  // student to rehearse the full pipeline against without hand-cleaning
  // ea_signed_at / ea_envelope_id / etc.
  const [resettingStudent, setResettingStudent] = useState(false);
  const handleFullResetStudent = async () => {
    if (!selectedStudent) return;
    const name = selectedStudent.full_name || 'this student';
    const confirmed = window.confirm(
      `Full reset ${name}?\n\n` +
      `This will:\n` +
      `  • Void the DocuSign EA envelope (if any)\n` +
      `  • Wipe every EA field (envelope, status, signed date, cached extraction)\n` +
      `  • Clear COE stamps + all tranche financials\n` +
      `  • Cancel any pending commission invoice flags\n` +
      `  • Drop pipeline stage back to Profile Created\n\n` +
      `The student's core profile (name, docs, agreement) is kept.\n\n` +
      `Continue?`
    );
    if (!confirmed) return;
    setResettingStudent(true);
    try {
      const res = await axios.post(
        `${API}/admin/students/${selectedStudent.id}/full-reset`,
        {},
      );
      if (res.data?.reset === false) {
        toast.info(res.data?.reason || 'Already at Profile Created');
      } else {
        const n = (res.data?.cleared_fields || []).length;
        toast.success(`Student reset — ${n} field${n === 1 ? '' : 's'} cleared, stage back to Profile Created`);
        // Locally patch the student so the dialog updates without a refetch.
        setSelectedStudent(prev => prev ? { ...prev, application_stage: 'profile_created', ea_envelope_id: null, ea_docusign_envelope_id: null, ea_status: null, ea_signed_at: null, ea_sent_at: null } : prev);
        setStudents(prev => prev.map(s => s.id === selectedStudent.id ? { ...s, application_stage: 'profile_created' } : s));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setResettingStudent(false);
    }
  };

  const handleSubmitForward = async () => {
    if (!forwardStudentId) return;
    const targetEmail = forwardDraft.override_email.trim();
    if (!targetEmail) { toast.error('Please provide a recipient email'); return; }
    setForwarding(true);
    try {
      const fd = new FormData();
      fd.append('override_email', targetEmail);
      if (forwardDraft.extra_notes.trim()) fd.append('extra_notes', forwardDraft.extra_notes.trim());
      forwardExtras.forEach(file => { fd.append('extra_files', file); });
      const res = await axios.post(
        `${API}/admin/students/${forwardStudentId}/forward-to-college`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(res.data.message);
      if (res.data.skipped?.length) toast.warning(`Skipped: ${res.data.skipped.join(', ')}`);
      setShowForwardDialog(false);
      fetchData();
      fetchForwards();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to forward');
    } finally {
      setForwarding(false);
    }
  };
  const [forwards, setForwards] = useState([]);

  const fetchForwards = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/forwards`);
      setForwards(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (tab === 'forwards') fetchForwards();
  }, [tab, fetchForwards]);

  const handleDeleteForward = async (fid) => {
    if (!window.confirm('Delete this forward history entry? (This does not un-send the email.)')) return;
    try {
      await axios.delete(`${API}/admin/forwards/${fid}`);
      toast.success('Entry removed');
      fetchForwards();
    } catch { toast.error('Failed to delete'); }
  };

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`);
      setConversations(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (tab === 'messages') {
      fetchConversations();
      const iv = setInterval(fetchConversations, 8000);
      return () => clearInterval(iv);
    }
  }, [tab, fetchConversations]);

  const openConversation = async (conv) => {
    setActiveConv(conv);
    setMessageDraft('');
    try {
      const res = await axios.get(`${API}/messages/conversations/${conv.id}/messages`);
      setConvMessages(res.data);
      fetchConversations();
    } catch { toast.error('Failed to load messages'); }
  };

  useEffect(() => {
    if (!activeConv) return;
    const iv = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/messages/conversations/${activeConv.id}/messages`);
        setConvMessages(res.data);
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [activeConv]);

  const sendMessage = async () => {
    if (!activeConv || (!messageDraft.trim() && messageAttachments.length === 0)) return;
    setSendingMessage(true);
    try {
      const res = await axios.post(`${API}/messages/conversations/${activeConv.id}/messages`, {
        body: messageDraft.trim(),
        attachments: messageAttachments,
      });
      setConvMessages(prev => [...prev, res.data]);
      setMessageDraft('');
      setMessageAttachments([]);
      fetchConversations();
    } catch { toast.error('Failed to send message'); }
    finally { setSendingMessage(false); }
  };

  const handleMessageAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/messages/upload-attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMessageAttachments(prev => [...prev, res.data]);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  const handleDeleteConversation = async (convId) => {
    if (!window.confirm('Delete this entire conversation? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/messages/conversations/${convId}`);
      toast.success('Conversation deleted');
      if (activeConv?.id === convId) { setActiveConv(null); setConvMessages([]); }
      fetchConversations();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteAllConversations = async () => {
    if (!window.confirm('Delete ALL conversations across all recruiters? This cannot be undone.')) return;
    try {
      const res = await axios.delete(`${API}/admin/messages`);
      toast.success(`Deleted ${res.data.deleted || 0} messages`);
      setActiveConv(null); setConvMessages([]);
      fetchConversations();
    } catch { toast.error('Failed to delete all'); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-gray-400">Loading...</div></div>;

  const NAV_ITEMS = [
    { id: 'recruiters', label: 'Recruiters', icon: Users, group: 'people' },
    { id: 'sub-agents', label: 'Sub-Agents', icon: UsersThree, group: 'people' },
    { id: 'outreach', label: 'Outreach', icon: Megaphone, group: 'people' },
    { id: 'calls', label: 'Calls', icon: CalendarBlank, group: 'people' },
    { id: 'students', label: 'Students', icon: Student, group: 'pipeline' },
    { id: 'applications', label: 'Direct Leads', icon: UserCirclePlus, group: 'pipeline' },
    { id: 'test-requests', label: 'Test Requests', icon: FileText, group: 'pipeline' },
    { id: 'forwards', label: 'Forward History', icon: PaperPlaneTilt, group: 'pipeline' },
    { id: 'commissions', label: 'Commissions', icon: CurrencyDollar, group: 'commerce' },
    { id: 'invoices', label: 'Invoices', icon: FilePdf, group: 'commerce' },
    { id: 'colleges', label: 'Colleges', icon: Buildings, group: 'commerce' },
    { id: 'institutions', label: 'Institutions', icon: GraduationCap, group: 'commerce' },
    { id: 'messages', label: 'Messages', icon: ChatCircle, group: 'system' },
    { id: 'contacts', label: 'Contact Forms', icon: EnvelopeSimple, group: 'system' },
    { id: 'score-report', label: 'Score Report', icon: FilePdf, group: 'system' },
    { id: 'sa-finance', label: 'SA Finance', icon: CurrencyDollar, group: 'commerce' },
    { id: 'finance-ledger', label: 'Finance Ledger', icon: CurrencyDollar, group: 'commerce' },
    { id: 'credit-cards', label: 'Credit Cards', icon: CurrencyDollar, group: 'commerce' },
    { id: 'preauth', label: 'Pre-Authorization', icon: ShieldCheck, group: 'commerce' },
    { id: 'recruiter-eod', label: 'Recruiter EOD Email', icon: EnvelopeSimple, group: 'system' },
    { id: 'trash', label: 'Trash', icon: TrashSimple, group: 'system' },
  ];
  const NAV_GROUPS = [
    { id: 'people',   label: 'People' },
    { id: 'pipeline', label: 'Pipeline' },
    { id: 'commerce', label: 'Commerce' },
    { id: 'system',   label: 'System' },
  ];

  return (
    <div className="min-h-screen bg-[#f8f7f4] text-gray-900" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar — editorial, monochrome, grouped */}
        <aside className="hidden md:flex md:flex-col md:w-64 md:min-h-screen md:shrink-0 bg-white border-r border-gray-200/70 px-5 py-8 sticky top-0" data-testid="admin-sidebar">
          {/* Brand mark */}
          <div className="mb-10">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400">Admin</p>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mt-1 leading-none" style={{ fontFamily: "'Libre Baskerville', serif" }}>Study Alliance</h1>
            <div className="mt-3 h-px w-8 bg-gray-900/80" aria-hidden />
          </div>
          <nav className="flex flex-col gap-6 flex-1" data-testid="admin-sidebar-nav">
            {NAV_GROUPS.map(g => (
              <div key={g.id}>
                <p className="px-2 mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400">{g.label}</p>
                <div className="flex flex-col gap-0.5">
                  {NAV_ITEMS.filter(n => n.group === g.id).map(t => {
                    const active = tab === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`relative flex items-center gap-3 pl-3 pr-2 py-2 text-[13px] rounded-md transition-colors ${active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                        data-testid={`admin-tab-${t.id}`}
                      >
                        <t.icon size={16} weight={active ? 'fill' : 'regular'} />
                        <span className="font-medium">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Mobile tab bar */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 z-40 flex overflow-x-auto" data-testid="admin-mobile-tabs">
          {NAV_ITEMS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 min-w-[80px] flex flex-col items-center gap-1 px-3 py-2 text-[10px] font-medium ${tab === t.id ? 'text-gray-900 bg-gray-100' : 'text-gray-500'}`} data-testid={`admin-mobile-tab-${t.id}`}>
              <t.icon size={18} />{t.label}
            </button>
          ))}
        </div>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 pb-24 md:pb-14">
            {/* Global pending-invoices banner — visible on ALL admin tabs. */}
            {pendingInvoices > 0 && (
              <button
                type="button"
                onClick={() => { setTab('students'); setStudentStatusFilter([]); setPendingInvoicesFilter(true); }}
                className="relative w-full mb-8 flex items-center gap-4 text-left rounded-none border-l-2 border-gray-900 bg-white hover:bg-gray-50 px-5 py-4 transition-colors group"
                data-testid="pending-invoices-banner"
              >
                <span className="shrink-0 w-8 h-8 rounded-full border border-gray-900/10 bg-gray-900/5 flex items-center justify-center">
                  <CurrencyDollar size={14} weight="fill" className="text-gray-900" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-gray-900">
                    {pendingInvoices} student{pendingInvoices === 1 ? '' : 's'} awaiting commission invoice
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Colleges confirmed 1/2 Money Received — raise your invoice to close the loop.
                  </p>
                </div>
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 group-hover:text-gray-900 transition-colors">
                  Review →
                </span>
              </button>
            )}
            {/* Editorial header — no card, just typography */}
            <header className="mb-10 flex items-start justify-between gap-6" data-testid="admin-hero">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400">{NAV_GROUPS.find(g => g.id === (NAV_ITEMS.find(n => n.id === tab)?.group || 'people'))?.label || 'Admin'}</p>
                <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight text-gray-900 mt-2 leading-[1.05]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  {NAV_ITEMS.find(t => t.id === tab)?.label || 'Dashboard'}
                </h1>
                <p className="text-gray-500 text-[13px] mt-3 max-w-xl leading-relaxed">Manage recruiters, students, commissions, and platform activity.</p>
              </div>
              <NotificationsBell />
            </header>

            {/* Stats — flat editorial row */}
            {stats && tab === 'recruiters' && (
              <section className="mb-12 border-t border-gray-200" data-testid="admin-stats">
                <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-gray-200">
                  {[
                    { icon: Users,            label: 'Recruiters',  value: stats.total_recruiters },
                    { icon: ClockCountdown,   label: 'Pending',     value: stats.pending_recruiters },
                    { icon: CheckCircle,      label: 'Active',      value: stats.active_recruiters },
                    { icon: Student,          label: 'Students',    value: stats.total_students },
                    { icon: CurrencyDollar,   label: 'Commissions', value: commSummary ? `$${commSummary.total_amount.toLocaleString()}` : '$0' },
                  ].map((s, i) => (
                    <div key={s.label} className={`px-5 py-6 ${i === 0 ? 'pl-0' : ''}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <s.icon size={13} className="text-gray-400" />
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">{s.label}</p>
                      </div>
                      <p className="text-3xl font-semibold text-gray-900 tabular-nums tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>{s.value}</p>
                    </div>
                  ))}
                </div>
                <div className="border-b border-gray-200" />
              </section>
            )}

        {/* Admin fee — slim editorial row */}
        {tab === 'recruiters' && (
          <div className="mb-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-gray-200" data-testid="admin-fee-card">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 mb-1">Admin Fee</p>
              <p className="text-[13px] text-gray-600 max-w-xl">
                Added on top of every student&rsquo;s sale price to compute the total tuition shown on invoices &amp; reports.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <Input
                  type="number"
                  min="0"
                  step="10"
                  value={adminFeeDraft}
                  onChange={e => setAdminFeeDraft(e.target.value)}
                  className="pl-7 h-10 w-32 tabular-nums bg-white border-gray-200"
                  data-testid="admin-fee-input"
                />
              </div>
              <Button
                size="sm"
                onClick={handleSaveAdminFee}
                disabled={savingAdminFee || Number(adminFeeDraft) === Number(adminFee)}
                className="bg-gray-900 hover:bg-black text-white h-10 px-5 rounded-md"
                data-testid="admin-fee-save-btn"
              >
                {savingAdminFee ? <CircleNotch size={14} className="mr-1 animate-spin" /> : null}
                {savingAdminFee ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}

        {/* Tabs nav removed — using sidebar */}

        {/* Revenue chart — shows on Recruiters (home) tab only */}
        {tab === 'recruiters' && students.length > 0 && (() => {
          const collegeById = Object.fromEntries(colleges.map(c => [c.id, c]));
          const recById = Object.fromEntries(recruiters.map(r => [r.id, r]));
          const netFor = (s) => {
            const c = collegeById[s.assigned_college];
            const r = recById[s.recruiter_id];
            const sale = Number(s.sale_price || 0);
            const fee = Number(s.college_fee_snapshot ?? c?.college_fee ?? c?.commission_amount ?? 0);
            const comm = Number(s.recruiter_commission_snapshot ?? r?.commission_per_student ?? 0);
            return Math.max(sale - fee - comm, 0);
          };
          const stageOf = (s) => s.application_stage || 'profile_created';
          // Build last-6-months buckets keyed by YYYY-MM (use updated_at falling back to created_at)
          const now = new Date();
          const months = [];
          for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const label = d.toLocaleString('en-US', { month: 'short' });
            months.push({ key, label, realised: 0, pipeline: 0 });
          }
          students.forEach(s => {
            if (!s.assigned_college) return;
            const net = netFor(s);
            if (net === 0) return;
            const ts = s.updated_at || s.created_at;
            if (!ts) return;
            const d = new Date(ts);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const bucket = months.find(m => m.key === key);
            if (!bucket) return;
            const stg = stageOf(s);
            if (stg === REALISED_STAGE) bucket.realised += net;
            if (!TERMINAL_STAGES.includes(stg)) bucket.pipeline += net;
          });
          const totalRealised = months.reduce((a, b) => a + b.realised, 0);
          const totalPipeline = months.reduce((a, b) => a + b.pipeline, 0);
          // Revenue-per-college bar chart (top 5)
          const perCollege = colleges.map(c => {
            const list = students.filter(s => s.assigned_college === c.id);
            const realised = list.filter(s => stageOf(s) === REALISED_STAGE).reduce((a, s) => a + netFor(s), 0);
            const potential = list.filter(s => !TERMINAL_STAGES.includes(stageOf(s))).reduce((a, s) => a + netFor(s), 0);
            return { name: c.name, realised, potential };
          }).filter(c => c.realised + c.potential > 0).sort((a, b) => (b.realised + b.potential) - (a.realised + a.potential)).slice(0, 5);

          if (totalRealised === 0 && totalPipeline === 0) {
            return (
              <div className="bg-white border border-gray-200 rounded-lg p-8 mb-12" data-testid="revenue-chart-empty">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 mb-1">Revenue</p>
                <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Nothing to plot yet</h3>
                <p className="text-[13px] text-gray-500 mt-2 max-w-md">Assign students to colleges with sale prices to see your net revenue trend appear here.</p>
              </div>
            );
          }

          return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 mb-12 border border-gray-200 rounded-lg overflow-hidden bg-white divide-y lg:divide-y-0 lg:divide-x divide-gray-200" data-testid="revenue-chart-section">
              {/* Monthly net revenue area chart */}
              <div className="lg:col-span-2 p-6 lg:p-8" data-testid="revenue-monthly-chart">
                <div className="flex items-start justify-between mb-6 gap-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">Study Alliance Earning</p>
                    <h3 className="text-lg font-semibold text-gray-900 mt-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>Last 6 months</h3>
                    <p className="text-[11px] text-gray-400 mt-1">sale price − college fee − recruiter commission</p>
                  </div>
                  <div className="flex gap-6 text-right shrink-0">
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.16em] text-gray-400">Realised</p>
                      <p className="text-lg font-semibold text-gray-900 tabular-nums mt-0.5">${totalRealised.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.16em] text-gray-400">Pipeline</p>
                      <p className="text-lg font-semibold text-gray-900 tabular-nums mt-0.5">${totalPipeline.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={months} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="pipeFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#111827" stopOpacity={0.14} />
                        <stop offset="100%" stopColor="#111827" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="realFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#111827" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#111827" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 4" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                    <Tooltip
                      contentStyle={{ fontSize: 11, borderRadius: 4, border: '1px solid #e5e7eb', boxShadow: 'none' }}
                      formatter={(value, name) => [`$${Number(value).toLocaleString()}`, name === 'pipeline' ? 'Pipeline' : 'Realised']}
                    />
                    <Area type="monotone" dataKey="pipeline" stroke="#9ca3af" strokeWidth={1.5} fill="url(#pipeFill)" />
                    <Area type="monotone" dataKey="realised" stroke="#111827" strokeWidth={1.75} fill="url(#realFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Top colleges bar chart */}
              <div className="p-6 lg:p-8" data-testid="revenue-college-chart">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">Top Colleges</p>
                <h3 className="text-lg font-semibold text-gray-900 mt-1 mb-6" style={{ fontFamily: "'Libre Baskerville', serif" }}>By earning</h3>
                {perCollege.length === 0 ? (
                  <p className="text-sm text-gray-400">No revenue data yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={perCollege} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="2 4" stroke="#e5e7eb" horizontal={false} />
                      <XAxis type="number" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                      <YAxis type="category" dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#4b5563' }} width={90} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: '1px solid #e5e7eb', boxShadow: 'none' }} formatter={v => `$${Number(v).toLocaleString()}`} />
                      <Legend wrapperStyle={{ fontSize: 10, color: '#6b7280' }} />
                      <Bar dataKey="realised" fill="#111827" name="Realised" radius={[0, 2, 2, 0]} />
                      <Bar dataKey="potential" fill="#9ca3af" name="Pipeline" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          );
        })()}

        {/* ===== RECRUITERS TAB ===== */}
        {tab === 'recruiters' && (
          <div data-testid="admin-recruiters-list">
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchData}
                  className="h-9 text-[12px] font-medium border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md bg-white"
                  data-testid="refresh-recruiters-btn"
                  title="Re-fetch the recruiter list"
                >
                  <ArrowsClockwise size={13} className="mr-1.5" /> Refresh
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openIdleReminderDialog}
                  className="h-9 text-[12px] font-medium text-gray-700 border-gray-200 hover:bg-gray-100 rounded-md bg-white"
                  data-testid="send-idle-reminders-btn"
                  title="Compose & email all active recruiters idle >24h"
                >
                  <EnvelopeSimple size={13} className="mr-1.5" /> Send Idle Reminders
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openAnnouncementHistory}
                  className="h-9 text-[12px] font-medium text-gray-700 border-gray-200 hover:bg-gray-100 rounded-md bg-white"
                  data-testid="announcement-history-btn"
                  title="Every past mass announcement with recipient & dismissal counts"
                >
                  <ClockCounterClockwise size={13} className="mr-1.5" /> Announcement History
                </Button>
              </div>
              <Button size="sm" onClick={() => { setNewRecruiterHasSignedAgreement(true); setShowAddRecruiterDialog(true); }} className="h-9 bg-gray-900 hover:bg-black text-white rounded-md px-4 text-[12px] font-medium" data-testid="add-recruiter-in-person-btn">
                <Plus size={13} className="mr-1.5" /> Add Recruiter
              </Button>
            </div>
            {selectedRecruiterIds.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 flex items-center justify-between">
                <span className="text-sm text-gray-600">{selectedRecruiterIds.length} selected</span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => { setAnnouncementDraft({ subject: '', body: '' }); setShowAnnouncementDialog(true); }}
                    className="bg-gray-900 hover:bg-black text-white h-9 rounded-md px-4 text-[12px] font-medium"
                    data-testid="send-announcement-btn"
                    title="Compose an announcement email that also shows as a news flash inside the recruiter portal"
                  >
                    <Megaphone size={14} className="mr-1.5" /> Send Announcement
                  </Button>
                  <Button size="sm" variant="destructive" onClick={handleBulkDeleteRecruiters} data-testid="bulk-delete-recruiters-btn"><Trash size={14} className="mr-1" /> Delete Selected</Button>
                </div>
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full table-auto text-[13px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-3 w-8"><Checkbox checked={selectedRecruiterIds.length === recruiters.length && recruiters.length > 0} onCheckedChange={toggleAllRecruiters} data-testid="select-all-recruiters" /></th>
                      <th className="text-left px-3 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em]">Recruiter</th>
                      <th className="text-left px-3 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em] hidden lg:table-cell">Company</th>
                      <th className="text-left px-3 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em] hidden xl:table-cell w-[90px]">Country</th>
                      <th className="text-center px-2 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em] w-[80px]">Students</th>
                      <th className="text-left px-3 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em] w-[90px]">Status</th>
                      <th className="text-left px-3 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em] hidden md:table-cell w-[95px]">Date</th>
                      <th className="text-right px-3 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em] w-[150px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recruiters.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400 text-sm">No recruiters yet</td></tr>
                    ) : recruiters.map(r => (
                      <tr key={r.id} className="hover:bg-gray-50/50" data-testid={`recruiter-row-${r.id}`}>
                        <td className="px-3 py-3"><Checkbox checked={selectedRecruiterIds.includes(r.id)} onCheckedChange={() => toggleRecruiterSelect(r.id)} /></td>
                        <td className="px-3 py-3">
                          <button onClick={() => handleRecruiterNameClick(r)} className="text-left hover:underline" data-testid={`recruiter-name-${r.id}`}>
                            <p className="text-[13px] font-medium text-gray-900 hover:text-gray-700 flex items-center gap-1.5">
                              {r.full_name}
                              {r.hide_commission && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0 text-[9px] font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-wider"
                                  title="Commission is HIDDEN from this recruiter — they cannot see sale price, tiers, or earnings"
                                  data-testid={`recruiter-hidden-badge-${r.id}`}
                                >
                                  <Lock size={9} weight="bold" /> Hidden
                                </span>
                              )}
                              {r.declaration_form_enabled === false && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0 text-[9px] font-semibold rounded-full bg-sky-100 text-sky-800 border border-sky-300 uppercase tracking-wider"
                                  title="This recruiter's students are exempt from uploading a Student Declaration Form"
                                  data-testid={`recruiter-declaration-off-badge-${r.id}`}
                                >
                                  Declaration: OFF
                                </span>
                              )}
                            </p>
                            <p className="text-[11px] text-gray-500 truncate max-w-[220px]">{r.email}</p>
                          </button>
                        </td>
                        <td className="px-3 py-3 text-[12px] text-gray-600 hidden lg:table-cell truncate max-w-[200px]" title={r.company_name}>{r.company_name}</td>
                        <td className="px-3 py-3 text-[12px] text-gray-600 hidden xl:table-cell">{r.country}</td>
                        <td className="px-2 py-3 text-center"><span className="inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 text-[11px] font-semibold rounded-full bg-gray-100 text-gray-700 tabular-nums">{r.student_count || 0}</span></td>
                        <td className="px-3 py-3"><span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${statusColors[r.status] || ''}`}>{statusLabels[r.status] || r.status}</span></td>
                        <td className="px-3 py-3 text-[11px] text-gray-500 hidden md:table-cell whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {/* Primary action button — varies by status */}
                            {r.status === 'pending' && (
                              <Button size="sm" onClick={() => handleSendAgreement(r)} className="h-8 px-2.5 text-[11px] font-medium bg-gray-900 hover:bg-black text-white rounded-md" data-testid={`send-agreement-${r.id}`} title="Send Agreement">
                                <EnvelopeSimple size={13} className="mr-1" /> Agreement
                              </Button>
                            )}
                            {r.status === 'agreement_sent' && (
                              <Button
                                size="sm"
                                onClick={() => handleRefreshAgreementStatus(r.id)}
                                disabled={refreshingStatusFor === r.id}
                                className="h-8 px-2.5 text-[11px] font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-md"
                                data-testid={`refresh-status-${r.id}`}
                                title="Re-check DocuSign signature status"
                              >
                                <ArrowsClockwise size={13} className={`mr-1 ${refreshingStatusFor === r.id ? 'animate-spin' : ''}`} />
                                {refreshingStatusFor === r.id ? 'Checking' : 'Refresh'}
                              </Button>
                            )}
                            {r.status === 'agreement_signed' && (
                              <Button size="sm" onClick={() => openActivateDialog(r)} className="h-8 px-2.5 text-[11px] font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-md" data-testid={`activate-recruiter-${r.id}`} title="Activate recruiter">
                                <CheckCircle size={13} className="mr-1" /> Activate
                              </Button>
                            )}
                            {r.status === 'active' && (
                              <Button size="sm" onClick={() => handleSendLoginInfo(r.id)} className="h-8 px-2.5 text-[11px] font-medium bg-gray-900 hover:bg-black text-white rounded-md" data-testid={`send-login-${r.id}`} title="Send login credentials">
                                <Key size={13} className="mr-1" /> Login
                              </Button>
                            )}

                            {/* Secondary actions — collapsed into dropdown */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-500 hover:text-gray-800" data-testid={`row-menu-${r.id}`} title="More actions">
                                  <DotsThreeVertical size={18} weight="bold" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem onClick={() => { setSelectedRecruiter(r); setTierDraft(Array.isArray(r.commission_tiers) ? [...r.commission_tiers] : []); setNewTierRow({ sale_price: '', commission_amount: '' }); }} data-testid={`view-recruiter-${r.id}`}>
                                  <Eye size={14} className="mr-2" /> View Details
                                </DropdownMenuItem>
                                {r.status === 'active' && (
                                  <DropdownMenuItem onClick={() => handleImpersonateRecruiter(r)} data-testid={`impersonate-recruiter-${r.id}`}>
                                    <UserSwitch size={14} className="mr-2" /> Login as this recruiter
                                  </DropdownMenuItem>
                                )}
                                {r.status === 'active' && (
                                  <DropdownMenuItem onClick={() => handlePingSingleRecruiter(r.id)} data-testid={`menu-ping-${r.id}`}>
                                    <EnvelopeSimple size={14} className="mr-2" /> Send Reminder
                                  </DropdownMenuItem>
                                )}
                                {r.status === 'agreement_sent' && (
                                  <DropdownMenuItem onClick={() => handleRefreshAgreementStatus(r.id)} data-testid={`menu-refresh-status-${r.id}`}>
                                    <ArrowsClockwise size={14} className="mr-2" /> Refresh Status
                                  </DropdownMenuItem>
                                )}
                                {(r.status === 'agreement_sent' || r.status === 'agreement_signed' || r.status === 'active') && (
                                  <DropdownMenuItem onClick={() => handleSendAgreement(r)} data-testid={`menu-resend-agreement-${r.id}`}>
                                    <PaperPlaneTilt size={14} className="mr-2" /> {r.status === 'active' ? 'Send New Agreement' : 'Resend Agreement'}
                                  </DropdownMenuItem>
                                )}
                                {r.status === 'pending' && (
                                  <DropdownMenuItem onClick={() => handleReject(r.id)} className="text-red-600 focus:text-red-700" data-testid={`reject-recruiter-${r.id}`}>
                                    <XCircle size={14} className="mr-2" /> Reject
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeleteRecruiter(r.id)} className="text-red-600 focus:text-red-700" data-testid={`delete-recruiter-${r.id}`}>
                                  <Trash size={14} className="mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===== STUDENTS TAB ===== */}
        {tab === 'students' && (
          <div data-testid="admin-students-list">
            {(() => {
              // Grand potential earning — computed off the currently-filtered list
              // so it stays in sync with recruiter/college/intake/date filters.
              // Excludes rejected students (they'll never earn); deleted students
              // aren't in the array to begin with.
              const liveStudents = filteredStudents.filter(s => !TERMINAL_STAGES.includes(s.application_stage || 'profile_created'));
              const grandTotal = potentialFor(filteredStudents);
              const intakeCount = new Set(liveStudents.map(s => s.intake_label).filter(Boolean)).size;
              return (
                <PotentialEarningGrandCard
                  total={grandTotal}
                  studentCount={liveStudents.length}
                  intakeCount={intakeCount}
                />
              );
            })()}
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">Student Pipeline</div>
              <div className="flex items-center gap-2">
                {bulkScanJob && (bulkScanJob.status === 'running' || bulkScanJob.status === 'queued') ? (
                  <Button
                    size="sm"
                    onClick={() => setShowBulkScanDialog(true)}
                    className="h-8 px-3 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="scan-all-id-match-btn"
                    title="View live progress of the running scan"
                  >
                    <CircleNotch size={14} className="mr-1 animate-spin" /> Scanning… ({bulkScanJob.processed}/{bulkScanJob.total}) · View
                  </Button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        disabled={bulkScanStarting}
                        className="h-8 px-3 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="scan-all-id-match-btn"
                        title="Cross-check student name, DOB and address against uploaded ID documents"
                      >
                        <IdentificationCard size={14} className="mr-1" /> Scan IDs vs Profiles
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                      <DropdownMenuItem
                        onClick={() => handleStartBulkIdScan()}
                        data-testid="scan-filtered-btn"
                        className="flex-col items-start py-2"
                      >
                        <div className="font-medium text-sm flex items-center gap-2">
                          <ShieldCheck size={14} /> Scan current view ({filteredStudents.length})
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5">Respects every active filter — intake, status, recruiter, college, search, date range.</div>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleStartBulkIdScan({ allStudents: true })}
                        data-testid="scan-everyone-btn"
                        className="flex-col items-start py-2"
                      >
                        <div className="font-medium text-sm flex items-center gap-2">
                          <IdentificationCard size={14} /> Scan everyone
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5">Every non-deleted student with at least one ID uploaded.</div>
                      </DropdownMenuItem>
                      {bulkScanJob && bulkScanJob.status === 'done' && (
                        <DropdownMenuItem
                          onClick={() => setShowBulkScanDialog(true)}
                          data-testid="scan-view-last-btn"
                          className="flex-col items-start py-2 border-t border-gray-100"
                        >
                          <div className="font-medium text-sm flex items-center gap-2">
                            <Eye size={14} /> View last scan results
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{bulkScanJob.flagged || 0} flagged out of {bulkScanJob.total || 0}</div>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
            <div className="mb-8 pb-6 border-b border-gray-200" data-testid="student-filters">
              {/* Simplified toolbar — search + filters drawer + download modal */}
              <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="student-simplified-toolbar">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                  <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input placeholder="Search students by name, email, program…" value={studentSearch} onChange={e => setStudentSearch(e.target.value)} className="pl-9 h-10 bg-white border-gray-200" data-testid="simplified-search" />
                </div>
                <Button variant="outline" className="h-10" onClick={() => setFilterDrawerOpen(true)} data-testid="open-filter-drawer-btn">
                  <FunnelSimple size={14} className="mr-1.5" /> Filters
                  {(() => {
                    const n = (studentRecruiterFilter !== 'all' ? 1 : 0)
                      + (studentStatusFilter.length ? 1 : 0)
                      + (studentCollegeFilter !== 'all' ? 1 : 0)
                      + (studentIntakeFilter !== 'all' ? 1 : 0)
                      + (studentStartDate || studentEndDate ? 1 : 0);
                    return n > 0 ? <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-indigo-600 text-white">{n}</span> : null;
                  })()}
                </Button>
                <Button className="h-10" onClick={() => setDownloadModalOpen(true)} data-testid="open-download-modal-btn">
                  <DownloadSimple size={14} className="mr-1.5" /> Download
                </Button>
                <Button variant="ghost" className="h-10 text-xs text-gray-500" onClick={() => setShowClassicFilters(v => !v)} data-testid="toggle-classic-filters">
                  {showClassicFilters ? 'Hide classic filters' : 'Show classic filters'}
                </Button>
                {/* Active filter chips — tap to clear */}
                {studentRecruiterFilter !== 'all' && (
                  <button type="button" onClick={() => setStudentRecruiterFilter('all')} className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-700 hover:border-rose-300 hover:text-rose-700">
                    Recruiter · {(recruiters.find(r => r.id === studentRecruiterFilter)?.full_name || 'selected')} <span className="opacity-50 group-hover:opacity-100">×</span>
                  </button>
                )}
                {studentIntakeFilter !== 'all' && (
                  <button type="button" onClick={() => setStudentIntakeFilter('all')} className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-700 hover:border-rose-300 hover:text-rose-700">
                    Intake · {studentIntakeFilter} <span className="opacity-50 group-hover:opacity-100">×</span>
                  </button>
                )}
                {studentCollegeFilter !== 'all' && (
                  <button type="button" onClick={() => setStudentCollegeFilter('all')} className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-700 hover:border-rose-300 hover:text-rose-700">
                    College · set <span className="opacity-50 group-hover:opacity-100">×</span>
                  </button>
                )}
                {studentStatusFilter.length > 0 && (
                  <button type="button" onClick={() => setStudentStatusFilter([])} className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-700 hover:border-rose-300 hover:text-rose-700">
                    Status · {studentStatusFilter.length} <span className="opacity-50 group-hover:opacity-100">×</span>
                  </button>
                )}
                {(studentStartDate || studentEndDate) && (
                  <button type="button" onClick={() => { setStudentStartDate(''); setStudentEndDate(''); }} className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-700 hover:border-rose-300 hover:text-rose-700">
                    Date · {studentStartDate || 'any'} → {studentEndDate || 'any'} <span className="opacity-50 group-hover:opacity-100">×</span>
                  </button>
                )}
              </div>

              {showClassicFilters && (
              <>
              {/* Row 1 — filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-4">
                <div className="lg:col-span-2 min-w-0">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-1.5 block">Search</Label>
                  <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input placeholder="Name, email, program…" value={studentSearch} onChange={e => setStudentSearch(e.target.value)} className="pl-9 h-10 bg-white border-gray-200" data-testid="student-search-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-1.5 block">Status</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-10 w-full justify-between bg-white border-gray-200 font-normal text-left px-3"
                        data-testid="student-status-filter"
                      >
                        <span className="truncate text-sm">
                          {studentStatusFilter.length === 0
                            ? 'All'
                            : studentStatusFilter.length === 1
                            ? (PIPELINE_STAGE_LABELS[studentStatusFilter[0]] || studentStatusFilter[0])
                            : `${studentStatusFilter.length} statuses selected`}
                        </span>
                        <DotsThreeVertical size={14} className="text-gray-400 shrink-0 ml-1" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0" align="start">
                      <div className="p-2 border-b border-gray-100 flex items-center justify-between gap-2">
                        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-gray-500">
                          {studentStatusFilter.length === 0 ? 'All stages' : `${studentStatusFilter.length} selected`}
                        </div>
                        {studentStatusFilter.length > 0 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setStudentStatusFilter([])}
                            className="h-6 px-2 text-xs"
                            data-testid="student-status-filter-clear"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto py-1">
                        {PIPELINE_STAGES.map(stg => {
                          const checked = studentStatusFilter.includes(stg);
                          return (
                            <label
                              key={stg}
                              className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 text-sm"
                              data-testid={`student-status-option-${stg}`}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => {
                                  setStudentStatusFilter(prev => checked ? prev.filter(x => x !== stg) : [...prev, stg]);
                                }}
                              />
                              <span className="text-gray-800">{PIPELINE_STAGE_LABELS[stg]}</span>
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-1.5 block">Recruiter</Label>
                  <Select value={studentRecruiterFilter} onValueChange={setStudentRecruiterFilter}>
                    <SelectTrigger data-testid="student-recruiter-filter" className="h-10 bg-white border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem>{uniqueRecruiters.map(r => (<SelectItem key={r.id} value={r.id}>{r.name || r.id}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-1.5 block">College</Label>
                  <Select value={studentCollegeFilter} onValueChange={setStudentCollegeFilter}>
                    <SelectTrigger data-testid="student-college-filter" className="h-10 bg-white border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="unassigned">Unassigned</SelectItem>{colleges.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-1.5 block">Intake</Label>
                  <Select value={studentIntakeFilter} onValueChange={setStudentIntakeFilter}>
                    <SelectTrigger data-testid="student-intake-filter" className="h-10 bg-white border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="unassigned">No intake</SelectItem>
                      {(() => {
                        const seen = new Map();
                        students.forEach(s => {
                          const lbl = s.intake_label;
                          if (lbl && !seen.has(lbl)) seen.set(lbl, s.intake_date || '');
                        });
                        const opts = [...seen.entries()].sort((a, b) => {
                          if (a[1] && b[1]) return a[1].localeCompare(b[1]);
                          return a[0].localeCompare(b[0]);
                        });
                        return opts.map(([lbl]) => (<SelectItem key={lbl} value={lbl}>{lbl}</SelectItem>));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-1.5 block">Created from</Label>
                  <Input type="date" value={studentStartDate} onChange={e => setStudentStartDate(e.target.value)} max={studentEndDate || undefined} data-testid="student-start-date-filter" className="h-10 bg-white border-gray-200" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-1.5 block">Created to</Label>
                  <Input type="date" value={studentEndDate} onChange={e => setStudentEndDate(e.target.value)} min={studentStartDate || undefined} data-testid="student-end-date-filter" className="h-10 bg-white border-gray-200" />
                </div>
              </div>
              </>
              )}

              {/* Row 2 — meta + toolbar */}
              <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-3">
                <p className="text-[12px] text-gray-500 mr-auto">
                  <span className="tabular-nums font-semibold text-gray-900">{filteredStudents.length}</span> result{filteredStudents.length !== 1 ? 's' : ''}
                  {filteredStudents.length > 0 && (() => {
                    const downloaded = filteredStudents.filter(s => s.last_downloaded_at).length;
                    const remaining = filteredStudents.length - downloaded;
                    return (
                      <>
                        <span className="mx-2 text-gray-300">·</span>
                        <span className="tabular-nums text-gray-700" data-testid="downloaded-counter" title="Students already packaged in a ZIP download">
                          {downloaded}/{filteredStudents.length} downloaded
                        </span>
                        {remaining > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowOnlyNotDownloaded(v => !v)}
                            className={`ml-2 underline decoration-dotted underline-offset-2 hover:text-gray-900 ${showOnlyNotDownloaded ? 'text-gray-900 font-semibold' : ''}`}
                            data-testid="toggle-only-not-downloaded"
                          >
                            {showOnlyNotDownloaded ? 'show all' : `show only ${remaining} pending`}
                          </button>
                        )}
                      </>
                    );
                  })()}
                  {(studentStartDate || studentEndDate) && (
                    <button
                      type="button"
                      onClick={() => { setStudentStartDate(''); setStudentEndDate(''); }}
                      className="ml-3 text-gray-400 hover:text-gray-900 underline decoration-dotted underline-offset-2"
                      data-testid="student-dates-clear-btn"
                    >clear dates</button>
                  )}
                </p>
                {/* Accuplacer-pending chip — appears only when there are
                    any such students; registrar uses this to schedule the
                    in-house placement test for students with no English
                    test proof + no 2-yr Canadian transcript. */}
                {accuplacerPendingCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAccuplacerPending(v => !v)}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 text-[11.5px] font-medium rounded-md border transition ${showAccuplacerPending ? 'bg-purple-600 text-white border-purple-600 shadow-sm' : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'}`}
                    data-testid="toggle-accuplacer-pending"
                    title="Students who need to take the Accuplacer: no English test proof, no 2-yr Canadian transcript, and intent=Yes"
                  >
                    <GraduationCap size={13} weight={showAccuplacerPending ? 'fill' : 'regular'} />
                    Accuplacer pending
                    <span className={`tabular-nums font-semibold ml-0.5 px-1.5 py-0.5 rounded ${showAccuplacerPending ? 'bg-white/20 text-white' : 'bg-purple-100 text-purple-800'}`}>
                      {accuplacerPendingCount}
                    </span>
                  </button>
                )}
                <GroupByIntakeToggle
                  value={adminGroupByIntake}
                  onChange={setAdminGroupByIntake}
                  storageKey="admin.students.groupByIntake"
                  testId="admin-group-by-intake-toggle"
                />
                <Button
                  size="sm" variant="outline"
                  onClick={handleBackfillCollegeAssignment}
                  disabled={backfillCollegeLoading}
                  className="h-9 text-[12px] font-medium border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md bg-white"
                  data-testid="admin-backfill-college-btn"
                  title="Auto-assign each student to a college using preferred_college → preferred program's parent college → campus/branch match."
                >
                  {backfillCollegeLoading ? <CircleNotch size={13} className="mr-1.5 animate-spin" /> : <Buildings size={13} className="mr-1.5" />}
                  {backfillCollegeLoading ? 'Backfilling…' : 'Auto-assign Colleges'}
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={handleBackfillIntakes}
                  disabled={backfillIntakeLoading}
                  className="h-9 text-[12px] font-medium border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md bg-white"
                  data-testid="admin-backfill-intake-btn"
                  title="Backfill missing intakes."
                >
                  {backfillIntakeLoading ? <CircleNotch size={13} className="mr-1.5 animate-spin" /> : <CalendarBlank size={13} className="mr-1.5" />}
                  {backfillIntakeLoading ? 'Backfilling…' : 'Backfill Intakes'}
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={handleBackfillAddresses}
                  disabled={backfillAddressLoading}
                  className="h-9 text-[12px] font-medium border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md bg-white"
                  data-testid="admin-backfill-address-btn"
                  title="Auto-parse the typed address on every historical student to populate line1 / city / province / postal_code."
                >
                  {backfillAddressLoading ? <CircleNotch size={13} className="mr-1.5 animate-spin" /> : <MapPin size={13} className="mr-1.5" />}
                  {backfillAddressLoading ? 'Backfilling…' : 'Backfill Addresses'}
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={handleExportStudentsXlsx}
                  className="h-9 text-[12px] font-medium border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md bg-white"
                  data-testid="admin-export-students-btn"
                  title="Download filtered students as .xlsx"
                >
                  <DownloadSimple size={13} className="mr-1.5" /> Export .xlsx
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setShowDsBulkDialog(true)}
                  className="h-9 text-[12px] font-medium border-indigo-200 text-indigo-700 hover:bg-indigo-50 rounded-md bg-white"
                  data-testid="admin-docusign-bulk-btn"
                  title="Download filtered students in the DocuSign Bulk Upload template"
                >
                  <FileText size={13} className="mr-1.5" /> DocuSign Bulk
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setShowBulkStageExcelDialog(true)}
                  className="h-9 text-[12px] font-medium border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-md bg-white"
                  data-testid="admin-bulk-stage-excel-btn"
                  title="Upload an Excel of students and set one stage on all of them at once"
                >
                  <UploadSimple size={13} className="mr-1.5" /> Bulk Stage from Excel
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setShowCoeSheetDialog(true)}
                  className="h-9 text-[12px] font-medium border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md bg-white"
                  data-testid="admin-coe-sheet-btn"
                  title="Sync a Google Sheet of COE-confirmed students."
                >
                  <CheckCircle size={13} className="mr-1.5" /> Sync COE Sheet
                </Button>
                <Button
                  size="sm"
                  onClick={() => navigate('/admin/create-student')}
                  className="h-9 bg-gray-900 hover:bg-black text-white rounded-md px-4 text-[12px] font-medium"
                  data-testid="admin-add-student-btn"
                >
                  <Plus size={13} className="mr-1.5" /> Add Student
                </Button>
              </div>
            </div>
            {selectedStudentIds.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 flex flex-wrap items-center justify-between gap-3" data-testid="student-bulk-action-bar">
                <span className="text-sm text-gray-600">{selectedStudentIds.length} selected</span>
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="text-xs text-gray-500 whitespace-nowrap">Set status:</Label>
                  <Select value={bulkStageValue} onValueChange={setBulkStageValue}>
                    <SelectTrigger className="h-9 w-[220px] text-xs" data-testid="bulk-stage-select">
                      <SelectValue placeholder="Pick a stage…" />
                    </SelectTrigger>
                    <SelectContent>
                      {PIPELINE_STAGES.map(stg => (
                        <SelectItem key={stg} value={stg} data-testid={`bulk-stage-option-${stg}`}>
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${PIPELINE_STAGE_BAR[stg] || 'bg-gray-300'}`} />
                          {PIPELINE_STAGE_LABELS[stg]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={handleBulkUpdateStage}
                    disabled={!bulkStageValue || bulkStageApplying}
                    className="bg-blue-700 hover:bg-blue-800 text-white"
                    data-testid="bulk-stage-apply-btn"
                  >
                    {bulkStageApplying ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
                    {bulkStageApplying ? 'Updating…' : 'Apply'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkDownloadStudentsZip}
                    disabled={bulkZipLoading}
                    data-testid="bulk-download-students-zip-btn"
                  >
                    {bulkZipLoading ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <DownloadSimple size={14} className="mr-1" />}
                    {bulkZipLoading ? 'Preparing…' : 'Download ZIP'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleAprilExport}
                    disabled={aprilExportLoading}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="bulk-quarterly-report-btn"
                    title="Export selected students in the Quarterly Report Format (27 columns). Runs OCR on attached IDs for any missing addresses."
                  >
                    {aprilExportLoading ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <DownloadSimple size={14} className="mr-1" />}
                    {aprilExportLoading ? 'Building…' : 'Quarterly Report Format'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Pre-fill from the first selected student so admins can
                      // SEE the current intake before overwriting it (helps
                      // with "recruiter picked Oct by mistake, switch to Jul").
                      const first = selectedStudents[0];
                      setBulkIntakeCollegeId(first?.assigned_college || '');
                      setBulkIntakeStart(first?.assigned_intake_start || '');
                      setBulkIntakeEnd(first?.assigned_intake_end || '');
                      setIntakeDialogSingleId('');
                      setShowBulkIntakeDialog(true);
                    }}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="bulk-assign-intake-btn"
                  >
                    <CalendarBlank size={14} className="mr-1" /> Change Intake
                  </Button>
                  {(() => {
                    // "Bulk Set Pricing" appears only when every ticked student
                    // belongs to the SAME recruiter — pricing is per-recruiter
                    // by design (each recruiter has their own commission tiers).
                    const recIds = new Set(selectedStudents.map(s => s.recruiter_id).filter(Boolean));
                    const sameRecruiter = recIds.size === 1;
                    const recName = sameRecruiter ? (selectedStudents[0]?.recruiter_name || 'recruiter') : '';
                    return (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!sameRecruiter}
                        onClick={() => openBulkPricing()}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="bulk-set-pricing-btn"
                        title={sameRecruiter ? `Set Sale Price / College Fee / Commission for all selected ${recName} students at once` : 'All selected students must belong to the same recruiter to bulk-set pricing'}
                      >
                        <CurrencyDollar size={14} className="mr-1" /> Set Pricing
                      </Button>
                    );
                  })()}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkMarkDownloaded(true)}
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    data-testid="bulk-mark-downloaded-btn"
                    title="Backfill the downloaded badge on students you already exported before this feature landed"
                  >
                    <CheckCircle size={14} className="mr-1" /> Mark Downloaded
                  </Button>
                  {selectedStudents.some(s => s.last_downloaded_at) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleBulkMarkDownloaded(false)}
                      className="text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                      data-testid="bulk-unmark-downloaded-btn"
                      title="Clear the downloaded badge on the selected students"
                    >
                      <XCircle size={14} className="mr-1" /> Un-mark
                    </Button>
                  )}
                  {(() => {
                    // Invoice Action button — visible when every ticked
                    // student is flagged for the SAME tranche (all 1/2 or all 2/2).
                    // Opens a chooser between "Remind Recruiter" and "Send Invoice".
                    const allPending1 = selectedStudents.length > 0 && selectedStudents.every(s => s.commission_invoice_pending);
                    const allPending2 = selectedStudents.length > 0 && selectedStudents.every(s => s.commission_invoice_pending_2);
                    if (!allPending1 && !allPending2) return null;
                    const tranche = allPending2 ? 2 : 1;
                    return (
                      <Button
                        size="sm"
                        onClick={openInvoiceAction}
                        disabled={invoiceLoading}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="bulk-invoice-action-btn"
                        title={`Send ${tranche === 2 ? '2/2' : '1/2'} commission-invoice reminder to recruiter OR generate the PDF invoice to the college`}
                      >
                        {invoiceLoading ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <FilePdf size={14} className="mr-1" />}
                        Invoice Action {tranche === 2 ? '(2/2)' : '(1/2)'}
                      </Button>
                    );
                  })()}
                  <Button
                    size="sm"
                    onClick={handleBulkGenerateScoreReports}
                    disabled={bulkScoreReportLoading}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="bulk-generate-score-reports-btn"
                    title="Generate Accuplacer score reports for every selected student. Dates are randomised before each student's intake; scores randomised above minimum (Reading 230, Writing 230, WritePlacer 4)."
                  >
                    {bulkScoreReportLoading ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <GraduationCap size={14} className="mr-1" />}
                    Generate Score Reports
                  </Button>
                  {(() => {
                    const withReports = selectedStudents.filter(s => (s.score_reports_count || 0) > 0);
                    if (!withReports.length) return null;
                    const totalReports = withReports.reduce((n, s) => n + (s.score_reports_count || 0), 0);
                    return (
                      <Button
                        size="sm"
                        onClick={handleBulkDeleteScoreReports}
                        disabled={bulkDeleteScoreReportsLoading}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="bulk-delete-score-reports-btn"
                        title={`Permanently delete ${totalReports} generated score report(s) across ${withReports.length} selected student(s). Removes them from registrar + president views too.`}
                      >
                        {bulkDeleteScoreReportsLoading ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <Trash size={14} className="mr-1" />}
                        Delete Score Reports ({totalReports})
                      </Button>
                    );
                  })()}
                  <Button size="sm" variant="destructive" onClick={handleBulkDeleteStudents} data-testid="bulk-delete-students-btn"><Trash size={14} className="mr-1" /> Delete Selected</Button>
                </div>
              </div>
            )}
            {(() => {
              const renderTable = (items) => {
                // Header checkbox scopes to THIS group's rows — in the
                // "By intake" view that means admin ticks only the
                // students belonging to this intake, not every student
                // across every intake on the page.
                const groupIds = items.map(s => s.id);
                const selectedInGroup = groupIds.filter(id => selectedStudentIds.includes(id)).length;
                const allInGroupSelected = groupIds.length > 0 && selectedInGroup === groupIds.length;
                const toggleGroup = () => {
                  if (allInGroupSelected) {
                    // Un-tick only this group's rows
                    setSelectedStudentIds(prev => prev.filter(id => !groupIds.includes(id)));
                  } else {
                    // Add every missing id from this group
                    setSelectedStudentIds(prev => Array.from(new Set([...prev, ...groupIds])));
                  }
                };
                return (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full table-fixed">
                  <thead className="bg-gradient-to-b from-gray-50 to-gray-50/60 border-b border-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-3 w-9"><Checkbox checked={allInGroupSelected} onCheckedChange={toggleGroup} data-testid="select-all-students" /></th>
                      <th className="text-left px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em]">Student</th>
                      <th className="text-left px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] hidden lg:table-cell w-[130px]">Recruiter</th>
                      <th className="text-left px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] hidden md:table-cell w-[90px]" title="Program intake this student is aiming for">Intake</th>
                      <th className="text-left px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] hidden lg:table-cell w-[160px]">College</th>
                      <th className="text-right px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] hidden 2xl:table-cell w-[100px]">SA Earning</th>
                      <th className="text-left px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] w-[170px]">Stage</th>
                      <th className="text-center px-2 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] hidden xl:table-cell w-[60px]" title="Has this student been uploaded on Student Aid BC?">SA&nbsp;BC</th>
                      <th className="text-right px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] w-[70px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                          <Student size={32} weight="duotone" className="text-gray-300" />
                          <p className="text-sm font-medium text-gray-500">No students match your filters</p>
                          <p className="text-xs text-gray-400">Try clearing a filter or importing a lead list.</p>
                        </div>
                      </td></tr>
                    ) : items.map(s => {
                      const collegeObj = colleges.find(c => c.id === s.assigned_college);
                      const recObj = recruiters.find(r => r.id === s.recruiter_id);
                      const sale = Number(s.sale_price || 0);
                      const fee = Number(s.college_fee_snapshot ?? collegeObj?.college_fee ?? collegeObj?.commission_amount ?? 0);
                      const comm = Number(s.recruiter_commission_snapshot ?? recObj?.commission_per_student ?? 0);
                      const net = Math.max(sale - fee - comm, 0);
                      const stage = s.application_stage || 'profile_created';
                      const stageClass = PIPELINE_STAGE_COLORS[stage] || 'bg-gray-50 text-gray-600 border-gray-200';
                      const stageDot = PIPELINE_STAGE_BAR[stage] || 'bg-gray-300';
                      const isDownloaded = !!s.last_downloaded_at;
                      // Downloaded students get a subtle left accent, not a full row wash.
                      const rowBg = 'hover:bg-blue-50/40 transition-colors';
                      // Initials for the avatar bubble.
                      const initials = (s.full_name || '?')
                        .trim()
                        .split(/\s+/)
                        .map(p => p[0])
                        .filter(Boolean)
                        .slice(0, 2)
                        .join('')
                        .toUpperCase() || '?';
                      // Deterministic avatar colour from student id so it stays stable across renders.
                      const avatarPalette = [
                        'bg-blue-100 text-blue-700',
                        'bg-emerald-100 text-emerald-700',
                        'bg-amber-100 text-amber-700',
                        'bg-violet-100 text-violet-700',
                        'bg-rose-100 text-rose-700',
                        'bg-teal-100 text-teal-700',
                        'bg-indigo-100 text-indigo-700',
                      ];
                      let hash = 0;
                      for (let i = 0; i < (s.id || '').length; i += 1) hash = ((hash << 5) - hash + (s.id || '').charCodeAt(i)) | 0;
                      const avatarClass = avatarPalette[Math.abs(hash) % avatarPalette.length];
                      return (
                      <tr key={s.id} className={`${rowBg} ${s.open_missing_docs > 0 ? 'bg-amber-50/60' : ''}`} data-testid={`student-row-${s.id}`} data-downloaded={isDownloaded ? 'true' : 'false'} data-missing-doc={s.open_missing_docs > 0 ? 'true' : 'false'}>
                        <td className="px-3 py-3 align-middle"><Checkbox checked={selectedStudentIds.includes(s.id)} onCheckedChange={() => toggleStudentSelect(s.id)} /></td>
                        <td className="px-3 py-3 align-middle">
                          <div className="flex items-center gap-2.5 min-w-0">
                            {/* Left accent bar — amber for missing-doc flag takes priority. */}
                            {s.open_missing_docs > 0 ? (
                              <span className="w-1 self-stretch rounded-full bg-amber-500 animate-pulse" title="Missing document flagged by college" aria-hidden />
                            ) : isDownloaded && (
                              <span className="w-1 self-stretch rounded-full bg-emerald-400" title="Included in a ZIP download" aria-hidden />
                            )}
                            {/* Avatar */}
                            <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold ${avatarClass}`} aria-hidden>
                              {initials}
                            </div>
                            {/* Name / email / badges */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                <button
                                  type="button"
                                  onClick={() => handleViewStudent(s)}
                                  className={`text-sm font-semibold truncate hover:text-blue-700 transition-colors text-left ${s.open_missing_docs > 0 ? 'text-amber-900' : 'text-gray-900'}`}
                                  title={`View ${s.full_name}`}
                                  data-testid={`student-name-btn-${s.id}`}
                                >{s.full_name}</button>
                                {s.open_missing_docs > 0 && (
                                  <span
                                    className="inline-flex items-center gap-0.5 shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-amber-100 text-amber-900 border border-amber-300 animate-pulse"
                                    title={`College flagged ${s.open_missing_docs} missing document${s.open_missing_docs === 1 ? '' : 's'}`}
                                    data-testid={`missing-doc-badge-${s.id}`}
                                  >
                                    <WarningCircle size={10} weight="fill" /> Missing doc{s.open_missing_docs > 1 ? ` ×${s.open_missing_docs}` : ''}
                                  </span>
                                )}
                                {s.passport_pr_status === 'refugee' && (
                                  <span
                                    className="inline-flex items-center gap-0.5 shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-violet-100 text-violet-900 border border-violet-300"
                                    title="Refugee / Protected Person — requires SIN, Notice of Decision & Canadian Photo ID"
                                    data-testid={`refugee-badge-${s.id}`}
                                  >
                                    Refugee
                                  </span>
                                )}
                                {s.commission_invoice_pending && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openInvoiceActionForStudent(s); }}
                                    disabled={invoiceLoading}
                                    className="inline-flex items-center gap-1 shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-amber-500 text-white border border-amber-600 hover:bg-amber-600 disabled:opacity-60 shadow-sm"
                                    title="1/2 Money Received — choose: Remind Recruiter or Send Invoice to College"
                                    data-testid={`invoice-pending-badge-${s.id}`}
                                  >
                                    <CurrencyDollar size={10} weight="fill" /> Raise Invoice (1/2)
                                  </button>
                                )}
                                {s.commission_invoice_pending_2 && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openInvoiceActionForStudent(s); }}
                                    disabled={invoiceLoading}
                                    className="inline-flex items-center gap-1 shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-emerald-600 text-white border border-emerald-700 hover:bg-emerald-700 disabled:opacity-60 shadow-sm"
                                    title="2/2 Money Received — choose: Remind Recruiter or Send Invoice to College"
                                    data-testid={`invoice-pending-badge-2-${s.id}`}
                                  >
                                    <CurrencyDollar size={10} weight="fill" /> Raise Invoice (2/2)
                                  </button>
                                )}
                                {(s.score_reports_count || 0) > 0 ? (
                                  <span
                                    className="inline-flex items-center gap-0.5 shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                                    title={`${s.score_reports_count} score report${s.score_reports_count === 1 ? '' : 's'} on file`}
                                    data-testid={`student-score-badge-${s.id}`}
                                  >
                                    <CheckCircle size={10} weight="fill" /> Score{s.score_reports_count > 1 ? ` ×${s.score_reports_count}` : ''}
                                  </span>
                                ) : null}
                                {isDownloaded && (
                                  <span
                                    className="inline-flex items-center gap-0.5 shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                                    title={`Included in a ZIP download on ${new Date(s.last_downloaded_at).toLocaleString()}${s.last_downloaded_by ? ` by ${s.last_downloaded_by}` : ''}`}
                                    data-testid={`student-downloaded-badge-${s.id}`}
                                  >
                                    <DownloadSimple size={10} weight="fill" /> Sent
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-gray-500 truncate mt-0.5" title={s.email}>{s.email}</p>
                            </div>
                            {/* Notes button */}
                            <button
                              type="button"
                              onClick={() => openStudentNotes(s)}
                              className={`relative flex-shrink-0 h-7 w-7 inline-flex items-center justify-center rounded-lg transition border ${
                                (s.admin_notes_count || 0) > 0
                                  ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                                  : 'bg-white border-gray-200 text-gray-400 hover:text-blue-700 hover:border-blue-300 hover:bg-blue-50'
                              }`}
                              title={(s.admin_notes_count || 0) > 0 ? `${s.admin_notes_count} note${s.admin_notes_count === 1 ? '' : 's'} — click to view` : 'Add admin note'}
                              data-testid={`student-notes-btn-${s.id}`}
                            >
                              <FileText size={14} />
                              {(s.admin_notes_count || 0) > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 inline-flex items-center justify-center text-[9px] font-bold rounded-full bg-amber-500 text-white shadow-sm">
                                  {s.admin_notes_count > 9 ? '9+' : s.admin_notes_count}
                                </span>
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-700 hidden lg:table-cell align-middle" title={s.recruiter_name || s.recruiter_email}>
                          <div className="flex flex-col leading-tight min-w-0">
                            {(() => {
                              // Prefer the value stored on the student. When it's blank
                              // (historic rows filed by a sub-agent before the backend
                              // captured parent identity), look the parent up live in
                              // the loaded recruiters state so the column self-heals
                              // without needing the backfill endpoint.
                              const parent = s.recruiter_id ? recruiters.find(r => r.id === s.recruiter_id) : null;
                              const name = s.recruiter_name || parent?.full_name || '';
                              const email = s.recruiter_email || parent?.email || '';
                              return (
                                <span className="font-medium truncate">
                                  {name || email || <span className="text-gray-400">—</span>}
                                </span>
                              );
                            })()}
                            {s.created_by_sub_agent_name && (
                              <span className="text-[10px] text-gray-500 truncate" data-testid={`sub-agent-hint-${s.id}`}>
                                via {s.created_by_sub_agent_name} (sub-agent)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-[11px] hidden md:table-cell align-middle whitespace-nowrap" data-testid={`student-intake-${s.id}`}>
                          {s.intake_label
                            ? <button type="button" onClick={() => openChangeIntakeForStudent(s)} title="Click to change intake" className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-800 border border-indigo-100 font-semibold hover:bg-indigo-100 hover:border-indigo-300 transition-colors cursor-pointer" data-testid={`intake-badge-${s.id}`}>{s.intake_label}</button>
                            : <button type="button" onClick={() => openChangeIntakeForStudent(s)} title="Click to set intake" className="text-gray-400 hover:text-indigo-700 cursor-pointer underline decoration-dotted" data-testid={`intake-badge-${s.id}`}>Set intake</button>}
                        </td>
                        <td className="px-3 py-3 text-xs hidden lg:table-cell align-middle" data-testid={`student-college-${s.id}`}>
                          {s.assigned_college_name ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border bg-blue-50/60 text-blue-800 border-blue-100 max-w-full truncate" title={s.assigned_college_name}>
                              <Buildings size={11} weight="fill" className="text-blue-500 shrink-0" />
                              <span className="truncate">{s.assigned_college_name}</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-dashed border-gray-200 text-gray-400">
                              <Buildings size={11} className="shrink-0" /> Unassigned
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right hidden 2xl:table-cell align-middle" data-testid={`student-net-${s.id}`}>
                          {net > 0 ? (<span className="text-sm font-bold text-emerald-700 tabular-nums">${net.toLocaleString()}</span>) : (<span className="text-[11px] text-gray-300">—</span>)}
                        </td>
                        <td className="px-3 py-3 align-middle" data-testid={`student-stage-${s.id}`}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className={`inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 text-[11px] font-semibold rounded-full border cursor-pointer hover:shadow-sm hover:brightness-95 transition-all ${stageClass}`}
                                  title="Click to change pipeline status"
                                  data-testid={`stage-trigger-${s.id}`}
                                >
                                  <span className={`w-1.5 h-1.5 rounded-full ${stageDot}`} />
                                  <span className="truncate max-w-[110px]">{PIPELINE_STAGE_LABELS[stage] || stage}</span>
                                  <DotsThreeVertical size={11} weight="bold" className="opacity-60" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="w-60">
                                {PIPELINE_STAGES.map(stg => (
                                  <DropdownMenuItem
                                    key={stg}
                                    onClick={() => { if (stg !== stage) handleUpdateStage(s.id, stg); }}
                                    className={stg === stage ? 'bg-gray-50 font-semibold' : ''}
                                    data-testid={`stage-set-${s.id}-${stg}`}
                                  >
                                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${PIPELINE_STAGE_BAR[stg] || 'bg-gray-300'}`} />
                                    {PIPELINE_STAGE_LABELS[stg]}
                                    {stg === stage && <CheckCircle size={12} weight="fill" className="ml-auto text-emerald-600" />}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                        <td className="px-2 py-3 text-center hidden xl:table-cell align-middle" data-testid={`student-sabc-${s.id}`}>
                          {s.student_aid_bc_uploaded
                            ? <CheckCircle size={18} weight="fill" className="inline text-emerald-500" title="Uploaded to Student Aid BC" />
                            : <span className="inline-block w-2 h-2 rounded-full bg-gray-300" title="Not uploaded" />}
                        </td>
                        <td className="px-3 py-3 text-right align-middle">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button type="button" className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors" data-testid={`student-actions-${s.id}`} title="Actions">
                                <DotsThreeVertical size={16} weight="bold" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => openAssignCollege(s)} data-testid={`assign-college-${s.id}`}><Buildings size={14} className="mr-2" /> Assign College</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleViewStudent(s)} data-testid={`view-student-${s.id}`}><Eye size={14} className="mr-2" /> View Profile</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleQuickScoreReport(s)} data-testid={`quick-score-report-${s.id}`}><FilePdf size={14} className="mr-2" /> Generate Score Report</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadStudentZip(s)} className="text-blue-600 focus:text-blue-700" data-testid={`download-student-${s.id}`}><DownloadSimple size={14} className="mr-2" /> Download .zip</DropdownMenuItem>
                              {isPdcEligible(s) && (
                                <DropdownMenuItem onClick={() => openPdcModal(s)} className="text-emerald-700 focus:text-emerald-800" data-testid={`send-pdc-declaration-${s.id}`}>
                                  <EnvelopeSimple size={14} className="mr-2" /> Send PDC Declaration
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDeleteStudent(s.id)} className="text-red-600 focus:text-red-700" data-testid={`delete-student-${s.id}`}><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
              );
              };
              return adminGroupByIntake ? (
                <IntakeAccordion
                  students={filteredStudents}
                  stages={PIPELINE_STAGES}
                  stageMeta={(k) => ({ label: PIPELINE_STAGE_LABELS[k] || k, dot: PIPELINE_STAGE_BAR[k] || 'bg-gray-400', className: PIPELINE_STAGE_COLORS[k] || 'bg-gray-50 text-gray-600 border-gray-200' })}
                  storageKey="admin.students.openIntakes"
                  emptyMessage="No students match your filters."
                  renderGroup={(items) => renderTable(items)}
                  renderGroupExtra={(items) => <PotentialEarningPill total={potentialFor(items)} />}
                  variant="editorial"
                />
              ) : renderTable(filteredStudents);
            })()}
          </div>
        )}

        {/* ===== STUDENT APPLICATIONS TAB ===== */}
        {tab === 'applications' && (
          <div data-testid="admin-applications-tab">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Direct Leads{directLeadsView === 'leads' ? ` (${filteredDirectLeads.length}${directLeadsStatusFilter !== 'all' ? ` of ${studentApps.length}` : ''})` : ''}</h2>
              <div className="flex items-center gap-2">
                {/* View switcher */}
                <div className="inline-flex bg-gray-100 rounded-lg p-0.5" data-testid="direct-leads-view-switcher">
                  <button
                    type="button"
                    onClick={() => setDirectLeadsView('leads')}
                    className={`text-xs font-medium px-3 py-1.5 rounded-md transition ${directLeadsView === 'leads' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    data-testid="direct-leads-view-leads"
                  >Leads</button>
                  <button
                    type="button"
                    onClick={() => setDirectLeadsView('history')}
                    className={`text-xs font-medium px-3 py-1.5 rounded-md transition flex items-center gap-1 ${directLeadsView === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    data-testid="direct-leads-view-history"
                  ><EnvelopeSimple size={11} /> Email History</button>
                </div>
                {directLeadsView === 'leads' && (
                  <Button onClick={() => setShowLeadImportDialog(true)} variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="direct-leads-import-btn">
                    <UploadSimple size={16} className="mr-2" /> Import .xlsx / .csv
                  </Button>
                )}
              </div>
            </div>
            {directLeadsView === 'leads' && (<>
            {/* Status filter chips */}
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="direct-leads-status-filter">
              <span className="text-xs font-semibold uppercase text-gray-500 mr-1">Filter:</span>
              <button
                type="button"
                onClick={() => setDirectLeadsStatusFilter('all')}
                className={`text-xs px-3 py-1 rounded-full border ${directLeadsStatusFilter === 'all' ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'}`}
                data-testid="direct-leads-filter-all"
              >All ({studentApps.length})</button>
              {APP_STATUS_OPTIONS.map(s => {
                const count = studentApps.filter(a => (a.status || 'new') === s).length;
                if (count === 0 && s !== 'new') return null;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setDirectLeadsStatusFilter(s)}
                    className={`text-xs px-3 py-1 rounded-full border ${directLeadsStatusFilter === s ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'}`}
                    data-testid={`direct-leads-filter-${s}`}
                  >{appStatusLabels[s]} ({count})</button>
                );
              })}
            </div>
            {/* Live job progress */}
            {directLeadEmailJob && directLeadEmailJob.status === 'running' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex items-center justify-between" data-testid="direct-leads-email-job-progress">
                <div className="flex items-center gap-2 text-sm text-blue-900">
                  <PaperPlaneTilt size={16} className="text-blue-700" />
                  Sending emails… <strong>{directLeadEmailJob.sent || 0}</strong> sent, <strong>{directLeadEmailJob.failed || 0}</strong> failed of <strong>{directLeadEmailJob.total}</strong>
                </div>
                <div className="w-40 h-2 bg-blue-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.round(((directLeadEmailJob.sent || 0) + (directLeadEmailJob.failed || 0)) / Math.max(1, directLeadEmailJob.total) * 100)}%` }} />
                </div>
              </div>
            )}
            {selectedAppIds.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 flex items-center justify-between flex-wrap gap-2">
                <span className="text-sm text-gray-600">{selectedAppIds.length} selected</span>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => setShowDirectLeadEmailDialog(true)} className="border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="direct-leads-email-btn">
                    <EnvelopeSimple size={14} className="mr-1" /> Email Selected
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowDirectLeadWhatsAppDialog(true)} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="direct-leads-whatsapp-btn">
                    <ChatCircle size={14} className="mr-1" /> WhatsApp Selected
                  </Button>
                  <Button size="sm" variant="destructive" onClick={handleBulkDeleteApps} data-testid="bulk-delete-apps-btn"><Trash size={14} className="mr-1" /> Delete</Button>
                </div>
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-3 w-10"><Checkbox checked={selectedAppIds.length > 0 && selectedAppIds.length === filteredDirectLeads.length && filteredDirectLeads.length > 0} onCheckedChange={() => setSelectedAppIds(prev => prev.length === filteredDirectLeads.length ? [] : filteredDirectLeads.map(a => a.id))} data-testid="select-all-apps" /></th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Reference</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Applicant</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Program</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Source</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Docs</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Date</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredDirectLeads.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400 text-sm">{directLeadsStatusFilter === 'all' ? 'No direct leads yet — public applications from the website will appear here.' : `No leads in "${appStatusLabels[directLeadsStatusFilter] || directLeadsStatusFilter}" — switch filter to see others.`}</td></tr>
                    ) : filteredDirectLeads.map(a => (
                      <tr key={a.id} className="hover:bg-gray-50/50" data-testid={`app-row-${a.id}`}>
                        <td className="px-3 py-3"><Checkbox checked={selectedAppIds.includes(a.id)} onCheckedChange={() => toggleAppSelect(a.id)} onClick={(e) => e.stopPropagation()} /></td>
                        <td className="px-4 py-3 cursor-pointer" onClick={() => openDirectLeadDetail(a)}><p className="text-sm font-mono font-semibold text-blue-700 hover:underline" data-testid={`app-reference-${a.id}`}>{a.reference_number || '-'}</p></td>
                        <td className="px-4 py-3 cursor-pointer" onClick={() => openDirectLeadDetail(a)}>
                          <p className="text-sm font-medium text-gray-900 hover:text-blue-700 hover:underline" data-testid={`app-name-${a.id}`}>{a.full_name}</p>
                          <p className="text-xs text-gray-500">{a.email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{a.preferred_program || '-'}</td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {a.lead_source ? (
                            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`app-lead-source-${a.id}`}>{a.lead_source}</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{a.documents?.length || 0}</td>
                        <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${appStatusColors[a.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{appStatusLabels[a.status] || a.status}</span></td>
                        <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">{new Date(a.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Select value={a.status} onValueChange={(v) => handleUpdateAppStatus(a.id, v)}>
                              <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`app-status-select-${a.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{APP_STATUS_OPTIONS.map(s => (<SelectItem key={s} value={s}>{appStatusLabels[s]}</SelectItem>))}</SelectContent>
                            </Select>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteApp(a.id)} className="h-8 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-app-${a.id}`}><Trash size={14} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            </>)}
            {/* ===== EMAIL HISTORY SUB-VIEW ===== */}
            {directLeadsView === 'history' && (
              <div data-testid="direct-leads-history-view">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Email History ({directLeadsEmailHistoryTotal})</h3>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        value={directLeadsHistorySearch}
                        onChange={e => setDirectLeadsHistorySearch(e.target.value)}
                        placeholder="Search lead, email, subject…"
                        className="h-8 pl-7 w-56 text-xs"
                        data-testid="direct-leads-history-search"
                      />
                    </div>
                    <Button size="sm" variant="ghost" onClick={fetchDirectLeadsEmailHistory} className="h-8" data-testid="direct-leads-history-refresh">
                      <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">When</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Lead</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Subject</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(() => {
                          const q = (directLeadsHistorySearch || '').trim().toLowerCase();
                          const rows = q
                            ? directLeadsEmailHistory.filter(h => [h.to_email, h.full_name, h.subject, h.reference_number].some(v => (v || '').toLowerCase().includes(q)))
                            : directLeadsEmailHistory;
                          if (rows.length === 0) {
                            return <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400 text-sm">{q ? 'No emails match your search.' : 'No email history yet — emails sent to Direct Leads will appear here.'}</td></tr>;
                          }
                          return rows.map(h => (
                            <tr key={h.id} className="hover:bg-gray-50/50" data-testid={`direct-leads-history-row-${h.id}`}>
                              <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{h.sent_at ? new Date(h.sent_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-gray-900">{h.full_name || '-'}</p>
                                <p className="text-xs text-gray-500">{h.to_email}{h.reference_number ? <> · <span className="font-mono text-blue-700">{h.reference_number}</span></> : null}</p>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 max-w-[400px] truncate" title={h.subject}>{h.subject}</td>
                              <td className="px-4 py-3">
                                {h.status === 'sent' ? (
                                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Sent</span>
                                ) : (
                                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700 border border-red-200" title={h.error || ''}>Failed</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Button size="sm" variant="ghost" onClick={() => setViewEmailEntry(h)} className="h-7 px-2 text-xs text-blue-700 hover:bg-blue-50" data-testid={`direct-leads-history-view-${h.id}`}>
                                  <Eye size={12} className="mr-1" /> View
                                </Button>
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {tab === 'commissions' && (
          <div data-testid="admin-commissions-tab">
            {commSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {[{ label: 'Total', value: commSummary.total_commissions },{ label: 'Pending', value: `$${commSummary.total_pending_amount.toLocaleString()}`, sub: `${commSummary.pending_count} entries` },{ label: 'Paid', value: `$${commSummary.total_paid_amount.toLocaleString()}`, sub: `${commSummary.paid_count} entries` },{ label: 'Total Value', value: `$${commSummary.total_amount.toLocaleString()}` }].map(s => (
                  <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-4"><p className="text-xs text-gray-500 font-medium">{s.label}</p><p className="text-xl font-bold text-gray-900 mt-1">{s.value}</p>{s.sub && <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>}</div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Commission Records</h2>
              <Button onClick={() => setShowCommForm(true)} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="create-commission-btn"><Plus size={16} className="mr-2" /> Add Commission</Button>
            </div>
            {selectedCommIds.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 flex items-center justify-between">
                <span className="text-sm text-gray-600">{selectedCommIds.length} selected</span>
                <Button size="sm" variant="destructive" onClick={handleBulkDeleteComms} data-testid="bulk-delete-comms-btn"><Trash size={14} className="mr-1" /> Delete Selected</Button>
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200"><tr>
                    <th className="px-3 py-3 w-10"><Checkbox checked={selectedCommIds.length === commissions.length && commissions.length > 0} onCheckedChange={toggleAllComms} data-testid="select-all-comms" /></th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Recruiter</th><th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Student</th><th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th><th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Description</th><th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th><th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {commissions.length === 0 ? (<tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">No commissions yet</td></tr>) : commissions.map(c => (
                      <tr key={c.id} className="hover:bg-gray-50/50" data-testid={`commission-row-${c.id}`}>
                        <td className="px-3 py-3"><Checkbox checked={selectedCommIds.includes(c.id)} onCheckedChange={() => toggleCommSelect(c.id)} /></td>
                        <td className="px-4 py-3"><p className="text-sm font-medium text-gray-900">{c.recruiter_name}</p><p className="text-xs text-gray-500">{c.recruiter_email}</p></td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{c.student_name || '-'}</td>
                        <td className="px-4 py-3"><p className="text-sm font-semibold text-gray-900">${c.amount?.toLocaleString()}</p></td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell truncate max-w-[200px]">{c.description || '-'}</td>
                        <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${commStatusColors[c.status] || ''}`}>{c.status}</span></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {c.status === 'pending' && (<Button size="sm" onClick={() => handleUpdateCommissionStatus(c.id, 'paid')} className="h-8 px-2 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid={`pay-commission-${c.id}`}><CheckCircle size={14} className="mr-1" /> Paid</Button>)}
                            {c.status === 'paid' && <span className="text-xs text-green-600 font-medium px-1">Paid</span>}
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteComm(c.id)} className="h-8 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-comm-${c.id}`}><Trash size={14} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===== INVOICES TAB (Commission Invoice History) ===== */}
        {tab === 'invoices' && (
          <InvoiceHistoryPanel />
        )}

        {/* ===== COLLEGES TAB ===== */}
        {tab === 'colleges' && (() => {
          const assignedStudents = students.filter(s => s.assigned_college);
          const unassignedStudents = students.filter(s => !s.assigned_college);
          const submittedAssigned = assignedStudents.filter(s => s.status === 'submitted').length;
          const stageOf = (s) => s.application_stage || 'profile_created';
          const collegeById = Object.fromEntries(colleges.map(c => [c.id, c]));
          const recruiterById = Object.fromEntries(recruiters.map(r => [r.id, r]));
          const netRevenueFor = (s) => {
            const c = collegeById[s.assigned_college];
            const r = recruiterById[s.recruiter_id];
            const sale = Number(s.sale_price || 0);
            const fee = Number(s.college_fee_snapshot ?? c?.college_fee ?? c?.commission_amount ?? 0);
            const comm = Number(s.recruiter_commission_snapshot ?? r?.commission_per_student ?? 0);
            return Math.max(sale - fee - comm, 0);
          };
          const realisedRevenue = assignedStudents.filter(s => stageOf(s) === REALISED_STAGE).reduce((sum, s) => sum + netRevenueFor(s), 0);
          const pipelinePotential = assignedStudents.filter(s => !TERMINAL_STAGES.includes(stageOf(s))).reduce((sum, s) => sum + netRevenueFor(s), 0);
          const aggStageCounts = PIPELINE_STAGES.reduce((acc, stg) => {
            acc[stg] = assignedStudents.filter(s => stageOf(s) === stg).length;
            return acc;
          }, {});
          const aggTotal = Object.values(aggStageCounts).reduce((a, b) => a + b, 0);
          const collegeStats = colleges.map(c => {
            const list = students.filter(s => s.assigned_college === c.id);
            const stageCounts = PIPELINE_STAGES.reduce((acc, stg) => {
              acc[stg] = list.filter(s => stageOf(s) === stg).length;
              return acc;
            }, {});
            const lastUpdated = list.length > 0 ? list.map(s => s.updated_at || s.created_at).filter(Boolean).sort().slice(-1)[0] : null;
            const realised = list.filter(s => stageOf(s) === REALISED_STAGE).reduce((sum, s) => sum + netRevenueFor(s), 0);
            const potential = list.filter(s => !TERMINAL_STAGES.includes(stageOf(s))).reduce((sum, s) => sum + netRevenueFor(s), 0);
            return { ...c, _total: list.length, _stageCounts: stageCounts, _lastUpdated: lastUpdated, _realised: realised, _potential: potential };
          });
          const topCollege = [...collegeStats].sort((a, b) => b._total - a._total)[0];
          return (
          <div data-testid="admin-colleges-tab">
            {/* Analytics Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="college-analytics-summary">
              {[
                { label: 'Partner Colleges', value: colleges.length, sub: 'total institutions' },
                { label: 'Assigned Students', value: assignedStudents.length, sub: `${submittedAssigned} submitted` },
                { label: 'Unassigned Students', value: unassignedStudents.length, sub: 'awaiting placement', warn: unassignedStudents.length > 0 },
                { label: 'Top College', value: topCollege?._total > 0 ? topCollege.name : '—', sub: topCollege?._total > 0 ? `${topCollege._total} student${topCollege._total !== 1 ? 's' : ''}` : 'no assignments yet' },
              ].map(s => (
                <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                  <p className={`text-xl font-bold mt-1 truncate ${s.warn ? 'text-amber-700' : 'text-gray-900'}`}>{s.value}</p>
                  {s.sub && <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>}
                </div>
              ))}
            </div>

            {/* Revenue Forecast Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-testid="revenue-forecast-cards">
              <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-lg p-5" data-testid="revenue-realised-card">
                <div className="flex items-center gap-2 mb-1">
                  <CurrencyDollar size={18} className="text-emerald-600" />
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Study Alliance Earning (Realised)</p>
                </div>
                <p className="text-3xl font-bold text-gray-900 mt-1">${realisedRevenue.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">Our earnings from <strong>enrolled</strong> students (sale price − college fee − recruiter payout)</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-5" data-testid="revenue-pipeline-card">
                <div className="flex items-center gap-2 mb-1">
                  <CurrencyDollar size={18} className="text-blue-600" />
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Study Alliance Earning (Pipeline)</p>
                </div>
                <p className="text-3xl font-bold text-gray-900 mt-1">${pipelinePotential.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Projected Study Alliance earning if every non-rejected student enrols</p>
              </div>
            </div>

            {/* Aggregate Pipeline Chart */}
            <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4" data-testid="pipeline-aggregate-chart">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Overall Pipeline (all colleges)</h3>
                <span className="text-xs text-gray-500">{aggTotal} assigned student{aggTotal !== 1 ? 's' : ''}</span>
              </div>
              {aggTotal === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">No students assigned to colleges yet</p>
              ) : (
                <>
                  <div className="flex h-3 w-full rounded-full overflow-hidden bg-gray-100" data-testid="pipeline-aggregate-bar">
                    {PIPELINE_STAGES.filter(s => !TERMINAL_STAGES.includes(s)).map(stg => {
                      const pct = aggTotal ? (aggStageCounts[stg] / aggTotal) * 100 : 0;
                      if (pct === 0) return null;
                      return <div key={stg} className={PIPELINE_STAGE_BAR[stg]} style={{ width: `${pct}%` }} title={`${PIPELINE_STAGE_LABELS[stg]}: ${aggStageCounts[stg]}`} />;
                    })}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {PIPELINE_STAGES.map(stg => (
                      <div key={stg} className="flex items-center gap-2 text-xs" data-testid={`pipeline-legend-${stg}`}>
                        <span className={`inline-block w-3 h-3 rounded-sm ${PIPELINE_STAGE_BAR[stg]}`} />
                        <span className="text-gray-700 font-medium">{PIPELINE_STAGE_LABELS[stg]}</span>
                        <span className="text-gray-400">{aggStageCounts[stg]}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>College Collaborations ({colleges.length})</h2>
              <Button onClick={() => openCollegeForm()} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="add-college-btn"><Plus size={16} className="mr-2" /> Add College</Button>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200"><tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">College</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">College Fee (CAD)</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Programs</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Students</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Pipeline</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Realised</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Potential</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {collegeStats.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400 text-sm">No partner colleges yet. Click "Add College" to start.</td></tr>
                    ) : collegeStats.map(c => (
                        <tr key={c.id} className="hover:bg-gray-50/50" data-testid={`college-row-${c.id}`}>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-900">{c.name}</p>
                            {c.location && <p className="text-xs text-gray-500">{c.location}{c.country ? `, ${c.country}` : ''}</p>}
                            {c.website && <a href={c.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate block max-w-[220px]">{c.website}</a>}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell" data-testid={`college-commission-${c.id}`}>
                            {(c.college_fee ?? c.commission_amount) > 0 ? (
                              <span className="text-sm font-semibold text-gray-900">${Number(c.college_fee ?? c.commission_amount).toLocaleString()}</span>
                            ) : (
                              <span className="text-xs text-gray-400">— Not set</span>
                            )}
                            <p className="text-xs text-gray-400">per application</p>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell" data-testid={`college-programs-${c.id}`}>
                            {c.programs?.length > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-purple-50 text-purple-700 border border-purple-200">{c.programs.length} program{c.programs.length !== 1 ? 's' : ''}</span>
                            ) : (
                              <span className="text-xs text-gray-400">None</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center" data-testid={`college-total-${c.id}`}>
                            <span className="inline-flex items-center justify-center w-7 h-7 text-xs font-semibold rounded-full bg-blue-50 text-blue-700">{c._total}</span>
                          </td>
                          <td className="px-4 py-3" data-testid={`college-pipeline-${c.id}`}>
                            {c._total === 0 ? (
                              <span className="text-xs text-gray-300">No students</span>
                            ) : (
                              <div className="flex flex-wrap gap-1.5 max-w-[320px]">
                                {PIPELINE_STAGES.filter(stg => c._stageCounts[stg] > 0).map(stg => (
                                  <span key={stg} className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${PIPELINE_STAGE_COLORS[stg]}`} data-testid={`college-${c.id}-chip-${stg}`}>
                                    <span className="font-semibold">{c._stageCounts[stg]}</span>
                                    {PIPELINE_STAGE_LABELS[stg]}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right hidden md:table-cell" data-testid={`college-realised-${c.id}`}>
                            {c._realised > 0 ? (<span className="text-sm font-semibold text-emerald-700">${c._realised.toLocaleString()}</span>) : (<span className="text-xs text-gray-300">$0</span>)}
                          </td>
                          <td className="px-4 py-3 text-right hidden lg:table-cell" data-testid={`college-potential-${c.id}`}>
                            {c._potential > 0 ? (<span className="text-sm text-blue-700">${c._potential.toLocaleString()}</span>) : (<span className="text-xs text-gray-300">$0</span>)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setStudentCollegeFilter(c.id); setTab('students'); }}
                                disabled={c._total === 0}
                                className="h-8 px-2 text-xs"
                                data-testid={`view-college-students-${c.id}`}
                              >
                                <Eye size={14} className="mr-1" /> View
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openCollegeForm(c)} className="h-8 px-2 text-xs" data-testid={`edit-college-${c.id}`}><PencilSimple size={14} className="mr-1" /> Edit</Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteCollege(c.id)} className="h-8 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-college-${c.id}`}><Trash size={14} /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          );
        })()}

        {/* ===== FORWARD HISTORY TAB ===== */}
        {tab === 'forwards' && (
          <div data-testid="admin-forwards-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">{forwards.length} forward{forwards.length !== 1 ? 's' : ''} recorded</p>
              <Button size="sm" variant="outline" onClick={fetchForwards} data-testid="refresh-forwards-btn">Refresh</Button>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200"><tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Student</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">College / Program</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Sent To</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Docs</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Forwarded By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">When</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {forwards.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">No forwards yet. When you click "Forward to College" on a student, it will appear here.</td></tr>
                    ) : forwards.map(f => {
                      const fullStudent = students.find(s => s.id === f.student_id);
                      return (
                        <tr key={f.id} className="hover:bg-gray-50/50" data-testid={`forward-row-${f.id}`}>
                          <td className="px-4 py-3">
                            {fullStudent ? (
                              <button onClick={() => handleViewStudent(fullStudent)} className="text-left hover:underline" data-testid={`forward-student-${f.id}`}>
                                <p className="text-sm font-medium text-blue-700 hover:text-blue-800">{f.student_name}</p>
                              </button>
                            ) : (
                              <p className="text-sm font-medium text-gray-400">{f.student_name} <span className="text-xs">(deleted)</span></p>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <p className="text-sm text-gray-900">{f.college_name || '—'}</p>
                            <p className="text-xs text-gray-500">{f.program_name || '—'}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700"><a href={`mailto:${f.to_email}`} className="hover:underline text-blue-600">{f.to_email}</a></td>
                          <td className="px-4 py-3 text-center hidden sm:table-cell">
                            <span className="inline-flex items-center justify-center w-7 h-7 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700">{f.attached_count}</span>
                            {f.skipped?.length > 0 && <p className="text-[10px] text-amber-600 mt-0.5">{f.skipped.length} skipped</p>}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 hidden lg:table-cell">{f.forwarded_by}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{f.forwarded_at ? new Date(f.forwarded_at).toLocaleString() : ''}</td>
                          <td className="px-4 py-3 text-right">
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteForward(f.id)} className="h-8 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-forward-${f.id}`}><Trash size={14} /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===== MESSAGES TAB (Admin ↔ Recruiter chat) ===== */}
        {tab === 'messages' && (
          <div data-testid="admin-messages-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">{conversations.length} recruiter{conversations.length !== 1 ? 's' : ''} · {conversations.filter(c => c.unread > 0).length} with unread</p>
              <Button size="sm" variant="destructive" onClick={handleDeleteAllConversations} data-testid="delete-all-conversations-btn"><Trash size={14} className="mr-2" /> Delete All</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
              {/* Conversation list */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col" data-testid="conversations-list">
                <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">Conversations</div>
                <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
                  {conversations.length === 0 ? (
                    <p className="p-8 text-center text-sm text-gray-400">No conversations yet</p>
                  ) : conversations.map(c => (
                    <button
                      key={c.id}
                      onClick={() => openConversation(c)}
                      className={`w-full text-left px-3 py-3 hover:bg-gray-50 transition-colors ${activeConv?.id === c.id ? 'bg-blue-50' : ''}`}
                      data-testid={`conversation-${c.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">{c.recruiter_name || c.recruiter_email}</p>
                        {c.unread > 0 && <span className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-4 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full" data-testid={`conv-unread-${c.id}`}>{c.unread}</span>}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{c.recruiter_company || c.recruiter_email}</p>
                      {c.last_body ? (
                        <p className="text-xs text-gray-600 mt-1 truncate"><span className="text-gray-400">{c.last_sender_role === 'admin' ? 'You: ' : ''}</span>{c.last_body}</p>
                      ) : (
                        <p className="text-xs text-gray-300 mt-1 italic">No messages yet</p>
                      )}
                      {c.last_at && <p className="text-[10px] text-gray-400 mt-0.5">{new Date(c.last_at).toLocaleString()}</p>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chat pane */}
              <div className="md:col-span-2 bg-white border border-gray-200 rounded-lg flex flex-col" data-testid="chat-pane">
                {!activeConv ? (
                  <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Select a conversation to start chatting</div>
                ) : (
                  <>
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{activeConv.recruiter_name || activeConv.recruiter_email}</p>
                        <p className="text-xs text-gray-500">{activeConv.recruiter_email}{activeConv.recruiter_company ? ` · ${activeConv.recruiter_company}` : ''}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteConversation(activeConv.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2 text-xs" data-testid="delete-current-conversation-btn"><Trash size={14} className="mr-1" /> Delete</Button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="messages-scroll">
                      {convMessages.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-8">No messages yet. Say hello.</p>
                      ) : convMessages.map(m => (
                        <div key={m.id} className={`flex ${m.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`} data-testid={`message-${m.id}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${m.sender_role === 'admin' ? 'bg-blue-700 text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'}`}>
                            {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                            {m.attachments?.length > 0 && (
                              <div className="mt-2 space-y-1" data-testid={`message-attachments-${m.id}`}>
                                {m.attachments.map(a => (
                                  <a
                                    key={a.file_id}
                                    href={`${API}/messages/attachments/${a.file_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={async (ev) => {
                                      ev.preventDefault();
                                      try {
                                        const resp = await axios.get(`${API}/messages/attachments/${a.file_id}`, { responseType: 'blob' });
                                        const url = window.URL.createObjectURL(resp.data);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = a.filename;
                                        link.click();
                                        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                      } catch { toast.error('Download failed'); }
                                    }}
                                    className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs ${m.sender_role === 'admin' ? 'bg-blue-800 hover:bg-blue-900 text-blue-100' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                                    data-testid={`message-attachment-${a.file_id}`}
                                  >
                                    <FileText size={14} />
                                    <span className="truncate">{a.filename}</span>
                                    {a.size > 0 && <span className="opacity-70 ml-auto">{(a.size / 1024).toFixed(1)} KB</span>}
                                  </a>
                                ))}
                              </div>
                            )}
                            <p className={`text-[10px] mt-1 ${m.sender_role === 'admin' ? 'text-blue-200' : 'text-gray-400'}`}>{new Date(m.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {messageAttachments.length > 0 && (
                      <div className="border-t border-gray-100 px-3 pt-2 flex flex-wrap gap-2" data-testid="pending-attachments">
                        {messageAttachments.map(a => (
                          <div key={a.file_id} className="flex items-center gap-1.5 bg-gray-100 rounded-md px-2 py-1 text-xs">
                            <FileText size={12} />
                            <span className="truncate max-w-[140px]">{a.filename}</span>
                            <button onClick={() => setMessageAttachments(prev => prev.filter(x => x.file_id !== a.file_id))} className="text-gray-500 hover:text-red-600 ml-1" data-testid={`remove-pending-${a.file_id}`}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="border-t border-gray-100 p-3 flex items-end gap-2">
                      <Label htmlFor="admin-msg-attach" className="cursor-pointer shrink-0" data-testid="attach-button">
                        <div className={`h-10 w-10 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 ${uploadingAttachment ? 'opacity-50' : ''}`}>
                          <UploadSimple size={18} className="text-gray-600" />
                        </div>
                      </Label>
                      <input id="admin-msg-attach" type="file" className="hidden" onChange={handleMessageAttach} disabled={uploadingAttachment} data-testid="attach-input" />
                      <Textarea
                        value={messageDraft}
                        onChange={e => setMessageDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        placeholder="Write a message… (Enter to send, Shift+Enter for new line)"
                        rows={2}
                        className="flex-1 resize-none text-sm"
                        data-testid="message-input"
                      />
                      <Button onClick={sendMessage} disabled={sendingMessage || (!messageDraft.trim() && messageAttachments.length === 0)} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="send-message-btn"><PaperPlaneTilt size={14} className="mr-1" /> Send</Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== CONTACTS TAB ===== */}
        {tab === 'outreach' && (
          <div className="space-y-6" data-testid="admin-outreach-section">
            {/* Stats + actions */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Total Leads</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="outreach-total">{outreachStats.total}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Not Contacted</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="outreach-new">{outreachStats.new || 0}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Emailed</p>
                <p className="text-2xl font-bold text-blue-700 mt-1" data-testid="outreach-emailed">{outreachStats.emailed || 0}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Replied</p>
                <p className="text-2xl font-bold text-purple-700 mt-1" data-testid="outreach-replied">{outreachStats.replied || 0}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Converted</p>
                <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="outreach-converted">{outreachStats.converted || 0}</p>
              </div>
            </div>

            {/* Sub-view switcher: Leads vs Email History */}
            <div className="flex items-center gap-2" data-testid="outreach-view-switcher">
              <button
                onClick={() => setOutreachView('leads')}
                className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${outreachView === 'leads' ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                data-testid="outreach-view-leads"
              >
                Leads
              </button>
              <button
                onClick={() => setOutreachView('history')}
                className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${outreachView === 'history' ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                data-testid="outreach-view-history"
              >
                <EnvelopeSimple size={14} className="inline mr-1 -mt-0.5" /> Email History{emailHistoryTotal ? ` (${emailHistoryTotal})` : ''}
              </button>
            </div>

            {/* Last import report (shows after each upload until dismissed) */}
            {importReport && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="outreach-import-report">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <p className="text-sm font-semibold text-amber-900">Last import summary</p>
                  <Button size="sm" variant="ghost" onClick={() => setImportReport(null)} className="h-7 text-xs">Dismiss</Button>
                </div>
                <p className="text-xs text-amber-900 mb-2">
                  Parsed <strong>{importReport.total_rows}</strong> rows → imported <strong>{importReport.inserted}</strong> · already in DB <strong>{importReport.duplicates}</strong> · skipped <strong>{importReport.invalid}</strong>
                </p>
                {importReport.skipped_rows?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-700 mb-1">Skipped rows (first {importReport.skipped_rows.length})</p>
                    <div className="bg-white border border-amber-200 rounded-md overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-amber-100/60">
                          <tr>
                            <th className="text-left px-3 py-1.5 font-semibold text-amber-900">Company</th>
                            <th className="text-left px-3 py-1.5 font-semibold text-amber-900">Email cell</th>
                            <th className="text-left px-3 py-1.5 font-semibold text-amber-900">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-100">
                          {importReport.skipped_rows.map((r, i) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 text-gray-700">{r.company_name || <span className="text-gray-400 italic">—</span>}</td>
                              <td className="px-3 py-1.5 text-gray-700 break-all">{r.company_email || <span className="text-gray-400 italic">empty</span>}</td>
                              <td className="px-3 py-1.5 text-amber-700">{r.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[11px] text-amber-700 mt-2">
                      Fix these in your spreadsheet (one valid email per row, no "NA" or URLs) and re-upload — duplicates will be skipped automatically.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Active job progress */}
            {massEmailJob && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="outreach-job-progress">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-sm font-medium text-blue-900">
                    {massEmailJob.status === 'completed' ? 'Mass email completed' : 'Sending in progress…'}
                  </p>
                  <Button size="sm" variant="ghost" onClick={() => setMassEmailJob(null)} className="h-7 text-xs">Dismiss</Button>
                </div>
                <p className="text-xs text-blue-800">
                  Sent: <strong>{massEmailJob.sent || 0}</strong> · Failed: <strong>{massEmailJob.failed || 0}</strong> · Total: <strong>{massEmailJob.total}</strong>
                </p>
                <div className="w-full h-1.5 bg-blue-100 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-blue-600 transition-all" style={{ width: `${massEmailJob.total ? Math.min(100, ((massEmailJob.sent + massEmailJob.failed) / massEmailJob.total) * 100) : 0}%` }} />
                </div>
              </div>
            )}

            {/* Action bar */}
            {outreachView === 'leads' && (<>
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="relative flex-1 max-w-md">
                  <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="search"
                    value={outreachSearch}
                    onChange={e => setOutreachSearch(e.target.value)}
                    placeholder="Search by company, email, phone…"
                    className="pl-9 h-9"
                    data-testid="outreach-search"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => setShowAddLeadDialog(true)} data-testid="outreach-add-lead-btn">
                    <Plus size={14} className="mr-1" /> Add Lead
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowImportDialog(true)} data-testid="outreach-import-btn">
                    <UploadSimple size={14} className="mr-1" /> Import .xlsx / .csv
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => { setMassEmailDraft(d => ({ ...d, subject: d.subject || '', body: d.body || '' })); setShowMassEmailDialog(true); }}
                    disabled={selectedLeadIds.length === 0}
                    className="bg-blue-700 hover:bg-blue-800 text-white"
                    data-testid="outreach-send-btn"
                  >
                    <PaperPlaneTilt size={14} className="mr-1" /> Send Mass Email ({selectedLeadIds.length})
                  </Button>
                  {selectedLeadIds.length > 0 && (
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleBulkDeleteLeads} data-testid="outreach-bulk-delete-btn">
                      <Trash size={14} className="mr-1" /> Delete ({selectedLeadIds.length})
                    </Button>
                  )}
                </div>
              </div>
              {/* Status filter chips */}
              <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-gray-100" data-testid="outreach-status-filter">
                <span className="text-[11px] font-semibold uppercase text-gray-500 tracking-wider mr-1">Filter:</span>
                {[
                  { key: 'all', label: 'All', count: outreachStats.total },
                  { key: 'new', label: 'New', count: outreachStats.new || 0 },
                  { key: 'emailed', label: 'Emailed', count: outreachStats.emailed || 0 },
                  { key: 'replied', label: 'Replied', count: outreachStats.replied || 0 },
                  { key: 'converted', label: 'Converted', count: outreachStats.converted || 0 },
                  { key: 'not_interested', label: 'Not Interested', count: outreachStats.not_interested || 0 },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setOutreachStatusFilter(f.key)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      outreachStatusFilter === f.key
                        ? 'bg-blue-700 text-white border-blue-700'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`outreach-filter-${f.key}`}
                  >
                    {f.label} <span className="opacity-70">({f.count})</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Leads table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {outreachLeads.length === 0 ? (
                <div className="px-6 py-16 text-center" data-testid="outreach-empty-state">
                  <Megaphone size={36} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500">No leads imported yet. Click "Import .xlsx / .csv" to get started.</p>
                  <p className="text-xs text-gray-400 mt-1">Required columns: <code>company name</code>, <code>company number</code>, <code>company email</code>.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 w-10">
                          <Checkbox
                            checked={outreachLeads.length > 0 && outreachLeads.every(l => selectedLeadIds.includes(l.id))}
                            onCheckedChange={handleToggleAllLeads}
                            data-testid="outreach-select-all"
                          />
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Company</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Number</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Last Emailed</th>
                        <th className="px-2 py-3 w-12"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {outreachLeads
                        .filter(l => {
                          if (outreachStatusFilter !== 'all' && (l.status || 'new') !== outreachStatusFilter) return false;
                          if (!outreachSearch) return true;
                          const s = outreachSearch.toLowerCase();
                          return (l.company_name || '').toLowerCase().includes(s) || (l.company_email || '').toLowerCase().includes(s) || (l.company_number || '').toLowerCase().includes(s);
                        })
                        .map(l => {
                          const status = l.status || 'new';
                          const STATUS_BADGE = {
                            new: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                            emailed: 'bg-blue-50 text-blue-700 border-blue-200',
                            replied: 'bg-purple-50 text-purple-700 border-purple-200',
                            converted: 'bg-amber-50 text-amber-700 border-amber-200',
                            not_interested: 'bg-gray-100 text-gray-600 border-gray-200',
                          };
                          const STATUS_LABEL = {
                            new: 'New',
                            emailed: `Emailed${l.email_count > 1 ? ` ×${l.email_count}` : ''}`,
                            replied: 'Replied',
                            converted: 'Converted',
                            not_interested: 'Not Interested',
                          };
                          return (
                            <tr key={l.id} className="hover:bg-gray-50" data-testid={`outreach-row-${l.id}`}>
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={selectedLeadIds.includes(l.id)}
                                  onCheckedChange={() => handleToggleLeadSelection(l.id)}
                                  data-testid={`outreach-select-${l.id}`}
                                />
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{l.company_name || <span className="text-gray-400 italic">—</span>}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{l.company_email}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{l.company_number || '—'}</td>
                              <td className="px-4 py-3 text-center hidden md:table-cell" data-testid={`outreach-status-${l.id}`}>
                                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${STATUS_BADGE[status] || STATUS_BADGE.new}`}>
                                  {STATUS_LABEL[status] || status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell whitespace-nowrap">
                                {l.last_emailed_at ? new Date(l.last_emailed_at).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-2 py-3 text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`outreach-actions-${l.id}`} title="Actions">
                                      <DotsThreeVertical size={16} weight="bold" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    {status !== 'replied' && (
                                      <DropdownMenuItem onClick={() => handleSetLeadStatus(l.id, 'replied')} data-testid={`outreach-mark-replied-${l.id}`}>
                                        <ChatCircle size={14} className="mr-2 text-purple-600" /> Mark as replied
                                      </DropdownMenuItem>
                                    )}
                                    {status !== 'converted' && (
                                      <DropdownMenuItem onClick={() => handleConvertToRecruiter(l)} data-testid={`outreach-convert-${l.id}`}>
                                        <UserCirclePlus size={14} className="mr-2 text-amber-600" /> Convert to recruiter
                                      </DropdownMenuItem>
                                    )}
                                    {status !== 'not_interested' && (
                                      <DropdownMenuItem onClick={() => handleSetLeadStatus(l.id, 'not_interested')} data-testid={`outreach-mark-ni-${l.id}`}>
                                        <XCircle size={14} className="mr-2 text-gray-500" /> Not interested
                                      </DropdownMenuItem>
                                    )}
                                    {status !== 'new' && (
                                      <DropdownMenuItem onClick={() => handleSetLeadStatus(l.id, 'new')} data-testid={`outreach-reset-${l.id}`}>
                                        <ArrowsClockwise size={14} className="mr-2 text-emerald-600" /> Reset to new
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDeleteLead(l.id)} className="text-red-600 focus:text-red-700" data-testid={`outreach-delete-${l.id}`}>
                                      <Trash size={14} className="mr-2" /> Delete lead
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </>)}

            {/* Email History sub-section */}
            {outreachView === 'history' && (
              <div className="space-y-3" data-testid="outreach-email-history-section">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="relative max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="search"
                      value={historySearch}
                      onChange={e => setHistorySearch(e.target.value)}
                      placeholder="Search by recipient, company, subject…"
                      className="pl-9 h-9"
                      data-testid="email-history-search"
                    />
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  {emailHistory.length === 0 ? (
                    <div className="px-6 py-16 text-center" data-testid="email-history-empty-state">
                      <EnvelopeSimple size={36} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-sm text-gray-500">No emails sent yet. History will appear here after your next mass email.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Sent At</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Company</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Recipient</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Subject</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-3 w-20"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {emailHistory
                            .filter(h => {
                              if (!historySearch) return true;
                              const s = historySearch.toLowerCase();
                              return (h.company_name || '').toLowerCase().includes(s) || (h.to_email || '').toLowerCase().includes(s) || (h.subject || '').toLowerCase().includes(s);
                            })
                            .map(h => (
                              <tr key={h.id} className="hover:bg-gray-50" data-testid={`email-history-row-${h.id}`}>
                                <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                                  {h.sent_at ? new Date(h.sent_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{h.company_name || <span className="text-gray-400 italic">—</span>}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{h.to_email}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell max-w-[280px] truncate" title={h.subject}>{h.subject}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${h.status === 'sent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`} title={h.error || ''}>
                                    {h.status === 'sent' ? 'Sent' : 'Failed'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setViewEmailEntry(h)} data-testid={`email-history-view-${h.id}`}>
                                    <Eye size={13} className="mr-1" /> View
                                  </Button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'calls' && (
          <div className="space-y-6" data-testid="admin-calls-section">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Upcoming</p>
                <p className="text-2xl font-bold text-blue-700 mt-1" data-testid="bookings-upcoming">{bookingsStats.upcoming || 0}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Total</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="bookings-total">{bookingsStats.total || 0}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Completed</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="bookings-completed">{bookingsStats.completed || 0}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">Cancelled</p>
                <p className="text-2xl font-bold text-gray-500 mt-1" data-testid="bookings-cancelled">{bookingsStats.cancelled || 0}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">No Show</p>
                <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="bookings-no-show">{bookingsStats.no_show || 0}</p>
              </div>
            </div>

            {/* Filter chips */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2 flex-wrap" data-testid="bookings-filter">
              <span className="text-[11px] font-semibold uppercase text-gray-500 tracking-wider mr-1">Show:</span>
              {[
                { key: 'upcoming', label: 'Upcoming' },
                { key: 'all', label: 'All' },
                { key: 'scheduled', label: 'Scheduled' },
                { key: 'completed', label: 'Completed' },
                { key: 'cancelled', label: 'Cancelled' },
                { key: 'no_show', label: 'No Show' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setBookingsFilter(f.key)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    bookingsFilter === f.key ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`bookings-filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {bookings.length === 0 ? (
                <div className="px-6 py-16 text-center" data-testid="bookings-empty-state">
                  <CalendarBlank size={36} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500">No bookings yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Bookings will appear here when leads schedule a 15-min call from your outreach emails.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">When</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Company</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Phone</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Email</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="px-2 py-3 w-12"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bookings
                        .filter(b => {
                          if (bookingsFilter === 'all') return true;
                          if (bookingsFilter === 'upcoming') {
                            return b.status === 'scheduled' && new Date(b.slot_start_utc) >= new Date();
                          }
                          return b.status === bookingsFilter;
                        })
                        .map(b => {
                          const STATUS_BADGE = {
                            scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
                            completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                            cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
                            no_show: 'bg-amber-50 text-amber-700 border-amber-200',
                          };
                          const STATUS_LABEL = { scheduled: 'Scheduled', completed: 'Completed', cancelled: 'Cancelled', no_show: 'No Show' };
                          const isPast = new Date(b.slot_start_utc) < new Date();
                          return (
                            <tr key={b.id} className="hover:bg-gray-50" data-testid={`booking-row-${b.id}`}>
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{b.slot_local_label}</p>
                                {b.notes && <p className="text-xs text-gray-500 mt-0.5 max-w-sm truncate" title={b.notes}>"{b.notes}"</p>}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium">{b.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 hidden md:table-cell">{b.company_name || '—'}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{b.phone}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">{b.email}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${STATUS_BADGE[b.status] || STATUS_BADGE.scheduled}`}>
                                  {STATUS_LABEL[b.status] || b.status}
                                </span>
                              </td>
                              <td className="px-2 py-3 text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`booking-actions-${b.id}`} title="Actions">
                                      <DotsThreeVertical size={16} weight="bold" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    {b.status !== 'completed' && (
                                      <DropdownMenuItem onClick={() => handleSetBookingStatus(b.id, 'completed')} data-testid={`booking-mark-completed-${b.id}`}>
                                        <CheckCircle size={14} className="mr-2 text-emerald-600" /> Mark completed
                                      </DropdownMenuItem>
                                    )}
                                    {b.status !== 'no_show' && isPast && (
                                      <DropdownMenuItem onClick={() => handleSetBookingStatus(b.id, 'no_show')} data-testid={`booking-mark-noshow-${b.id}`}>
                                        <XCircle size={14} className="mr-2 text-amber-600" /> Mark no-show
                                      </DropdownMenuItem>
                                    )}
                                    {b.status !== 'cancelled' && (
                                      <DropdownMenuItem onClick={() => handleSetBookingStatus(b.id, 'cancelled')} data-testid={`booking-mark-cancelled-${b.id}`}>
                                        <XCircle size={14} className="mr-2 text-gray-500" /> Mark cancelled
                                      </DropdownMenuItem>
                                    )}
                                    {b.status !== 'scheduled' && (
                                      <DropdownMenuItem onClick={() => handleSetBookingStatus(b.id, 'scheduled')} data-testid={`booking-reset-${b.id}`}>
                                        <ArrowsClockwise size={14} className="mr-2 text-blue-600" /> Reset to scheduled
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDeleteBooking(b.id)} className="text-red-600 focus:text-red-700" data-testid={`booking-delete-${b.id}`}>
                                      <Trash size={14} className="mr-2" /> Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'contacts' && (
          <div className="space-y-4" data-testid="admin-contacts-list">
            {contacts.length === 0 ? (<div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-gray-400 text-sm">No messages</div>) : contacts.map(c => (
              <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-3"><div><p className="text-sm font-medium text-gray-900">{c.name}</p><p className="text-xs text-gray-500">{c.email} {c.phone && `| ${c.phone}`}</p></div><span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString()}</span></div>
                {c.subject && <p className="text-sm font-medium text-gray-700 mb-1">{c.subject}</p>}
                <p className="text-sm text-gray-600">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'score-report' && (
          <div data-testid="admin-score-report-panel">
            <ScoreReportGenerator />
          </div>
        )}

        {tab === 'sa-finance' && (
          <div data-testid="admin-sa-finance-panel">
            <SaFinanceTab />
          </div>
        )}

        {tab === 'finance-ledger' && (
          <div data-testid="admin-finance-ledger-panel">
            <FinanceLedgerPanel />
          </div>
        )}

        {tab === 'credit-cards' && (
          <div data-testid="admin-credit-cards-panel">
            <CreditCardsPanel />
          </div>
        )}

        {tab === 'preauth' && (
          <div data-testid="admin-preauth-panel">
            <PreAuthorizationPanel />
          </div>
        )}
        {tab === 'recruiter-eod' && (
          <div data-testid="admin-recruiter-eod-panel">
            <RecruiterEodPanel />
          </div>
        )}

        {tab === 'test-requests' && (
          <TestRequestsPanel />
        )}

        {tab === 'institutions' && (
          <InstitutionsPanel colleges={colleges} />
        )}

        {tab === 'sub-agents' && (
          <AdminSubAgentsPanel />
        )}

        {tab === 'trash' && (
          <TrashPanel
            recruiters={trashRecruiters}
            students={trashStudents}
            loading={trashLoading}
            onReload={loadTrash}
            onAfterMutation={fetchData}
          />
        )}
          </div>
        </main>
      </div>

      {/* ===== DIALOGS ===== */}

      {/* Email Compose Dialog */}
      <Dialog open={showEmailCompose} onOpenChange={(open) => { setShowEmailCompose(open); if (!open) { setAgreementFile(null); } }}>
        <DialogContent className="max-w-lg" data-testid="email-compose-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>
              {emailTarget?._isRenewal ? 'Send New Agreement (Renewal)' : emailTarget?._isResend ? 'Resend Agreement' : 'Send Agreement'}
            </DialogTitle>
            <DialogDescription>
              {emailTarget?._isRenewal
                ? 'The recruiter remains ACTIVE while signing. The existing DocuSign envelope (if any) is voided; a new envelope and signing link are issued. Every send is tracked in the recruiter\u2019s Agreement History.'
                : emailTarget?._isResend
                  ? 'The previous DocuSign envelope (if any) will be voided so the old signing link stops working. A fresh signing email goes out with the PDF you upload here.'
                  : 'Upload the agreement PDF and customise the email. The recruiter receives a signing link via DocuSign (or an HTML fallback if DocuSign is unavailable).'}
            </DialogDescription>
          </DialogHeader>
          {emailTarget && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-gray-500">To</Label>
                <Input value={emailTarget.email} disabled className="bg-gray-50" data-testid="email-to-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-500">Subject</Label>
                <Input value={emailForm.subject} onChange={e => setEmailForm(p => ({ ...p, subject: e.target.value }))} data-testid="email-subject-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-500">Message</Label>
                <Textarea rows={6} value={emailForm.body} onChange={e => setEmailForm(p => ({ ...p, body: e.target.value }))} className="text-sm" data-testid="email-body-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-500">Agreement Document (PDF) *</Label>
                {agreementFile ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <FileText size={20} className="text-green-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{agreementFile.name}</p>
                      <p className="text-xs text-gray-500">{(agreementFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setAgreementFile(null)} className="h-8 px-2 text-xs text-red-500 hover:bg-red-50" data-testid="remove-agreement-file">
                      <Trash size={14} />
                    </Button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-blue-300 transition-colors">
                    <UploadSimple size={24} className="text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-2">Upload the agreement PDF for this recruiter</p>
                    <Label htmlFor="agreement-upload" className="cursor-pointer">
                      <span className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-800 transition-colors">
                        <UploadSimple size={16} /> Choose PDF
                      </span>
                    </Label>
                    <input
                      id="agreement-upload"
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => { if (e.target.files?.[0]) setAgreementFile(e.target.files[0]); e.target.value = ''; }}
                      data-testid="agreement-file-input"
                    />
                  </div>
                )}
              </div>
              <details className="text-xs">
                <summary className="text-blue-600 cursor-pointer font-medium hover:text-blue-800">How to add signature fields to your PDF</summary>
                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                  <p className="text-gray-600">Add these tags anywhere in your PDF/Word document. DocuSign will replace them with interactive fields for the recruiter to fill:</p>
                  <div className="grid grid-cols-2 gap-1.5 font-mono">
                    <div className="bg-white px-2 py-1 rounded border border-gray-200"><span className="text-blue-700">{'{{sign_here}}'}</span> <span className="text-gray-400">- Signature</span></div>
                    <div className="bg-white px-2 py-1 rounded border border-gray-200"><span className="text-blue-700">{'{{initials}}'}</span> <span className="text-gray-400">- Initials</span></div>
                    <div className="bg-white px-2 py-1 rounded border border-gray-200"><span className="text-blue-700">{'{{full_name}}'}</span> <span className="text-gray-400">- Full name</span></div>
                    <div className="bg-white px-2 py-1 rounded border border-gray-200"><span className="text-blue-700">{'{{company_name}}'}</span> <span className="text-gray-400">- Company</span></div>
                    <div className="bg-white px-2 py-1 rounded border border-gray-200"><span className="text-blue-700">{'{{date_signed}}'}</span> <span className="text-gray-400">- Date</span></div>
                    <div className="bg-white px-2 py-1 rounded border border-gray-200"><span className="text-blue-700">{'{{title}}'}</span> <span className="text-gray-400">- Title</span></div>
                    <div className="bg-white px-2 py-1 rounded border border-gray-200"><span className="text-blue-700">{'{{email}}'}</span> <span className="text-gray-400">- Email (auto-filled)</span></div>
                  </div>
                  <p className="text-gray-500 mt-1">Tip: Use small white/light colored font for tags so they blend into the document background.</p>
                </div>
              </details>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs text-blue-700 font-medium">The uploaded agreement will be sent via DocuSign for the recruiter to review and sign electronically.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowEmailCompose(false); setAgreementFile(null); }}>Cancel</Button>
                <Button onClick={handleSendEmailCompose} disabled={sendingEmail || !agreementFile} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="email-send-btn">
                  <PaperPlaneTilt size={16} className="mr-2" /> {sendingEmail ? 'Sending...' : 'Send Agreement'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recruiter Detail */}
      <Dialog open={!!selectedRecruiter} onOpenChange={(open) => { if (!open) { setSelectedRecruiter(null); setEditingRecruiter(false); setRecruiterNewPassword(''); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="recruiter-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Recruiter Details</DialogTitle>
            <DialogDescription>Full profile, contact info, agreement status, and document access.</DialogDescription>
          </DialogHeader>
          {selectedRecruiter && (
            <div className="space-y-4">
              {!editingRecruiter ? (
                <>
                  <div className="flex items-center justify-end">
                    <Button size="sm" variant="outline" onClick={beginEditRecruiter} data-testid="recruiter-edit-btn">
                      <PencilSimple size={14} className="mr-1" /> Edit Profile
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                    {[{ label: 'Full Name', value: selectedRecruiter.full_name },{ label: 'Company', value: selectedRecruiter.company_name },{ label: 'Email', value: selectedRecruiter.email },{ label: 'Phone', value: selectedRecruiter.phone },{ label: 'Country', value: selectedRecruiter.country },{ label: 'City', value: selectedRecruiter.city },{ label: 'Experience', value: selectedRecruiter.years_of_experience },{ label: 'Status', value: statusLabels[selectedRecruiter.status] },{ label: 'Students', value: selectedRecruiter.student_count || 0 },{ label: 'Signed Up', value: new Date(selectedRecruiter.created_at).toLocaleDateString() }].map(f => (
                      <div key={f.label} className="min-w-0"><p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{f.label}</p><p className="text-sm text-gray-900 mt-0.5 break-words">{f.value}</p></div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-3 border border-blue-200 bg-blue-50/40 rounded-md p-4" data-testid="recruiter-edit-form">
                  <p className="text-xs uppercase tracking-widest font-medium text-blue-700 mb-1">Editing recruiter profile</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { key: 'full_name', label: 'Full Name' },
                      { key: 'company_name', label: 'Company' },
                      { key: 'email', label: 'Email', type: 'email' },
                      { key: 'phone', label: 'Phone' },
                      { key: 'country', label: 'Country' },
                      { key: 'city', label: 'City' },
                      { key: 'years_of_experience', label: 'Experience' },
                    ].map(f => (
                      <div key={f.key} className="space-y-1">
                        <Label htmlFor={`edit-${f.key}`} className="text-xs text-gray-600">{f.label}</Label>
                        <Input
                          id={`edit-${f.key}`}
                          type={f.type || 'text'}
                          value={recruiterEditForm[f.key] || ''}
                          onChange={(e) => setRecruiterEditForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                          data-testid={`recruiter-edit-${f.key}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="edit-message" className="text-xs text-gray-600">Notes</Label>
                    <Textarea
                      id="edit-message"
                      rows={3}
                      value={recruiterEditForm.message || ''}
                      onChange={(e) => setRecruiterEditForm(prev => ({ ...prev, message: e.target.value }))}
                      data-testid="recruiter-edit-message"
                    />
                  </div>
                  <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    Changing the email also updates the recruiter&apos;s login address. They will need to sign in with the new email going forward.
                  </p>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={() => setEditingRecruiter(false)} disabled={savingRecruiterEdit} data-testid="recruiter-edit-cancel-btn">Cancel</Button>
                    <Button size="sm" onClick={handleSaveRecruiterEdit} disabled={savingRecruiterEdit} className="bg-blue-800 hover:bg-blue-700 text-white" data-testid="recruiter-edit-save-btn">
                      {savingRecruiterEdit ? 'Saving…' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              )}
              {selectedRecruiter.message && (<div><p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Message</p><p className="text-sm text-gray-700 mt-0.5">{selectedRecruiter.message}</p></div>)}
              {selectedRecruiter.guidelines_accepted && (
                <div className="pt-2 border-t border-gray-100" data-testid={`guidelines-audit-${selectedRecruiter.id}`}>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Recruiter Guidelines Acceptance</p>
                  <p className="text-sm text-gray-900">Accepted{selectedRecruiter.guidelines_version ? ` v${selectedRecruiter.guidelines_version}` : ''} on {selectedRecruiter.guidelines_accepted_at ? new Date(selectedRecruiter.guidelines_accepted_at).toLocaleString() : '—'}</p>
                  {selectedRecruiter.guidelines_accepted_ip && (<p className="text-xs text-gray-500 mt-0.5">IP: {selectedRecruiter.guidelines_accepted_ip}</p>)}
                  {selectedRecruiter.guidelines_accepted_user_agent && (<p className="text-xs text-gray-500 mt-0.5 break-all">UA: {selectedRecruiter.guidelines_accepted_user_agent}</p>)}
                </div>
              )}
              {selectedRecruiter.business_license_storage_path && (
                <div className="pt-2 border-t border-gray-100" data-testid={`business-license-block-${selectedRecruiter.id}`}>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Business License</p>
                  <button
                    type="button"
                    onClick={() => handleViewBusinessLicense(selectedRecruiter.id)}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-900 hover:underline"
                    data-testid={`view-business-license-${selectedRecruiter.id}`}
                  >
                    <FileText size={14} />
                    {selectedRecruiter.business_license_filename || 'View business license'}
                  </button>
                  {selectedRecruiter.business_license_uploaded_at && (
                    <p className="text-xs text-gray-500 mt-0.5">Uploaded {new Date(selectedRecruiter.business_license_uploaded_at).toLocaleString()}</p>
                  )}
                </div>
              )}
              {selectedRecruiter.agreement_storage_path && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Current Signed Agreement</p>
                  <button
                    type="button"
                    onClick={() => handleViewAgreement(selectedRecruiter.id)}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-900 hover:underline"
                    data-testid={`view-agreement-${selectedRecruiter.id}`}
                  >
                    <FileText size={14} />
                    {selectedRecruiter.agreement_filename || 'View signed agreement'}
                    {selectedRecruiter.agreement_method === 'in_person' && <span className="text-xs text-gray-400">(in-person)</span>}
                  </button>
                </div>
              )}

              {/* Agreement History — every send is tracked here. Active recruiters
                  can be sent fresh agreements (e.g. annual renewals) without
                  losing their `active` status. */}
              {(() => {
                const history = (selectedRecruiter.agreement_history || []).slice().sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
                const handleResend = () => handleSendAgreement(selectedRecruiter);
                const handleDownloadHistory = async (entryId, filename) => {
                  try {
                    const res = await axios.get(`${API}/admin/recruiters/${selectedRecruiter.id}/agreement-history/${entryId}/file`, { responseType: 'blob' });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement('a');
                    a.href = url; a.target = '_blank'; a.rel = 'noopener';
                    if (filename) a.download = filename;
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Failed to fetch agreement');
                  }
                };
                const methodLabel = (m) => ({ docusign: 'DocuSign', email: 'Email', in_person: 'In-Person', manual: 'Manual' }[m] || m || '—');
                const statusBadge = (st) => {
                  const map = {
                    sent:    { cls: 'bg-blue-50 text-blue-700 border-blue-200',       label: 'Sent · Awaiting Signature' },
                    signed:  { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Signed' },
                    voided:  { cls: 'bg-gray-100 text-gray-600 border-gray-200',       label: 'Voided · Superseded' },
                    failed:  { cls: 'bg-red-50 text-red-700 border-red-200',          label: 'Send Failed' },
                  };
                  const s = map[st] || { cls: 'bg-gray-100 text-gray-600 border-gray-200', label: st || '—' };
                  return <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full border ${s.cls}`}>{s.label}</span>;
                };
                return (
                  <div className="pt-3 border-t border-gray-100" data-testid="agreement-history-section">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Agreement History</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{history.length} agreement{history.length === 1 ? '' : 's'} sent · most recent first</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleResend}
                        className="h-8 px-3 text-xs"
                        data-testid="send-new-agreement-from-detail"
                      >
                        <PaperPlaneTilt size={14} className="mr-1" /> {selectedRecruiter.status === 'active' ? 'Send New Agreement' : 'Resend Agreement'}
                      </Button>
                    </div>
                    {history.length === 0 ? (
                      <div className="rounded-md border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-400" data-testid="agreement-history-empty">No agreements sent yet.</div>
                    ) : (
                      <div className="space-y-2" data-testid="agreement-history-list">
                        {history.map((h, idx) => {
                          const v = history.length - idx; // v1, v2, … oldest = lowest
                          return (
                            <div key={h.id || idx} className="flex items-start justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2" data-testid={`agreement-history-row-${h.id || idx}`}>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-[11px] font-semibold text-gray-500">v{v}</span>
                                  {statusBadge(h.status)}
                                  <span className="text-[11px] text-gray-400">· {methodLabel(h.method)}</span>
                                </div>
                                <p className="text-xs text-gray-700 mt-1 truncate">{h.subject || 'Study Alliance Recruitment Agreement'}</p>
                                <p className="text-[11px] text-gray-500 mt-0.5">
                                  Sent {h.sent_at ? new Date(h.sent_at).toLocaleString() : '—'}
                                  {h.sent_by ? ` · by ${h.sent_by}` : ''}
                                </p>
                                {h.signed_at && (
                                  <p className="text-[11px] text-emerald-700 mt-0.5">Signed {new Date(h.signed_at).toLocaleString()}</p>
                                )}
                              </div>
                              {h.agreement_storage_path && (
                                <button
                                  type="button"
                                  onClick={() => handleDownloadHistory(h.id, h.agreement_filename)}
                                  className="shrink-0 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 hover:underline"
                                  data-testid={`agreement-history-download-${h.id || idx}`}
                                  title={h.agreement_filename || 'Download agreement PDF'}
                                >
                                  <DownloadSimple size={12} /> PDF
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Commission Visibility Toggle */}
              <div className="pt-3 border-t border-gray-100" data-testid="commission-visibility-section">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Hide Commission From Recruiter</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 max-w-md">
                      When ON, this recruiter sees NO commission info, NO sale price field, NO earnings forecast. They just upload student data. Admin still controls pricing internally.
                    </p>
                  </div>
                  <Switch
                    checked={!!selectedRecruiter.hide_commission}
                    onCheckedChange={handleToggleHideCommission}
                    data-testid="hide-commission-toggle"
                  />
                </div>
              </div>

              {/* Student Declaration Form Toggle (per-recruiter override) */}
              <div className="pt-3 border-t border-gray-100" data-testid="declaration-form-section">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Student Declaration Form</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 max-w-md">
                      When OFF, this recruiter&apos;s students never need to upload a Student Declaration Form &mdash; regardless of the college&apos;s own requirement. Default: ON.
                    </p>
                  </div>
                  <Switch
                    checked={selectedRecruiter.declaration_form_enabled !== false}
                    onCheckedChange={handleToggleDeclarationForm}
                    data-testid="declaration-form-toggle"
                  />
                </div>
              </div>

              {/* Admin whitelist: let this recruiter self-complete Pre-Authorization */}
              <div className="pt-3 border-t border-gray-100" data-testid="self-pre-auth-section">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Self-Complete Pre-Authorization</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 max-w-md">
                      When ON, this recruiter skips the "email the student" flow and signs the declaration on the student&apos;s behalf (typed cursive signature + audit log). Each action is attestation-gated and tamper-logged. Default: OFF.
                    </p>
                  </div>
                  <Switch
                    checked={!!selectedRecruiter.can_self_complete_pre_auth}
                    onCheckedChange={handleToggleSelfPreAuth}
                    data-testid="self-pre-auth-toggle"
                  />
                </div>
              </div>

              {/* Admin: Reset recruiter password */}
              <div className="pt-3 border-t border-gray-100" data-testid="recruiter-password-reset-section">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">Reset Login Password</p>
                <p className="text-[11px] text-gray-500 mb-2">Sets a new password immediately. Copy it and share it with the recruiter via a secure channel &mdash; it will not be emailed.</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder="New password (min 8 chars)"
                    value={recruiterNewPassword}
                    onChange={(e) => setRecruiterNewPassword(e.target.value)}
                    className="flex-1 h-9 text-sm font-mono"
                    data-testid="recruiter-new-password-input"
                  />
                  <Button size="sm" variant="outline" onClick={() => setRecruiterNewPassword(Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6).toUpperCase() + '!')} data-testid="recruiter-generate-password-btn">
                    Generate
                  </Button>
                  <Button size="sm" onClick={handleSetRecruiterPassword} disabled={settingRecruiterPassword || !recruiterNewPassword} className="bg-blue-800 hover:bg-blue-700 text-white" data-testid="recruiter-set-password-btn">
                    <Key size={14} className="mr-1" />
                    {settingRecruiterPassword ? 'Saving…' : 'Set Password'}
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    id="recruiter-must-change"
                    checked={recruiterMustChangePw}
                    onCheckedChange={(v) => setRecruiterMustChangePw(!!v)}
                    data-testid="recruiter-must-change-password-checkbox"
                  />
                  <Label htmlFor="recruiter-must-change" className="text-[11px] text-gray-600 cursor-pointer">
                    Require the recruiter to change this password on their next login
                  </Label>
                </div>
              </div>

              {/* Commission Tiers (Option A) */}
              <div className={`pt-3 border-t border-gray-100 ${selectedRecruiter.hide_commission ? 'opacity-50 pointer-events-none' : ''}`} data-testid="commission-tiers-section">
                {selectedRecruiter.hide_commission && (
                  <div className="mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid="tiers-hidden-banner">
                    Commission visibility is OFF for this recruiter. These tiers are admin-only — the recruiter will not see them.
                  </div>
                )}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Commission Tiers</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      When this recruiter enters a Sale Price on a student, the system picks the highest tier whose threshold is ≤ that price and applies its commission. Leave empty to use the flat commission ({selectedRecruiter.commission_per_student ? `$${Number(selectedRecruiter.commission_per_student).toLocaleString()}` : '—'}).
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm" data-testid="tiers-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase">Sale Price ≥</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase">Recruiter Earns</th>
                        <th className="px-3 py-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tierDraft.length === 0 ? (
                        <tr><td colSpan={3} className="px-3 py-4 text-center text-xs text-gray-400">No tiers configured yet</td></tr>
                      ) : tierDraft.map((t, idx) => (
                        <tr key={idx} data-testid={`tier-row-${idx}`}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-400">$</span>
                              <Input type="number" min="0" step="100" value={t.sale_price} onChange={e => handleEditTier(idx, 'sale_price', e.target.value)} className="h-8 w-28 text-sm" data-testid={`tier-sale-${idx}`} />
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-400">$</span>
                              <Input type="number" min="0" step="100" value={t.commission_amount} onChange={e => handleEditTier(idx, 'commission_amount', e.target.value)} className="h-8 w-28 text-sm" data-testid={`tier-comm-${idx}`} />
                            </div>
                          </td>
                          <td className="px-2 py-2 text-right">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveTier(idx)} data-testid={`remove-tier-${idx}`} title="Remove tier">
                              <Trash size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50/50">
                      <tr>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400">$</span>
                            <Input type="number" min="0" step="100" placeholder="e.g. 17500" value={newTierRow.sale_price} onChange={e => setNewTierRow(p => ({ ...p, sale_price: e.target.value }))} className="h-8 w-28 text-sm" data-testid="new-tier-sale" />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400">$</span>
                            <Input type="number" min="0" step="100" placeholder="e.g. 7500" value={newTierRow.commission_amount} onChange={e => setNewTierRow(p => ({ ...p, commission_amount: e.target.value }))} className="h-8 w-28 text-sm" data-testid="new-tier-comm" />
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={handleAddTier} data-testid="add-tier-btn"><Plus size={12} className="mr-1" /> Add</Button>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="flex justify-end mt-3">
                  <Button size="sm" onClick={handleSaveTiers} disabled={savingTiers} className="h-8 px-3 text-xs bg-blue-700 hover:bg-blue-800 text-white" data-testid="save-tiers-btn">
                    {savingTiers ? 'Saving…' : 'Save Tier Table'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recruiter Students View — matches the Students tab look:
          colored pipeline pill, clickable row opens the same student-detail dialog. */}
      <Dialog open={!!recruiterStudentsView} onOpenChange={() => setRecruiterStudentsView(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="recruiter-students-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Students by {recruiterStudentsView?.full_name}</DialogTitle>
            <DialogDescription>
              {recruiterStudents.length} student{recruiterStudents.length === 1 ? '' : 's'} on file. Click any row to open full details.
            </DialogDescription>
          </DialogHeader>
          {recruiterStudents.length === 0 ? (
            <p className="text-center text-gray-400 py-12 text-sm" data-testid="recruiter-students-empty">No students submitted by this recruiter</p>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-2">
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Student</th>
                      <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell w-[100px]">Created</th>
                      <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell w-[160px]">College</th>
                      <th className="text-left px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide w-[160px]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recruiterStudents.map(s => {
                      const stage = s.application_stage || 'profile_created';
                      const stageClass = PIPELINE_STAGE_COLORS[stage] || 'bg-gray-50 text-gray-600 border-gray-200';
                      return (
                        <tr
                          key={s.id}
                          className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                          onClick={() => { setRecruiterStudentsView(null); handleViewStudent(s); }}
                          data-testid={`recruiter-student-row-${s.id}`}
                        >
                          <td className="px-3 py-2 align-top">
                            <p className="text-sm font-medium text-gray-900 truncate" title={s.full_name}>{s.full_name}</p>
                            <p className="text-[11px] text-gray-500 truncate" title={s.email}>{s.email}</p>
                          </td>
                          <td className="px-2 py-2 text-[11px] text-gray-600 hidden sm:table-cell align-top whitespace-nowrap">
                            {s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-700 hidden md:table-cell align-top truncate" title={s.assigned_college_name || 'Unassigned'}>
                            {s.assigned_college_name || <span className="text-gray-400">Unassigned</span>}
                          </td>
                          <td className="px-2 py-2 align-top">
                            <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${stageClass}`}>
                              {PIPELINE_STAGE_LABELS[stage] || stage}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Direct Lead Detail — clickable from Direct Leads tab. Shows every
          captured field for this applicant plus the email history that was
          sent to their address from the mass-email blasts. Read-only details
          (status/delete actions live on the table row). */}
      <Dialog open={!!directLeadDetail} onOpenChange={() => setDirectLeadDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="direct-lead-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>
              {directLeadDetail?.full_name || 'Lead Details'}
            </DialogTitle>
            <DialogDescription>
              Ref: <span className="font-mono">{directLeadDetail?.reference_number || '—'}</span>
              {directLeadDetail?.lead_source ? <> · Source: <strong>{directLeadDetail.lead_source}</strong></> : null}
              {directLeadDetailLoading ? <> · <em className="text-blue-600">loading full record…</em></> : null}
            </DialogDescription>
          </DialogHeader>
          {directLeadDetail && (
            <div className="space-y-5">
              {/* Identity + contact */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {[
                  ['Full Name', directLeadDetail.full_name],
                  ['Email', directLeadDetail.email],
                  ['Phone', [directLeadDetail.phone_code, directLeadDetail.phone].filter(Boolean).join(' ') || directLeadDetail.phone],
                  ['Date of Birth', directLeadDetail.dob],
                  ['Gender', directLeadDetail.gender],
                  ['Nationality', directLeadDetail.nationality],
                  ['Address', directLeadDetail.address],
                  ['Lead Source', directLeadDetail.lead_source],
                  ['Preferred Program', directLeadDetail.preferred_program],
                  ['Preferred Intake', directLeadDetail.preferred_intake],
                  ['Passport / PR Status', directLeadDetail.passport_pr_status],
                  ['Status', appStatusLabels[directLeadDetail.status] || directLeadDetail.status],
                  ['Created', directLeadDetail.created_at ? new Date(directLeadDetail.created_at).toLocaleString() : '—'],
                  ['Updated', directLeadDetail.updated_at ? new Date(directLeadDetail.updated_at).toLocaleString() : '—'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{k}</p>
                    <p className="text-sm text-gray-900 mt-0.5 break-words" data-testid={`lead-field-${k.toLowerCase().replace(/\s+/g, '-')}`}>{v || <span className="text-gray-400">—</span>}</p>
                  </div>
                ))}
              </div>

              {/* Notes / message */}
              {(directLeadDetail.notes || directLeadDetail.message || directLeadDetail.status_message) && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1">Message / Notes</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{directLeadDetail.notes || directLeadDetail.message || directLeadDetail.status_message}</p>
                </div>
              )}

              {/* Academic history if present (public form may capture it) */}
              {Array.isArray(directLeadDetail.academic_history) && directLeadDetail.academic_history.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1">Academic History</p>
                  <ul className="text-sm text-gray-700 list-disc pl-5 space-y-0.5">
                    {directLeadDetail.academic_history.map((h, idx) => (
                      <li key={idx}>{[h.qualification, h.institution, h.year, h.grade].filter(Boolean).join(' · ')}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Documents */}
              <div>
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1">Documents ({(directLeadDetail.documents || []).length})</p>
                {(directLeadDetail.documents || []).length === 0 ? (
                  <p className="text-xs text-gray-400">No documents uploaded</p>
                ) : (
                  <ul className="text-xs text-gray-700 space-y-1">
                    {directLeadDetail.documents.map((d, idx) => (
                      <li key={idx} className="flex items-center gap-1.5">
                        <FileText size={12} className="text-gray-400" />
                        <span className="truncate">{d.original_filename || d.filename || d.name || `Document ${idx + 1}`}</span>
                        {d.size ? <span className="text-gray-400">({Math.round(d.size / 1024)} KB)</span> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Email history sent to this lead's address */}
              <div className="pt-3 border-t border-gray-100" data-testid="lead-email-history">
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-2">
                  Email History · {(directLeadDetail.email_history || []).length}
                </p>
                {(directLeadDetail.email_history || []).length === 0 ? (
                  <p className="text-xs text-gray-400">No mass emails have been sent to this address yet.</p>
                ) : (
                  <div className="space-y-2">
                    {directLeadDetail.email_history.map((h, idx) => (
                      <div key={h.id || idx} className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-gray-900 truncate" title={h.subject}>{h.subject || '(no subject)'}</p>
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${h.status === 'sent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{h.status || 'sent'}</span>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {h.sent_at ? new Date(h.sent_at).toLocaleString() : '—'}
                          {h.error ? <span className="text-red-600"> · {h.error}</span> : null}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Student Detail with File Preview */}
      <Dialog open={!!selectedStudent} onOpenChange={() => { setSelectedStudent(null); setStudentFiles([]); setFilePreview(null); setStudentScoreReports([]); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="student-detail-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Student Details</DialogTitle>
              {selectedStudent && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleFullResetStudent}
                    disabled={resettingStudent}
                    className="border-red-300 text-red-700 bg-red-50 hover:bg-red-100"
                    title="Void DocuSign EA + wipe all pipeline stamps and drop to Profile Created"
                    data-testid="student-full-reset-btn"
                  >
                    <ArrowCounterClockwise size={14} className="mr-1" />
                    {resettingStudent ? 'Resetting…' : 'Reset Student'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { const sid = selectedStudent.id; setSelectedStudent(null); navigate(`/admin/student/${sid}`); }}
                    data-testid="student-edit-profile-btn"
                  >
                    <PencilSimple size={14} className="mr-1" /> Edit Profile
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {(() => {
                  const yn = (v) => v === true ? 'Yes' : v === false ? 'No' : '';
                  const line1 = selectedStudent.address_line1 || selectedStudent.address_line1_extracted;
                  const line2 = selectedStudent.address_line2 || selectedStudent.address_line2_extracted;
                  const city = selectedStudent.city || selectedStudent.city_extracted;
                  const province = selectedStudent.province || selectedStudent.province_extracted;
                  const l1Hint = !selectedStudent.address_line1 && selectedStudent.address_line1_extracted ? 'auto-extracted from ID' : undefined;
                  const l2Hint = !selectedStudent.address_line2 && selectedStudent.address_line2_extracted ? 'auto-extracted from ID' : undefined;
                  const cityHint = !selectedStudent.city && selectedStudent.city_extracted ? 'auto-extracted from ID' : undefined;
                  const provHint = !selectedStudent.province && selectedStudent.province_extracted ? 'auto-extracted from ID' : undefined;
                  const progStart = selectedStudent.intake_date || selectedStudent.assigned_intake_start;
                  const progEnd = selectedStudent.assigned_intake_end;
                  const progStartHint = !selectedStudent.intake_date && selectedStudent.assigned_intake_start ? 'from assigned intake' : undefined;
                  const isMature = selectedStudent.is_mature_student;
                  return [
                  { label: 'Full Name', value: selectedStudent.full_name },
                  { label: 'Email', value: selectedStudent.email },
                  { label: 'Phone', value: selectedStudent.phone },
                  { label: 'DOB', value: selectedStudent.dob },
                  { label: 'Gender', value: selectedStudent.gender },
                  { label: 'Nationality', value: selectedStudent.nationality },
                  { label: 'Marital Status', value: selectedStudent.marital_status },
                  { label: 'Passport / PR Status', value: selectedStudent.passport_pr_status },
                  { label: 'Mature Student', value: yn(isMature), hint: 'colleges use this to decide if Class 12 is required' },
                  ...(isMature === true ? [{ label: '2-yr Canadian Diploma', value: yn(selectedStudent.has_canadian_2yr_diploma), hint: 'skips English test ladder if Yes' }] : []),
                  ...(isMature !== true ? [
                    { label: 'English Test Proof', value: yn(selectedStudent.english_test_proof) },
                    { label: '2-yr Canadian Transcript', value: yn(selectedStudent.canadian_transcript_2yr) },
                    { label: 'Accuplacer Intent', value: yn(selectedStudent.accuplacer_intent) },
                  ] : []),
                  { label: 'Address', value: selectedStudent.address, colSpan: 2 },
                  { label: 'Address Line 1', value: line1, hint: l1Hint },
                  { label: 'Address Line 2', value: line2, hint: l2Hint },
                  { label: 'City', value: city, hint: cityHint },
                  { label: 'Province', value: province, hint: provHint },
                  { label: 'Postal Code', value: selectedStudent.postal_code || selectedStudent.postal_code_extracted, hint: !selectedStudent.postal_code && selectedStudent.postal_code_extracted ? 'auto-extracted from ID' : undefined },
                  { label: 'Country', value: selectedStudent.country_extracted || 'Canada' },
                  { label: 'Program (typed)', value: selectedStudent.preferred_program },
                  { label: 'Intake (typed)', value: selectedStudent.preferred_intake || selectedStudent.intake_label },
                  { label: 'Assigned Intake', value: selectedStudent.intake_label || '—', hint: 'admin-assigned window' },
                  { label: 'Program Start', value: progStart || '—', hint: progStartHint },
                  { label: 'Program End', value: progEnd || '—' },
                  { label: 'Status', value: selectedStudent.status },
                  { label: 'Pipeline Stage', value: PIPELINE_STAGE_LABELS[selectedStudent.application_stage || 'profile_created'] },
                  { label: 'Recruiter', value: selectedStudent.recruiter_name },
                  { label: 'Assigned College', value: selectedStudent.assigned_college_name || 'Unassigned' },
                  { label: 'Campus / Branch', value: selectedStudent.assigned_branch || selectedStudent.preferred_branch || '—' },
                  { label: 'Assigned Program', value: selectedStudent.assigned_program_name || (selectedStudent.preferred_program_name ? `(preferred: ${selectedStudent.preferred_program_name})` : '—') },
                  { label: 'Serious Lead', value: yn(selectedStudent.is_serious) },
                  { label: 'Needs Exam Prep', value: selectedStudent.needs_exam_prep ? (selectedStudent.exam_prep_types?.length ? selectedStudent.exam_prep_types.join(', ') : 'Yes') : 'No' },
                  { label: 'Academic History', value: selectedStudent.academic_history, colSpan: 2 },
                  { label: 'Notes', value: selectedStudent.notes, colSpan: 2 },
                  {
                    label: 'Profile Created On',
                    value: selectedStudent.created_at
                      ? new Date(selectedStudent.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                      : '—',
                    hint: selectedStudent.recruiter_name ? `by ${selectedStudent.recruiter_name}` : undefined,
                  },
                  {
                    label: 'Last Updated',
                    value: selectedStudent.updated_at
                      ? new Date(selectedStudent.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                      : '—',
                  },
                ];
                })().map(f => (
                  <div key={f.label} className={f.colSpan === 2 ? 'col-span-2' : ''} data-testid={`student-profile-field-${f.label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                      {f.label}
                      {f.hint && <span className="ml-2 text-[9px] font-normal text-indigo-500 normal-case tracking-normal">({f.hint})</span>}
                    </p>
                    <p className="text-sm text-gray-900 mt-0.5 whitespace-pre-line break-words">{f.value || '-'}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => openAssignCollege(selectedStudent)} data-testid="assign-college-from-detail"><Buildings size={14} className="mr-2" /> Assign College</Button>
                <Button size="sm" variant="outline" onClick={() => openChangeIntakeForStudent(selectedStudent)} data-testid="change-intake-from-detail"><CalendarBlank size={14} className="mr-2" /> Change Intake</Button>
                <Button size="sm" onClick={handleForwardToCollege} disabled={forwarding} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="forward-to-college-btn">
                  <PaperPlaneTilt size={14} className="mr-2" /> {forwarding ? 'Forwarding…' : 'Forward to College'}
                </Button>
                {selectedStudent.last_forwarded_to && (
                  <span className="text-[10px] text-gray-500" data-testid="last-forwarded-indicator">Last forwarded to <strong>{selectedStudent.last_forwarded_to}</strong> · {selectedStudent.last_forwarded_at ? new Date(selectedStudent.last_forwarded_at).toLocaleString() : ''}</span>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <Label className="text-xs text-gray-500 whitespace-nowrap">Pipeline Status:</Label>
                  <Select
                    value={selectedStudent.application_stage || 'profile_created'}
                    onValueChange={(v) => handleUpdateStage(selectedStudent.id, v)}
                  >
                    <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="student-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PIPELINE_STAGES.map(stg => (<SelectItem key={stg} value={stg}>{PIPELINE_STAGE_LABELS[stg]}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedStudent.needs_exam_prep && (<div><p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Exam Prep</p><div className="flex flex-wrap gap-1">{selectedStudent.exam_prep_types?.map(t => (<Badge key={t} variant="outline" className="text-xs">{t}</Badge>))}</div></div>)}

              {/* Financials — admin view (institution-only data, recruiters never see this) */}
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Financials <span className="text-[9px] normal-case text-indigo-500">(institution-entered)</span></p>
                <FinancialsSummary studentId={selectedStudent.id} canEdit onEditClick={() => setAdminFinancialsOpen(true)} />
              </div>

              {/* Student Aid BC upload status */}
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2" data-testid="student-aid-bc-row">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wider whitespace-nowrap">Student Aid BC</span>
                  {selectedStudent.student_aid_bc_uploaded ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
                      <CheckCircle size={12} weight="fill" /> Uploaded
                      {selectedStudent.student_aid_bc_uploaded_at && (
                        <span className="text-[10px] text-gray-500 font-normal ml-1">
                          on {new Date(selectedStudent.student_aid_bc_uploaded_at).toLocaleDateString()}
                          {selectedStudent.student_aid_bc_uploaded_by ? ` by ${selectedStudent.student_aid_bc_uploaded_by}` : ''}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">Not yet uploaded</span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAdminToggleSABC(selectedStudent.id, !!selectedStudent.student_aid_bc_uploaded)}
                  className="h-7 text-xs"
                  data-testid="admin-sabc-toggle-btn"
                >
                  {selectedStudent.student_aid_bc_uploaded ? 'Mark as not uploaded' : 'Mark as uploaded'}
                </Button>
              </div>

              {/* Financials panel (admin only) */}
              <div className="border-t border-gray-100 pt-4" data-testid="student-financials-panel">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Financials</p>
                  {(() => {
                    const sale = Number(pricingDraft.sale_price || 0);
                    const fee = Number(pricingDraft.college_fee_snapshot || 0);
                    const comm = Number(pricingDraft.recruiter_commission_snapshot || 0);
                    const net = Math.max(sale - fee - comm, 0);
                    const snapshotFee = Number(selectedStudent.admin_fee_snapshot ?? adminFee);
                    const totalTuition = sale > 0 ? sale + snapshotFee : 0;
                    return (
                      <div className="flex items-center gap-4">
                        {sale > 0 && (
                          <span className="text-xs text-gray-600 tabular-nums" title={`Sale $${sale.toLocaleString()} + $${snapshotFee.toLocaleString()} admin fee`} data-testid="student-total-tuition">
                            Total tuition: <span className="font-semibold text-gray-900">${totalTuition.toLocaleString()}</span>
                          </span>
                        )}
                        <span className="text-sm font-semibold text-emerald-700" data-testid="student-net-revenue">
                          Study Alliance Earning: ${net.toLocaleString()}
                        </span>
                      </div>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Sale Price (CAD)</Label>
                    <Input type="number" min="0" step="100" value={pricingDraft.sale_price} onChange={e => { setPricingDraft(p => ({ ...p, sale_price: e.target.value })); setPricingDirty(true); }} className="h-9 text-sm" data-testid="student-sale-price-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">College Fee (CAD)</Label>
                    <Input type="number" min="0" step="100" value={pricingDraft.college_fee_snapshot} onChange={e => { setPricingDraft(p => ({ ...p, college_fee_snapshot: e.target.value })); setPricingDirty(true); }} className="h-9 text-sm" data-testid="student-college-fee-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Recruiter Comm. (CAD)</Label>
                    <Input type="number" min="0" step="100" value={pricingDraft.recruiter_commission_snapshot} onChange={e => { setPricingDraft(p => ({ ...p, recruiter_commission_snapshot: e.target.value })); setPricingDirty(true); }} className="h-9 text-sm" data-testid="student-recruiter-comm-input" />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-xs text-gray-400">Snapshots: what Study Alliance charged this student, paid the college, and owes the recruiter.</p>
                  <Button size="sm" onClick={handleSavePricing} disabled={!pricingDirty} className="bg-blue-700 hover:bg-blue-800 text-white h-8 px-3 text-xs" data-testid="student-pricing-save-btn">Save Pricing</Button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Documents ({studentFiles.length || selectedStudent.documents?.length || 0})</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {((selectedStudent.preauth_coe_received_at || selectedStudent.application_stage === 'coe_sent') && (selectedStudent.preauth_ea_signed_at || selectedStudent.ea_signed_at || selectedStudent.ea_status === 'signed')) && (
                      <Button
                        size="sm"
                        onClick={() => setPreAuthStudent(selectedStudent)}
                        disabled={!!selectedStudent.pre_auth_sent_at || !!selectedStudent.pre_auth_self_completed_at}
                        className={`h-8 px-3 text-xs shrink-0 ${
                          (selectedStudent.pre_auth_sent_at || selectedStudent.pre_auth_self_completed_at)
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200 cursor-default hover:bg-emerald-100'
                            : 'bg-blue-700 hover:bg-blue-800 text-white'
                        }`}
                        title={
                          selectedStudent.pre_auth_sent_at
                            ? `Pre-Auth email sent ${(selectedStudent.pre_auth_sent_at || '').slice(0, 10)}`
                            : selectedStudent.pre_auth_self_completed_at
                              ? `Self-completed ${(selectedStudent.pre_auth_self_completed_at || '').slice(0, 10)}`
                              : 'Send the Pre-Authorization email + Declaration'
                        }
                        data-testid="admin-pre-auth-btn"
                      >
                        <ShieldCheck size={14} className="mr-1" />
                        {(selectedStudent.pre_auth_sent_at || selectedStudent.pre_auth_self_completed_at) ? 'Pre-Auth Sent ✓' : 'Pre-Authorization'}
                      </Button>
                    )}
                    {((selectedStudent.preauth_coe_received_at || selectedStudent.application_stage === 'coe_sent') && (selectedStudent.preauth_ea_signed_at || selectedStudent.ea_signed_at || selectedStudent.ea_status === 'signed')) && !selectedStudent.pre_auth_sent_at && !selectedStudent.pre_auth_self_completed_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelfPreAuthStudent(selectedStudent)}
                        className="h-8 px-3 text-xs shrink-0 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-300"
                        title="Self-complete the Pre-Authorization on behalf of the student (audit-logged)"
                        data-testid="admin-self-pre-auth-btn"
                      >
                        <ShieldCheck size={14} className="mr-1" /> Self-Complete
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleVerifyIdMatch(selectedStudent)}
                      disabled={idMatchLoading && idMatchStudent?.id === selectedStudent.id}
                      className="h-8 px-3 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="verify-id-match-btn"
                      title="Cross-check profile name, DOB and address against uploaded ID documents"
                    >
                      {idMatchLoading && idMatchStudent?.id === selectedStudent.id
                        ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Verifying…</>
                        : <><ShieldCheck size={14} className="mr-1" /> Verify ID Match</>}
                    </Button>
                  </div>
                </div>
                {studentFiles.length > 0 ? (
                  <div className="space-y-2">{studentFiles.map(f => (
                    <div key={f.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid={`student-file-row-${f.id}`}>
                      {f.content_type?.startsWith('image/') ? <ImageIcon size={18} className="text-blue-500 shrink-0" /> : <FileText size={18} className="text-gray-500 shrink-0" />}
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-900 truncate">{f.original_filename}</p><p className="text-xs text-gray-500">{f.doc_type?.replace(/_/g, ' ')}</p></div>
                      <Button size="sm" variant="ghost" onClick={() => handleDownloadFile(f)} className="h-8 px-2 text-xs shrink-0">{f.content_type?.startsWith('image/') || f.content_type === 'application/pdf' ? <><Eye size={14} className="mr-1" /> Preview</> : <><DownloadSimple size={14} className="mr-1" /> Download</>}</Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteStudentFile(f)}
                        className="h-8 px-2 text-xs shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Remove this document"
                        data-testid={`student-file-delete-${f.id}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  ))}</div>
                ) : (<p className="text-sm text-gray-400">No documents</p>)}
              </div>
              {filePreview && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between"><p className="text-sm font-medium text-gray-700 truncate">{filePreview.name}</p><Button size="sm" variant="ghost" onClick={() => setFilePreview(null)} className="h-7 px-2 text-xs">Close</Button></div>
                  {filePreview.type === 'image' && <div className="p-4 bg-white flex justify-center"><img src={filePreview.url} alt={filePreview.name} className="max-h-[400px] max-w-full object-contain rounded" /></div>}
                  {filePreview.type === 'pdf' && <div className="p-4 bg-white text-center"><a href={filePreview.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-800"><DownloadSimple size={16} /> Open PDF</a></div>}
                </div>
              )}

              {/* Score Reports — intentionally not rendered as a dedicated
                  section. Generated Accuplacer PDFs land in the Documents
                  list above (doc_type: english_test_proof) so admin sees
                  them alongside every other uploaded file, matching the
                  registrar + president views. */}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== DocuSign Bulk Upload export dialog ===== */}
      <Dialog open={showDsBulkDialog} onOpenChange={setShowDsBulkDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ds-bulk-export-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
              <FileText size={20} className="text-indigo-600" />
              DocuSign Bulk Upload — Export
            </DialogTitle>
            <DialogDescription>
              Downloads a .xlsx matching the 29-column DocuSign template. The values below are applied to every row; per-student fields (name, address, DOB, ID doc, English proof, program &amp; price) are filled automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 mb-1 block">Students to include</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={dsBulkScope === 'filtered' ? 'default' : 'outline'}
                    onClick={() => setDsBulkScope('filtered')}
                    className="h-8 px-3 text-xs"
                    data-testid="ds-bulk-scope-filtered"
                  >
                    Current view ({filteredStudents.length})
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={dsBulkScope === 'all' ? 'default' : 'outline'}
                    onClick={() => setDsBulkScope('all')}
                    className="h-8 px-3 text-xs"
                    data-testid="ds-bulk-scope-all"
                  >
                    All students
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 mb-1 block">Program Start Date</Label>
                <Input
                  type="date"
                  value={dsBulkDefaults.program_start_date}
                  onChange={(e) => setDsBulkDefaults(p => ({ ...p, program_start_date: e.target.value }))}
                  className="h-9 text-sm"
                  data-testid="ds-bulk-start"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 mb-1 block">Program End Date</Label>
                <Input
                  type="date"
                  value={dsBulkDefaults.program_end_date}
                  onChange={(e) => setDsBulkDefaults(p => ({ ...p, program_end_date: e.target.value }))}
                  className="h-9 text-sm"
                  data-testid="ds-bulk-end"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 mb-1 block">Credential</Label>
                <select
                  value={dsBulkDefaults.credential}
                  onChange={(e) => setDsBulkDefaults(p => ({ ...p, credential: e.target.value }))}
                  className="h-9 text-sm w-full rounded-md border border-input bg-background px-3"
                  data-testid="ds-bulk-credential"
                >
                  <option value="Diploma">Diploma</option>
                  <option value="Certificate">Certificate</option>
                  <option value="Degree">Degree</option>
                  <option value="Advanced Diploma">Advanced Diploma</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 mb-1 block">Delivery</Label>
                <select
                  value={dsBulkDefaults.delivery}
                  onChange={(e) => setDsBulkDefaults(p => ({ ...p, delivery: e.target.value }))}
                  className="h-9 text-sm w-full rounded-md border border-input bg-background px-3"
                  data-testid="ds-bulk-delivery"
                >
                  <option value="Self Paced/Online">Self Paced/Online</option>
                  <option value="In-person">In-person</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Online (Scheduled)">Online (Scheduled)</option>
                </select>
              </div>
              <div className="col-span-2 pt-2 border-t border-gray-100">
                <div className="text-[11px] text-gray-600 space-y-1" data-testid="ds-bulk-auto-note">
                  <div className="font-semibold uppercase tracking-wider text-gray-500 text-[10px]">Filled per-student automatically</div>
                  <div>• <span className="font-medium">Campus</span> — from each student's selected branch</div>
                  <div>• <span className="font-medium">Quarter 1</span> = 60% of the student's Sale Price &nbsp; • <span className="font-medium">Quarter 2</span> = 40%</div>
                  <div>• <span className="font-medium">Application Fee</span> fixed at $250 &nbsp; • <span className="font-medium">Total Program Cost</span> = Sale Price + $250</div>
                  <div>• Date1 and Date2 left as "As per SABC" &nbsp; • International = "No" &nbsp; • PEN left blank</div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={() => setShowDsBulkDialog(false)} data-testid="ds-bulk-cancel">Cancel</Button>
              <Button
                type="button"
                onClick={handleDsBulkExport}
                disabled={dsBulkExporting}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="ds-bulk-download"
              >
                {dsBulkExporting
                  ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Preparing…</>
                  : <><DownloadSimple size={14} className="mr-1" /> Download .xlsx</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* ===== Bulk Stage from Excel ===== */}
      <Dialog
        open={showBulkStageExcelDialog}
        onOpenChange={(open) => {
          setShowBulkStageExcelDialog(open);
          if (!open) resetBulkStageExcel();
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="bulk-stage-excel-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
              <UploadSimple size={20} className="text-emerald-600" />
              Bulk Stage from Excel
            </DialogTitle>
            <DialogDescription>
              Upload a sheet with Email, Last Name, and First/Middle Name columns. The system will match each row against existing students and let you move all of them to a single stage in one click.
            </DialogDescription>
          </DialogHeader>

          {!bulkStageExcelPreview ? (
            <div className="space-y-4 pt-2">
              <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center bg-gray-50">
                <UploadSimple size={28} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-700 mb-3">Pick a .xlsx file (DocuSign Bulk Upload template works out of the box).</p>
                <Input
                  type="file"
                  accept=".xlsx,.xlsm"
                  onChange={e => setBulkStageExcelFile(e.target.files?.[0] || null)}
                  className="max-w-sm mx-auto"
                  data-testid="bulk-stage-excel-file-input"
                />
                {bulkStageExcelFile && (
                  <p className="text-xs text-gray-500 mt-2">Selected: {bulkStageExcelFile.name}</p>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                <Button variant="outline" onClick={() => { setShowBulkStageExcelDialog(false); resetBulkStageExcel(); }} data-testid="bulk-stage-excel-cancel">Cancel</Button>
                <Button
                  onClick={handleBulkStageExcelUpload}
                  disabled={!bulkStageExcelFile || bulkStageExcelPreviewing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="bulk-stage-excel-parse-btn"
                >
                  {bulkStageExcelPreviewing
                    ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Parsing…</>
                    : <><MagnifyingGlass size={14} className="mr-1" /> Parse & Preview</>}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              {/* Summary cards */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Total rows</p>
                  <p className="text-xl font-semibold text-gray-900">{bulkStageExcelPreview.total_rows}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700">Confident match</p>
                  <p className="text-xl font-semibold text-emerald-800">{bulkStageExcelPreview.matched.length}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-[11px] uppercase tracking-wide text-amber-700">Needs review</p>
                  <p className="text-xl font-semibold text-amber-800">{bulkStageExcelPreview.review.length}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-[11px] uppercase tracking-wide text-red-700">Not found</p>
                  <p className="text-xl font-semibold text-red-800">{bulkStageExcelPreview.not_found.length}</p>
                </div>
              </div>

              {/* Target stage picker */}
              <div className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="text-xs font-medium text-blue-900">Move all selected students to:</Label>
                <Select value={bulkStageExcelTarget} onValueChange={setBulkStageExcelTarget}>
                  <SelectTrigger className="h-9 w-[280px] text-xs bg-white" data-testid="bulk-stage-excel-target-select">
                    <SelectValue placeholder="Pick a target stage…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(bulkStageExcelPreview.stages || []).map(s => (
                      <SelectItem key={s.value} value={s.value} data-testid={`bulk-stage-excel-target-${s.value}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Confident matches */}
              {bulkStageExcelPreview.matched.length > 0 && (
                <div className="border border-emerald-200 rounded-lg overflow-hidden">
                  <div className="bg-emerald-50 px-4 py-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-emerald-900">Confident matches ({bulkStageExcelPreview.matched.length})</h4>
                    <div className="flex gap-2">
                      <Button
                        size="sm" variant="ghost" className="h-7 text-xs"
                        onClick={() => {
                          const m = {};
                          bulkStageExcelPreview.matched.forEach(r => { if (r.student_id) m[r.student_id] = true; });
                          setBulkStageExcelCheckedMatched(m);
                        }}
                        data-testid="bulk-stage-excel-matched-select-all"
                      >Select all</Button>
                      <Button
                        size="sm" variant="ghost" className="h-7 text-xs"
                        onClick={() => setBulkStageExcelCheckedMatched({})}
                        data-testid="bulk-stage-excel-matched-clear"
                      >Clear</Button>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-white sticky top-0">
                        <tr className="border-b border-gray-200 text-left text-gray-600">
                          <th className="px-3 py-2 w-8"></th>
                          <th className="px-3 py-2">Sheet email</th>
                          <th className="px-3 py-2">Matched student</th>
                          <th className="px-3 py-2">Recruiter</th>
                          <th className="px-3 py-2">Current stage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkStageExcelPreview.matched.map(r => (
                          <tr key={`m-${r.row}`} className="border-b border-gray-100">
                            <td className="px-3 py-2">
                              <Checkbox
                                checked={!!bulkStageExcelCheckedMatched[r.student_id]}
                                onCheckedChange={v => setBulkStageExcelCheckedMatched(prev => ({ ...prev, [r.student_id]: !!v }))}
                                data-testid={`bulk-stage-excel-matched-${r.student_id}`}
                              />
                            </td>
                            <td className="px-3 py-2 text-gray-700">{r.sheet_email}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900">{r.student_full_name}</div>
                              <div className="text-gray-500">{r.student_email}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-700" data-testid={`bulk-stage-excel-matched-recruiter-${r.student_id}`}>{r.recruiter_name || <span className="text-gray-400">—</span>}</td>
                            <td className="px-3 py-2 text-gray-600">{r.current_stage_label || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Review */}
              {bulkStageExcelPreview.review.length > 0 && (
                <div className="border border-amber-200 rounded-lg overflow-hidden">
                  <div className="bg-amber-50 px-4 py-2">
                    <h4 className="text-sm font-semibold text-amber-900">Needs review ({bulkStageExcelPreview.review.length})</h4>
                    <p className="text-xs text-amber-700">Email found but name doesn't match, or the sheet has duplicates. Tick each row you want to include.</p>
                  </div>
                  <div className="max-h-72 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-white sticky top-0">
                        <tr className="border-b border-gray-200 text-left text-gray-600">
                          <th className="px-3 py-2 w-8"></th>
                          <th className="px-3 py-2">Sheet row</th>
                          <th className="px-3 py-2">Matched student</th>
                          <th className="px-3 py-2">Recruiter</th>
                          <th className="px-3 py-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkStageExcelPreview.review.map(r => (
                          <tr key={`r-${r.row}`} className="border-b border-gray-100">
                            <td className="px-3 py-2">
                              <Checkbox
                                checked={!!bulkStageExcelCheckedReview[r.student_id]}
                                onCheckedChange={v => setBulkStageExcelCheckedReview(prev => ({ ...prev, [r.student_id]: !!v }))}
                                data-testid={`bulk-stage-excel-review-${r.student_id}`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-gray-700">{r.sheet_email}</div>
                              <div className="text-gray-500">{r.sheet_first_middle} {r.sheet_last_name}</div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900">{r.student_full_name}</div>
                              <div className="text-gray-500">{r.student_email}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-700" data-testid={`bulk-stage-excel-review-recruiter-${r.student_id}`}>{r.recruiter_name || <span className="text-gray-400">—</span>}</td>
                            <td className="px-3 py-2 text-amber-700">{r.match_reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Not found */}
              {bulkStageExcelPreview.not_found.length > 0 && (
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="bg-red-50 px-4 py-2">
                    <h4 className="text-sm font-semibold text-red-900">Not found ({bulkStageExcelPreview.not_found.length})</h4>
                    <p className="text-xs text-red-700">We couldn't find a student with this email. Search and pick the correct one below, or skip the row.</p>
                  </div>
                  <div className="max-h-96 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-white sticky top-0">
                        <tr className="border-b border-gray-200 text-left text-gray-600">
                          <th className="px-3 py-2">Sheet row</th>
                          <th className="px-3 py-2 w-[320px]">Pick student manually</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkStageExcelPreview.not_found.map(r => (
                          <tr key={`nf-${r.row}`} className="border-b border-gray-100 align-top">
                            <td className="px-3 py-2">
                              <div className="text-gray-700">{r.sheet_email}</div>
                              <div className="text-gray-500">{r.sheet_first_middle} {r.sheet_last_name}</div>
                            </td>
                            <td className="px-3 py-2">
                              <BulkStagePickStudent rowKey={`nf-${r.row}`} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100">
                <Button variant="ghost" onClick={() => { resetBulkStageExcel(); }} data-testid="bulk-stage-excel-reupload">
                  <ArrowCounterClockwise size={14} className="mr-1" /> Upload a different file
                </Button>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => { setShowBulkStageExcelDialog(false); resetBulkStageExcel(); }} data-testid="bulk-stage-excel-close">Cancel</Button>
                  <Button
                    onClick={handleBulkStageExcelApply}
                    disabled={!bulkStageExcelTarget || bulkStageExcelApplying}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="bulk-stage-excel-apply-btn"
                  >
                    {bulkStageExcelApplying
                      ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Applying…</>
                      : <><CheckCircle size={14} className="mr-1" /> Apply to selected</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== ID Match Report (per student) ===== */}
      <Dialog open={showIdMatchDialog} onOpenChange={setShowIdMatchDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="id-match-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-indigo-600" />
              ID Match Report — {idMatchStudent?.full_name || ''}
            </DialogTitle>
          </DialogHeader>
          {idMatchLoading && !idMatchReport ? (
            <div className="flex items-center gap-2 py-10 text-gray-500">
              <CircleNotch size={18} className="animate-spin" /> Extracting ID fields with Gemini…
            </div>
          ) : !idMatchReport ? (
            <div className="py-10 text-sm text-gray-500">No report available.</div>
          ) : (
            <div className="space-y-5">
              {/* Rolled-up field verdicts */}
              <div className="grid grid-cols-3 gap-3">
                {['name', 'dob', 'address'].map(f => {
                  const v = idMatchReport.field_verdicts?.[f] || 'missing';
                  const color = v === 'match' ? 'bg-green-50 border-green-200 text-green-700'
                               : v === 'mismatch' ? 'bg-red-50 border-red-200 text-red-700'
                               : v === 'n/a' ? 'bg-gray-50 border-gray-200 text-gray-500'
                               : 'bg-yellow-50 border-yellow-200 text-yellow-700';
                  const icon = v === 'match' ? <CheckCircle size={16} weight="fill" />
                              : v === 'mismatch' ? <XCircle size={16} weight="fill" />
                              : <WarningCircle size={16} weight="fill" />;
                  const label = f === 'dob' ? 'Date of Birth' : f.charAt(0).toUpperCase() + f.slice(1);
                  return (
                    <div key={f} className={`border rounded-lg p-3 ${color}`} data-testid={`id-match-rollup-${f}`}>
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                        {icon} {label}
                      </div>
                      <div className="text-xs mt-1 capitalize">{v}</div>
                    </div>
                  );
                })}
              </div>

              {/* Profile vs ID side-by-side per document */}
              {idMatchReport.docs_checked === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                  No ID documents found for this student. Ask the recruiter to upload at least one passport, PR card, driver's licence or provincial ID so we can verify.
                </div>
              ) : (
                <>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                    Profile vs {idMatchReport.docs_checked} ID document{idMatchReport.docs_checked === 1 ? '' : 's'}
                  </div>
                  {idMatchReport.docs?.map((d, i) => (
                    <div key={d.file_id || i} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`id-match-doc-${i}`}>
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-800 truncate">
                          {d.filename} <span className="text-gray-500 text-xs">({d.doc_type?.replace(/_/g, ' ')})</span>
                        </div>
                        {d.error && <span className="text-xs text-red-600">⚠ {d.error}</span>}
                      </div>
                      <div className="divide-y divide-gray-100">
                        {['name', 'dob', 'address'].map(f => {
                          const verdict = d.verdict?.[f] || 'missing';
                          const profile = idMatchReport.profile_snapshot?.[f] || '—';
                          const extracted = d.extracted?.[f] || '—';
                          const reason = d.reasons?.[f] || '';
                          const bg = verdict === 'match' ? 'bg-green-50'
                                    : verdict === 'mismatch' ? 'bg-red-50'
                                    : verdict === 'n/a' ? 'bg-gray-50'
                                    : 'bg-yellow-50';
                          const label = f === 'dob' ? 'Date of Birth' : f.charAt(0).toUpperCase() + f.slice(1);
                          return (
                            <div key={f} className={`grid grid-cols-12 gap-2 px-4 py-3 text-sm ${bg}`}>
                              <div className="col-span-2 text-[11px] uppercase font-semibold text-gray-500 tracking-wider self-center">
                                {label}
                              </div>
                              <div className="col-span-5">
                                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Profile (recruiter)</div>
                                <div className="text-sm text-gray-900 break-words">{profile || '—'}</div>
                              </div>
                              <div className="col-span-5">
                                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Extracted from ID</div>
                                <div className="text-sm text-gray-900 break-words">{extracted || '—'}</div>
                                {reason && (
                                  <div className={`text-[11px] mt-1 ${verdict === 'mismatch' ? 'text-red-700' : verdict === 'match' ? 'text-green-700' : 'text-gray-500'}`}>
                                    {reason}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </>
              )}

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                <div className="text-[11px] text-gray-500">
                  Last checked: {idMatchReport.checked_at ? new Date(idMatchReport.checked_at).toLocaleString() : '—'}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVerifyIdMatch(idMatchStudent)}
                    disabled={idMatchLoading}
                    data-testid="id-match-rerun-btn"
                  >
                    <ArrowsClockwise size={14} className="mr-1" /> Re-run
                  </Button>
                  <Button size="sm" onClick={() => setShowIdMatchDialog(false)} data-testid="id-match-close-btn">Close</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Bulk Scan Results ===== */}
      <Dialog
        open={showBulkScanDialog}
        onOpenChange={(v) => {
          setShowBulkScanDialog(v);
          // When opening, auto-refresh the flagged list so any students the
          // admin fixed elsewhere (Full view / direct edit) are dropped.
          if (v && bulkScanJob?.id && bulkScanJob?.flagged_students?.length) {
            axios.post(`${API}/admin/id-match-jobs/${bulkScanJob.id}/refresh`)
              .then(({ data }) => setBulkScanJob(data))
              .catch(() => { /* best-effort */ });
          }
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="bulk-id-scan-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
              <IdentificationCard size={20} className="text-indigo-600" />
              ID Match — Pipeline Scan
            </DialogTitle>
            {bulkScanJob?.scope_label && (
              <div className="text-[11px] text-gray-500 mt-1" data-testid="bulk-scan-scope-label">
                Scope: <span className="font-medium text-gray-700">{bulkScanJob.scope_label}</span>
              </div>
            )}
          </DialogHeader>          {!bulkScanJob ? (
            <div className="py-10 text-sm text-gray-500">Starting…</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">Status</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1 capitalize">{bulkScanJob.status}</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">Progress</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {bulkScanJob.processed || 0} / {bulkScanJob.total || 0}
                  </div>
                </div>
                <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-red-600">Flagged</div>
                  <div className="text-sm font-semibold text-red-700 mt-1">{bulkScanJob.flagged || 0}</div>
                </div>
                <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-green-700">Clean</div>
                  <div className="text-sm font-semibold text-green-700 mt-1">
                    {Math.max(0, (bulkScanJob.processed || 0) - (bulkScanJob.flagged || 0))}
                  </div>
                </div>
              </div>

              {(bulkScanJob.status === 'running' || bulkScanJob.status === 'queued') && (
                <div className="space-y-2">
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-indigo-600 h-2 transition-all"
                      style={{ width: `${bulkScanJob.total ? Math.round((bulkScanJob.processed / bulkScanJob.total) * 100) : 0}%` }}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        if (!bulkScanJob?.id) return;
                        if (!window.confirm('Stop the scan? Progress so far is kept — you can start a new scan anytime.')) return;
                        try {
                          await axios.post(`${API}/admin/id-match-jobs/${bulkScanJob.id}/cancel`);
                          setBulkScanJob(prev => prev ? { ...prev, status: 'cancelled' } : prev);
                          toast.success('Scan stopped');
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Could not stop the scan');
                        }
                      }}
                      className="h-7 px-3 text-xs border-red-200 text-red-700 hover:bg-red-50"
                      data-testid="scan-stop-btn"
                    >
                      <XCircle size={12} className="mr-1" /> Stop scan
                    </Button>
                  </div>
                </div>
              )}
              {bulkScanJob.status === 'cancelled' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                  Scan was stopped by {bulkScanJob.cancelled_by || 'admin'}. {bulkScanJob.processed || 0} student(s) were already checked — results below are complete for those.
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 text-[11px] uppercase font-semibold text-gray-500 tracking-wider flex items-center justify-between">
                  <span>Flagged Students ({bulkScanJob.flagged_students?.length || 0})</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      if (!bulkScanJob?.id) return;
                      try {
                        const { data } = await axios.post(`${API}/admin/id-match-jobs/${bulkScanJob.id}/refresh`);
                        setBulkScanJob(data);
                        toast.success(`Refreshed — ${data.flagged_students?.length || 0} flagged after reconciliation`);
                      } catch {
                        toast.error('Refresh failed');
                      }
                    }}
                    className="h-6 px-2 text-[10px] text-gray-600 hover:text-gray-900"
                    data-testid="scan-refresh-btn"
                  >
                    <ArrowsClockwise size={11} className="mr-1" /> Refresh
                  </Button>
                </div>
                {!bulkScanJob.flagged_students?.length ? (
                  <div className="p-6 text-center text-sm text-gray-500">
                    {bulkScanJob.status === 'done' ? '✅ No mismatches found.' : 'No flagged students yet…'}
                  </div>
                ) : (
                  <table className="min-w-full">
                    <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                      <tr>
                        <th className="text-left px-4 py-2 w-6"></th>
                        <th className="text-left px-4 py-2">Student</th>
                        <th className="text-left px-4 py-2">Email</th>
                        <th className="text-left px-4 py-2">Flagged Fields</th>
                        <th className="text-right px-4 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bulkScanJob.flagged_students.map(fs => (
                        <FlaggedStudentRow
                          key={fs.student_id}
                          fs={fs}
                          onOpen={() => openStudentFromScan(fs.student_id)}
                          onResolved={(result) => {
                            // Keep the row visible so admin sees the "Resolved" green state.
                            // Only update chip-level flags + the top counters.
                            setBulkScanJob(prev => {
                              if (!prev) return prev;
                              const next = { ...prev };
                              const stillFlagged = (result.flagged_fields || []).length > 0;
                              next.flagged_students = (prev.flagged_students || []).map(x =>
                                x.student_id === result.student_id ? { ...x, flagged_fields: result.flagged_fields || [], _resolved: !stillFlagged } : x
                              );
                              // Decrement the top "Flagged" counter when a student transitions clean.
                              const wasResolvedBefore = (prev.flagged_students || []).find(x => x.student_id === result.student_id)?._resolved;
                              if (!stillFlagged && !wasResolvedBefore) {
                                next.flagged = Math.max(0, (prev.flagged || 1) - 1);
                              } else if (stillFlagged && wasResolvedBefore) {
                                next.flagged = (prev.flagged || 0) + 1;
                              }
                              return next;
                            });
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="flex justify-end">
                <Button size="sm" onClick={() => setShowBulkScanDialog(false)} data-testid="bulk-scan-close-btn">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Commission Dialog */}
      <Dialog open={showCommForm} onOpenChange={setShowCommForm}>
        <DialogContent className="max-w-md" data-testid="create-commission-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Add Commission</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Recruiter *</Label><Select value={commForm.recruiter_id} onValueChange={v => setCommForm(p => ({ ...p, recruiter_id: v }))}><SelectTrigger data-testid="comm-recruiter-select"><SelectValue placeholder="Select recruiter" /></SelectTrigger><SelectContent>{recruiters.filter(r => r.status === 'active').map(r => (<SelectItem key={r.id} value={r.id}>{r.full_name} ({r.company_name})</SelectItem>))}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Student Name</Label><Input value={commForm.student_name} onChange={e => setCommForm(p => ({ ...p, student_name: e.target.value }))} placeholder="Optional" data-testid="comm-student-name" /></div>
            <div className="space-y-2"><Label>Amount (CAD) *</Label><Input type="number" value={commForm.amount} onChange={e => setCommForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" data-testid="comm-amount-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={commForm.description} onChange={e => setCommForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g., Fall 2026" data-testid="comm-description-input" /></div>
            <Button onClick={handleCreateCommission} className="w-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="comm-submit-btn"><Plus size={16} className="mr-2" /> Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Set Pricing Dialog */}
      <Dialog open={bulkPricingOpen} onOpenChange={setBulkPricingOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-pricing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              <CurrencyDollar size={18} className="text-emerald-600" /> Set Pricing for {selectedStudentIds.length} students
            </DialogTitle>
            <DialogDescription>
              Applies to every selected student of <strong>{selectedStudents[0]?.recruiter_name || 'this recruiter'}</strong>.
              Leave a field blank to keep each student&rsquo;s existing value untouched. Study Alliance Earning is recomputed automatically per student (Sale Price − College Fee − Recruiter Commission).
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2" data-testid="bulk-pricing-fields">
            {[
              { key: 'sale_price', label: 'Sale Price (CAD)', placeholder: 'e.g. 15000' },
              { key: 'college_fee_snapshot', label: 'College Fee (CAD)', placeholder: 'e.g. 8500' },
              { key: 'recruiter_commission_snapshot', label: 'Recruiter Comm. (CAD)', placeholder: 'e.g. 2000' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">$</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    className="pl-6"
                    placeholder={f.placeholder}
                    value={bulkPricingForm[f.key]}
                    onChange={e => setBulkPricingForm(v => ({ ...v, [f.key]: e.target.value }))}
                    data-testid={`bulk-pricing-${f.key}`}
                  />
                </div>
              </div>
            ))}
          </div>
          {(() => {
            const sp = parseFloat(bulkPricingForm.sale_price);
            const cf = parseFloat(bulkPricingForm.college_fee_snapshot);
            const rc = parseFloat(bulkPricingForm.recruiter_commission_snapshot);
            if ([sp, cf, rc].every(n => !Number.isNaN(n))) {
              const earn = sp - cf - rc;
              return (
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-900 flex justify-between" data-testid="bulk-pricing-earning-preview">
                  <span>Study Alliance Earning (per student)</span>
                  <strong className="tabular-nums">${earn.toLocaleString('en-CA', { maximumFractionDigits: 2 })}</strong>
                </div>
              );
            }
            return null;
          })()}
          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
            <Button variant="outline" onClick={() => setBulkPricingOpen(false)} disabled={bulkPricingSaving}>Cancel</Button>
            <Button onClick={submitBulkPricing} disabled={bulkPricingSaving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="bulk-pricing-save-btn">
              {bulkPricingSaving ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Saving…</> : 'Apply to Selected'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Financials modal — admin edit for institution-entered fields */}
      {selectedStudent && (
        <FinancialsModal
          open={adminFinancialsOpen}
          onOpenChange={setAdminFinancialsOpen}
          student={selectedStudent}
          mode="edit"
          onSaved={({ financials, financials_updated_at }) => {
            setSelectedStudent(s => s ? { ...s, financials, financials_updated_at } : s);
          }}
        />
      )}

      {/* ============================================================
          Commission-Invoice bulk dialogs: Chooser → Remind / Send
          ============================================================ */}
      <Dialog open={invoiceChooserOpen} onOpenChange={setInvoiceChooserOpen}>
        <DialogContent className="max-w-md" data-testid="invoice-chooser-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Invoice Action — {invoicePreview?.tranche === 2 ? '2/2 (Final)' : '1/2 (First Tranche)'}</DialogTitle>
            <DialogDescription>
              {selectedStudentIds.length} student{selectedStudentIds.length === 1 ? '' : 's'} selected. Pick an action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button
              type="button"
              onClick={openReminderPopup}
              className="w-full text-left border border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg p-4 transition-colors"
              data-testid="invoice-choose-remind-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <EnvelopeSimple size={20} className="text-blue-700" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Remind Recruiter</div>
                  <div className="text-xs text-gray-500">Email each recruiter their {Math.round(invoicePreview?.tranche_pct ?? invoicePreview?.commission_first_pct ?? 60)}% {invoicePreview?.tranche === 2 ? 'final-tranche' : 'first-tranche'} commission owed.</div>
                </div>
              </div>
            </button>
            {(() => {
              // "Send to College" requires all selected students to share a
              // single college. Disable + explain when they don't.
              const colleges = new Set((selectedStudents || []).map(s => s.assigned_college || ''));
              const sameCollege = colleges.size === 1 && !colleges.has('');
              return (
                <button
                  type="button"
                  onClick={openInvoicePopup}
                  disabled={!sameCollege || !invoicePreview}
                  className="w-full text-left border border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 rounded-lg p-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="invoice-choose-send-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <FilePdf size={20} className="text-emerald-700" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">Send Invoice to College</div>
                      <div className="text-xs text-gray-500">
                        {sameCollege ? `Generate Invoice #${invoicePreview?.invoice_number || ''} and email PDF to ${invoicePreview?.college_name || 'college'}.` : 'All selected students must belong to the same college.'}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Popup A — Recruiter Reminder editor */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Remind Recruiter — Commission Invoice</DialogTitle>
            <DialogDescription>
              One email will be sent to each recruiter listing their students and the {Math.round(reminderPreview?.commission_first_pct ?? 60)}% {reminderPreview?.tranche === 2 ? 'final-tranche' : 'first-tranche'} amount owed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 space-y-2" data-testid="reminder-preview-groups">
              <div className="font-semibold">Recipients ({(reminderPreview?.recruiter_groups || []).length}):</div>
              {(reminderPreview?.recruiter_groups || []).map((g, i) => (
                <div key={i} className="pl-2 border-l-2 border-blue-300">
                  <div className="font-semibold">{g.recruiter_name || '(unnamed)'} — <span className="font-mono text-blue-800">{g.recruiter_email || 'no email'}</span></div>
                  <div className="text-blue-800">
                    {(g.students || []).length} student{(g.students || []).length === 1 ? '' : 's'} — Total 1st tranche: <strong>C${(g.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </div>
                </div>
              ))}
              {!(reminderPreview?.recruiter_groups || []).length && <div className="italic text-blue-800">No recruiters resolved from selection.</div>}
            </div>
            <div className="space-y-2">
              <Label>Email Subject</Label>
              <Input value={reminderSubject} onChange={e => setReminderSubject(e.target.value)} data-testid="reminder-subject-input" />
            </div>
            <div className="space-y-2">
              <Label>Email Body</Label>
              <Textarea rows={12} value={reminderBody} onChange={e => setReminderBody(e.target.value)} className="font-mono text-xs" data-testid="reminder-body-input" />
              <p className="text-xs text-gray-500">This text is sent verbatim to <strong>every</strong> recruiter above. If you want per-recruiter dynamic amounts, leave the body blank and the backend will auto-compose one per recruiter.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setReminderOpen(false)} disabled={reminderSending} data-testid="reminder-cancel-btn">Cancel</Button>
              <Button onClick={sendReminderEmails} disabled={reminderSending || !(reminderPreview?.recruiter_groups || []).length} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="reminder-send-btn">
                {reminderSending ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Sending…</> : <><PaperPlaneTilt size={14} className="mr-1" /> Send Reminder{(reminderPreview?.recruiter_groups || []).length > 1 ? 's' : ''}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Popup B — Invoice Preview editor */}
      <Dialog open={invoicePopupOpen} onOpenChange={setInvoicePopupOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="invoice-preview-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Invoice #{invoicePreview?.invoice_number || '—'} · {invoicePreview?.college_name || 'College'} <span className="ml-2 text-xs font-normal text-gray-500">({invoicePreview?.tranche === 2 ? '2/2 Final Tranche' : '1/2 First Tranche'})</span>
            </DialogTitle>
            <DialogDescription>
              Review + edit the invoice before sending. PDF is generated on send and attached to the email.
            </DialogDescription>
          </DialogHeader>
          {invoicePreview && (
            <div className="space-y-5">
              {/* Header meta */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Invoice Number</Label>
                  <Input value={invoicePreview.invoice_number || ''} onChange={e => updateInvoiceField('invoice_number', e.target.value)} data-testid="invoice-number-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Invoice Date</Label>
                  <Input type="date" value={invoicePreview.invoice_date || ''} onChange={e => updateInvoiceField('invoice_date', e.target.value)} data-testid="invoice-date-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Recipient Email</Label>
                  <Input type="email" value={invoicePreview.recipient_email || ''} onChange={e => updateInvoiceField('recipient_email', e.target.value)} data-testid="invoice-recipient-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email Subject</Label>
                  <Input value={invoicePreview.email_subject || ''} onChange={e => updateInvoiceField('email_subject', e.target.value)} data-testid="invoice-subject-input" />
                </div>
              </div>

              {/* Billed To */}
              <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Billed To</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Company Name</Label>
                    <Input value={invoicePreview.billed_to?.company_name || ''} onChange={e => updateInvoiceField('billed_to.company_name', e.target.value)} data-testid="invoice-billedto-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Address Line 1</Label>
                    <Input value={invoicePreview.billed_to?.address_line1 || ''} onChange={e => updateInvoiceField('billed_to.address_line1', e.target.value)} data-testid="invoice-billedto-addr1" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Address Line 2</Label>
                    <Input value={invoicePreview.billed_to?.address_line2 || ''} onChange={e => updateInvoiceField('billed_to.address_line2', e.target.value)} data-testid="invoice-billedto-addr2" />
                  </div>
                </div>
              </div>

              {/* Line items */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Line Items ({(invoicePreview.line_items || []).length})</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50/50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-2 py-2 font-semibold text-gray-600">Student</th>
                        <th className="text-left px-2 py-2 font-semibold text-gray-600">DOB</th>
                        <th className="text-right px-2 py-2 font-semibold text-gray-600">Tuition</th>
                        <th className="text-right px-2 py-2 font-semibold text-gray-600">Application Fee</th>
                        <th className="text-right px-2 py-2 font-semibold text-gray-600">{invoicePreview?.tranche === 2 ? '2nd Payment' : '1st Payment'}</th>
                        <th className="text-right px-2 py-2 font-semibold text-gray-600">{invoicePreview?.tranche === 2 ? '2/2 Commission' : 'Commission'}</th>
                        <th className="w-9"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoicePreview.line_items || []).map((li, idx) => (
                        <tr key={idx} className="border-b border-gray-100" data-testid={`invoice-line-${idx}`}>
                          <td className="px-2 py-2">
                            <Input value={li.student_name || ''} onChange={e => updateLineItem(idx, 'student_name', e.target.value)} className="h-8 text-xs" data-testid={`invoice-line-name-${idx}`} />
                          </td>
                          <td className="px-2 py-2">
                            <Input value={li.dob || ''} onChange={e => updateLineItem(idx, 'dob', e.target.value)} className="h-8 text-xs w-28" placeholder="YYYY-MM-DD" data-testid={`invoice-line-dob-${idx}`} />
                          </td>
                          <td className="px-2 py-2">
                            <Input type="number" min="0" step="0.01" value={li.tuition_fee ?? 0} onChange={e => updateLineItem(idx, 'tuition_fee', e.target.value)} className="h-8 text-xs text-right w-28" data-testid={`invoice-line-tuition-${idx}`} />
                          </td>
                          <td className="px-2 py-2">
                            <Input type="number" min="0" step="0.01" value={li.application_fee ?? 0} onChange={e => updateLineItem(idx, 'application_fee', e.target.value)} className="h-8 text-xs text-right w-24" data-testid={`invoice-line-appfee-${idx}`} />
                          </td>
                          <td className="px-2 py-2">
                            <Input type="number" min="0" step="0.01" value={li.first_payment_claimed ?? 0} onChange={e => updateLineItem(idx, 'first_payment_claimed', e.target.value)} className="h-8 text-xs text-right w-28" data-testid={`invoice-line-firstpay-${idx}`} />
                          </td>
                          <td className="px-2 py-2">
                            <Input type="number" min="0" step="0.01" value={li.commission_claimed ?? 0} onChange={e => updateLineItem(idx, 'commission_claimed', e.target.value)} className="h-8 text-xs text-right w-28 font-semibold" data-testid={`invoice-line-commission-${idx}`} />
                          </td>
                          <td className="px-2 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeLineItem(idx)}
                              className="text-red-500 hover:text-red-700"
                              title="Remove this line"
                              data-testid={`invoice-line-remove-${idx}`}
                            >
                              <XCircle size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!(invoicePreview.line_items || []).length && (
                        <tr><td colSpan={7} className="px-2 py-6 text-center text-gray-400 italic">No line items.</td></tr>
                      )}
                    </tbody>
                    <tfoot className="bg-emerald-50 border-t-2 border-emerald-200">
                      <tr>
                        <td colSpan={5} className="px-2 py-3 text-right font-bold text-gray-800 uppercase tracking-wide text-xs">Grand Total Commission</td>
                        <td className="px-2 py-3 text-right font-bold text-emerald-800 text-sm" data-testid="invoice-grand-total">
                          C${invoiceGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Email body */}
              <div className="space-y-2">
                <Label className="text-xs">Email Body</Label>
                <Textarea rows={6} value={invoicePreview.email_body || ''} onChange={e => updateInvoiceField('email_body', e.target.value)} className="font-mono text-xs" data-testid="invoice-body-input" />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <Button variant="outline" onClick={() => setInvoicePopupOpen(false)} disabled={invoiceSending} data-testid="invoice-cancel-btn">Cancel</Button>
                <Button onClick={sendInvoiceToCollege} disabled={invoiceSending || !(invoicePreview.line_items || []).length} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="invoice-send-btn">
                  {invoiceSending ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Sending…</> : <><PaperPlaneTilt size={14} className="mr-1" /> Send Invoice PDF</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Intake Dialog */}
      <Dialog open={showBulkIntakeDialog} onOpenChange={(o) => { setShowBulkIntakeDialog(o); if (!o) setIntakeDialogSingleId(''); }}>
        <DialogContent className="max-w-md" data-testid="bulk-intake-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>{intakeDialogSingleId ? 'Change Intake' : 'Change Intake'}</DialogTitle>
            <DialogDescription>{intakeDialogSingleId
              ? 'Pick any intake window — including back-dated months (Jul 2026, Apr 2026, or earlier). The student\'s intake label will update everywhere immediately.'
              : `Change program start & end dates for ${selectedStudentIds.length} selected student${selectedStudentIds.length === 1 ? '' : 's'}. Back-dating (e.g. Oct → Jul 2026) is allowed.`}</DialogDescription>
          </DialogHeader>
          {(() => {
            // Show a quick "currently on X" hint when the operator is
            // correcting a mistake in bulk. Only render when every selected
            // student is on the same intake.
            if (intakeDialogSingleId || !selectedStudents.length) return null;
            const labels = new Set(selectedStudents.map(s => s.intake_label || '—'));
            if (labels.size !== 1) return null;
            const current = [...labels][0];
            if (current === '—') return null;
            return (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900" data-testid="current-intake-hint">
                All {selectedStudentIds.length} selected are currently on <strong>{current}</strong>. Pick new dates below to move them.
              </div>
            );
          })()}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Pick From College Intakes (optional)</Label>
              <Select value={bulkIntakeCollegeId} onValueChange={setBulkIntakeCollegeId}>
                <SelectTrigger data-testid="bulk-intake-college-select"><SelectValue placeholder="Choose a college to see its saved intakes…" /></SelectTrigger>
                <SelectContent>
                  {colleges.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{(c.intakes || []).length ? ` · ${(c.intakes || []).length} saved` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const college = colleges.find(c => c.id === bulkIntakeCollegeId);
                const intakes = (college && college.intakes) || [];
                if (!bulkIntakeCollegeId) return null;
                if (!intakes.length) return <p className="text-xs text-gray-500 italic">No intakes saved for this college yet. Add them from Colleges → Edit College.</p>;
                return (
                  <div className="grid grid-cols-1 gap-1.5 max-h-40 overflow-y-auto pt-1">
                    {intakes.map(i => (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => { setBulkIntakeStart(i.start_date); setBulkIntakeEnd(i.end_date); }}
                        className={`flex items-center justify-between p-2 rounded-md border text-xs transition-colors ${bulkIntakeStart === i.start_date && bulkIntakeEnd === i.end_date ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                        data-testid={`bulk-intake-preset-${i.id}`}
                      >
                        <span className="font-semibold text-gray-800">{i.start_date}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-semibold text-gray-800">{i.end_date}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="border-t border-gray-200 pt-3 grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Start Date *</Label>
                <Input type="date" value={bulkIntakeStart} onChange={e => setBulkIntakeStart(e.target.value)} max={bulkIntakeEnd || undefined} data-testid="bulk-intake-start" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">End Date *</Label>
                <Input type="date" value={bulkIntakeEnd} onChange={e => setBulkIntakeEnd(e.target.value)} min={bulkIntakeStart || undefined} data-testid="bulk-intake-end" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowBulkIntakeDialog(false); setIntakeDialogSingleId(''); }} disabled={bulkIntakeSaving}>Cancel</Button>
              <Button onClick={handleBulkAssignIntake} className="bg-blue-700 hover:bg-blue-800 text-white" disabled={bulkIntakeSaving} data-testid="bulk-intake-save">
                {bulkIntakeSaving ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Saving…</> : (intakeDialogSingleId ? 'Save Intake' : 'Assign to Selected')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* College Form Dialog */}
      <Dialog open={showCollegeForm} onOpenChange={(o) => { setShowCollegeForm(o); if (!o) setEditingCollege(null); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="college-form-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>{editingCollege ? 'Edit College' : 'Add Partner College'}</DialogTitle><DialogDescription>Manage a partner institution used for student assignment.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name *</Label><Input value={collegeForm.name} onChange={e => setCollegeForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Centennial College" data-testid="college-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 sm:col-span-2">
                <Label>Locations <span className="text-xs text-gray-400 font-normal">(add one per branch)</span></Label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(collegeForm.locations || []).map((loc, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-2.5 py-0.5 text-xs font-medium" data-testid={`college-location-chip-${i}`}>
                      {loc}
                      <button
                        type="button"
                        onClick={() => setCollegeForm(p => ({ ...p, locations: p.locations.filter((_, idx) => idx !== i) }))}
                        className="ml-1 text-blue-500 hover:text-blue-800"
                        title="Remove"
                        aria-label={`Remove ${loc}`}
                      >×</button>
                    </span>
                  ))}
                  {(!collegeForm.locations || collegeForm.locations.length === 0) && (
                    <span className="text-xs text-gray-400 italic">No locations yet — add at least one below</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g. Brampton, Mississauga, North Vancouver…"
                    value={collegeForm.location || ''}
                    onChange={e => setCollegeForm(p => ({ ...p, location: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = (collegeForm.location || '').trim();
                        if (v && !(collegeForm.locations || []).includes(v)) {
                          setCollegeForm(p => ({ ...p, locations: [...(p.locations || []), v], location: '' }));
                        }
                      }
                    }}
                    data-testid="college-location-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const v = (collegeForm.location || '').trim();
                      if (!v) return;
                      if ((collegeForm.locations || []).includes(v)) { toast.info('Already added'); return; }
                      setCollegeForm(p => ({ ...p, locations: [...(p.locations || []), v], location: '' }));
                    }}
                    data-testid="college-add-location-btn"
                  >Add</Button>
                </div>
              </div>
              <div className="space-y-2"><Label>Country</Label><Input value={collegeForm.country} onChange={e => setCollegeForm(p => ({ ...p, country: e.target.value }))} placeholder="Canada" data-testid="college-country-input" /></div>
            </div>
            <div className="space-y-2"><Label>Website</Label><Input value={collegeForm.website} onChange={e => setCollegeForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." data-testid="college-website-input" /></div>
            <div className="space-y-2">
              <Label>College Representative Email</Label>
              <Input type="email" value={collegeForm.representative_email} onChange={e => setCollegeForm(p => ({ ...p, representative_email: e.target.value }))} placeholder="admissions@college.edu" data-testid="college-rep-email-input" />
              <p className="text-xs text-gray-500">Student applications will be auto-forwarded to this email when you click "Forward to College" on a student profile.</p>
            </div>
            <div className="space-y-2">
              <Label>College Fee (CAD per application)</Label>
              <Input type="number" min="0" step="100" value={collegeForm.college_fee} onChange={e => setCollegeForm(p => ({ ...p, college_fee: e.target.value }))} placeholder="e.g., 8500" data-testid="college-fee-input" />
              <p className="text-xs text-gray-500">Fixed amount Study Alliance pays this college per application. Used in net revenue calculations.</p>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea rows={2} value={collegeForm.notes} onChange={e => setCollegeForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes about this partnership" data-testid="college-notes-input" /></div>

            {/* Declaration form requirement */}
            <div className="space-y-2 border-t border-gray-100 pt-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={!!collegeForm.requires_declaration_form}
                  onCheckedChange={(v) => setCollegeForm(p => ({ ...p, requires_declaration_form: !!v }))}
                  data-testid="college-requires-declaration"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Requires Student Declaration Form</p>
                  <p className="text-xs text-gray-500">Tick if students applying here must submit a college-specific declaration. Recruiters will see a mandatory "Student Declaration Form" upload slot with a download/fill-online option.</p>
                </div>
              </label>
              {collegeForm.requires_declaration_form && editingCollege && (
                <div className="ml-6 mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md space-y-2">
                  <Label className="text-xs">Blank declaration template (PDF) — recruiters download this</Label>
                  {collegeForm.declaration_template_filename ? (
                    <p className="text-xs text-emerald-700"><FileText size={12} className="inline mr-1" />Current: {collegeForm.declaration_template_filename}</p>
                  ) : (
                    <p className="text-xs text-amber-700">No template uploaded yet.</p>
                  )}
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        const res = await axios.post(`${API}/admin/colleges/${editingCollege.id}/declaration-template`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        toast.success(res.data.message);
                        setCollegeForm(p => ({ ...p, declaration_template_filename: res.data.filename, requires_declaration_form: true }));
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Upload failed');
                      }
                    }}
                    className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    data-testid="college-declaration-template-upload"
                  />
                </div>
              )}
              {collegeForm.requires_declaration_form && !editingCollege && (
                <p className="ml-6 text-xs text-gray-500 italic">Save the college first, then re-open it to upload the blank template PDF.</p>
              )}
            </div>

            {/* EA Autofill — DocuSign template + PTIB notes */}
            <div className="space-y-3 border-t border-gray-100 pt-4" data-testid="college-ea-autofill-section">
              <div>
                <Label className="text-sm font-semibold">EA Autofill · DocuSign Template ID</Label>
                <p className="text-xs text-gray-500 mb-1">
                  Paste the DocuSign template UUID for this college&apos;s Enrollment Agreement.
                  Staff clicking &quot;Prepare EA&quot; will send envelopes using this template.
                </p>
                <Input
                  className="h-9 text-sm font-mono"
                  value={collegeForm.docusign_ea_template_id || ''}
                  onChange={(e) => setCollegeForm(p => ({ ...p, docusign_ea_template_id: e.target.value }))}
                  placeholder="e.g. be6e49a7-4847-473f-9eb5-897d0ae3cbdb"
                  data-testid="college-ea-template-id"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-semibold">PTIB Admission Rules · Extra Notes</Label>
                  <button
                    type="button"
                    onClick={() => setCollegeForm(p => ({ ...p, _ptib_help_open: !p._ptib_help_open }))}
                    className="text-[11px] text-blue-600 hover:text-blue-800 underline decoration-dotted"
                    data-testid="college-ptib-help-toggle"
                  >
                    {collegeForm._ptib_help_open ? 'Hide' : 'Show'} baseline PTIB rules
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-1">
                  Optional extras appended to the AI classifier prompt. Use this to add
                  program-specific IELTS minimums, additional acceptable proofs, or
                  campus-specific quirks. Baseline PTIB rules always apply.
                </p>
                {collegeForm._ptib_help_open && (
                  <div className="mb-2 rounded-md bg-gray-50 border border-gray-200 p-3 text-[11px] text-gray-700 leading-relaxed" data-testid="college-ptib-help-content">
                    <p className="font-bold uppercase text-[10px] tracking-wider text-gray-500 mb-1">Baseline (always applied)</p>
                    <p>· <strong>English Proof</strong>: IELTS, TOEFL, Accuplacer, CELPIP, Duolingo, or 2-3 years secondary education in English.</p>
                    <p>· <strong>Acceptable ID</strong>: Canadian/Foreign Passport, PR Card, Provincial Driver&apos;s Licence, BC Services Card, Provincial ID, Canadian Birth Certificate, Citizenship Card.</p>
                    <p>· <strong>Age</strong>: 19+ OR mature-student with 2-year Canadian diploma.</p>
                    <p className="mt-1 italic text-gray-500">Anything you paste below is appended to these rules verbatim.</p>
                  </div>
                )}
                <Textarea
                  rows={8}
                  className="text-sm font-mono"
                  value={collegeForm.ptib_admission_rules_notes || ''}
                  onChange={(e) => setCollegeForm(p => ({ ...p, ptib_admission_rules_notes: e.target.value }))}
                  placeholder={"e.g.\n- Minimum IELTS 6.5 overall required for Health Care Assistant program.\n- Duolingo score 105+ accepted for Business Admin.\n- Vernon campus does not accept Accuplacer for HCA."}
                  data-testid="college-ptib-notes"
                />
                <p className="mt-1 text-[10px] text-gray-400">Saved changes apply immediately — no redeploy needed.</p>
              </div>
            </div>

            {/* Programs */}
            <div className="space-y-2 border-t border-gray-100 pt-4" data-testid="college-programs-section">
              <Label className="text-sm font-semibold">Programs Offered</Label>
              <p className="text-xs text-gray-500">List the programs Study Alliance sells under this college. Default sale price is optional; admin can override per student.</p>
              {collegeForm.programs.length > 0 && (
                <div className="space-y-2">
                  {collegeForm.programs.map(p => (
                    <div key={p.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md border border-gray-100" data-testid={`program-row-${p.id}`}>
                      <Input className="h-8 text-sm flex-1" value={p.name} onChange={e => handleEditProgramInForm(p.id, 'name', e.target.value)} placeholder="Program name" data-testid={`program-name-${p.id}`} />
                      <Input className="h-8 text-sm w-32" type="number" min="0" step="100" value={p.default_sale_price} onChange={e => handleEditProgramInForm(p.id, 'default_sale_price', e.target.value)} placeholder="Default $" data-testid={`program-price-${p.id}`} />
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveProgramFromForm(p.id)} data-testid={`program-remove-${p.id}`}><Trash size={14} /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2 pt-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Program Name</Label>
                  <Input className="h-8 text-sm" value={newProgramForm.name} onChange={e => setNewProgramForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Diploma in Business Admin" data-testid="new-program-name" />
                </div>
                <div className="w-32 space-y-1">
                  <Label className="text-xs">Default Price</Label>
                  <Input className="h-8 text-sm" type="number" min="0" step="100" value={newProgramForm.default_sale_price} onChange={e => setNewProgramForm(p => ({ ...p, default_sale_price: e.target.value }))} placeholder="$" data-testid="new-program-price" />
                </div>
                <Button size="sm" type="button" onClick={handleAddProgramToForm} className="h-8 bg-blue-700 hover:bg-blue-800 text-white" data-testid="add-program-btn"><Plus size={14} className="mr-1" /> Add</Button>
              </div>
            </div>

            {/* Program Intakes — only appears once the college has been created, since
                intakes are persisted via a dedicated endpoint (they aren't part of the
                CollegeUpdate payload). Admin can bulk-assign these to students later. */}
            {editingCollege && (
              <div className="space-y-2 border-t border-gray-200 pt-4">
                <Label className="text-sm font-semibold" style={{ fontFamily: "'Libre Baskerville', serif" }}>Program Intakes</Label>
                <p className="text-xs text-gray-500">Set start/end date pairs for this college so admins can bulk-assign them to students without retyping dates.</p>
                {(editingCollege.intakes || []).length > 0 && (
                  <div className="space-y-1.5">
                    {(editingCollege.intakes || []).map(intake => (
                      <div key={intake.id} className="flex items-center gap-2 p-2 bg-blue-50 rounded-md border border-blue-100" data-testid={`intake-row-${intake.id}`}>
                        <span className="text-xs font-semibold text-blue-900">{intake.start_date}</span>
                        <span className="text-xs text-blue-600">→</span>
                        <span className="text-xs font-semibold text-blue-900 flex-1">{intake.end_date}</span>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={async () => {
                          if (!window.confirm('Remove this intake?')) return;
                          try {
                            await axios.delete(`${API}/admin/colleges/${editingCollege.id}/intakes/${intake.id}`);
                            setEditingCollege(prev => ({ ...prev, intakes: (prev.intakes || []).filter(i => i.id !== intake.id) }));
                            toast.success('Intake removed');
                            fetchData();
                          } catch { toast.error('Failed to remove intake'); }
                        }} data-testid={`intake-remove-${intake.id}`}><Trash size={13} /></Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2 pt-1">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Start Date</Label>
                    <Input type="date" className="h-8 text-sm" value={newIntakeForm.start_date} onChange={e => setNewIntakeForm(p => ({ ...p, start_date: e.target.value }))} max={newIntakeForm.end_date || undefined} data-testid="new-intake-start" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">End Date</Label>
                    <Input type="date" className="h-8 text-sm" value={newIntakeForm.end_date} onChange={e => setNewIntakeForm(p => ({ ...p, end_date: e.target.value }))} min={newIntakeForm.start_date || undefined} data-testid="new-intake-end" />
                  </div>
                  <Button size="sm" type="button" className="h-8 bg-blue-700 hover:bg-blue-800 text-white" data-testid="add-intake-btn" onClick={async () => {
                    if (!newIntakeForm.start_date || !newIntakeForm.end_date) { toast.error('Enter both dates'); return; }
                    if (newIntakeForm.end_date < newIntakeForm.start_date) { toast.error('End must be on or after start'); return; }
                    try {
                      const res = await axios.post(`${API}/admin/colleges/${editingCollege.id}/intakes`, { start_date: newIntakeForm.start_date, end_date: newIntakeForm.end_date });
                      setEditingCollege(prev => ({ ...prev, intakes: [...(prev.intakes || []), res.data.intake] }));
                      setNewIntakeForm({ start_date: '', end_date: '' });
                      toast.success('Intake added');
                      fetchData();
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to add intake'); }
                  }}><Plus size={14} className="mr-1" /> Add</Button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowCollegeForm(false); setEditingCollege(null); }}>Cancel</Button>
              <Button onClick={handleSaveCollege} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="college-save-btn">{editingCollege ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* COE Sheet Sync Dialog */}
      <Dialog open={showCoeSheetDialog} onOpenChange={setShowCoeSheetDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="coe-sheet-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-600" /> Sync COE-Confirmed Sheet
            </DialogTitle>
            <DialogDescription>
              Upload the sheet where you track confirmed COEs. Every matching student
              (by <strong>email</strong> or <strong>full name</strong>) will be moved to
              &ldquo;COE Sent / Money Received&rdquo;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-md text-[11px] text-blue-900 leading-relaxed">
              <strong>How to prep the sheet</strong>
              <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                <li>Open your Google Sheet (org-restricted is fine).</li>
                <li>Click <em>File → Download → Microsoft Excel (.xlsx)</em>.</li>
                <li>Drop that file below. The sheet should have either an
                  <em> Email</em>, <em>Full Name</em>, or <em>First Name</em> + <em>Last Name</em> columns.</li>
              </ol>
            </div>
            <label
              htmlFor="coe-sheet-file"
              className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
                coeSheetFile ? 'border-emerald-300 bg-emerald-50/60' : 'border-gray-300 hover:border-emerald-300 hover:bg-emerald-50/30'
              }`}
              data-testid="coe-sheet-drop-zone"
            >
              <UploadSimple size={24} className={coeSheetFile ? 'text-emerald-600' : 'text-gray-400'} />
              {coeSheetFile ? (
                <>
                  <p className="text-sm font-semibold text-emerald-800">{coeSheetFile.name}</p>
                  <p className="text-[11px] text-emerald-700">{(coeSheetFile.size / 1024).toFixed(1)} KB — click to pick a different file</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-gray-800">Click to pick an .xlsx or .csv file</p>
                  <p className="text-[11px] text-gray-500">Max 25 MB</p>
                </>
              )}
              <input
                id="coe-sheet-file"
                type="file"
                accept=".xlsx,.xlsm,.csv,.tsv"
                className="hidden"
                onChange={(e) => setCoeSheetFile(e.target.files?.[0] || null)}
                data-testid="coe-sheet-file-input"
              />
            </label>
            {coeSheetMeta.last_synced_at && (
              <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-100 rounded-md p-2">
                Last synced <strong>{new Date(coeSheetMeta.last_synced_at).toLocaleString()}</strong>
                {coeSheetMeta.last_synced_by ? ` by ${coeSheetMeta.last_synced_by}` : ''}
                {coeSheetMeta.last_result && (
                  <span> — advanced {coeSheetMeta.last_result.updated || 0} of {coeSheetMeta.last_result.matched || 0} matched ({coeSheetMeta.last_result.total_rows || 0} row{coeSheetMeta.last_result.total_rows === 1 ? '' : 's'} in sheet).</span>
                )}
              </div>
            )}
            {coeSheetResult && (
              <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-xs text-emerald-900 space-y-1" data-testid="coe-sheet-result">
                <div className="font-semibold text-sm">Sync complete</div>
                <div>Rows scanned: <strong>{coeSheetResult.total_rows}</strong></div>
                <div>Matched to portal students: <strong>{coeSheetResult.matched}</strong></div>
                <div>Advanced to COE Sent: <strong>{coeSheetResult.updated}</strong></div>
                {coeSheetResult.already_at_stage > 0 && (
                  <div>Already at COE stage: {coeSheetResult.already_at_stage}</div>
                )}
                {coeSheetResult.skipped_terminal > 0 && (
                  <div className="text-amber-700">Skipped (rejected — not overwritten): {coeSheetResult.skipped_terminal}</div>
                )}
                {coeSheetResult.unmatched > 0 && (
                  <div className="text-gray-600">Unmatched sheet rows: {coeSheetResult.unmatched}</div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCoeSheetDialog(false)} disabled={coeSheetSyncing} data-testid="coe-sheet-close-btn">Close</Button>
              <Button onClick={handleSyncCoeSheet} disabled={coeSheetSyncing || !coeSheetFile} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="coe-sheet-sync-btn">
                {coeSheetSyncing ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
                {coeSheetSyncing ? 'Syncing…' : 'Sync Now'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>



      {/* Assign College Dialog */}
      <Dialog open={!!assigningCollegeFor} onOpenChange={(o) => { if (!o) setAssigningCollegeFor(null); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="assign-college-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Assign College</DialogTitle><DialogDescription>Choose a partner college + program and set the sale price for this student.</DialogDescription></DialogHeader>
          {assigningCollegeFor && (() => {
            const selCollege = colleges.find(c => c.id === selectedCollegeToAssign);
            const programs = selCollege?.programs || [];
            return (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm font-medium text-gray-900">{assigningCollegeFor.full_name}</p>
                <p className="text-xs text-gray-500">{assigningCollegeFor.email}</p>
                {assigningCollegeFor.preferred_college_name && (
                  <p className="text-xs text-blue-700 mt-1">Recruiter preference: <strong>{assigningCollegeFor.preferred_college_name}</strong>{assigningCollegeFor.preferred_program_name ? ` → ${assigningCollegeFor.preferred_program_name}` : ''}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Partner College</Label>
                <Select value={selectedCollegeToAssign || 'none'} onValueChange={(v) => { setSelectedCollegeToAssign(v === 'none' ? '' : v); setSelectedProgramToAssign(''); setSalePriceToAssign(''); }}>
                  <SelectTrigger data-testid="assign-college-select"><SelectValue placeholder="Select college" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {colleges.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}{c.location ? ` (${c.location})` : ''}</SelectItem>))}
                  </SelectContent>
                </Select>
                {colleges.length === 0 && (
                  <p className="text-xs text-amber-600">No partner colleges yet. Add one from the Colleges tab.</p>
                )}
              </div>
              {selectedCollegeToAssign && (
                <div className="space-y-2">
                  <Label>Program</Label>
                  <Select
                    value={selectedProgramToAssign || 'none'}
                    onValueChange={(v) => {
                      const pid = v === 'none' ? '' : v;
                      setSelectedProgramToAssign(pid);
                      const p = programs.find(x => x.id === pid);
                      if (p && salePriceToAssign === '') setSalePriceToAssign(p.default_sale_price ? String(p.default_sale_price) : '');
                    }}
                    disabled={!programs.length}
                  >
                    <SelectTrigger data-testid="assign-program-select"><SelectValue placeholder={programs.length ? 'Select program' : 'No programs listed'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not selected —</SelectItem>
                      {programs.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}{p.default_sale_price ? ` · default $${Number(p.default_sale_price).toLocaleString()}` : ''}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {selectedCollegeToAssign && (
                <div className="space-y-2">
                  <Label>Sale Price (CAD) <span className="text-xs text-gray-400 font-normal">(what Study Alliance charges for this student)</span></Label>
                  <Input type="number" min="0" step="100" value={salePriceToAssign} onChange={e => setSalePriceToAssign(e.target.value)} placeholder="e.g., 16500" data-testid="assign-sale-price-input" />
                  <p className="text-xs text-gray-500">Leave empty to use the program default. You can edit this later from the student's detail page.</p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAssigningCollegeFor(null)}>Cancel</Button>
                <Button onClick={handleAssignCollege} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="assign-college-save-btn"><Buildings size={14} className="mr-2" /> Save Assignment</Button>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
      {/* Activate Recruiter (Commission) Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={(o) => { if (!o) { setShowActivateDialog(false); setActivatingRecruiter(null); setActivateCommission(''); } }}>
        <DialogContent className="max-w-md" data-testid="activate-recruiter-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Activate Recruiter</DialogTitle>
            <DialogDescription>Set the commission Study Alliance will pay this recruiter per enrolled student.</DialogDescription>
          </DialogHeader>
          {activatingRecruiter && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm font-medium text-gray-900">{activatingRecruiter.full_name}</p>
                <p className="text-xs text-gray-500">{activatingRecruiter.email} · {activatingRecruiter.company_name}</p>
              </div>
              <div className="space-y-2">
                <Label>Commission per Student (CAD) *</Label>
                <Input
                  autoFocus
                  type="number"
                  min="0"
                  step="50"
                  value={activateCommission}
                  onChange={e => setActivateCommission(e.target.value)}
                  placeholder="e.g., 500"
                  data-testid="activate-commission-input"
                />
                <p className="text-xs text-gray-500">This is what Study Alliance pays the recruiter for each enrolled student they bring in. You can change it later from the recruiter list.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowActivateDialog(false); setActivatingRecruiter(null); setActivateCommission(''); }}>Cancel</Button>
                <Button onClick={handleConfirmActivate} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-activate-btn"><CheckCircle size={14} className="mr-2" /> Activate Recruiter</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== OUTREACH DIALOGS ===== */}

      {/* Import .xlsx / .csv Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md" data-testid="outreach-import-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Import Outreach Leads</DialogTitle>
            <DialogDescription>
              Upload an Excel (.xlsx) or CSV (.csv) file. Required columns: <strong>company name</strong>, <strong>company number</strong>, <strong>company email</strong>. Duplicates by email are skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs">File (max 5 MB)</Label>
              <Input
                type="file"
                accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={e => setImportFile(e.target.files?.[0] || null)}
                className="h-9 text-xs file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mt-1"
                data-testid="outreach-import-file"
              />
              {importFile && (
                <p className="text-xs text-emerald-700 mt-1">
                  <FileText size={12} className="inline mr-1" />
                  {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-[11px] text-amber-800 space-y-1">
              <p><strong>Tip:</strong> First row should be headers. Column names are matched loosely — "Email", "company_email", and "Company Email" all work. No headers? We'll fall back to col 1 = name, col 2 = number, col 3 = email.</p>
              <p>Cells with <strong>two emails</strong> (e.g. <code>x@y.ca / a@b.ca</code>) are auto-split into two leads. Rows with <code>NA</code>, empty emails, or URLs are skipped — you'll see exactly what was rejected after upload.</p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setShowImportDialog(false)} data-testid="outreach-import-cancel">Cancel</Button>
              <Button onClick={handleImportLeads} disabled={importing || !importFile} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="outreach-import-submit">
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== DIRECT LEADS IMPORT DIALOG ===== */}
      <Dialog open={showLeadImportDialog} onOpenChange={(o) => { if (!leadImporting) { setShowLeadImportDialog(o); if (!o) { setLeadImportFile(null); setLeadImportResult(null); } } }}>
        <DialogContent className="max-w-md" data-testid="direct-leads-import-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Import Direct Leads</DialogTitle>
            <DialogDescription>
              Upload an Excel (.xlsx) or CSV (.csv) file. Recognised columns: <strong>Full Name</strong>, <strong>Phone</strong>, <strong>Email</strong>, <strong>Lead Source</strong>. Each new lead gets a fresh reference number. Duplicates by email are skipped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs">File (max 5 MB)</Label>
              <Input
                type="file"
                accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={e => { setLeadImportFile(e.target.files?.[0] || null); setLeadImportResult(null); }}
                className="h-9 text-xs file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mt-1"
                data-testid="direct-leads-import-file"
              />
              {leadImportFile && (
                <p className="text-xs text-emerald-700 mt-1">
                  <FileText size={12} className="inline mr-1" />
                  {leadImportFile.name} ({(leadImportFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-[11px] text-amber-800 space-y-1.5">
              <p><strong>Tip:</strong> First row should be headers. Column names are matched loosely — &quot;Name&quot;, &quot;Full Name&quot;, &quot;Mobile&quot;, &quot;Phone&quot;, &quot;Source&quot;, &quot;Lead Source&quot; all work. No headers? We&apos;ll fall back to col 1 = name, col 2 = phone, col 3 = email, col 4 = source.</p>
              <button type="button" onClick={handleDownloadDirectLeadTemplate} className="text-amber-900 underline hover:text-amber-700 font-medium" data-testid="direct-leads-template-download">
                Download CSV template
              </button>
            </div>
            {leadImportResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900 space-y-1" data-testid="direct-leads-import-result">
                <p><strong>Inserted:</strong> {leadImportResult.inserted} · <strong>Duplicates skipped:</strong> {leadImportResult.duplicates} · <strong>Invalid rows:</strong> {leadImportResult.invalid} / {leadImportResult.total_rows}</p>
                {leadImportResult.skipped_rows?.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-emerald-800 hover:text-emerald-900">Show skipped rows ({leadImportResult.skipped_rows.length})</summary>
                    <ul className="mt-1 ml-3 list-disc text-[10px] space-y-0.5">
                      {leadImportResult.skipped_rows.map((r, i) => (
                        <li key={i}>{r.full_name || '(no name)'} — <code className="text-rose-700">{r.email || 'empty'}</code> — {r.reason}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => { setShowLeadImportDialog(false); setLeadImportFile(null); setLeadImportResult(null); }} disabled={leadImporting} data-testid="direct-leads-import-cancel">Close</Button>
              <Button onClick={handleImportDirectLeads} disabled={leadImporting || !leadImportFile} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="direct-leads-import-submit">
                {leadImporting ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== DIRECT LEADS EMAIL COMPOSE DIALOG ===== */}
      <Dialog open={showDirectLeadEmailDialog} onOpenChange={(o) => { setShowDirectLeadEmailDialog(o); if (!o) setDirectLeadEmailAttachments([]); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="direct-leads-email-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Email Direct Leads</DialogTitle>
            <DialogDescription>
              You&apos;re emailing <strong>{selectedAppIds.length}</strong> selected lead{selectedAppIds.length === 1 ? '' : 's'}. Use <code>{'{{first_name}}'}</code>, <code>{'{{full_name}}'}</code>, <code>{'{{reference_number}}'}</code>, <code>{'{{book_call_link}}'}</code> for personalisation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setDirectLeadEmailDraft(d => ({ ...d, body: (d.body ? d.body.trim() + '\n\n' : '') + 'Book a free 15-min consultation:\n{{book_call_link}}' })); toast.success('Booking-link added to body'); }} className="border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="direct-leads-insert-booking-btn">
                <CalendarBlank size={14} className="mr-1" /> Insert booking link
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowDirectLeadAIDialog(true)} className="border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="direct-leads-ai-open-btn">
                <Sparkle size={14} className="mr-1" weight="fill" /> Generate with AI
              </Button>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={directLeadEmailDraft.subject} onChange={e => setDirectLeadEmailDraft(d => ({ ...d, subject: e.target.value }))} placeholder="e.g. Welcome to Study Alliance, {{first_name}}" className="mt-1" data-testid="direct-leads-subject-input" />
            </div>
            <div>
              <Label className="text-xs">Body</Label>
              <textarea
                value={directLeadEmailDraft.body}
                onChange={e => setDirectLeadEmailDraft(d => ({ ...d, body: e.target.value }))}
                placeholder="Hi {{first_name}}, thanks for applying. Your reference is {{reference_number}}…"
                rows={10}
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 font-sans"
                data-testid="direct-leads-body-input"
              />
              <p className="text-[10px] text-gray-500 mt-1">Plain text — line breaks are preserved.</p>
            </div>

            {/* Attachments */}
            <div className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Paperclip size={14} className="text-gray-600" />
                  <p className="text-xs font-semibold text-gray-700">Attachments</p>
                  {directLeadEmailAttachments.length > 0 && (
                    <span className="text-[10px] text-gray-500">({directLeadEmailAttachments.length})</span>
                  )}
                </div>
                <Label htmlFor="direct-leads-attach-input" className="cursor-pointer">
                  <span className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 ${directLeadEmailUploadingAttachment ? 'opacity-60 pointer-events-none' : ''}`} data-testid="direct-leads-attach-btn">
                    <UploadSimple size={12} /> {directLeadEmailUploadingAttachment ? 'Uploading…' : 'Add files'}
                  </span>
                </Label>
                <input id="direct-leads-attach-input" type="file" multiple className="hidden" onChange={handleDirectLeadEmailAttach} disabled={directLeadEmailUploadingAttachment} data-testid="direct-leads-attach-input" />
              </div>
              {directLeadEmailAttachments.length === 0 ? (
                <p className="text-[11px] text-gray-500">No files attached. Up to 10 MB per file, 20 MB total.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {directLeadEmailAttachments.map(a => (
                    <span key={a.file_id} className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full pl-2 pr-1 py-0.5 text-[11px] text-gray-700">
                      <FileText size={12} className="text-gray-500" />
                      <span className="max-w-[180px] truncate" title={a.filename}>{a.filename}</span>
                      <button type="button" onClick={() => handleDirectLeadEmailRemoveAttachment(a.file_id)} className="ml-0.5 text-gray-400 hover:text-red-600 px-1" data-testid={`direct-leads-attach-remove-${a.file_id}`}>✕</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Delay between sends (seconds)</Label>
                <Input type="number" min="0" max="30" step="0.5" value={directLeadEmailDraft.delay_seconds} onChange={e => setDirectLeadEmailDraft(d => ({ ...d, delay_seconds: e.target.value }))} className="mt-1" data-testid="direct-leads-delay-input" />
                <p className="text-[10px] text-gray-500 mt-1">Recommended: 2s</p>
              </div>
              <div className="flex items-end">
                <p className="text-xs text-gray-500">Approx. total time: <strong>{Math.ceil(selectedAppIds.length * (Number(directLeadEmailDraft.delay_seconds) || 0))}s</strong></p>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <EnvelopeSimple size={14} className="text-emerald-700" />
                <p className="text-xs font-semibold text-emerald-900">Send a TEST first</p>
              </div>
              <p className="text-[11px] text-emerald-800 mb-2">Preview deliverability + rendering before blasting all {selectedAppIds.length} leads.</p>
              <div className="flex items-center gap-2">
                <Input type="email" value={directLeadEmailTestAddr} onChange={e => setDirectLeadEmailTestAddr(e.target.value)} placeholder="you@studyalliance.ca" className="h-9 flex-1" data-testid="direct-leads-test-email-input" />
                <Button size="sm" variant="outline" onClick={handleDirectLeadSendTest} disabled={directLeadEmailSendingTest || !directLeadEmailTestAddr.trim()} className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-100" data-testid="direct-leads-send-test-btn">
                  <PaperPlaneTilt size={14} className="mr-1" /> {directLeadEmailSendingTest ? 'Sending…' : 'Send test'}
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => { setShowDirectLeadEmailDialog(false); setDirectLeadEmailAttachments([]); }} data-testid="direct-leads-email-cancel">Cancel</Button>
              <Button onClick={handleDirectLeadSendMass} disabled={directLeadEmailSendingMass || selectedAppIds.length === 0} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="direct-leads-email-send-btn">
                <PaperPlaneTilt size={14} className="mr-1" /> {directLeadEmailSendingMass ? 'Starting…' : `Send to ${selectedAppIds.length}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== DIRECT LEADS AI SUGGEST DIALOG ===== */}
      <Dialog open={showDirectLeadAIDialog} onOpenChange={setShowDirectLeadAIDialog}>
        <DialogContent className="max-w-lg" data-testid="direct-leads-ai-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-purple-600" /> AI Email Draft
            </DialogTitle>
            <DialogDescription>Briefly describe what to say. Claude Sonnet 4.6 will draft a warm, student-facing email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Brief</Label>
              <textarea value={directLeadAIBrief} onChange={e => setDirectLeadAIBrief(e.target.value)} rows={4} placeholder="e.g. Welcome new student leads who applied online. Invite them to a free 15-min consult." className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-purple-400" data-testid="direct-leads-ai-brief" />
            </div>
            <div>
              <Label className="text-xs">Tone</Label>
              <Input value={directLeadAITone} onChange={e => setDirectLeadAITone(e.target.value)} placeholder="warm and friendly" className="mt-1" data-testid="direct-leads-ai-tone" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowDirectLeadAIDialog(false)} data-testid="direct-leads-ai-cancel">Cancel</Button>
              <Button onClick={handleDirectLeadGenerateAI} disabled={directLeadAIGenerating || !directLeadAIBrief.trim()} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="direct-leads-ai-generate">
                <Sparkle size={14} className="mr-1" weight="fill" /> {directLeadAIGenerating ? 'Generating…' : 'Generate'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== DIRECT LEADS WHATSAPP DIALOG ===== */}
      <Dialog open={showDirectLeadWhatsAppDialog} onOpenChange={setShowDirectLeadWhatsAppDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="direct-leads-whatsapp-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
              <ChatCircle size={18} className="text-emerald-600" /> WhatsApp Direct Leads
            </DialogTitle>
            <DialogDescription>
              Compose your message once — we&apos;ll generate a per-lead WhatsApp link with the message pre-filled. Click each &quot;Open&quot; to launch WhatsApp Web/app with the right phone number. Use <code>{'{{first_name}}'}</code> and <code>{'{{full_name}}'}</code> for personalisation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Message</Label>
              <textarea
                value={directLeadWhatsAppMessage}
                onChange={e => setDirectLeadWhatsAppMessage(e.target.value)}
                rows={6}
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 font-sans"
                data-testid="direct-leads-whatsapp-message"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-[11px] text-amber-800">
              <strong>Heads-up:</strong> WhatsApp opens in a new tab with your message pre-filled. You still need to click <em>Send</em> in WhatsApp itself — this is a legal requirement of WhatsApp for outbound messages.
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700">{selectedDirectLeadsWithPhone.length} lead{selectedDirectLeadsWithPhone.length === 1 ? '' : 's'} with valid phone numbers</p>
                {selectedDirectLeads.length !== selectedDirectLeadsWithPhone.length && (
                  <span className="text-[10px] text-amber-700">{selectedDirectLeads.length - selectedDirectLeadsWithPhone.length} skipped (no phone)</span>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                {selectedDirectLeadsWithPhone.length === 0 ? (
                  <p className="text-xs text-gray-400 px-3 py-6 text-center">None of the selected leads have a phone number on file.</p>
                ) : selectedDirectLeadsWithPhone.map(lead => {
                  const rendered = renderLeadWhatsAppMessage(lead, directLeadWhatsAppMessage);
                  const link = buildWhatsAppLink(lead.phone, rendered);
                  return (
                    <div key={lead.id} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`direct-leads-whatsapp-row-${lead.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{lead.full_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{lead.phone}</p>
                      </div>
                      {lead.last_whatsapped_at && (
                        <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">Sent</span>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleOpenWhatsAppLink(lead, link)} disabled={!link} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-8" data-testid={`direct-leads-whatsapp-open-${lead.id}`}>
                        <ChatCircle size={12} className="mr-1" /> Open
                      </Button>
                    </div>
                  );
                })}
              </div>
              {selectedDirectLeadsWithPhone.length > 1 && (
                <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const valid = selectedDirectLeadsWithPhone.slice(0, 10);
                      if (selectedDirectLeadsWithPhone.length > 10) toast.message(`Opening first 10 of ${selectedDirectLeadsWithPhone.length} — browsers block bulk popups beyond ~10 tabs`);
                      valid.forEach(lead => {
                        const rendered = renderLeadWhatsAppMessage(lead, directLeadWhatsAppMessage);
                        const link = buildWhatsAppLink(lead.phone, rendered);
                        if (link) handleOpenWhatsAppLink(lead, link);
                      });
                    }}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="direct-leads-whatsapp-open-all"
                  >
                    Open all (max 10)
                  </Button>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowDirectLeadWhatsAppDialog(false)} data-testid="direct-leads-whatsapp-close">Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== STUDENT ADMIN NOTES DIALOG ===== */}
      <Dialog open={!!noteDialogStudent} onOpenChange={(o) => { if (!o) { setNoteDialogStudent(null); setNewNoteText(''); setStudentNotes([]); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="student-notes-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
              <FileText size={18} className="text-amber-600" /> Admin Notes
            </DialogTitle>
            <DialogDescription>
              Internal notes for <strong>{noteDialogStudent?.full_name}</strong>. Visible to admins only — never shown to the recruiter or the student.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Add note */}
            <div className="space-y-2">
              <Label className="text-xs">New note</Label>
              <textarea
                value={newNoteText}
                onChange={e => setNewNoteText(e.target.value)}
                placeholder="e.g. Called to confirm enrollment. Asked for IELTS prep schedule."
                rows={3}
                maxLength={5000}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                data-testid="student-note-input"
              />
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-gray-400">{newNoteText.length} / 5000</p>
                <Button
                  size="sm"
                  onClick={handleAddStudentNote}
                  disabled={savingNote || !newNoteText.trim()}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="student-note-save-btn"
                >
                  <Plus size={14} className="mr-1" /> {savingNote ? 'Saving…' : 'Add note'}
                </Button>
              </div>
            </div>

            {/* Notes list */}
            <div className="border-t border-gray-200 pt-3">
              <p className="text-xs font-semibold text-gray-700 mb-2">All notes ({studentNotes.length})</p>
              {studentNotesLoading ? (
                <p className="text-xs text-gray-400 py-4 text-center">Loading…</p>
              ) : studentNotes.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center bg-gray-50 rounded-md border border-dashed border-gray-200">No notes yet — add the first one above.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="student-notes-list">
                  {studentNotes.map(n => (
                    <div key={n.id} className="bg-amber-50/40 border border-amber-100 rounded-md p-3" data-testid={`student-note-${n.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap flex-1 leading-relaxed">{n.text}</p>
                        <button
                          type="button"
                          onClick={() => handleDeleteStudentNote(n.id)}
                          className="text-gray-300 hover:text-red-600 flex-shrink-0 p-0.5"
                          title="Delete note"
                          data-testid={`student-note-delete-${n.id}`}
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-500 mt-2">
                        {n.author_name || n.author_email || 'Admin'} · {n.created_at ? new Date(n.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>



      {/* Add Lead (manual) Dialog */}
      <Dialog open={showAddLeadDialog} onOpenChange={setShowAddLeadDialog}>
        <DialogContent className="max-w-md" data-testid="outreach-add-lead-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Add Lead Manually</DialogTitle>
            <DialogDescription>Add a single outreach lead without importing a file.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Company Name</Label>
              <Input
                value={addLeadForm.company_name}
                onChange={e => setAddLeadForm(f => ({ ...f, company_name: e.target.value }))}
                placeholder="e.g. Maple Leaf Immigration Inc"
                className="mt-1 h-9"
                data-testid="add-lead-company-input"
              />
            </div>
            <div>
              <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={addLeadForm.company_email}
                onChange={e => setAddLeadForm(f => ({ ...f, company_email: e.target.value }))}
                placeholder="contact@company.com"
                className="mt-1 h-9"
                data-testid="add-lead-email-input"
              />
            </div>
            <div>
              <Label className="text-xs">Phone Number</Label>
              <Input
                value={addLeadForm.company_number}
                onChange={e => setAddLeadForm(f => ({ ...f, company_number: e.target.value }))}
                placeholder="e.g. 6045551234"
                className="mt-1 h-9"
                data-testid="add-lead-number-input"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={addLeadForm.status} onValueChange={v => setAddLeadForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className="mt-1 h-9" data-testid="add-lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="emailed">Emailed</SelectItem>
                  <SelectItem value="replied">Replied</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="not_interested">Not Interested</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setShowAddLeadDialog(false)} data-testid="add-lead-cancel-btn">Cancel</Button>
              <Button onClick={handleAddLead} disabled={addingLead || !addLeadForm.company_email.trim()} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="add-lead-submit-btn">
                {addingLead ? 'Adding…' : 'Add Lead'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View sent email (history) Dialog */}
      <Dialog open={!!viewEmailEntry} onOpenChange={(o) => { if (!o) setViewEmailEntry(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-history-view-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Sent Email</DialogTitle>
            <DialogDescription>
              To <strong>{viewEmailEntry?.to_email}</strong>{viewEmailEntry?.company_name || viewEmailEntry?.full_name ? <> · {viewEmailEntry.company_name || viewEmailEntry.full_name}</> : null}{viewEmailEntry?.reference_number ? <> · <span className="font-mono text-blue-700">{viewEmailEntry.reference_number}</span></> : null} · {viewEmailEntry?.sent_at ? new Date(viewEmailEntry.sent_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : ''}
            </DialogDescription>
          </DialogHeader>
          {viewEmailEntry && (
            <div className="space-y-3 mt-1">
              {viewEmailEntry.status === 'failed' && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-700" data-testid="email-history-error">
                  <strong>Send failed:</strong> {viewEmailEntry.error || 'Unknown error'}
                </div>
              )}
              <div>
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider mb-1">Subject</p>
                <p className="text-sm font-medium text-gray-900 bg-gray-50 border border-gray-200 rounded-md px-3 py-2" data-testid="email-history-subject">{viewEmailEntry.subject}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider mb-1">Body</p>
                <div className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 whitespace-pre-wrap leading-relaxed" data-testid="email-history-body">{viewEmailEntry.body}</div>
              </div>
              {viewEmailEntry.attachments && viewEmailEntry.attachments.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider mb-1">Attachments ({viewEmailEntry.attachments.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {viewEmailEntry.attachments.map((a, i) => (
                      <span key={a.file_id || i} className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5 text-[11px] text-gray-700">
                        <FileText size={12} className="text-gray-500" />
                        <span className="max-w-[180px] truncate">{a.filename}</span>
                        {a.size > 0 && <span className="text-[10px] text-gray-400">({a.size < 1024 ? `${a.size} B` : a.size < 1024 * 1024 ? `${(a.size / 1024).toFixed(0)} KB` : `${(a.size / 1024 / 1024).toFixed(1)} MB`})</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end pt-1">
                <Button variant="ghost" onClick={() => setViewEmailEntry(null)} data-testid="email-history-close-btn">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mass Email Compose Dialog */}
      <Dialog open={showMassEmailDialog} onOpenChange={setShowMassEmailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="outreach-mass-email-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Compose Mass Outreach Email</DialogTitle>
            <DialogDescription>
              You&apos;re about to email <strong>{selectedLeadIds.length}</strong> leads. Use <code>{'{{company_name}}'}</code> in subject or body for personalisation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setMassEmailDraft(d => ({
                    ...d,
                    body: (d.body ? d.body.trim() + '\n\n' : '') + 'Book a quick 15-min call here:\n{{book_call_link}}',
                  }));
                  toast.success('Booking-link placeholder added to body');
                }}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                data-testid="outreach-insert-booking-link-btn"
              >
                <CalendarBlank size={14} className="mr-1" /> Insert booking link
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAIDialog(true)}
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
                data-testid="outreach-ai-open-btn"
              >
                <Sparkle size={14} className="mr-1" weight="fill" /> Generate with AI
              </Button>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input
                value={massEmailDraft.subject}
                onChange={e => setMassEmailDraft(d => ({ ...d, subject: e.target.value }))}
                placeholder="e.g. Partner with Study Alliance — quick intro"
                className="mt-1"
                data-testid="outreach-subject-input"
              />
            </div>
            <div>
              <Label className="text-xs">Body</Label>
              <textarea
                value={massEmailDraft.body}
                onChange={e => setMassEmailDraft(d => ({ ...d, body: e.target.value }))}
                placeholder="Hi {{company_name}}, …"
                rows={10}
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 font-sans"
                data-testid="outreach-body-input"
              />
              <p className="text-[10px] text-gray-500 mt-1">Plain text — line breaks are preserved.</p>
            </div>

            {/* Attachments */}
            <div className="border border-gray-200 rounded-lg p-3" data-testid="outreach-attachments-section">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Paperclip size={14} className="text-gray-600" />
                  <p className="text-xs font-semibold text-gray-700">Attachments</p>
                  {composeAttachments.length > 0 && (
                    <span className="text-[10px] text-gray-500" data-testid="outreach-attachment-count">({composeAttachments.length})</span>
                  )}
                </div>
                <Label htmlFor="outreach-attach-input" className="cursor-pointer">
                  <span
                    className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 ${uploadingComposeAttachment ? 'opacity-60 pointer-events-none' : ''}`}
                    data-testid="outreach-attach-btn"
                  >
                    <UploadSimple size={12} /> {uploadingComposeAttachment ? 'Uploading…' : 'Add files'}
                  </span>
                </Label>
                <input
                  id="outreach-attach-input"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleComposeAttach}
                  disabled={uploadingComposeAttachment}
                  data-testid="outreach-attach-input"
                />
              </div>
              {composeAttachments.length === 0 ? (
                <p className="text-[11px] text-gray-500">No files attached. Up to 10 MB per file, 20 MB total per email.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="outreach-attachment-list">
                  {composeAttachments.map(a => (
                    <span
                      key={a.file_id}
                      className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full pl-2 pr-1 py-0.5 text-[11px] text-gray-700"
                      data-testid={`outreach-attachment-pill-${a.file_id}`}
                    >
                      <FileText size={12} className="text-gray-500" />
                      <span className="max-w-[180px] truncate" title={a.filename}>{a.filename}</span>
                      {a.size > 0 && (
                        <span className="text-[10px] text-gray-400">{formatAttachmentSize(a.size)}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveComposeAttachment(a.file_id)}
                        className="ml-0.5 text-gray-400 hover:text-red-600 px-1"
                        data-testid={`outreach-attachment-remove-${a.file_id}`}
                        aria-label={`Remove ${a.filename}`}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Delay between sends (seconds)</Label>
                <Input
                  type="number"
                  min="0"
                  max="30"
                  step="0.5"
                  value={massEmailDraft.delay_seconds}
                  onChange={e => setMassEmailDraft(d => ({ ...d, delay_seconds: e.target.value }))}
                  className="mt-1"
                  data-testid="outreach-delay-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">Recommended: 2s to avoid rate limits</p>
              </div>
              <div className="flex items-end">
                <p className="text-xs text-gray-500">
                  Approx. total time: <strong>{Math.ceil(selectedLeadIds.length * (Number(massEmailDraft.delay_seconds) || 0))}s</strong>
                </p>
              </div>
            </div>

            {/* Test send section */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="outreach-test-send-section">
              <div className="flex items-center gap-2 mb-2">
                <EnvelopeSimple size={14} className="text-emerald-700" />
                <p className="text-xs font-semibold text-emerald-900">Send a TEST first</p>
              </div>
              <p className="text-[11px] text-emerald-800 mb-2">Send this draft to any email so you can preview deliverability and rendering before blasting all {selectedLeadIds.length} leads. <code>{'{{company_name}}'}</code> will render as <em>Acme Education</em> in the test.</p>
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  placeholder="you@studyalliance.ca"
                  className="h-9 flex-1"
                  data-testid="outreach-test-email-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSendTestEmail}
                  disabled={sendingTest || !testEmail.trim()}
                  className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  data-testid="outreach-send-test-btn"
                >
                  <PaperPlaneTilt size={14} className="mr-1" /> {sendingTest ? 'Sending…' : 'Send test'}
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => { setShowMassEmailDialog(false); setComposeAttachments([]); }} data-testid="outreach-cancel-send-btn">Cancel</Button>
              <Button onClick={handleSendMassEmail} disabled={sendingMass} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="outreach-confirm-send-btn">
                <PaperPlaneTilt size={14} className="mr-1" /> {sendingMass ? 'Starting…' : `Send to ${selectedLeadIds.length}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Suggest Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="max-w-lg" data-testid="outreach-ai-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-purple-600" /> AI Email Draft
            </DialogTitle>
            <DialogDescription>
              Describe what you want to say. The AI will draft a subject + body you can edit. Uses Claude Sonnet 4.6.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Brief</Label>
              <textarea
                value={aiBrief}
                onChange={e => setAIBrief(e.target.value)}
                rows={4}
                placeholder="e.g. Invite Indian recruitment agents to partner with Study Alliance. Mention competitive commission and easy onboarding."
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 font-sans"
                data-testid="outreach-ai-brief"
              />
            </div>
            <div>
              <Label className="text-xs">Tone</Label>
              <Input
                value={aiTone}
                onChange={e => setAITone(e.target.value)}
                placeholder="professional and friendly"
                className="mt-1"
                data-testid="outreach-ai-tone"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowAIDialog(false)} data-testid="outreach-ai-cancel">Cancel</Button>
              <Button onClick={handleGenerateAI} disabled={aiGenerating || !aiBrief.trim()} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="outreach-ai-generate">
                <Sparkle size={14} className="mr-1" weight="fill" /> {aiGenerating ? 'Generating…' : 'Generate'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Recruiter In-Person Dialog */}
      {/* Announcement compose dialog */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent className="max-w-2xl" data-testid="announcement-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Send Announcement</DialogTitle>
            <DialogDescription>
              Compose an email that will be delivered to {selectedRecruiterIds.length} recruiter{selectedRecruiterIds.length === 1 ? '' : 's'} and shown as a news flash on their portal until they dismiss it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Subject</Label>
              <Input
                value={announcementDraft.subject}
                onChange={e => setAnnouncementDraft(d => ({ ...d, subject: e.target.value }))}
                placeholder="e.g. Winter intake is now open — action required"
                maxLength={200}
                data-testid="announcement-subject-input"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Body</Label>
              <Textarea
                value={announcementDraft.body}
                onChange={e => setAnnouncementDraft(d => ({ ...d, body: e.target.value }))}
                rows={8}
                placeholder={"Hi team,\n\nWinter intake applications are now open. Please submit your students by Nov 30…"}
                maxLength={10000}
                className="font-mono text-[13px]"
                data-testid="announcement-body-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">{announcementDraft.body.length}/10,000 characters · line breaks are preserved on the recruiter portal</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAnnouncementDialog(false)} data-testid="announcement-cancel-btn">Cancel</Button>
            <Button
              onClick={handleSendAnnouncement}
              disabled={announcementSending || !announcementDraft.subject.trim() || !announcementDraft.body.trim()}
              className="bg-gray-900 hover:bg-black text-white"
              data-testid="announcement-send-btn"
            >
              {announcementSending ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Sending…</> : <><Megaphone size={14} className="mr-1" /> Send to {selectedRecruiterIds.length}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAnnouncementHistory} onOpenChange={setShowAnnouncementHistory}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="announcement-history-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Announcement History</DialogTitle>
            <DialogDescription>Every mass announcement you&apos;ve sent, with delivery + dismissal counts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="announcement-history-list">
            {announcementHistoryLoading && (
              <p className="text-sm text-gray-500 py-4 text-center"><CircleNotch size={14} className="inline mr-1.5 animate-spin" /> Loading…</p>
            )}
            {!announcementHistoryLoading && announcementHistory.length === 0 && (
              <p className="text-sm text-gray-500 py-6 text-center italic">No announcements sent yet.</p>
            )}
            {announcementHistory.map((a) => {
              const recipients = (a.recipient_ids || []).length;
              const dismissed = (a.dismissed_by || []).length;
              const readPct = recipients > 0 ? Math.round((dismissed / recipients) * 100) : 0;
              return (
                <div key={a.id || a.created_at} className="border border-gray-200 rounded-md p-3 bg-white" data-testid={`announcement-history-row-${a.id || ''}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{a.subject || '(no subject)'}</p>
                    <span className="text-[10px] text-gray-500 shrink-0 tabular-nums">
                      {(a.created_at || '').replace('T', ' · ').split('.')[0]}
                    </span>
                  </div>
                  <p className="text-[12px] text-gray-600 line-clamp-3 whitespace-pre-wrap">{a.body}</p>
                  <div className="mt-2 flex items-center gap-4 text-[11px] text-gray-500">
                    <span>Recipients: <span className="tabular-nums font-semibold text-gray-800">{recipients}</span></span>
                    <span>Dismissed: <span className="tabular-nums font-semibold text-emerald-700">{dismissed}</span> <span className="tabular-nums">({readPct}%)</span></span>
                    <div className="flex-1 h-1 rounded-full bg-gray-100 overflow-hidden max-w-[240px] ml-auto">
                      <div className="h-full bg-emerald-500" style={{ width: `${readPct}%` }} aria-hidden />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAnnouncementHistory(false)} data-testid="announcement-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddRecruiterDialog} onOpenChange={setShowAddRecruiterDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-recruiter-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>{newRecruiterHasSignedAgreement ? 'Add Recruiter (In-Person)' : 'Add Recruiter (Lead Conversion)'}</DialogTitle>
            <DialogDescription>{newRecruiterHasSignedAgreement
              ? <>For recruiters met face-to-face with a signed agreement. Upload the signed PDF and we&apos;ll set status to &quot;Agreement Signed&quot; — click Activate after to email their login credentials.</>
              : <>The recruiter has NOT signed yet. We&apos;ll create them as <strong>Pending</strong> — then click <strong>Send Agreement</strong> from their row to issue the DocuSign agreement. Status flips to <em>Agreement Signed</em> only when DocuSign confirms.</>
            }</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Full Name *</Label>
                <Input value={newRecruiter.full_name} onChange={e => setNewRecruiter({ ...newRecruiter, full_name: e.target.value })} placeholder="Jane Doe" data-testid="new-recruiter-fullname" />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={newRecruiter.email} onChange={e => setNewRecruiter({ ...newRecruiter, email: e.target.value })} placeholder="jane@agency.com" data-testid="new-recruiter-email" />
              </div>
              <div>
                <Label className="text-xs">Company</Label>
                <Input value={newRecruiter.company_name} onChange={e => setNewRecruiter({ ...newRecruiter, company_name: e.target.value })} placeholder="Acme Education" data-testid="new-recruiter-company" />
              </div>
              <div>
                <Label className="text-xs">Country</Label>
                <Input value={newRecruiter.country} onChange={e => setNewRecruiter({ ...newRecruiter, country: e.target.value })} placeholder="Canada" data-testid="new-recruiter-country" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={newRecruiter.phone} onChange={e => setNewRecruiter({ ...newRecruiter, phone: e.target.value })} placeholder="+1 416 555 0123" data-testid="new-recruiter-phone" />
              </div>
              <div>
                <Label className="text-xs">Default Commission</Label>
                <div className="flex items-stretch gap-1 mt-0.5">
                  <div className="inline-flex rounded-md border border-gray-200 overflow-hidden shrink-0">
                    <button
                      type="button"
                      onClick={() => setNewRecruiter({ ...newRecruiter, commission_type: 'flat' })}
                      className={`px-3 text-xs font-semibold ${((newRecruiter.commission_type || 'flat') === 'flat') ? 'bg-blue-700 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                      data-testid="new-recruiter-commission-type-flat"
                    >$</button>
                    <button
                      type="button"
                      onClick={() => setNewRecruiter({ ...newRecruiter, commission_type: 'percent' })}
                      className={`px-3 text-xs font-semibold border-l border-gray-200 ${newRecruiter.commission_type === 'percent' ? 'bg-blue-700 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                      data-testid="new-recruiter-commission-type-percent"
                    >%</button>
                  </div>
                  {newRecruiter.commission_type === 'percent' ? (
                    <Input type="number" min="0" max="100" value={newRecruiter.commission_percent} onChange={e => setNewRecruiter({ ...newRecruiter, commission_percent: e.target.value })} placeholder="0" data-testid="new-recruiter-commission-percent" />
                  ) : (
                    <Input type="number" min="0" value={newRecruiter.commission_per_student} onChange={e => setNewRecruiter({ ...newRecruiter, commission_per_student: e.target.value })} placeholder="0" data-testid="new-recruiter-commission" />
                  )}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  {newRecruiter.commission_type === 'percent'
                    ? '% of Sale Price. Tier table is ignored while percent is selected.'
                    : 'Flat $ amount. Used as a fallback when no tier matches.'}
                </p>
              </div>
            </div>

            {/* Commission Tiers (optional) */}
            <div className="pt-2 border-t border-gray-100" data-testid="new-recruiter-tiers-section">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="text-xs font-semibold text-gray-700">Commission Tiers <span className="font-normal text-gray-400">(optional)</span></p>
                  <p className="text-[11px] text-gray-500 mt-0.5">Higher sale price → higher commission. Leave empty to use the default flat commission above. You can edit these anytime from the recruiter details.</p>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase">Sale Price ≥</th>
                      <th className="text-left px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase">Recruiter Earns</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {newRecruiterTiers.length === 0 ? (
                      <tr><td colSpan={3} className="px-3 py-3 text-center text-xs text-gray-400">No tiers yet</td></tr>
                    ) : newRecruiterTiers.map((t, idx) => (
                      <tr key={idx} data-testid={`new-tier-row-${idx}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400">$</span>
                            <Input type="number" min="0" step="100" value={t.sale_price} onChange={e => setNewRecruiterTiers(prev => prev.map((x, i) => i === idx ? { ...x, sale_price: e.target.value } : x))} className="h-8 w-28 text-sm" data-testid={`new-tier-sale-${idx}`} />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400">$</span>
                            <Input type="number" min="0" step="100" value={t.commission_amount} onChange={e => setNewRecruiterTiers(prev => prev.map((x, i) => i === idx ? { ...x, commission_amount: e.target.value } : x))} className="h-8 w-28 text-sm" data-testid={`new-tier-comm-${idx}`} />
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setNewRecruiterTiers(prev => prev.filter((_, i) => i !== idx))} data-testid={`new-tier-remove-${idx}`}>
                            <Trash size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50/50">
                    <tr>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">$</span>
                          <Input type="number" min="0" step="100" placeholder="e.g. 17500" value={newRecruiterTierRow.sale_price} onChange={e => setNewRecruiterTierRow(p => ({ ...p, sale_price: e.target.value }))} className="h-8 w-28 text-sm" data-testid="new-recruiter-new-tier-sale" />
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">$</span>
                          <Input type="number" min="0" step="100" placeholder="e.g. 7500" value={newRecruiterTierRow.commission_amount} onChange={e => setNewRecruiterTierRow(p => ({ ...p, commission_amount: e.target.value }))} className="h-8 w-28 text-sm" data-testid="new-recruiter-new-tier-comm" />
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          data-testid="new-recruiter-add-tier-btn"
                          onClick={() => {
                            const sp = parseFloat(newRecruiterTierRow.sale_price);
                            const ca = parseFloat(newRecruiterTierRow.commission_amount);
                            if (isNaN(sp) || sp < 0) { toast.error('Enter a valid sale price'); return; }
                            if (isNaN(ca) || ca < 0) { toast.error('Enter a valid commission'); return; }
                            if (newRecruiterTiers.some(t => Number(t.sale_price) === sp)) { toast.error('A tier with this sale price already exists'); return; }
                            const next = [...newRecruiterTiers, { sale_price: sp, commission_amount: ca }].sort((a, b) => Number(a.sale_price) - Number(b.sale_price));
                            setNewRecruiterTiers(next);
                            setNewRecruiterTierRow({ sale_price: '', commission_amount: '' });
                          }}
                        >
                          <Plus size={12} className="mr-1" /> Add
                        </Button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Has the recruiter already signed an agreement? Drives the
                signed-vs-pending path described in the dialog header above. */}
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5" data-testid="has-signed-toggle-section">
              <p className="text-xs font-semibold text-gray-700 mb-2">Has the recruiter already signed an agreement?</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <label className={`flex-1 flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${newRecruiterHasSignedAgreement ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-200' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid="has-signed-yes-option">
                  <input
                    type="radio"
                    name="has-signed-agreement"
                    checked={newRecruiterHasSignedAgreement}
                    onChange={() => setNewRecruiterHasSignedAgreement(true)}
                    className="mt-0.5"
                    data-testid="has-signed-yes-radio"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900">Yes — I have a signed PDF</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">Upload below. Status → Agreement Signed → Activate.</p>
                  </div>
                </label>
                <label className={`flex-1 flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${!newRecruiterHasSignedAgreement ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-200' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid="has-signed-no-option">
                  <input
                    type="radio"
                    name="has-signed-agreement"
                    checked={!newRecruiterHasSignedAgreement}
                    onChange={() => setNewRecruiterHasSignedAgreement(false)}
                    className="mt-0.5"
                    data-testid="has-signed-no-radio"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900">No — I will send DocuSign</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">No PDF needed. Status → Pending. Use “Send Agreement” next.</p>
                  </div>
                </label>
              </div>
            </div>

            {newRecruiterHasSignedAgreement && (
              <div>
                <Label className="text-xs">Signed Agreement (PDF, max 15 MB) *</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={e => setNewRecruiterAgreement(e.target.files?.[0] || null)}
                    className="h-9 text-xs file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    data-testid="new-recruiter-agreement-file"
                  />
                </div>
                {newRecruiterAgreement && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid="new-recruiter-agreement-chip">
                    <FileText size={12} className="inline mr-1" />
                    {newRecruiterAgreement.name} ({(newRecruiterAgreement.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowAddRecruiterDialog(false)} data-testid="cancel-add-recruiter-btn">Cancel</Button>
              <Button
                onClick={handleCreateRecruiterInPerson}
                disabled={creatingRecruiter || !newRecruiter.full_name.trim() || !newRecruiter.email.trim() || (newRecruiterHasSignedAgreement && !newRecruiterAgreement)}
                className="bg-blue-700 hover:bg-blue-800 text-white"
                data-testid="submit-add-recruiter-btn"
              >
                <Plus size={14} className="mr-1" /> {creatingRecruiter ? 'Creating...' : 'Create Recruiter'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Idle-Reminder Compose Dialog */}
      <Dialog open={showIdleReminderDialog} onOpenChange={(o) => { if (!sendingIdleReminder) setShowIdleReminderDialog(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="idle-reminder-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Send Idle Reminder Email</DialogTitle>
            <DialogDescription>
              Edit the subject, message, and admission-date callout below. The header, greeting (auto-filled with each recruiter's name), and "Open Recruiter Portal" button stay the same.
            </DialogDescription>
          </DialogHeader>

          {loadingIdlePreview ? (
            <div className="py-12 text-center text-sm text-gray-400">Loading preview...</div>
          ) : (
            <div className="space-y-4 mt-2">
              {/* Mode toggle */}
              <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5" data-testid="idle-reminder-mode-toggle">
                <button
                  type="button"
                  onClick={() => setIdleReminderMode('auto')}
                  className={`px-3 py-1.5 text-xs font-medium rounded ${idleReminderMode === 'auto' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="idle-reminder-mode-auto"
                >
                  Idle recruiters (auto-filter)
                </button>
                <button
                  type="button"
                  onClick={() => setIdleReminderMode('manual')}
                  className={`px-3 py-1.5 text-xs font-medium rounded ${idleReminderMode === 'manual' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="idle-reminder-mode-manual"
                >
                  Pick recipients manually
                </button>
              </div>

              {/* Recipients banner / picker */}
              {idleReminderMode === 'auto' ? (
                <div className={`rounded-md border px-3 py-2.5 text-sm ${idleReminderCandidates.length > 0 ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-600'}`} data-testid="idle-reminder-recipients">
                  {idleReminderCandidates.length > 0 ? (
                    <>
                      <strong>{idleReminderCandidates.length}</strong> recruiter{idleReminderCandidates.length === 1 ? '' : 's'} will receive this email
                      <span className="text-xs block mt-1 opacity-90">
                        {idleReminderCandidates.slice(0, 5).map(c => c.full_name || c.email).join(', ')}
                        {idleReminderCandidates.length > 5 && ` +${idleReminderCandidates.length - 5} more`}
                      </span>
                    </>
                  ) : (
                    <span>No recruiters currently match the criteria (idle &gt; 24h and not in cooldown). Switch to "Pick recipients manually" or use "Send test to me" to preview.</span>
                  )}
                </div>
              ) : (
                <div className="rounded-md border border-gray-200 bg-white" data-testid="idle-reminder-manual-picker">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 gap-2">
                    <Input
                      placeholder="Search by name, email, or company..."
                      value={idleReminderSearch}
                      onChange={e => setIdleReminderSearch(e.target.value)}
                      className="h-8 text-xs"
                      data-testid="idle-reminder-search"
                    />
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          const visible = idleReminderActiveList
                            .filter(r => {
                              const q = idleReminderSearch.trim().toLowerCase();
                              if (!q) return true;
                              return (r.full_name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q) || (r.company_name || '').toLowerCase().includes(q);
                            })
                            .map(r => r.id);
                          const allSelected = visible.length > 0 && visible.every(id => idleReminderPickedIds.includes(id));
                          if (allSelected) {
                            setIdleReminderPickedIds(prev => prev.filter(id => !visible.includes(id)));
                          } else {
                            setIdleReminderPickedIds(prev => [...new Set([...prev, ...visible])]);
                          }
                        }}
                        className="text-xs text-blue-700 hover:text-blue-900 whitespace-nowrap"
                        data-testid="idle-reminder-toggle-all"
                      >
                        Toggle visible
                      </button>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{idleReminderPickedIds.length} selected</span>
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                    {idleReminderActiveList.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-6">No active recruiters in your account yet.</p>
                    ) : (
                      idleReminderActiveList
                        .filter(r => {
                          const q = idleReminderSearch.trim().toLowerCase();
                          if (!q) return true;
                          return (r.full_name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q) || (r.company_name || '').toLowerCase().includes(q);
                        })
                        .map(r => {
                          const checked = idleReminderPickedIds.includes(r.id);
                          return (
                            <label key={r.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer" data-testid={`idle-reminder-pick-${r.id}`}>
                              <Checkbox checked={checked} onCheckedChange={() => setIdleReminderPickedIds(prev => checked ? prev.filter(x => x !== r.id) : [...prev, r.id])} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{r.full_name}</p>
                                <p className="text-xs text-gray-500 truncate">{r.email}{r.company_name ? ` · ${r.company_name}` : ''}</p>
                              </div>
                              {r.last_idle_reminder_at && (
                                <span className="text-[10px] text-amber-600 whitespace-nowrap" title={`Last reminded: ${new Date(r.last_idle_reminder_at).toLocaleString()}`}>
                                  reminded {new Date(r.last_idle_reminder_at).toLocaleDateString()}
                                </span>
                              )}
                            </label>
                          );
                        })
                    )}
                  </div>
                </div>
              )}

              {/* Subject */}
              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  value={idleReminderDraft.subject}
                  onChange={e => setIdleReminderDraft({ ...idleReminderDraft, subject: e.target.value })}
                  className="mt-1"
                  data-testid="idle-reminder-subject"
                />
              </div>

              {/* Body */}
              <div>
                <Label className="text-xs">Message body</Label>
                <p className="text-[11px] text-gray-400 mb-1">Plain text. Blank line = new paragraph. Sent right after "Dear {'{Recruiter Name}'},"</p>
                <Textarea
                  value={idleReminderDraft.body}
                  onChange={e => setIdleReminderDraft({ ...idleReminderDraft, body: e.target.value })}
                  rows={8}
                  className="text-sm"
                  data-testid="idle-reminder-body"
                />
              </div>

              {/* Dates callout */}
              <div>
                <Label className="text-xs">Important dates (amber callout box)</Label>
                <p className="text-[11px] text-gray-400 mb-1">One bullet per line. Leave empty to hide the callout entirely.</p>
                <Textarea
                  value={idleReminderDraft.dates_text}
                  onChange={e => setIdleReminderDraft({ ...idleReminderDraft, dates_text: e.target.value })}
                  rows={3}
                  className="text-sm"
                  data-testid="idle-reminder-dates"
                />
              </div>

              {/* Test send */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <Label className="text-xs">Send test to my email (optional, ignores cooldown)</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Input
                    type="email"
                    placeholder="hello@studyalliance.ca"
                    value={testEmailValue}
                    onChange={e => setTestEmailValue(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="idle-reminder-test-email"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSendIdleReminders(testEmailValue.trim())}
                    disabled={sendingIdleReminder || !testEmailValue.trim() || !idleReminderDraft.subject.trim() || !idleReminderDraft.body.trim()}
                    className="h-9 text-xs whitespace-nowrap"
                    data-testid="idle-reminder-send-test-btn"
                  >
                    {sendingIdleReminder ? 'Sending...' : 'Send Test'}
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                <Button variant="ghost" onClick={() => setShowIdleReminderDialog(false)} disabled={sendingIdleReminder} data-testid="idle-reminder-cancel-btn">Cancel</Button>
                <Button
                  onClick={() => handleSendIdleReminders(null)}
                  disabled={sendingIdleReminder || !idleReminderDraft.subject.trim() || !idleReminderDraft.body.trim() || (idleReminderMode === 'manual' && idleReminderPickedIds.length === 0)}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="idle-reminder-send-all-btn"
                >
                  <EnvelopeSimple size={14} className="mr-1.5" />
                  {sendingIdleReminder
                    ? 'Sending...'
                    : idleReminderMode === 'manual'
                      ? `Send to ${idleReminderPickedIds.length || 'selected'}`
                      : `Send to ${idleReminderCandidates.length || 'all'}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Forward-to-College Dialog with optional extra attachments */}
      <Dialog open={showForwardDialog} onOpenChange={(o) => { if (!forwarding) setShowForwardDialog(o); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="forward-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Forward to College</DialogTitle>
            <DialogDescription>
              All existing student documents will be bundled into a single .zip file along with any extra attachments you upload below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Recipient */}
            <div>
              <Label className="text-xs">College representative email *</Label>
              <Input
                type="email"
                value={forwardDraft.override_email}
                onChange={e => setForwardDraft({ ...forwardDraft, override_email: e.target.value })}
                placeholder="rep@college.edu"
                className="mt-1"
                data-testid="forward-email-input"
              />
              {!forwardDefaultEmail && (
                <p className="text-[11px] text-amber-700 mt-1">No representative email on the assigned college — enter one to proceed.</p>
              )}
            </div>

            {/* Existing docs summary */}
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900" data-testid="forward-existing-docs">
              <strong>{forwardExistingDocs}</strong> existing student document{forwardExistingDocs === 1 ? '' : 's'} will be included.
            </div>

            {/* Extra attachments */}
            <div>
              <Label className="text-xs">Any more documents to attach?</Label>
              <p className="text-[11px] text-gray-400 mb-1.5">Optional. PDFs, images, Word, Excel — max 15 MB per file. They&apos;ll be added to the .zip.</p>
              <Input
                type="file"
                multiple
                onChange={e => setForwardExtras(Array.from(e.target.files || []))}
                className="h-9 text-xs file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                data-testid="forward-extra-files-input"
              />
              {forwardExtras.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="forward-extras-list">
                  {forwardExtras.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1">
                      <span className="truncate flex-1"><FileText size={12} className="inline mr-1 text-gray-500" /> {f.name}</span>
                      <span className="text-gray-400 ml-2">{(f.size / 1024).toFixed(0)} KB</span>
                      <button
                        type="button"
                        onClick={() => setForwardExtras(forwardExtras.filter((_, idx) => idx !== i))}
                        className="ml-2 text-red-500 hover:text-red-700"
                        data-testid={`forward-extra-remove-${i}`}
                        title="Remove"
                      ><XCircle size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Note */}
            <div>
              <Label className="text-xs">Optional note for the college</Label>
              <Textarea
                value={forwardDraft.extra_notes}
                onChange={e => setForwardDraft({ ...forwardDraft, extra_notes: e.target.value })}
                rows={3}
                placeholder="e.g. Strong candidate — please prioritise for the May intake."
                className="text-sm mt-1"
                data-testid="forward-notes-input"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <Button variant="ghost" onClick={() => setShowForwardDialog(false)} disabled={forwarding} data-testid="forward-cancel-btn">Cancel</Button>
              <Button
                onClick={handleSubmitForward}
                disabled={forwarding || !forwardDraft.override_email.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="forward-send-btn"
              >
                <PaperPlaneTilt size={14} className="mr-1.5" />
                {forwarding ? 'Sending…' : `Send to ${forwardStudentName}'s college`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDC Declaration Send Modal (Oct 2026 intake, EA signed) */}
      <Dialog open={!!pdcStudent} onOpenChange={(open) => { if (!open) { setPdcStudent(null); setPdcMeta(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="pdc-declaration-modal">
          <DialogHeader>
            <DialogTitle>Send PDC Declaration</DialogTitle>
            <DialogDescription>
              Emails the Confirmation of Enrollment &amp; Tuition Payment Undertaking PDF to the student.
              Only point 4 (the amount) is pre-filled — the student completes everything else.
            </DialogDescription>
          </DialogHeader>
          {pdcLoading || !pdcMeta ? (
            <div className="py-10 flex items-center justify-center text-sm text-gray-500">
              <CircleNotch size={18} className="animate-spin mr-2" /> Loading declaration details…
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-2">Amount stamped on point 4</div>
                <div className="flex items-end justify-between gap-6 flex-wrap">
                  <div className="space-y-0.5 text-xs text-emerald-900">
                    <div>60% × Sale Price (C${(pdcMeta.sale_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) = <b>C${(pdcMeta.sixty_percent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></div>
                    <div>+ Application Fees = <b>C$250.00</b></div>
                  </div>
                  <div className="text-2xl font-bold text-emerald-800" data-testid="pdc-total-amount">{pdcMeta.total_amount_text}</div>
                </div>
                <button type="button" onClick={previewPdcPdf} className="mt-3 text-xs text-emerald-700 hover:text-emerald-900 underline underline-offset-2" data-testid="pdc-preview-pdf-btn">
                  Preview the filled PDF
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input value={pdcMeta.sender_email} readOnly className="bg-gray-50 cursor-not-allowed" data-testid="pdc-from-input" />
                </div>
                <div>
                  <Label className="text-xs">To (student email)</Label>
                  <Input value={pdcForm.to} onChange={(e) => setPdcForm({ ...pdcForm, to: e.target.value })} placeholder="student@example.com" data-testid="pdc-to-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={pdcForm.subject} onChange={(e) => setPdcForm({ ...pdcForm, subject: e.target.value })} data-testid="pdc-subject-input" />
              </div>
              <div>
                <Label className="text-xs">Message</Label>
                <Textarea value={pdcForm.body} onChange={(e) => setPdcForm({ ...pdcForm, body: e.target.value })} rows={14} className="font-sans leading-relaxed" data-testid="pdc-body-input" />
                <div className="text-[11px] text-gray-500 mt-1">Plain-text paragraphs — wrap any phrase with <code>*asterisks*</code> to make it <b>bold</b>. The filled PDF is attached automatically.</div>
              </div>
              {pdcMeta.last_sent_at && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Previously sent to <b>{pdcMeta.last_sent_to}</b> on {new Date(pdcMeta.last_sent_at).toLocaleString()}. Sending again will create a new email.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPdcStudent(null); setPdcMeta(null); }} disabled={pdcSending} data-testid="pdc-cancel-btn">Cancel</Button>
            <Button onClick={sendPdcDeclaration} disabled={pdcSending || pdcLoading || !pdcMeta} data-testid="pdc-send-btn">
              {pdcSending ? (<><CircleNotch size={14} className="animate-spin mr-1.5" /> Sending…</>) : (<><PaperPlaneTilt size={14} className="mr-1.5" /> Send declaration</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Simplified filter drawer + Download modal */}
      <StudentsFilterDrawer
        open={filterDrawerOpen}
        onOpenChange={setFilterDrawerOpen}
        filters={{
          search: studentSearch,
          recruiter_id: studentRecruiterFilter,
          status: studentStatusFilter,
          college_id: studentCollegeFilter,
          intake: studentIntakeFilter,
          start_date: studentStartDate,
          end_date: studentEndDate,
        }}
        setFilters={(f) => {
          if (f.search !== undefined) setStudentSearch(f.search);
          if (f.recruiter_id !== undefined) setStudentRecruiterFilter(f.recruiter_id);
          if (f.status !== undefined) setStudentStatusFilter(f.status);
          if (f.college_id !== undefined) setStudentCollegeFilter(f.college_id);
          if (f.intake !== undefined) setStudentIntakeFilter(f.intake);
          if (f.start_date !== undefined) setStudentStartDate(f.start_date);
          if (f.end_date !== undefined) setStudentEndDate(f.end_date);
        }}
        recruiters={recruiters}
        colleges={colleges}
      />
      <StudentsDownloadModal
        open={downloadModalOpen}
        onOpenChange={setDownloadModalOpen}
        filters={{
          search: studentSearch,
          recruiter_id: studentRecruiterFilter,
          status: studentStatusFilter,
          college_id: studentCollegeFilter,
          intake: studentIntakeFilter,
          start_date: studentStartDate,
          end_date: studentEndDate,
          _recruiter_label: recruiters.find(r => r.id === studentRecruiterFilter)?.full_name,
          _college_label: colleges.find(c => c.id === studentCollegeFilter)?.name,
        }}
        onExportStandard={handleExportStudentsXlsx}
        onExportQuarterly={handleAprilExport}
        onExportDocusign={() => setShowDsBulkDialog(true)}
        onExportPTIB={handlePtibExport}
      />
      <PreAuthorizationModal
        open={!!preAuthStudent}
        onClose={() => setPreAuthStudent(null)}
        student={preAuthStudent}
        onSent={({ sent_at, to }) => {
          // Instant local lock so the admin sees the Sent state without a full refetch.
          setStudents(prev => prev.map(s => s.id === preAuthStudent?.id
            ? { ...s, pre_auth_sent_at: sent_at, pre_auth_sent_to: to }
            : s));
          if (selectedStudent?.id === preAuthStudent?.id) {
            setSelectedStudent(prev => prev ? { ...prev, pre_auth_sent_at: sent_at, pre_auth_sent_to: to } : prev);
          }
        }}
      />
      <SelfCompletePreAuthModal
        open={!!selfPreAuthStudent}
        onClose={() => setSelfPreAuthStudent(null)}
        student={selfPreAuthStudent}
        onCompleted={({ completed_at, file_id }) => {
          setStudents(prev => prev.map(s => s.id === selfPreAuthStudent?.id
            ? { ...s, pre_auth_self_completed_at: completed_at, pre_auth_signed_file_id: file_id }
            : s));
          if (selectedStudent?.id === selfPreAuthStudent?.id) {
            setSelectedStudent(prev => prev ? { ...prev, pre_auth_self_completed_at: completed_at, pre_auth_signed_file_id: file_id } : prev);
          }
        }}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// FlaggedStudentRow — expandable row inside the ID-match Scan dialog.
// Click the row to lazily fetch the per-field detail (profile vs ID) from
// the student's persisted id_match_report, then inline-edit any flagged
// field + hit "Save & re-verify" to patch the student record and re-run
// the comparison without leaving the dialog.
// ---------------------------------------------------------------------------
function FlaggedStudentRow({ fs, onOpen, onResolved }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);     // { fields: [...], editable: {...} }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});          // editable field values
  const [resolved, setResolved] = useState(!!fs._resolved); // row stays visible, green, after a successful fix

  const _fieldToInputs = {
    name: ['first_name', 'middle_name', 'last_name'],
    dob: ['dob'],
    address: ['address_line1', 'address_line2', 'city', 'province', 'postal_code'],
  };
  const _labels = {
    first_name: 'First Name', middle_name: 'Middle Name', last_name: 'Last Name',
    dob: 'Date of Birth',
    address_line1: 'Street Address', address_line2: 'Line 2 (optional)',
    city: 'City', province: 'Province', postal_code: 'Postal Code',
  };

  const toggleOpen = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (!detail) {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/students/${fs.student_id}/id-match-detail`);
        setDetail(data);
        setDraft({ ...(data.editable || {}) });
      } catch (err) {
        toast.error('Could not load mismatch details');
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (!detail) return;
    // Only send fields the admin actually edited (or that belong to a flagged field group).
    const touchedKeys = new Set();
    for (const flag of (fs.flagged_fields || [])) {
      for (const k of (_fieldToInputs[flag] || [])) touchedKeys.add(k);
    }
    const patch = {};
    for (const k of touchedKeys) {
      if ((draft[k] || '') !== (detail.editable[k] || '')) patch[k] = draft[k];
    }
    if (Object.keys(patch).length === 0) {
      toast('No changes to save.', { icon: 'ℹ️' });
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/students/${fs.student_id}/id-match-quick-fix`, patch);
      const stillFlagged = (data.flagged_fields || []).length;
      if (stillFlagged === 0) {
        toast.success(`${fs.full_name || 'Student'} — now matches the ID ✓`);
        setResolved(true);
      } else {
        toast(`${fs.full_name || 'Student'} — ${stillFlagged} field(s) still flagged. Keep editing or Review.`, { icon: '⚠️' });
      }
      // Refresh local detail with new editable + flagged list.
      const nextEditable = { ...detail.editable, ...patch };
      setDetail({ ...detail, editable: nextEditable, fields: data.fields || [], flagged_fields: data.flagged_fields || [] });
      setDraft({ ...nextEditable });
      // Tell parent — it keeps the row visible but drops the flagged counter
      // when stillFlagged is 0, and updates the chips when some remain.
      onResolved?.({ student_id: fs.student_id, flagged_fields: data.flagged_fields || [] });
      if (stillFlagged === 0) setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <tr
        className={`text-sm cursor-pointer transition-colors ${resolved ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-gray-50'}`}
        data-testid={`flagged-row-${fs.student_id}`}
        onClick={toggleOpen}
      >
        <td className="px-4 py-2 text-gray-400 w-6">
          <span className={`inline-block transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        </td>
        <td className="px-4 py-2 font-medium text-gray-900">{fs.full_name || '—'}</td>
        <td className="px-4 py-2 text-gray-600">{fs.email || '—'}</td>
        <td className="px-4 py-2">
          {resolved ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`flagged-resolved-${fs.student_id}`}>
              <CheckCircle size={12} weight="fill" /> Resolved — now matches ID
            </span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {(fs.flagged_fields || []).map(f => (
                <span key={f} className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                  {f === 'dob' ? 'DOB' : f.charAt(0).toUpperCase() + f.slice(1)}
                </span>
              ))}
            </div>
          )}
        </td>
        <td className="px-4 py-2 text-right">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
            className="h-7 px-2 text-xs"
            data-testid={`open-flagged-${fs.student_id}`}
          >
            <Eye size={12} className="mr-1" /> Full view
          </Button>
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50">
          <td></td>
          <td colSpan={4} className="px-4 py-3">
            {loading ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <CircleNotch size={14} className="animate-spin" /> Loading mismatch details…
              </div>
            ) : !detail ? null : (
              <div className="space-y-3" data-testid={`inline-edit-${fs.student_id}`}>
                {(detail.fields || []).map(f => (
                  <div key={f.field} className="border border-amber-200 bg-amber-50 rounded-md p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <WarningCircle size={13} weight="fill" className="text-amber-600" />
                      <span className="text-xs font-semibold capitalize">{f.field === 'dob' ? 'Date of Birth' : f.field}</span>
                      <span className="text-[10px] text-gray-500">
                        ID: {f.filename || f.doc_type?.replace(/_/g, ' ') || '—'}
                      </span>
                    </div>
                    <div className="grid grid-cols-12 gap-3 items-start">
                      <div className="col-span-5 space-y-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Edit typed value</div>
                        {(_fieldToInputs[f.field] || []).map(key => {
                          const isDob = key === 'dob';
                          return (
                            <div key={key}>
                              <Label className="text-[10px] text-gray-600 mb-0.5 block">{_labels[key]}</Label>
                              <Input
                                type={isDob ? 'date' : 'text'}
                                value={draft[key] || ''}
                                onChange={(e) => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
                                className="h-8 text-sm"
                                data-testid={`inline-input-${fs.student_id}-${key}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="col-span-3">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">ID shows</div>
                        <div className="text-sm text-gray-900 break-words bg-white border border-gray-200 rounded p-2 min-h-[36px]">{f.id_value || '—'}</div>
                        {f.reason && <div className="text-[11px] text-amber-700 mt-1">{f.reason}</div>}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (f.field === 'dob') {
                              setDraft(prev => ({ ...prev, dob: f.id_value || '' }));
                              toast('Copied DOB into the editor. Review and click Save.', { icon: '✓' });
                            } else if (f.field === 'name') {
                              const raw = String(f.id_value || '').trim();
                              let first = '', middle = '', last = '';
                              if (raw.includes(',')) {
                                const [surname, rest] = raw.split(',', 2);
                                last = (surname || '').trim();
                                const parts = (rest || '').trim().split(/\s+/).filter(Boolean);
                                first = parts.shift() || '';
                                middle = parts.join(' ');
                              } else {
                                const parts = raw.split(/\s+/).filter(Boolean);
                                first = parts.shift() || '';
                                last = parts.pop() || '';
                                middle = parts.join(' ');
                              }
                              setDraft(prev => ({ ...prev, first_name: first, middle_name: middle, last_name: last }));
                              toast('Copied name into the editor. Review and click Save.', { icon: '✓' });
                            } else if (f.field === 'address') {
                              try {
                                const { data } = await axios.post(`${API}/admin/parse-address`, { address: f.id_value || '' });
                                setDraft(prev => ({
                                  ...prev,
                                  address_line1: data.address_line1 || prev.address_line1 || '',
                                  address_line2: data.address_line2 || prev.address_line2 || '',
                                  city: data.city || prev.city || '',
                                  province: data.province || prev.province || '',
                                  postal_code: data.postal_code || prev.postal_code || '',
                                }));
                                toast('Copied address into the editor. Review and click Save.', { icon: '✓' });
                              } catch (err) {
                                setDraft(prev => ({ ...prev, address_line1: f.id_value || prev.address_line1 }));
                                toast('Copied address (unparsed). Please review the fields.', { icon: '⚠️' });
                              }
                            }
                          }}
                          className="h-7 px-2 text-xs text-indigo-700 hover:bg-indigo-50 mt-1"
                          data-testid={`copy-id-${fs.student_id}-${f.field}`}
                        >
                          <ArrowCounterClockwise size={12} className="mr-1" /> Copy to editor
                        </Button>
                      </div>
                      <div className="col-span-4">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">ID document preview</div>
                        <IdDocPreview fileId={f.file_id} filename={f.filename} docType={f.doc_type} />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDraft({ ...(detail.editable || {}) })}
                    disabled={saving}
                    className="h-8 px-3 text-xs"
                    data-testid={`inline-reset-${fs.student_id}`}
                  >
                    Reset
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`inline-save-${fs.student_id}`}
                  >
                    {saving ? <><CircleNotch size={12} className="mr-1 animate-spin" /> Saving…</> : <><CheckCircle size={12} className="mr-1" /> Save & re-verify</>}
                  </Button>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// IdDocPreview — inline preview of an uploaded ID doc inside the Scan dialog.
// Fetches the file as a Blob (with the Authorization header) and renders:
//   * image/*  → <img> at the actual pixel dimensions, max-h-56
//   * pdf      → a short "Open PDF" + "Download" link, since embedding
//                PDFs reliably inside a dialog iframe is unreliable
//   * other    → filename + Download link
// Falls back silently if the fetch fails.
// ---------------------------------------------------------------------------
function IdDocPreview({ fileId, filename, docType }) {
  const [state, setState] = useState({ loading: false, url: null, mime: '', error: '' });
  useEffect(() => {
    if (!fileId) { setState({ loading: false, url: null, mime: '', error: '' }); return; }
    let revokedUrl = null;
    let cancelled = false;
    (async () => {
      setState({ loading: true, url: null, mime: '', error: '' });
      try {
        const res = await axios.get(`${API}/files/${fileId}/download`, { responseType: 'blob' });
        const mime = res.headers['content-type'] || '';
        const blob = new Blob([res.data], { type: mime });
        const url = URL.createObjectURL(blob);
        revokedUrl = url;
        if (!cancelled) setState({ loading: false, url, mime, error: '' });
      } catch (err) {
        if (!cancelled) setState({ loading: false, url: null, mime: '', error: err?.response?.status === 404 ? 'File not found' : 'Could not load preview' });
      }
    })();
    return () => { cancelled = true; if (revokedUrl) URL.revokeObjectURL(revokedUrl); };
  }, [fileId]);

  if (!fileId) {
    return <div className="text-[11px] text-gray-500 italic">No preview available</div>;
  }
  if (state.loading) {
    return <div className="flex items-center gap-2 text-[11px] text-gray-500"><CircleNotch size={12} className="animate-spin" /> Loading preview…</div>;
  }
  if (state.error) {
    return <div className="text-[11px] text-red-600">{state.error}</div>;
  }
  const isImage = (state.mime || '').startsWith('image/');
  const isPdf = (state.mime || '').includes('pdf') || (filename || '').toLowerCase().endsWith('.pdf');
  return (
    <div className="space-y-1">
      {isImage ? (
        <a href={state.url} target="_blank" rel="noreferrer">
          <img
            src={state.url}
            alt={filename || docType || 'ID preview'}
            className="w-full max-h-56 object-contain rounded border border-gray-200 bg-white"
            data-testid="id-doc-preview-img"
          />
        </a>
      ) : isPdf ? (
        <div className="bg-white border border-gray-200 rounded p-3 flex items-center gap-2" data-testid="id-doc-preview-pdf">
          <FilePdf size={28} className="text-red-500 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate text-gray-800">{filename || 'ID.pdf'}</div>
            <div className="flex gap-3 mt-1">
              <a href={state.url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-700 hover:underline">Open</a>
              <a href={state.url} download={filename || 'id.pdf'} className="text-[11px] text-indigo-700 hover:underline">Download</a>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded p-3 text-xs" data-testid="id-doc-preview-other">
          <div className="truncate font-medium">{filename || 'Document'}</div>
          <a href={state.url} download={filename || 'id'} className="text-[11px] text-indigo-700 hover:underline">Download</a>
        </div>
      )}
      {filename && <div className="text-[10px] text-gray-500 truncate">{filename}</div>}
    </div>
  );
}

